import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { StatusBadge, PriorityBadge } from '../components/ui/StatusBadge'
import Modal from '../components/ui/Modal'
import FileUpload from '../components/files/FileUpload'
import FileList from '../components/files/FileList'
import Comments from '../components/files/Comments'
import AIReportButton from '../components/ai/AIReportButton'
import CajaGuidePanel from '../components/caja/CajaGuidePanel'
import {
  ChevronRight, ChevronUp, ChevronDown, Plus, User, Calendar, FileText, MessageSquare,
  CheckSquare, Pencil, Trash2, LayoutList, Kanban, Trophy, Save, Edit3,
  Upload, Lock, Unlock, BarChart2, X, Target, CheckCircle2, Building2, Loader,
  TrendingUp, TrendingDown, Activity, Calendar as CalendarIcon, Database, Download, Sparkles,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import * as XLSX from 'xlsx'
import pptxgen from 'pptxgenjs'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  LineChart, Line, BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from 'recharts'

const STATUSES = ['pending', 'in_progress', 'review', 'completed', 'blocked']
const STATUS_LABEL = {
  pending: 'Pendiente', in_progress: 'En Progreso',
  review: 'En Revisión', completed: 'Completado', blocked: 'Bloqueado',
}
const KANBAN_COLS = [
  { key: 'pending',     label: 'Pendiente',   color: 'border-t-gray-400',   dot: 'bg-gray-400'   },
  { key: 'in_progress', label: 'En progreso', color: 'border-t-blue-500',   dot: 'bg-blue-500'   },
  { key: 'review',      label: 'En revisión', color: 'border-t-purple-500', dot: 'bg-purple-500' },
  { key: 'completed',   label: 'Completado',  color: 'border-t-green-500',  dot: 'bg-green-500'  },
  { key: 'blocked',     label: 'Bloqueado',   color: 'border-t-red-500',    dot: 'bg-red-500'    },
]

// ── Canonical P&L schema ──────────────────────────────────────────────────────
// Source of truth for the P&L hierarchy (level + bold + sign). Used in two
// places: when parsing an uploaded Excel (parseExcelPyl) and when loading a
// previously-saved P&L from the DB (so old records get the new visual
// hierarchy automatically). Keep this in sync with the Excel template the
// user provided ("Esquema PYL.xlsx", April 2026).
const PYL_CANONICAL = [
  { key: 'venta_bruta',       match: ['venta bruta'],                                            level: 0, bold: true,  isTotal: true,  sign: '+' },
  { key: 'descuentos',        match: ['descuentos'],                                              level: 0, bold: false, isTotal: false, sign: '-' },
  { key: 'venta_neta_1',      match: ['venta neta 1', 'vta neta 1'],                              level: 0, bold: true,  isTotal: true,  isBaseline: true, sign: '=' },
  { key: 'gasto_canal',       match: ['gasto canal', 'gastos canal'],                             level: 0, bold: false, isTotal: false, sign: '-' },
  { key: 'venta_neta_2',      match: ['venta neta 2', 'vta neta 2'],                              level: 0, bold: true,  isTotal: true,  sign: '=' },
  { key: 'costo_total',       match: ['costo total'],                                              level: 0, bold: true,  isTotal: true,  sign: '-' },
  { key: 'costo_directo',     match: ['costo directo'],                                            level: 1, bold: false, isTotal: false },
  { key: 'averias',           match: ['averías', 'averias'],                                       level: 1, bold: false, isTotal: false },
  { key: 'utilidad_bruta',    match: ['utilidad bruta', 'utulidad bruta'],                         level: 0, bold: true,  isTotal: true,  sign: '=' },
  { key: 'gastos_totales',    match: ['gastos totales'],                                           level: 0, bold: true,  isTotal: true,  sign: '-' },
  { key: 'gasto_mercadeo',    match: ['gasto de mercadeo', 'gastos de mercadeo'],                  level: 1, bold: true,  isTotal: false },
  { key: 'com_directa',       match: ['comunicación directa', 'comunicacion directa'],             level: 2, bold: false, isTotal: false },
  { key: 'com_masiva',        match: ['comunicación masiva', 'comunicacion masiva'],               level: 2, bold: false, isTotal: false },
  { key: 'com_canal',         match: ['comunicación canal', 'comunicacion canal'],                 level: 2, bold: false, isTotal: false },
  { key: 'inv_mercado',       match: ['investigación mercado', 'investigacion mercado'],           level: 2, bold: false, isTotal: false },
  { key: 'inv_consumidor',    match: ['investigación consumidor', 'investigacion consumidor'],     level: 2, bold: false, isTotal: false },
  { key: 'desarrollo',        match: ['desarrollo y mejoramiento', 'desarrollo de producto'],      level: 2, bold: false, isTotal: false },
  { key: 'gastos_op',         match: ['gastos operacionales'],                                     level: 1, bold: true,  isTotal: false },
  { key: 'transporte',        match: ['transporte'],                                                level: 2, bold: false, isTotal: false },
  { key: 'bodegaje',          match: ['bodegaje'],                                                  level: 2, bold: false, isTotal: false },
  { key: 'administrativos',   match: ['administrativos'],                                           level: 2, bold: false, isTotal: false },
  { key: 'gasto_financiero',  match: ['gasto financiero'],                                          level: 2, bold: false, isTotal: false },
  { key: 'otros_gastos',      match: ['otros gastos de ventas', 'otros gastos'],                    level: 2, bold: false, isTotal: false },
  { key: 'utilidad_neta',     match: ['utilidad neta antes impuestos', 'utilidad neta'],            level: 0, bold: true,  isTotal: true,  sign: '=' },
]

const _normPyl = s => String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim()

function matchPylCanonical(label) {
  const norm = _normPyl(label)
  if (!norm) return null
  for (const c of PYL_CANONICAL) {
    for (const m of c.match) {
      if (norm.includes(_normPyl(m))) return c
    }
  }
  return null
}

// PylCell — editable P&L cell that abbreviates large numbers (1.1M, 12k)
// and renders fractional ratios as percentages while not focused. On focus
// it switches to a raw <input type="number"> so the user can edit naturally.
//
// Props
//   value:    string|number — the underlying numeric value
//   isPct:    boolean       — render as % (multiply by 100 when value <= 1)
//   onChange: (newVal) => void — receives the raw string from the input
//   className: passed through to the input
function PylCell({ value, isPct, onChange, className = '' }) {
  const [focused, setFocused] = useState(false)
  const formatted = formatPylNumber(value, isPct)
  if (focused) {
    return (
      <input
        type="number"
        autoFocus
        defaultValue={value}
        onBlur={e => { setFocused(false); onChange(e.target.value) }}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
        className={className}
      />
    )
  }
  return (
    <button
      type="button"
      onClick={() => setFocused(true)}
      title={value === '' || value == null ? '' : `Valor exacto: ${value}`}
      className={`${className} cursor-text text-left tabular-nums`}
    >
      {formatted || <span className="text-gray-300">—</span>}
    </button>
  )
}

function formatPylNumber(v, isPct) {
  if (v === '' || v == null) return ''
  const n = Number(v)
  if (!Number.isFinite(n)) return String(v)
  if (isPct) {
    // Treat values |x| <= 1.5 as fractions (e.g. 0.0734 → 7.3%); larger
    // values are already in percentage form.
    const pct = Math.abs(n) <= 1.5 ? n * 100 : n
    return `${pct.toFixed(1)}%`
  }
  const abs = Math.abs(n)
  if (abs >= 1e12) return `${(n / 1e12).toFixed(2)}T`
  if (abs >= 1e9)  return `${(n / 1e9).toFixed(2)}B`
  if (abs >= 1e6)  return `${(n / 1e6).toFixed(1)}M`
  if (abs >= 1e3)  return `${(n / 1e3).toFixed(0)}k`
  return n.toLocaleString('es-CO', { maximumFractionDigits: 0 })
}

// Walk a saved pylRows array and fill in missing level/bold/isTotal/sign
// based on label match. Old records were stored before hierarchy existed, so
// this lets them render with proper indentation without a DB migration.
function enrichPylRows(rows) {
  if (!Array.isArray(rows)) return []
  return rows.map(r => {
    if (r.level !== undefined && r.isTotal !== undefined) return r  // already enriched
    const c = matchPylCanonical(r.label)
    return {
      ...r,
      level:   r.level   ?? c?.level   ?? 0,
      bold:    r.bold    ?? c?.bold    ?? false,
      isTotal: r.isTotal ?? c?.isTotal ?? false,
      sign:    r.sign    ?? c?.sign,
    }
  })
}

export default function SectionPage() {
  const { id, buId, phaseId, sectionId } = useParams()
  const navigate = useNavigate()
  const { profile, isNVC, isAdmin, isCaja } = useAuth()

  const [section, setSection]         = useState(null)
  const [phaseName, setPhaseName]     = useState('')
  const [buName, setBuName]           = useState('')
  const [tasks, setTasks]             = useState([])
  const [consultants, setConsultants] = useState([])
  const [allPhases, setAllPhases]     = useState([])   // all phases+sections for move picker
  const [allBUs, setAllBUs]           = useState([])   // all BUs for move picker
  const [loading, setLoading]         = useState(true)
  const [activeTab, setActiveTab]     = useState('tasks')
  const [viewMode, setViewMode]       = useState('list')
  const [phaseLocked, setPhaseLocked] = useState(false)
  const [changeLog, setChangeLog]     = useState([])
  const [changeLogLoading, setChangeLogLoading] = useState(false)
  const [importingPyl, setImportingPyl] = useState(false)

  const [showTaskModal, setShowTaskModal] = useState(false)
  const [editTask, setEditTask]           = useState(null)
  const [taskForm, setTaskForm]           = useState({
    name: '', description: '', assignee_id: '', due_date: '', priority: 'medium', status: 'pending',
  })
  const [moveBuId, setMoveBuId]           = useState('')   // target BU for move
  const [movePhaseId, setMovePhaseId]     = useState('')   // target phase for move
  const [moveSectionId, setMoveSectionId] = useState('')   // target section for move
  const [saving, setSaving] = useState(false)

  // Task resultado
  const [taskResultText, setTaskResultText]         = useState('')
  const [taskSavedResult, setTaskSavedResult]       = useState('')
  const [taskEditingResult, setTaskEditingResult]   = useState(false)
  const [taskSavingResult, setTaskSavingResult]     = useState(false)
  const [taskResultRecordId, setTaskResultRecordId] = useState(null)

  const [resultText, setResultText]         = useState('')
  const [savedResult, setSavedResult]       = useState('')
  const [editingResult, setEditingResult]   = useState(false)
  const [savingResult, setSavingResult]     = useState(false)
  const [resultRecordId, setResultRecordId] = useState(null)
  const [resultFileKey, setResultFileKey]   = useState(0)

  // AI draft generator (calls ai-report with reportType='draft_section')
  const [generatingDraft, setGeneratingDraft] = useState(false)
  const [draftError, setDraftError]           = useState('')

  // Bulk delete modal (for products / channels / competitors). Picks a name,
  // then deletes EVERY row in that table for the current section_id with
  // that name — so all years and all quarters of data get wiped at once.
  const [bulkDeleteOpen, setBulkDeleteOpen]   = useState(false)
  const [bulkDeleteKind, setBulkDeleteKind]   = useState(null) // 'product' | 'channel' | 'competitor'
  const [bulkDeleteNames, setBulkDeleteNames] = useState([])    // distinct names available
  const [bulkDeleteSel, setBulkDeleteSel]     = useState('')
  const [bulkDeleteBusy, setBulkDeleteBusy]   = useState(false)
  const [bulkDeleteErr, setBulkDeleteErr]     = useState('')

  // Market matrix
  const [markets, setMarkets]               = useState([])
  const [showMarketModal, setShowMarketModal] = useState(false)
  const [editMarket, setEditMarket]         = useState(null)
  const [marketForm, setMarketForm]         = useState({ name: '', sales_size: '', description: '' })
  const [savingMarket, setSavingMarket]     = useState(false)
  const [matrixFinalized, setMatrixFinalized] = useState(false)
  const [finalizingMatrix, setFinalizingMatrix] = useState(false)

  // Market selection
  const [sourceMarkets, setSourceMarkets]       = useState([])   // from Matriz de Mercados sibling section
  const [selectedMarketIds, setSelectedMarketIds] = useState(new Set())
  const [selectionRecords, setSelectionRecords]   = useState([]) // DB records { id, market_id, bu_id }
  const [creatingBUs, setCreatingBUs]           = useState(false)
  const [consultancyId, setConsultancyId]       = useState(null)
  const [consultancyName, setConsultancyName]   = useState('')

  // Market performance
  const EMPTY_PERF = { year: new Date().getFullYear(), market_size: '', size_unit: 'USD millones', volume: '', volume_unit: 'toneladas', reference_year: new Date().getFullYear(), data_source: '', maturity: '', has_seasonality: false, seasonality_months: '' }
  const [perfRecord, setPerfRecord]       = useState(null)   // DB record id
  const [selectedVariable, setSelectedVariable] = useState('ventas') // 'ventas' | 'volumen' | 'precio'
  const [perfData, setPerfData]           = useState(EMPTY_PERF)
  const [editingPerf, setEditingPerf]     = useState(false)
  const [savingPerf, setSavingPerf]       = useState(false)
  const [perfForm, setPerfForm]           = useState(EMPTY_PERF)

  // Market products
  const EMPTY_PRODUCT = { name: '', sales_size: '', size_unit: 'USD millones', volume: '', volume_unit: 'toneladas', reference_year: new Date().getFullYear(), data_source: '', cagr: '', last_year_growth: '', description: '' }
  const [products, setProducts]                 = useState([])
  const [showProductModal, setShowProductModal] = useState(false)
  const [editProduct, setEditProduct]           = useState(null)
  const [productForm, setProductForm]           = useState(EMPTY_PRODUCT)
  const [savingProduct, setSavingProduct]       = useState(false)

  // Year navigation (shared across all special tabs)
  const CURRENT_YEAR = new Date().getFullYear()
  const [selectedYear, setSelectedYear]         = useState(CURRENT_YEAR)
  const [selectedQuarter, setSelectedQuarter]   = useState(null) // null=Anual, 1=Q1, 2=Q2, 3=Q3, 4=Q4
  // Historical data for evolution charts
  const [perfHistory, setPerfHistory]           = useState([]) // [{data_year, data_quarter, market_size, size_unit}]
  const [productsHistory, setProductsHistory]   = useState([]) // [{year, total_size, count, size_unit}]
  const [competitorsHistory, setCompetitorsHistory] = useState([]) // [{year, count, total_coverage}]
  const [channelsHistory, setChannelsHistory]   = useState([]) // [{year, count}]
  // Trend data for variable-based charts (all years, per entity)
  const [productsTrendData, setProductsTrendData]     = useState([]) // [{year, sizeUnit, volUnit, products:{name:{sales,volume}}}]
  const [channelsTrendData, setChannelsTrendData]     = useState([]) // [{year, channels:{name:{weight,count}}}]
  const [competitorsTrendData, setCompetitorsTrendData] = useState([]) // [{year, competitors:{name:share}}]
  const [prodTrendVar, setProdTrendVar]               = useState('ventas') // 'ventas' | 'volumen'
  const [compTrendVar, setCompTrendVar]               = useState('share')  // 'share'

  // Market channels
  const [channels, setChannels]                     = useState([])
  const [channelWeights, setChannelWeights]         = useState({}) // { channelId: { productId: weight% } }
  const [channelSiblingProducts, setChannelSiblingProducts] = useState([])
  const [showChannelModal, setShowChannelModal]     = useState(false)
  const [editChannel, setEditChannel]               = useState(null)
  const [channelForm, setChannelForm]               = useState({ name: '', description: '', num_clientes: '', clientes_ponderado: '' })
  const [expandedClientChannelId, setExpandedClientChannelId] = useState(null)
  const [uploadingClients, setUploadingClients]     = useState(false)
  const [channelWeightForm, setChannelWeightForm]   = useState({}) // { productId: weightValue }
  const [savingChannel, setSavingChannel]           = useState(false)
  const [activeChannelFilter, setActiveChannelFilter] = useState('all')

  // Market competitors
  const [competitors, setCompetitors]               = useState([])
  const [competitorShares, setCompetitorShares]     = useState({}) // { competitorId: { productId: share% } }
  const [competitorPrices, setCompetitorPrices]     = useState({}) // { competitorId: { productId: { avg_price, price_unit } } }
  const [siblingProducts, setSiblingProducts]       = useState([]) // products from sibling "Soluciones" section
  const [showCompetitorModal, setShowCompetitorModal] = useState(false)
  const [editCompetitor, setEditCompetitor]         = useState(null)
  const [competitorForm, setCompetitorForm]         = useState({ name: '', description: '', is_own_brand: false })
  const [competitorShareForm, setCompetitorShareForm] = useState({}) // { productId: shareValue }
  const [competitorPriceForm, setCompetitorPriceForm] = useState({}) // { productId: { avg_price, price_unit } }
  const [savingCompetitor, setSavingCompetitor]     = useState(false)
  const [activeProductFilter, setActiveProductFilter] = useState('all') // 'all' or productId

  // Posicionamiento section — structured competitor positioning
  const PRECIO_RELATIVO_OPTIONS = [
    { value: 'premium',   label: 'Premium',   color: 'text-amber-700 bg-amber-50 border-amber-300' },
    { value: 'valor',     label: 'Valor',     color: 'text-blue-700 bg-blue-50 border-blue-300' },
    { value: 'economia',  label: 'Economía',  color: 'text-emerald-700 bg-emerald-50 border-emerald-300' },
  ]
  const EMPTY_POSITIONING = {
    propuesta_valor: '', segmento_objetivo: '', territorio_marca: '',
    precio_relativo: 'valor', atributos_clave: '', diferenciadores: '',
    fortalezas: '', debilidades: '', promesa_marca: '', espacios_no_ocupados: '',
  }
  // v2 schema (stored in competitor_positioning.pos_v2 JSONB).
  // Aristas are arrays of { name, tom_pct } so the % per arista lives next
  // to the name and survives reorders. The "Nivel de asociación" UI below
  // the aristas just edits the same array's tom_pct field.
  const EMPTY_POS_V2 = {
    segmentos: '',
    posicionamiento: '',
    aristas_desempeno: [],   // [{ name, tom_pct }]
    aristas_afinidad:  [],   // [{ name, tom_pct }]
    beneficio_funcional: '',
    beneficio_emocional: '',
    reason_to_believe: '',
    tom_conocimiento_espontaneo: '',
  }
  const [positioning, setPositioning]         = useState(null)   // loaded record or null
  const [posForm, setPosForm]                 = useState(EMPTY_POSITIONING)
  const [posV2, setPosV2]                     = useState(EMPTY_POS_V2)
  const [savingPos, setSavingPos]             = useState(false)
  const [posSaved, setPosSaved]               = useState(false)

  // Portafolio section — competitor portfolio products
  const [portfolioProducts, setPortfolioProducts]         = useState([])
  const [portfolioExpandedId, setPortfolioExpandedId]     = useState(null)
  const [portfolioForms, setPortfolioForms]               = useState({}) // { productId: { ...fields } }
  const [savingPortfolio, setSavingPortfolio]             = useState({}) // { productId: bool }
  const [portfolioImporting, setPortfolioImporting]       = useState(false)
  const [portfolioSavedId, setPortfolioSavedId]           = useState(null)

  // Strategic Objective section
  const CURRENT_YEAR_NOW = new Date().getFullYear()
  const OBJ_REAL_YEARS   = [CURRENT_YEAR_NOW - 3, CURRENT_YEAR_NOW - 2, CURRENT_YEAR_NOW - 1]
  const OBJ_GOAL_YEARS   = [CURRENT_YEAR_NOW, CURRENT_YEAR_NOW + 1, CURRENT_YEAR_NOW + 2, CURRENT_YEAR_NOW + 3, CURRENT_YEAR_NOW + 4]
  const EMPTY_OBJ = { vision: '', mision: '', declaracion: '', estrategia: '', generated_text: '' }
  const [stratObj, setStratObj]               = useState(null)   // DB record
  const [stratObjForm, setStratObjForm]       = useState(EMPTY_OBJ)
  const [stratMetrics, setStratMetrics]       = useState({})     // { year_type: { ventas, ventas_unit, participacion_pct, ventas_objetivo, participacion_objetivo, notes } }
  const [savingStratObj, setSavingStratObj]   = useState(false)
  const [stratObjSaved, setStratObjSaved]     = useState(false)
  const [generatingObj, setGeneratingObj]     = useState(false)
  const [pillars, setPillars]                 = useState([])     // strategic pillars for BU-level
  const [newPillarForm, setNewPillarForm]     = useState({ name: '', description: '', kpi: '' })
  const [savingPillar, setSavingPillar]       = useState(false)

  // Tab conclusions
  const [tabConclusions, setTabConclusions]             = useState({})
  const [savingConclusion, setSavingConclusion]         = useState({})
  const [conclusionSaved, setConclusionSaved]           = useState({})
  const [generatingConclusion, setGeneratingConclusion] = useState({})

  // BU Price Strategy section (Definición de Estrategia > Precio)
  const PRICE_GOAL_YEARS = [CURRENT_YEAR_NOW, CURRENT_YEAR_NOW + 1, CURRENT_YEAR_NOW + 2]
  const [priceStratRecord, setPriceStratRecord]       = useState(null)
  const [priceStratProducts, setPriceStratProducts]   = useState([]) // [{id, nombre, precio_actual, moneda, posicion, objetivos:[{año, precio, moneda, posicion}]}]
  const [savingPriceStrat, setSavingPriceStrat]       = useState(false)
  const [priceStratSaved, setPriceStratSaved]         = useState(false)

  // BU Strategy Portfolio section (Definición de Estrategia > Portafolio)
  const [stratPortRecord, setStratPortRecord]         = useState(null)
  const [stratPortProducts, setStratPortProducts]     = useState([])  // [{id, nombre, decision, rationale, palancas:[]}]
  const [stratPortNewRefs, setStratPortNewRefs]       = useState([])  // [{id, nombre, tipo_ref, rationale, palancas:[]}]
  const [savingStratPort, setSavingStratPort]         = useState(false)
  const [stratPortSaved, setStratPortSaved]           = useState(false)

  // Competitor Precio section — comparison chart (read-only, auto-calculated)
  const [compPrecioData, setCompPrecioData] = useState([])  // [{ product_name, mkt_price, mkt_unit, comp_price, comp_unit, delta_pct }]

  // Market Comunicación section — media mix
  const DEFAULT_MEDIA_CHANNELS = ['Televisión', 'Radio', 'Digital Paid', 'Redes Sociales Orgánicas', 'OOH / Vía Pública', 'PR / Prensa', 'Influencers', 'Email / CRM', 'Eventos', 'Punto de Venta']
  const DIGITAL_PLATFORMS = ['Instagram', 'Facebook', 'TikTok', 'LinkedIn', 'YouTube', 'X / Twitter', 'WhatsApp', 'Pinterest']
  const [mktMediaMix, setMktMediaMix]               = useState([])   // market_media_mix rows
  const [mktMediaForm, setMktMediaForm]             = useState([])   // editable rows { id?, medium_name, weight_pct, investment_ref, notes }
  const [savingMktMedia, setSavingMktMedia]         = useState(false)
  const [mktMediaSaved, setMktMediaSaved]           = useState(false)

  // Competitor Estrategia de Comunicación section
  const EMPTY_COMM = { tono_voz: '', mensajes_clave: '', propuesta_valor_comunicada: '', nivel_inversion: '', tipo_contenido: '', tematicas: '', influencers: '', campanas_destacadas: '', share_of_voice: '', fortalezas_comunicacion: '', debilidades_comunicacion: '' }
  const TONO_OPTS   = ['Formal', 'Cercano', 'Experto', 'Emocional', 'Humorístico', 'Inspiracional']
  const INVERSION_OPTS = ['Bajo', 'Medio', 'Alto', 'Muy alto']
  const CONTENIDO_OPTS = ['Video', 'Imagen', 'Carrusel', 'UGC', 'Texto', 'Mixto']
  const [commStrategy, setCommStrategy]             = useState(null)
  const [commForm, setCommForm]                     = useState(EMPTY_COMM)
  const [compMediaMix, setCompMediaMix]             = useState([])   // competitor_media_mix rows
  const [compMediaForms, setCompMediaForms]         = useState({})   // { id: weight_pct }
  const [mktMediaRef, setMktMediaRef]               = useState([])   // market reference media mix
  const [digitalPresence, setDigitalPresence]       = useState([])   // competitor_digital_presence rows
  const [digitalForms, setDigitalForms]             = useState({})   // { id: { followers, engagement_rate, posts_per_week } }
  const [savingComm, setSavingComm]                 = useState(false)
  const [commSaved, setCommSaved]                   = useState(false)
  const [commImporting, setCommImporting]           = useState(false)

  // Competitor Capacidad Industrial
  const EMPTY_CAP = {
    bloque1_aplica: true, num_plantas: '', ubicacion_plantas: '', capacidad_instalada: '', capacidad_unidad: 'toneladas/año', utilizacion_pct: '',
    bloque2_aplica: true, nivel_tecnologico: '', grado_automatizacion: '', antiguedad_equipos: '', descripcion_tecnologia: '',
    bloque3_aplica: true, certificaciones: [], estandares_calidad: '',
    bloque4_aplica: true, integracion_vertical: '', flexibilidad_linea: '', capacidad_expansion: '', notas_integracion: '',
    bloque5_aplica: true, ventajas_productivas: '', brechas_limitaciones: '',
  }
  const CERT_OPTS = ['ISO 9001', 'ISO 14001', 'ISO 45001', 'FSSC 22000', 'BPM', 'HACCP', 'GMP', 'BASC', 'Kosher', 'Halal']
  const NIVEL_TEC_OPTS = ['Básico', 'Intermedio', 'Avanzado', 'Altamente automatizado']
  const INTEG_OPTS = ['Baja', 'Media', 'Alta']
  const [capIndustrial, setCapIndustrial]           = useState(null)
  const [capForm, setCapForm]                       = useState(EMPTY_CAP)
  const [savingCap, setSavingCap]                   = useState(false)
  const [capSaved, setCapSaved]                     = useState(false)

  // BU P&L section
  const [pylRecord, setPylRecord]   = useState(null)
  const [pylCols, setPylCols]       = useState([])   // [{year, colNames:[]}]
  const [pylRows, setPylRows]       = useState([])   // [{id, label, bold, level, isTotal, sign, values:{}}]
  const [pylUnit, setPylUnit]       = useState('USD millones')
  const [savingPyl, setSavingPyl]   = useState(false)
  const [pylSaved, setPylSaved]     = useState(false)

  // BU Internal comm strategy (Análisis Interno — same schema as competitor)
  // Consumer Needs & Tensions (per profile)
  const NEEDS_EMPTY_FORM = { profile_name: '', bloque1_aplica: true, necesidades_funcionales: [], bloque2_aplica: true, necesidades_emocionales: [], bloque3_aplica: true, tensiones: [], bloque4_aplica: true, jtbd_descripcion: '', jtbd_cuando: '', jtbd_alternativas: '', bloque5_aplica: true, insight_clave: '' }
  const IMPORTANCIA_OPTS = ['Alta', 'Media', 'Baja']
  const TENSION_ORIGEN_OPTS = ['Precio', 'Disponibilidad', 'Confianza', 'Hábito', 'Información', 'Oferta insuficiente', 'Otro']
  const [needsProfiles, setNeedsProfiles]           = useState([])
  const [activeNeedsId, setActiveNeedsId]           = useState(null)
  const [needsForms, setNeedsForms]                 = useState({})  // { profileId: form }
  const [savingNeeds, setSavingNeeds]               = useState(false)
  const [needsSaved, setNeedsSaved]                 = useState(false)

  // Consumer Brand Adoption (Adopción de Marca y Nivel de Lealtad — per profile)
  const BRAND_ADOPTION_EMPTY_FORM = { profile_name: '', bloque1_aplica: true, atributos_marca: [], velocidad_adopcion: '', bloque2_aplica: true, nivel_lealtad: '', razones_fidelidad: '', bloque3_aplica: true, drivers_cambio: '', frecuencia_cambio: '', bloque4_aplica: true, influenciadores: '' }
  const ATRIBUTO_MARCA_TIPO_OPTS = ['Calidad / desempeño', 'Precio / valor', 'Disponibilidad', 'Reputación / confianza', 'Diseño / empaque', 'Innovación', 'Servicio / soporte', 'Sostenibilidad']
  const VELOCIDAD_OPTS       = ['Innovador', 'Early adopter', 'Espera referencias', 'Conservador', 'Solo si no hay otra opción']
  const LEALTAD_OPTS         = ['Muy leal', 'Leal', 'Indiferente', 'Propenso a cambiar', 'Cambia frecuentemente']
  const FREC_CAMBIO_OPTS     = ['Nunca', 'Rara vez', 'A veces', 'Con frecuencia', 'Siempre busca algo nuevo']
  const [brandProfiles, setBrandProfiles]   = useState([])
  const [activeBrandId, setActiveBrandId]   = useState(null)
  const [brandForms, setBrandForms]         = useState({})
  const [savingBrand, setSavingBrand]       = useState(false)
  const [brandSaved, setBrandSaved]         = useState(false)

  // Consumer Category Adoption (Adopción de la Categoría — per profile)
  const ADOPTION_EMPTY_FORM = { profile_name: '', bloque1_aplica: true, penetracion_pct: '', frecuencia_compra: '', volumen_por_ocasion: '', bloque2_aplica: true, satisfaccion_nivel: '', satisfaccion_gaps: '', bloque3_aplica: true, riesgos_abandono: '', bloque4_aplica: true, influenciadores: [] }
  const INFLUENCIADOR_TIPO_OPTS = ['Familia / amigos', 'Redes sociales', 'Expertos / profesionales', 'Publicidad', 'Punto de venta', 'Reviews / boca a boca', 'Tendencias', 'Precio / promoción']
  const FRECUENCIA_OPTS   = ['Diaria', 'Semanal', 'Quincenal', 'Mensual', 'Ocasional']
  const SATISFACCION_OPTS = ['Muy alta', 'Alta', 'Media', 'Baja', 'Muy baja']
  const [adoptProfiles, setAdoptProfiles]   = useState([])
  const [activeAdoptId, setActiveAdoptId]   = useState(null)
  const [adoptForms, setAdoptForms]         = useState({})
  const [savingAdopt, setSavingAdopt]       = useState(false)
  const [adoptSaved, setAdoptSaved]         = useState(false)

  // Consumer Solution (Sobre la Solución — per profile)
  const SOLUTION_EMPTY_FORM = { profile_name: '', bloque1_aplica: true, drivers: [], bloque2_aplica: true, barreras: [], bloque3_aplica: true, motivadores: '', bloque4_aplica: true, proceso_decision: '', bloque5_aplica: true, ocasiones_consumo: '' }
  const NIVEL_OPTS = ['Alta', 'Media', 'Baja']
  const [solProfiles, setSolProfiles]       = useState([])
  const [activeSolId, setActiveSolId]       = useState(null)
  const [solForms, setSolForms]             = useState({})   // { profileId: form }
  const [savingSol, setSavingSol]           = useState(false)
  const [solSaved, setSolSaved]             = useState(false)

  const [buCommStrategy, setBuCommStrategy]         = useState(null)
  const [buCommForm, setBuCommForm]                 = useState(EMPTY_COMM)
  const [buMediaMix, setBuMediaMix]                 = useState([])   // bu_media_mix rows
  const [buMediaForms, setBuMediaForms]             = useState({})   // { id: pct }
  const [buDigitalPresence, setBuDigitalPresence]   = useState([])   // bu_digital_presence rows
  const [buDigitalForms, setBuDigitalForms]         = useState({})   // { id: { followers, engagement_rate, posts_per_week } }
  const [savingBuComm, setSavingBuComm]             = useState(false)
  const [buCommSaved, setBuCommSaved]               = useState(false)

  // Competitor Canales section — channel matrix per competitor
  const [compChanPresence, setCompChanPresence]         = useState([])   // competitor_channel_presence rows
  const [compChanWeights, setCompChanWeights]           = useState({})   // { presenceId: { productId: weight } }
  const [compChanForms, setCompChanForms]               = useState({})   // { presenceId: { num_clientes, clientes_ponderado } }
  const [compChanWeightForms, setCompChanWeightForms]   = useState({})   // { presenceId: { productId: weight } }
  const [mktChanRef, setMktChanRef]                     = useState([])   // market channels reference
  const [mktChanWeightsRef, setMktChanWeightsRef]       = useState({})   // { mktChannelId: { productId: weight } }
  const [mktProdRef, setMktProdRef]                     = useState([])   // market products reference
  const [savingCompChans, setSavingCompChans]           = useState(false)
  const [compChansSaved, setCompChansSaved]             = useState(false)
  const [compChansImporting, setCompChansImporting]     = useState(false)

  // Segmentación section — consumer profiles managed here
  const [segProfiles, setSegProfiles]           = useState([])  // consumer_profiles for this phase
  const [showSegProfileModal, setShowSegProfileModal] = useState(false)
  const [segProfileForm, setSegProfileForm]     = useState({ name: '', description: '' })
  const [savingSegProfile, setSavingSegProfile] = useState(false)

  // Market Precio section
  const [precioMktHistory, setPrecioMktHistory]           = useState([]) // [{year, avg_price, unit}] global market
  const [precioProductHistory, setPrecioProductHistory]   = useState([]) // [{year, name, avg_price, price_unit}] per product
  const [precioCompHistory, setPrecioCompHistory]         = useState([]) // [{year, competitor_name, product_name, product_id, avg_price, price_unit}]
  const [precioActiveProduct, setPrecioActiveProduct]     = useState('__all__') // product name or '__all__'

  // Industry Definition section
  const [industryDef, setIndustryDef]               = useState(null)
  const [industryDefForm, setIndustryDefForm]       = useState({ descripcion: '', tamano_ventas: '', unidad: 'USD' })
  const [savingIndustryDef, setSavingIndustryDef]   = useState(false)
  const [editingIndustryDef, setEditingIndustryDef] = useState(false)

  const isProjectPhase = !buId
  const dragTaskId = useRef(null)
  const posExcelInputRef = useRef(null)

  useEffect(() => { fetchData() }, [sectionId])
  useEffect(() => { if (section) fetchData() }, [selectedYear, selectedQuarter])
  useEffect(() => {
    if (!section) return
    const nn = (section.name ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    if (nn.includes('definicion') && nn.includes('industria')) setActiveTab('def_industria')
  }, [section?.id])

  async function fetchData() {
    setLoading(true)
    const baseQueries = [
      supabase.from('sections').select('*').eq('id', sectionId).single(),
      supabase.from('tasks').select('*, assignee:profiles!tasks_assignee_id_fkey(full_name)').eq('section_id', sectionId).order('task_order'),
      supabase.from('profiles').select('id, full_name').in('role', ['admin', 'consultant', 'client']),
      supabase.from('phases').select('name, consultancy_id').eq('id', phaseId).single(),
    ]
    if (buId) baseQueries.push(supabase.from('business_units').select('name').eq('id', buId).single())

    const [{ data: sec }, { data: tks }, { data: cons }, { data: ph }, buResult] = await Promise.all(baseQueries)
    setSection(sec)
    setTasks(tks ?? [])
    setConsultants(cons ?? [])
    setPhaseName(ph?.name ?? '')
    setBuName(buResult?.data?.name ?? '')
    setMatrixFinalized(sec?.matrix_finalized ?? false)
    if (ph?.consultancy_id) {
      setConsultancyId(ph.consultancy_id)
      // Load the consultancy display name for the breadcrumb (so we can show
      // "Proyecto: Grupo Ersa" instead of generic "Consultoría").
      supabase.from('consultancies').select('client_company, name').eq('id', ph.consultancy_id).single()
        .then(({ data }) => setConsultancyName(data?.client_company || data?.name || ''))
    }

    // Load markets if this is a market matrix section
    if (sec?.name?.toLowerCase().includes('matriz de mercados')) {
      const { data: mks } = await supabase
        .from('industry_markets')
        .select('*')
        .eq('section_id', sectionId)
        .order('market_order')
      setMarkets(mks ?? [])
    }

    // Load source markets + selections if this is the market selection section
    const normalize = s => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    if (normalize(sec?.name ?? '').includes('seleccion de mercados')) {
      // Find sibling section "Matriz de Mercados de la Industria" in the same phase
      const { data: siblings } = await supabase
        .from('sections')
        .select('id, name')
        .eq('phase_id', phaseId)
      const matrizSec = (siblings ?? []).find(s => normalize(s.name).includes('matriz de mercados'))
      if (matrizSec) {
        const { data: srcMarkets } = await supabase
          .from('industry_markets')
          .select('*')
          .eq('section_id', matrizSec.id)
          .order('sales_size', { ascending: false })
        setSourceMarkets(srcMarkets ?? [])
      }
      // Load existing selections for this section
      const { data: sels } = await supabase
        .from('market_selections')
        .select('*, bu:business_units(id, name)')
        .eq('selection_section_id', sectionId)
      const recs = sels ?? []
      setSelectionRecords(recs)
      setSelectedMarketIds(new Set(recs.map(r => r.market_id)))
    }

    // Helper: apply quarter filter to a Supabase query
    // In Anual view (null): no stored annual records exist (Opción A), returns empty → UI shows prompt
    // Desempeño handles Anual aggregation separately with custom logic above
    const withQtr = (q) => selectedQuarter == null
      ? q.is('data_quarter', null)   // returns empty in Anual view (no annual records stored)
      : q.eq('data_quarter', selectedQuarter)

    // Load market performance if this is a desempeño del mercado section
    if (normalize(sec?.name ?? "").includes("desempeno") && normalize(sec?.name ?? "").includes("mercado")) {
      if (selectedQuarter === null) {
        // Anual = aggregate (sum) of all quarters for selected year
        const { data: quarterRecs } = await supabase
          .from('market_performance').select('*').eq('section_id', sectionId).eq('data_year', selectedYear)
          .not('data_quarter', 'is', null)
        if (quarterRecs && quarterRecs.length > 0) {
          const totalSize   = quarterRecs.reduce((s, r) => s + (Number(r.market_size) || 0), 0)
          const totalVol    = quarterRecs.reduce((s, r) => s + (Number(r.volume) || 0), 0)
          const ref         = quarterRecs[0]
          const filled = {
            market_size:        totalSize,
            size_unit:          ref.size_unit ?? 'USD millones',
            volume:             totalVol || '',
            volume_unit:        ref.volume_unit ?? 'toneladas',
            reference_year:     ref.reference_year ?? selectedYear,
            data_source:        ref.data_source ?? '',
            maturity:           ref.maturity ?? '',
            has_seasonality:    quarterRecs.some(r => r.has_seasonality),
            seasonality_months: ref.seasonality_months ?? '',
          }
          setPerfRecord('__aggregated__')   // sentinel: prevents edit form, enables KPI view
          setPerfData(filled)
          setPerfForm(filled)
        } else {
          // No quarterly records — try annual record (data_quarter IS NULL, imported from Excel)
          const { data: annualRec } = await supabase
            .from('market_performance').select('*').eq('section_id', sectionId)
            .eq('data_year', selectedYear).is('data_quarter', null).maybeSingle()
          if (annualRec) {
            const filled = {
              market_size:        annualRec.market_size ?? '',
              size_unit:          annualRec.size_unit ?? 'USD millones',
              volume:             annualRec.volume ?? '',
              volume_unit:        annualRec.volume_unit ?? 'toneladas',
              reference_year:     annualRec.reference_year ?? selectedYear,
              data_source:        annualRec.data_source ?? '',
              maturity:           annualRec.maturity ?? '',
              has_seasonality:    annualRec.has_seasonality ?? false,
              seasonality_months: annualRec.seasonality_months ?? '',
            }
            setPerfRecord(annualRec.id)
            setPerfData(filled)
            setPerfForm(filled)
          } else {
            // Last fallback: any record for this section regardless of year (imported data may have different year)
            const { data: anyRec } = await supabase
              .from('market_performance').select('*').eq('section_id', sectionId)
              .is('data_quarter', null).order('data_year', { ascending: false }).limit(1)
            const rec = anyRec?.[0] ?? null
            if (rec) {
              const filled = {
                market_size:        rec.market_size ?? '',
                size_unit:          rec.size_unit ?? 'USD millones',
                volume:             rec.volume ?? '',
                volume_unit:        rec.volume_unit ?? 'toneladas',
                reference_year:     rec.reference_year ?? rec.data_year,
                data_source:        rec.data_source ?? '',
                maturity:           rec.maturity ?? '',
                has_seasonality:    rec.has_seasonality ?? false,
                seasonality_months: rec.seasonality_months ?? '',
              }
              setPerfRecord(rec.id)
              setPerfData(filled)
              setPerfForm(filled)
            } else {
              setPerfRecord(null)
              setPerfData(EMPTY_PERF)
              setPerfForm(EMPTY_PERF)
            }
          }
        }
      } else {
        const { data: perf } = await supabase
          .from('market_performance').select('*').eq('section_id', sectionId)
          .eq('data_year', selectedYear).eq('data_quarter', selectedQuarter).maybeSingle()
        if (perf) {
          setPerfRecord(perf.id)
          const filled = { market_size: perf.market_size ?? '', size_unit: perf.size_unit ?? 'USD millones', volume: perf.volume ?? '', volume_unit: perf.volume_unit ?? 'toneladas', reference_year: perf.reference_year ?? new Date().getFullYear(), data_source: perf.data_source ?? '', maturity: perf.maturity ?? '', has_seasonality: perf.has_seasonality ?? false, seasonality_months: perf.seasonality_months ?? '' }
          setPerfData(filled)
          setPerfForm(filled)
        } else {
          setPerfRecord(null)
          setPerfData(EMPTY_PERF)
          setPerfForm(EMPTY_PERF)
        }
      }
      // Load full history — all fields needed for trend charts and table
      const { data: allPerf } = await supabase
        .from('market_performance').select('*')
        .eq('section_id', sectionId)
        .order('data_year', { ascending: false })
      setPerfHistory(allPerf ?? [])
    }

    // Load market products if this is a Soluciones (Producto) section
    if (normalize(sec?.name ?? "").includes("soluciones") || normalize(sec?.name ?? "").includes("producto")) {
      const prodsQ = withQtr(
        supabase.from('market_products').select('*').eq('section_id', sectionId).eq('data_year', selectedYear)
      )
      const { data: prods } = await prodsQ.order('product_order')
      setProducts(prods ?? [])
      // Load history for evolution chart — aggregate quarterly data per year
      const { data: allProds } = await supabase
        .from('market_products').select('data_year, data_quarter, sales_size, size_unit, name')
        .eq('section_id', sectionId).not('data_quarter', 'is', null).order('data_year')
      const byYear = {}
      ;(allProds ?? []).forEach(p => {
        const y = p.data_year ?? CURRENT_YEAR
        if (!byYear[y]) byYear[y] = { year: y, total_size: 0, count: 0, size_unit: p.size_unit ?? 'USD millones' }
        byYear[y].total_size += Number(p.sales_size) || 0
        byYear[y].count++
      })
      setProductsHistory(Object.values(byYear).sort((a, b) => a.year - b.year))
      // Load ALL products across ALL years for variable-based trend charts
      const { data: allProdsTrend } = await supabase
        .from('market_products')
        .select('data_year, name, sales_size, size_unit, volume, volume_unit')
        .eq('section_id', sectionId)
        .order('data_year')
      const trendByYear = {}
      ;(allProdsTrend ?? []).forEach(p => {
        const y = p.data_year ?? CURRENT_YEAR
        if (!trendByYear[y]) trendByYear[y] = { year: y, sizeUnit: p.size_unit ?? 'USD millones', volUnit: p.volume_unit ?? 'toneladas', products: {} }
        if (!trendByYear[y].products[p.name]) trendByYear[y].products[p.name] = { sales: 0, volume: 0 }
        trendByYear[y].products[p.name].sales  += Number(p.sales_size) || 0
        trendByYear[y].products[p.name].volume += Number(p.volume)     || 0
      })
      setProductsTrendData(Object.values(trendByYear).sort((a, b) => a.year - b.year))
    }

    // Load channels if this is a Canales section
    if (normalize(sec?.name ?? "").includes("canal")) {
      const { data: siblings } = await supabase.from('sections').select('id, name').eq('phase_id', phaseId)
      const solucionesSec = (siblings ?? []).find(s =>
        normalize(s.name).includes('soluciones') || normalize(s.name).includes('producto')
      )
      if (solucionesSec) {
        const sibProdsQ = withQtr(
          supabase.from('market_products').select('*').eq('section_id', solucionesSec.id).eq('data_year', selectedYear)
        )
        const { data: sibProds } = await sibProdsQ.order('product_order')
        setChannelSiblingProducts(sibProds ?? [])
      }
      const chansQ = withQtr(
        supabase.from('market_channels').select('*').eq('section_id', sectionId).eq('data_year', selectedYear)
      )
      const { data: chans } = await chansQ.order('channel_order')
      const chanList = chans ?? []
      setChannels(chanList)
      if (chanList.length > 0) {
        const { data: weights } = await supabase
          .from('channel_product_weights').select('*').in('channel_id', chanList.map(c => c.id))
        const wMap = {}
        ;(weights ?? []).forEach(w => {
          if (!wMap[w.channel_id]) wMap[w.channel_id] = {}
          wMap[w.channel_id][w.product_id] = w.weight
        })
        setChannelWeights(wMap)
      }
      // Load history — aggregate quarterly data per year (count distinct channels)
      const { data: allChans } = await supabase
        .from('market_channels').select('data_year, name').eq('section_id', sectionId).not('data_quarter', 'is', null)
      const chanByYear = {}
      ;(allChans ?? []).forEach(c => {
        const y = c.data_year ?? CURRENT_YEAR
        if (!chanByYear[y]) chanByYear[y] = new Set()
        chanByYear[y].add(c.name)
      })
      setChannelsHistory(Object.entries(chanByYear).map(([y, names]) => ({ year: parseInt(y), count: names.size })).sort((a, b) => a.year - b.year))
      // Load ALL channels across ALL years for variable-based trend charts
      const { data: allChansTrend } = await supabase
        .from('market_channels').select('id, data_year, name').eq('section_id', sectionId).order('data_year')
      const chanTrendIds = (allChansTrend ?? []).map(c => c.id)
      let allChanWeightsTrend = []
      if (chanTrendIds.length > 0) {
        const { data: cwd } = await supabase
          .from('channel_product_weights').select('channel_id, weight').in('channel_id', chanTrendIds)
        allChanWeightsTrend = cwd ?? []
      }
      // Build: { year → { channelName → { totalWeight, count } } }
      const chanTrendByYear = {}
      ;(allChansTrend ?? []).forEach(c => {
        const y = c.data_year ?? CURRENT_YEAR
        if (!chanTrendByYear[y]) chanTrendByYear[y] = { year: y, channels: {} }
        if (!chanTrendByYear[y].channels[c.name]) chanTrendByYear[y].channels[c.name] = { totalWeight: 0, count: 0 }
      })
      allChanWeightsTrend.forEach(w => {
        const chan = (allChansTrend ?? []).find(c => c.id === w.channel_id)
        if (!chan) return
        const y = chan.data_year ?? CURRENT_YEAR
        if (chanTrendByYear[y]?.channels[chan.name]) {
          chanTrendByYear[y].channels[chan.name].totalWeight += Number(w.weight) || 0
          chanTrendByYear[y].channels[chan.name].count++
        }
      })
      setChannelsTrendData(Object.values(chanTrendByYear).sort((a, b) => a.year - b.year))
    }

    // Load competitors if this is a Participaciones de Mercado section
    if (normalize(sec?.name ?? "").includes("participacion")) {
      const { data: siblings } = await supabase.from('sections').select('id, name').eq('phase_id', phaseId)
      const solucionesSec = (siblings ?? []).find(s =>
        normalize(s.name).includes('soluciones') || normalize(s.name).includes('producto')
      )
      if (solucionesSec) {
        const sibProdsQ = withQtr(
          supabase.from('market_products').select('*').eq('section_id', solucionesSec.id).eq('data_year', selectedYear)
        )
        const { data: sibProds } = await sibProdsQ.order('sales_size', { ascending: false })
        setSiblingProducts(sibProds ?? [])
      }
      const compsQ = withQtr(
        supabase.from('market_competitors').select('*').eq('section_id', sectionId).eq('data_year', selectedYear)
      )
      const { data: comps } = await compsQ.order('competitor_order')
      const compList = comps ?? []
      setCompetitors(compList)
      if (compList.length > 0) {
        const { data: shares } = await supabase
          .from('competitor_product_shares').select('*').in('competitor_id', compList.map(c => c.id))
        const sharesMap = {}
        const pricesMap = {}
        ;(shares ?? []).forEach(s => {
          if (!sharesMap[s.competitor_id]) sharesMap[s.competitor_id] = {}
          sharesMap[s.competitor_id][s.product_id] = s.market_share
          if (s.avg_price != null) {
            if (!pricesMap[s.competitor_id]) pricesMap[s.competitor_id] = {}
            pricesMap[s.competitor_id][s.product_id] = { avg_price: s.avg_price, price_unit: s.price_unit ?? '' }
          }
        })
        setCompetitorShares(sharesMap)
        setCompetitorPrices(pricesMap)
      }
      // Load history — aggregate quarterly data per year (count distinct competitors)
      const { data: allComps } = await supabase
        .from('market_competitors').select('data_year, name').eq('section_id', sectionId).not('data_quarter', 'is', null)
      const compByYear = {}
      ;(allComps ?? []).forEach(c => {
        const y = c.data_year ?? CURRENT_YEAR
        if (!compByYear[y]) compByYear[y] = new Set()
        compByYear[y].add(c.name)
      })
      setCompetitorsHistory(Object.entries(compByYear).map(([y, names]) => ({ year: parseInt(y), count: names.size })).sort((a, b) => a.year - b.year))
      // Load ALL competitors + shares across ALL years for variable-based trend charts
      const { data: allCompsTrend } = await supabase
        .from('market_competitors').select('id, data_year, name, is_own_brand').eq('section_id', sectionId).order('data_year')
      const compTrendIds = (allCompsTrend ?? []).map(c => c.id)
      let allSharesTrend = []
      if (compTrendIds.length > 0) {
        const { data: sd } = await supabase
          .from('competitor_product_shares').select('competitor_id, market_share').in('competitor_id', compTrendIds).not('market_share', 'is', null)
        allSharesTrend = sd ?? []
      }
      // Build: { year → { compName → { totalShare, count, isOwn } } }
      const compTrendByYear = {}
      ;(allCompsTrend ?? []).forEach(c => {
        const y = c.data_year ?? CURRENT_YEAR
        if (!compTrendByYear[y]) compTrendByYear[y] = { year: y, competitors: {} }
        if (!compTrendByYear[y].competitors[c.name]) compTrendByYear[y].competitors[c.name] = { totalShare: 0, count: 0, isOwn: c.is_own_brand }
      })
      allSharesTrend.forEach(s => {
        const comp = (allCompsTrend ?? []).find(c => c.id === s.competitor_id)
        if (!comp) return
        const y = comp.data_year ?? CURRENT_YEAR
        if (compTrendByYear[y]?.competitors[comp.name]) {
          compTrendByYear[y].competitors[comp.name].totalShare += Number(s.market_share) || 0
          compTrendByYear[y].competitors[comp.name].count++
        }
      })
      setCompetitorsTrendData(Object.values(compTrendByYear).sort((a, b) => a.year - b.year))
    }

    // Load Precio section data — aggregates prices from sibling sections across all years
    if (normalize(sec?.name ?? "").includes("precio")) {
      const { data: siblings } = await supabase.from('sections').select('id, name').eq('phase_id', phaseId)
      const norm2 = n => n.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      const desempenoSec     = (siblings ?? []).find(s => norm2(s.name).includes('desempeno') && norm2(s.name).includes('mercado'))
      const solucionesSec    = (siblings ?? []).find(s => norm2(s.name).includes('soluciones') || (norm2(s.name).includes('producto') && !norm2(s.name).includes('precio')))
      const participacionSec = (siblings ?? []).find(s => norm2(s.name).includes('participacion'))

      // 1. Market global avg price history (market_performance: market_size / volume)
      if (desempenoSec) {
        const { data: perfRows } = await supabase
          .from('market_performance')
          .select('data_year, market_size, volume, size_unit, volume_unit')
          .eq('section_id', desempenoSec.id)
          .not('volume', 'is', null)
          .order('data_year')
        const mktHist = (perfRows ?? [])
          .filter(r => Number(r.volume) > 0)
          .map(r => ({
            year:      r.data_year ?? CURRENT_YEAR,
            avg_price: Math.round((Number(r.market_size) / Number(r.volume)) * 100) / 100,
            unit:      `${r.size_unit ?? ''} / ${r.volume_unit ?? ''}`,
          }))
        setPrecioMktHistory(mktHist)
      }

      // 2. Product avg price history (market_products: sales_size / volume, per product name & year)
      if (solucionesSec) {
        const { data: prodRows } = await supabase
          .from('market_products')
          .select('data_year, name, sales_size, size_unit, volume, volume_unit')
          .eq('section_id', solucionesSec.id)
          .not('volume', 'is', null)
          .order('data_year')
        const prodHist = (prodRows ?? [])
          .filter(r => Number(r.volume) > 0)
          .map(r => ({
            year:       r.data_year ?? CURRENT_YEAR,
            name:       r.name,
            product_id: null, // no id in this query, use name as key
            avg_price:  Math.round((Number(r.sales_size) / Number(r.volume)) * 100) / 100,
            price_unit: `${r.size_unit ?? ''} / ${r.volume_unit ?? ''}`,
          }))
        setPrecioProductHistory(prodHist)
      }

      // 3. Competitor price history (market_competitors + competitor_product_shares)
      if (participacionSec) {
        // Get all competitors across all years
        const { data: allComps } = await supabase
          .from('market_competitors')
          .select('id, name, data_year')
          .eq('section_id', participacionSec.id)
        const compIds = (allComps ?? []).map(c => c.id)
        if (compIds.length > 0) {
          const { data: allShares } = await supabase
            .from('competitor_product_shares')
            .select('competitor_id, product_id, avg_price, price_unit')
            .in('competitor_id', compIds)
            .not('avg_price', 'is', null)

          // Map product_id → product name using solucionesSec products (any year)
          let prodNameMap = {}
          if (solucionesSec) {
            const { data: allProds } = await supabase
              .from('market_products').select('id, name').eq('section_id', solucionesSec.id)
            ;(allProds ?? []).forEach(p => { prodNameMap[p.id] = p.name })
          }

          const compMap = {}
          ;(allComps ?? []).forEach(c => { compMap[c.id] = c })

          const compHist = (allShares ?? []).map(s => {
            const comp = compMap[s.competitor_id] ?? {}
            return {
              year:            comp.data_year ?? CURRENT_YEAR,
              competitor_name: comp.name ?? '?',
              product_id:      s.product_id,
              product_name:    prodNameMap[s.product_id] ?? 'Producto',
              avg_price:       Math.round(Number(s.avg_price) * 100) / 100,
              price_unit:      s.price_unit ?? '',
            }
          })
          setPrecioCompHistory(compHist)
        }
      }
    }

    // Load Strategic Objective section
    if (normalize(sec?.name ?? '').includes('objetivo')) {
      const [{ data: so }, { data: pillarRows }] = await Promise.all([
        supabase.from('strategic_objectives').select('*').eq('section_id', sectionId).maybeSingle(),
        supabase.from('strategic_pillars').select('*').eq('section_id', sectionId).order('pilar_order'),
      ])
      setPillars(pillarRows ?? [])
      if (so) {
        setStratObj(so)
        setStratObjForm({ vision: so.vision ?? '', mision: so.mision ?? '', declaracion: so.declaracion ?? '', estrategia: so.estrategia ?? '', generated_text: so.generated_text ?? '' })
        // Load metrics
        const { data: metrics } = await supabase
          .from('strategic_objective_metrics').select('*')
          .eq('strategic_objective_id', so.id)
        const mMap = {}
        ;(metrics ?? []).forEach(m => {
          mMap[`${m.year}_${m.type}`] = {
            ventas: m.ventas ?? '', ventas_unit: m.ventas_unit ?? 'USD millones',
            participacion_pct: m.participacion_pct ?? '',
            ventas_objetivo: m.ventas_objetivo ?? '', participacion_objetivo: m.participacion_objetivo ?? '',
            notes: m.notes ?? '',
          }
        })
        setStratMetrics(mMap)
      } else {
        setStratObj(null)
        setStratObjForm(EMPTY_OBJ)
        const mMap = {}
        OBJ_REAL_YEARS.forEach(y => {
          mMap[`${y}_real`] = { ventas: '', ventas_unit: 'USD millones', participacion_pct: '', ventas_objetivo: '', participacion_objetivo: '', notes: '' }
        })
        setStratMetrics(mMap)
      }
    }

    // Load competitor portfolio products for Portafolio section
    if (normalize(sec?.name ?? '').includes('portafolio') && sec?.competitor_id) {
      const { data: existing } = await supabase
        .from('competitor_portfolio_products')
        .select('*')
        .eq('section_id', sectionId)
        .order('product_order')
      if (existing?.length > 0) {
        setPortfolioProducts(existing)
        const formsInit = {}
        existing.forEach(p => {
          formsInit[p.id] = {
            presentaciones:       p.presentaciones ?? '',
            origen:               p.origen ?? '',
            precio_min:           p.precio_min ?? '',
            precio_max:           p.precio_max ?? '',
            nivel_calidad:        p.nivel_calidad ?? '',
            etapa_ciclo_vida:     p.etapa_ciclo_vida ?? '',
            segmento_objetivo:    p.segmento_objetivo ?? '',
            canales_distribucion: p.canales_distribucion ?? '',
            diferenciadores:      p.diferenciadores ?? '',
            fortalezas:           p.fortalezas ?? '',
            debilidades:          p.debilidades ?? '',
            observaciones:        p.observaciones ?? '',
          }
        })
        setPortfolioForms(formsInit)
      } else {
        // Auto-import from Análisis de Mercado: find competitor by name match
        setPortfolioImporting(true)
        const { data: comp } = await supabase
          .from('competitors').select('name').eq('id', sec.competitor_id).single()
        if (comp && buId) {
          // Find all phases of this BU
          const { data: buPhases } = await supabase
            .from('phases').select('id').eq('business_unit_id', buId)
          const phaseIds = (buPhases ?? []).map(p => p.id)
          if (phaseIds.length > 0) {
            // Find Participaciones section across those phases
            const { data: partSecs } = await supabase
              .from('sections').select('id, name').in('phase_id', phaseIds)
            const norm2 = n => n.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            const partSec = (partSecs ?? []).find(s => norm2(s.name).includes('participacion'))
            if (partSec) {
              // Match market competitor by name (case-insensitive)
              const { data: mktComps } = await supabase
                .from('market_competitors').select('id, name')
                .eq('section_id', partSec.id)
                .ilike('name', `%${comp.name}%`)
              if (mktComps?.length > 0) {
                const mktCompId = mktComps[0].id
                // Get their product shares with product names
                const { data: shares } = await supabase
                  .from('competitor_product_shares')
                  .select('*, product:market_products(id, name)')
                  .eq('competitor_id', mktCompId)
                  .not('product_id', 'is', null)
                if (shares?.length > 0) {
                  const inserts = shares.map((s, i) => ({
                    competitor_id:    sec.competitor_id,
                    section_id:       sectionId,
                    product_name:     s.product?.name ?? 'Producto',
                    market_product_id: s.product_id,
                    participacion_pct: s.market_share ?? null,
                    precio_promedio:  s.avg_price ?? null,
                    precio_unidad:    s.price_unit ?? null,
                    product_order:    i + 1,
                  }))
                  const { data: newProds } = await supabase
                    .from('competitor_portfolio_products').insert(inserts).select()
                  const prods = newProds ?? []
                  setPortfolioProducts(prods)
                  const formsInit = {}
                  prods.forEach(p => { formsInit[p.id] = { presentaciones: '', origen: '', precio_min: '', precio_max: '', nivel_calidad: '', etapa_ciclo_vida: '', segmento_objetivo: '', canales_distribucion: '', diferenciadores: '', fortalezas: '', debilidades: '', observaciones: '' } })
                  setPortfolioForms(formsInit)
                }
              }
            }
          }
        }
        setPortfolioImporting(false)
      }
    }

    // Load BU Strategy Portfolio (Definición de Estrategia > Portafolio)
    const normSecName2 = normalize(sec?.name ?? '')
    const normPhName2  = (phaseName ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    if (normSecName2.includes('portafolio') && buId && !sec?.competitor_id && (normPhName2.includes('estrategia') || normPhName2.includes('definicion'))) {
      const { data: spRec } = await supabase.from('bu_strategy_portfolio').select('*').eq('section_id', sectionId).maybeSingle()
      if (spRec) {
        setStratPortRecord(spRec)
        setStratPortProducts(spRec.products ?? [])
        setStratPortNewRefs(spRec.new_refs ?? [])
      } else {
        // Attempt auto-import: find Análisis Interno > Portafolio section for this BU
        const { data: buPhases } = await supabase.from('phases').select('id, name').eq('business_unit_id', buId)
        const internoPhase = (buPhases ?? []).find(p => normalize(p.name).includes('interno'))
        if (internoPhase) {
          const { data: portSecs } = await supabase.from('sections').select('id, name').eq('phase_id', internoPhase.id)
          const portSec = (portSecs ?? []).find(s => normalize(s.name).includes('portafolio'))
          if (portSec) {
            const { data: prods } = await supabase.from('competitor_portfolio_products').select('*').eq('section_id', portSec.id).order('product_order')
            if (prods?.length > 0) {
              const imported = prods.map(p => ({
                id: p.id, nombre: p.product_name, decision: 'Mantener', rationale: '', palancas: [],
              }))
              setStratPortProducts(imported)
            }
          }
        }
      }
    }

    // Load BU Price Strategy (Definición de Estrategia > Precio)
    const normPhName3 = (phaseName ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    if (normSecName2.includes('precio') && buId && !sec?.competitor_id && (normPhName3.includes('estrategia') || normPhName3.includes('definicion'))) {
      const { data: psRec } = await supabase.from('bu_price_strategy').select('*').eq('section_id', sectionId).maybeSingle()
      if (psRec) {
        setPriceStratRecord(psRec)
        setPriceStratProducts(psRec.products ?? [])
      } else {
        // Try to auto-import product names from bu_strategy_portfolio for this BU
        const { data: buPhasesList } = await supabase.from('phases').select('id, name').eq('business_unit_id', buId)
        const estratPhase = (buPhasesList ?? []).find(p => normalize(p.name).includes('estrategia') || normalize(p.name).includes('definicion'))
        if (estratPhase) {
          const { data: portSecs2 } = await supabase.from('sections').select('id, name').eq('phase_id', estratPhase.id)
          const portSec2 = (portSecs2 ?? []).find(s => normalize(s.name).includes('portafolio'))
          if (portSec2) {
            const { data: spRec2 } = await supabase.from('bu_strategy_portfolio').select('products').eq('section_id', portSec2.id).maybeSingle()
            if (spRec2?.products?.length > 0) {
              const priceItems = spRec2.products.map(p => ({
                id: p.id, nombre: p.nombre,
                precio_actual: '', moneda: 'USD', posicion: 'Paridad',
                objetivos: PRICE_GOAL_YEARS.map(año => ({ año, precio: '', moneda: 'USD', posicion: 'Paridad' })),
              }))
              setPriceStratProducts(priceItems)
            }
          }
        }
      }
    }

    // Load competitor Precio section — auto comparison (no data entry)
    if (normalize(sec?.name ?? '').includes('precio') && sec?.competitor_id && buId) {
      // 1. Competitor prices per product (from competitor_portfolio_products)
      const { data: portProds } = await supabase
        .from('competitor_portfolio_products')
        .select('product_name, precio_promedio, precio_unidad, market_product_id')
        .eq('competitor_id', sec.competitor_id)
        .not('precio_promedio', 'is', null)
        .order('product_order')

      if (portProds?.length > 0) {
        // 2. Market prices per product (sales_size / volume from market_products)
        const mktProductIds = portProds.map(p => p.market_product_id).filter(Boolean)
        let mktPriceMap = {}  // { market_product_id: { avg_price, price_unit } }

        if (mktProductIds.length > 0) {
          // Get latest year available for each product
          const { data: mktProds } = await supabase
            .from('market_products')
            .select('id, name, sales_size, size_unit, volume, volume_unit')
            .in('id', mktProductIds)
            .not('volume', 'is', null)
          ;(mktProds ?? []).forEach(p => {
            const v = Number(p.volume), s = Number(p.sales_size)
            if (v > 0 && s > 0) {
              mktPriceMap[p.id] = {
                avg_price:  Math.round((s / v) * 100) / 100,
                price_unit: `${p.size_unit ?? ''} / ${p.volume_unit ?? ''}`.trim(),
              }
            }
          })
        }

        // 3. Build comparison rows
        const rows = portProds.map(p => {
          const mkt = mktPriceMap[p.market_product_id] ?? null
          const compPrice = Number(p.precio_promedio)
          const mktPrice  = mkt ? Number(mkt.avg_price) : null
          const deltaPct  = mktPrice && mktPrice > 0
            ? Math.round(((compPrice - mktPrice) / mktPrice) * 1000) / 10
            : null
          return {
            product_name: p.product_name,
            comp_price:   compPrice,
            comp_unit:    p.precio_unidad ?? '',
            mkt_price:    mktPrice,
            mkt_unit:     mkt?.price_unit ?? '',
            delta_pct:    deltaPct,
          }
        })
        setCompPrecioData(rows)
      } else {
        setCompPrecioData([])
      }
    }

    // Load market Comunicación — Mix de Medios
    if (normalize(sec?.name ?? '').includes('comunicacion') && !sec?.competitor_id) {
      const { data: rows } = await supabase
        .from('market_media_mix').select('*')
        .eq('section_id', sectionId).order('medium_order')
      const existing = rows ?? []
      setMktMediaMix(existing)
      if (existing.length > 0) {
        setMktMediaForm(existing.map(r => ({ id: r.id, medium_name: r.medium_name, weight_pct: r.weight_pct ?? '', investment_ref: r.investment_ref ?? '', notes: r.notes ?? '' })))
      } else {
        // Pre-populate with default channels
        setMktMediaForm(DEFAULT_MEDIA_CHANNELS.map(name => ({ id: null, medium_name: name, weight_pct: '', investment_ref: '', notes: '' })))
      }
    }

    // Load competitor Estrategia de Comunicación
    if (normalize(sec?.name ?? '').includes('comunicacion') && sec?.competitor_id && buId) {
      setCommImporting(true)
      // 1. Find market Comunicación section in this BU for reference media mix
      //    Look ONLY in non-"interno" phases to avoid picking up the BU internal comm section
      const norm2c = n => n.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      const { data: buPhases2 } = await supabase.from('phases').select('id, name').eq('business_unit_id', buId)
      const internoPhaseIds2 = new Set((buPhases2 ?? []).filter(p => norm2c(p.name).includes('interno')).map(p => p.id))
      const mktPhaseIds2 = (buPhases2 ?? []).filter(p => !internoPhaseIds2.has(p.id)).map(p => p.id)
      if (mktPhaseIds2.length > 0) {
        const { data: allSecs2 } = await supabase.from('sections').select('id, name, phase_id, competitor_id').in('phase_id', mktPhaseIds2)
        const mktCommSec = (allSecs2 ?? []).find(s => norm2c(s.name).includes('comunicacion') && !s.competitor_id)
        if (mktCommSec) {
          const { data: mktMedia } = await supabase
            .from('market_media_mix').select('*')
            .eq('section_id', mktCommSec.id).order('medium_order')
          setMktMediaRef(mktMedia ?? [])
        }
      }
      // 2. Load existing comm strategy record
      const { data: cs } = await supabase
        .from('competitor_comm_strategy').select('*')
        .eq('section_id', sectionId).maybeSingle()
      if (cs) {
        setCommStrategy(cs)
        setCommForm({ tono_voz: cs.tono_voz ?? '', mensajes_clave: cs.mensajes_clave ?? '', propuesta_valor_comunicada: cs.propuesta_valor_comunicada ?? '', nivel_inversion: cs.nivel_inversion ?? '', tipo_contenido: cs.tipo_contenido ?? '', tematicas: cs.tematicas ?? '', influencers: cs.influencers ?? '', campanas_destacadas: cs.campanas_destacadas ?? '', share_of_voice: cs.share_of_voice ?? '', fortalezas_comunicacion: cs.fortalezas_comunicacion ?? '', debilidades_comunicacion: cs.debilidades_comunicacion ?? '' })
      } else {
        setCommStrategy(null)
        setCommForm(EMPTY_COMM)
      }
      // 3. Load competitor media mix — auto-create from market if empty
      const { data: cmm } = await supabase
        .from('competitor_media_mix').select('*')
        .eq('section_id', sectionId).order('medium_order')
      let mediaRows = cmm ?? []
      if (mediaRows.length === 0 && mktMediaRef.length > 0) {
        const inserts = mktMediaRef.map((m, i) => ({ section_id: sectionId, competitor_id: sec.competitor_id, market_medium_id: m.id, medium_name: m.medium_name, medium_order: i + 1 }))
        const { data: newMedia } = await supabase.from('competitor_media_mix').insert(inserts).select()
        mediaRows = newMedia ?? []
      } else if (mediaRows.length === 0) {
        // No market reference yet — pre-populate with defaults
        const inserts = DEFAULT_MEDIA_CHANNELS.map((name, i) => ({ section_id: sectionId, competitor_id: sec.competitor_id, medium_name: name, medium_order: i + 1 }))
        const { data: newMedia } = await supabase.from('competitor_media_mix').insert(inserts).select()
        mediaRows = newMedia ?? []
      }
      setCompMediaMix(mediaRows)
      const mf = {}
      mediaRows.forEach(r => { mf[r.id] = r.weight_pct ?? '' })
      setCompMediaForms(mf)
      // 4. Load digital presence — auto-create platforms if empty
      const { data: dp } = await supabase
        .from('competitor_digital_presence').select('*')
        .eq('section_id', sectionId).order('platform_order')
      let dpRows = dp ?? []
      if (dpRows.length === 0) {
        const inserts = DIGITAL_PLATFORMS.map((platform, i) => ({ section_id: sectionId, competitor_id: sec.competitor_id, platform, platform_order: i + 1 }))
        const { data: newDp } = await supabase.from('competitor_digital_presence').insert(inserts).select()
        dpRows = newDp ?? []
      }
      setDigitalPresence(dpRows)
      const df = {}
      dpRows.forEach(r => { df[r.id] = { followers: r.followers ?? '', engagement_rate: r.engagement_rate ?? '', posts_per_week: r.posts_per_week ?? '' } })
      setDigitalForms(df)
      setCommImporting(false)
    }

    // Load Capacidad Industrial (competitor or internal BU)
    if (normalize(sec?.name ?? '').includes('capacidad') && (sec?.competitor_id || buId)) {
      const { data: cap } = await supabase.from('competitor_industrial_capacity').select('*').eq('section_id', sectionId).maybeSingle()
      if (cap) {
        setCapIndustrial(cap)
        setCapForm({
          bloque1_aplica: cap.bloque1_aplica ?? true, num_plantas: cap.num_plantas ?? '', ubicacion_plantas: cap.ubicacion_plantas ?? '',
          capacidad_instalada: cap.capacidad_instalada ?? '', capacidad_unidad: cap.capacidad_unidad ?? 'toneladas/año', utilizacion_pct: cap.utilizacion_pct ?? '',
          bloque2_aplica: cap.bloque2_aplica ?? true, nivel_tecnologico: cap.nivel_tecnologico ?? '', grado_automatizacion: cap.grado_automatizacion ?? '',
          antiguedad_equipos: cap.antiguedad_equipos ?? '', descripcion_tecnologia: cap.descripcion_tecnologia ?? '',
          bloque3_aplica: cap.bloque3_aplica ?? true, certificaciones: cap.certificaciones ?? [], estandares_calidad: cap.estandares_calidad ?? '',
          bloque4_aplica: cap.bloque4_aplica ?? true, integracion_vertical: cap.integracion_vertical ?? '', flexibilidad_linea: cap.flexibilidad_linea ?? '',
          capacidad_expansion: cap.capacidad_expansion ?? '', notas_integracion: cap.notas_integracion ?? '',
          bloque5_aplica: cap.bloque5_aplica ?? true, ventajas_productivas: cap.ventajas_productivas ?? '', brechas_limitaciones: cap.brechas_limitaciones ?? '',
        })
      } else {
        setCapIndustrial(null)
        setCapForm({ bloque1_aplica: true, num_plantas: '', ubicacion_plantas: '', capacidad_instalada: '', capacidad_unidad: 'toneladas/año', utilizacion_pct: '', bloque2_aplica: true, nivel_tecnologico: '', grado_automatizacion: '', antiguedad_equipos: '', descripcion_tecnologia: '', bloque3_aplica: true, certificaciones: [], estandares_calidad: '', bloque4_aplica: true, integracion_vertical: '', flexibilidad_linea: '', capacidad_expansion: '', notas_integracion: '', bloque5_aplica: true, ventajas_productivas: '', brechas_limitaciones: '' })
      }
    }

    // ── Helper: load or auto-create a single consumer-subsection record ──────
    async function loadOrCreate(table, defaultFields, setId, setForms, buildForm) {
      let { data: rec } = await supabase.from(table).select('*').eq('section_id', sectionId).maybeSingle()
      if (!rec) {
        const { data: created } = await supabase.from(table).insert({ section_id: sectionId, profile_name: sec?.name ?? '', profile_order: 0, ...defaultFields }).select().single()
        rec = created
      }
      if (rec) { setId(rec.id); setForms({ [rec.id]: buildForm(rec) }) }
      else { setId(null); setForms({}) }
    }

    // Load Consumer Needs & Tensions (single record — section already IS a specific profile)
    if (normalize(sec?.name ?? '').includes('necesidades') && !sec?.competitor_id) {
      await loadOrCreate('consumer_needs_tensions',
        { necesidades_funcionales: [], necesidades_emocionales: [], tensiones: [] },
        setActiveNeedsId, setNeedsForms,
        p => ({ profile_name: p.profile_name ?? '', bloque1_aplica: p.bloque1_aplica ?? true, necesidades_funcionales: p.necesidades_funcionales ?? [], bloque2_aplica: p.bloque2_aplica ?? true, necesidades_emocionales: p.necesidades_emocionales ?? [], bloque3_aplica: p.bloque3_aplica ?? true, tensiones: p.tensiones ?? [], bloque4_aplica: p.bloque4_aplica ?? true, jtbd_descripcion: p.jtbd_descripcion ?? '', jtbd_cuando: p.jtbd_cuando ?? '', jtbd_alternativas: p.jtbd_alternativas ?? '', bloque5_aplica: p.bloque5_aplica ?? true, insight_clave: p.insight_clave ?? '' })
      )
    }

    // Load Consumer Solution — Sobre la Solución (single record)
    if (normalize(sec?.name ?? '').includes('sobre') && normalize(sec?.name ?? '').includes('solucion') && !sec?.competitor_id) {
      await loadOrCreate('consumer_solution',
        { drivers: [], barreras: [] },
        setActiveSolId, setSolForms,
        p => ({ profile_name: p.profile_name ?? '', bloque1_aplica: p.bloque1_aplica ?? true, drivers: p.drivers ?? [], bloque2_aplica: p.bloque2_aplica ?? true, barreras: p.barreras ?? [], bloque3_aplica: p.bloque3_aplica ?? true, motivadores: p.motivadores ?? '', bloque4_aplica: p.bloque4_aplica ?? true, proceso_decision: p.proceso_decision ?? '', bloque5_aplica: p.bloque5_aplica ?? true, ocasiones_consumo: p.ocasiones_consumo ?? '' })
      )
    }

    // Load Consumer Category Adoption (single record)
    if (normalize(sec?.name ?? '').includes('adopcion') && normalize(sec?.name ?? '').includes('categoria') && !sec?.competitor_id) {
      await loadOrCreate('consumer_category_adoption', { influenciadores: [] },
        setActiveAdoptId, setAdoptForms,
        p => ({ profile_name: p.profile_name ?? '', bloque1_aplica: p.bloque1_aplica ?? true, penetracion_pct: p.penetracion_pct ?? '', frecuencia_compra: p.frecuencia_compra ?? '', volumen_por_ocasion: p.volumen_por_ocasion ?? '', bloque2_aplica: p.bloque2_aplica ?? true, satisfaccion_nivel: p.satisfaccion_nivel ?? '', satisfaccion_gaps: p.satisfaccion_gaps ?? '', bloque3_aplica: p.bloque3_aplica ?? true, riesgos_abandono: p.riesgos_abandono ?? '', bloque4_aplica: p.bloque4_aplica ?? true, influenciadores: p.influenciadores ?? [] })
      )
    }

    // Load Consumer Brand Adoption (single record)
    if (normalize(sec?.name ?? '').includes('adopcion') && normalize(sec?.name ?? '').includes('marca') && !sec?.competitor_id) {
      await loadOrCreate('consumer_brand_adoption', {},
        setActiveBrandId, setBrandForms,
        p => ({ profile_name: p.profile_name ?? '', bloque1_aplica: p.bloque1_aplica ?? true, atributos_marca: p.atributos_marca ?? [], velocidad_adopcion: p.velocidad_adopcion ?? '', bloque2_aplica: p.bloque2_aplica ?? true, nivel_lealtad: p.nivel_lealtad ?? '', razones_fidelidad: p.razones_fidelidad ?? '', bloque3_aplica: p.bloque3_aplica ?? true, drivers_cambio: p.drivers_cambio ?? '', frecuencia_cambio: p.frecuencia_cambio ?? '', bloque4_aplica: p.bloque4_aplica ?? true, influenciadores: p.influenciadores ?? '' })
      )
    }

    // Load BU P&L section
    if ((normalize(sec?.name ?? '').includes('p&l') || normalize(sec?.name ?? '').includes('pyg') ||
        (normalize(sec?.name ?? '').includes('estrategia') && normalize(sec?.name ?? '').includes('desde'))) && !sec?.competitor_id) {
      const { data: pyl } = await supabase.from('bu_pyl').select('*').eq('section_id', sectionId).maybeSingle()
      if (pyl) { setPylRecord(pyl); setPylCols(pyl.pyl_columns ?? []); setPylRows(enrichPylRows(pyl.pyl_rows ?? [])); setPylUnit(pyl.unit ?? 'USD millones') }
      else { setPylRecord(null); setPylCols([]); setPylRows([]); setPylUnit('USD millones') }
    }

    // Load BU Internal comm strategy (Análisis Interno — same schema as competitor)
    if (normalize(sec?.name ?? '').includes('comunicacion') && !sec?.competitor_id && normalize(ph?.name ?? '').includes('interno')) {
      const norm2 = s => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')

      // 1. Load market media mix reference — find "Comunicación" section in non-"interno" BU phases
      //    (e.g., "Análisis del Mercado" phase of this same BU)
      let loadedMktRef = []
      if (buId) {
        const { data: buPhasesRef } = await supabase.from('phases').select('id, name').eq('business_unit_id', buId)
        const internoSet = new Set((buPhasesRef ?? []).filter(p => norm2(p.name).includes('interno')).map(p => p.id))
        const mktPhaseIds = (buPhasesRef ?? []).filter(p => !internoSet.has(p.id)).map(p => p.id)
        if (mktPhaseIds.length > 0) {
          const { data: allSecsRef } = await supabase.from('sections').select('id, name, phase_id, competitor_id').in('phase_id', mktPhaseIds)
          const mktCommSec = (allSecsRef ?? []).find(s =>
            norm2(s.name).includes('comunicacion') && !s.competitor_id
          )
          if (mktCommSec) {
            const { data: mktMedia } = await supabase.from('market_media_mix').select('*').eq('section_id', mktCommSec.id).order('medium_order')
            loadedMktRef = mktMedia ?? []
            setMktMediaRef(loadedMktRef)
          }
        }
      }

      // 2. Load comm strategy record
      const { data: bcs } = await supabase.from('bu_comm_strategy').select('*').eq('section_id', sectionId).maybeSingle()
      if (bcs) {
        setBuCommStrategy(bcs)
        setBuCommForm({ tono_voz: bcs.tono_voz ?? '', mensajes_clave: bcs.mensajes_clave ?? '', propuesta_valor_comunicada: bcs.propuesta_valor_comunicada ?? '', nivel_inversion: bcs.nivel_inversion ?? '', tipo_contenido: bcs.tipo_contenido ?? '', tematicas: bcs.tematicas ?? '', influencers: bcs.influencers ?? '', campanas_destacadas: bcs.campanas_destacadas ?? '', share_of_voice: bcs.share_of_voice ?? '', fortalezas_comunicacion: bcs.fortalezas_comunicacion ?? '', debilidades_comunicacion: bcs.debilidades_comunicacion ?? '' })
      } else {
        setBuCommStrategy(null)
        setBuCommForm(EMPTY_COMM)
      }

      // 3. Load media mix — auto-create from market reference or defaults
      // Use loadedMktRef (local var) not mktMediaRef (state) to avoid stale closure
      const { data: bmm } = await supabase.from('bu_media_mix').select('*').eq('section_id', sectionId).order('medium_order')
      let buMediaRows = bmm ?? []
      if (buMediaRows.length === 0) {
        const sourceChannels = loadedMktRef.length > 0 ? loadedMktRef.map(m => m.medium_name) : DEFAULT_MEDIA_CHANNELS
        const inserts = sourceChannels.map((name, i) => ({ section_id: sectionId, medium_name: name, medium_order: i + 1 }))
        const { data: newBmm } = await supabase.from('bu_media_mix').insert(inserts).select()
        buMediaRows = newBmm ?? []
      }
      setBuMediaMix(buMediaRows)
      const bmf = {}
      buMediaRows.forEach(r => { bmf[r.id] = r.weight_pct ?? '' })
      setBuMediaForms(bmf)

      // 4. Load digital presence — auto-create platforms if empty
      const { data: dp } = await supabase.from('bu_digital_presence').select('*').eq('section_id', sectionId).order('platform_order')
      let dpRows = dp ?? []
      if (dpRows.length === 0) {
        const inserts = DIGITAL_PLATFORMS.map((platform, i) => ({ section_id: sectionId, platform, platform_order: i + 1 }))
        const { data: newDp } = await supabase.from('bu_digital_presence').insert(inserts).select()
        dpRows = newDp ?? []
      }
      setBuDigitalPresence(dpRows)
      const bdf = {}
      dpRows.forEach(r => { bdf[r.id] = { followers: r.followers ?? '', engagement_rate: r.engagement_rate ?? '', posts_per_week: r.posts_per_week ?? '' } })
      setBuDigitalForms(bdf)
    }

    // Load competitor Canales section (channel matrix with market reference)
    if (normalize(sec?.name ?? '').includes('canal') && sec?.competitor_id && buId) {
      setCompChansImporting(true)

      // 1. Find market Canales + Soluciones sections in this BU (across phases)
      const { data: buPhases } = await supabase.from('phases').select('id').eq('business_unit_id', buId)
      const phaseIds = (buPhases ?? []).map(p => p.id)
      let mktCanalSec = null, mktSolucionesSec = null
      if (phaseIds.length > 0) {
        const { data: allSecs } = await supabase.from('sections').select('id, name').in('phase_id', phaseIds)
        const norm2 = n => n.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        // Exclude competitor sections (only market-level sections)
        const marketSecs = (allSecs ?? []).filter(s => true) // all — we filter by name below
        mktCanalSec     = marketSecs.find(s => norm2(s.name).includes('canal') && !s.competitor_id)
        mktSolucionesSec= marketSecs.find(s => (norm2(s.name).includes('soluciones') || norm2(s.name).includes('producto')) && !norm2(s.name).includes('precio'))
      }

      // 2. Load market reference channels + weights + products
      let refChans = [], refProds = [], refWeightsMap = {}
      if (mktCanalSec) {
        const { data: mChans } = await supabase
          .from('market_channels').select('*')
          .eq('section_id', mktCanalSec.id).order('channel_order')
        refChans = mChans ?? []
        if (refChans.length > 0) {
          const { data: wRows } = await supabase
            .from('channel_product_weights').select('*')
            .in('channel_id', refChans.map(c => c.id))
          ;(wRows ?? []).forEach(w => {
            if (!refWeightsMap[w.channel_id]) refWeightsMap[w.channel_id] = {}
            refWeightsMap[w.channel_id][w.product_id] = w.weight
          })
        }
      }
      if (mktSolucionesSec) {
        const { data: mProds } = await supabase
          .from('market_products').select('id, name').eq('section_id', mktSolucionesSec.id)
          .order('product_order')
        refProds = mProds ?? []
      }
      setMktChanRef(refChans)
      setMktChanWeightsRef(refWeightsMap)
      setMktProdRef(refProds)

      // 3. Load existing competitor channel presence for this section
      const { data: existingPresence } = await supabase
        .from('competitor_channel_presence').select('*')
        .eq('section_id', sectionId).order('channel_order')

      let presence = existingPresence ?? []
      if (presence.length === 0 && refChans.length > 0) {
        // Auto-create from market channels
        const inserts = refChans.map((c, i) => ({
          competitor_id:     sec.competitor_id,
          section_id:        sectionId,
          market_channel_id: c.id,
          channel_name:      c.name,
          channel_order:     i + 1,
        }))
        const { data: newPresence } = await supabase
          .from('competitor_channel_presence').insert(inserts).select()
        presence = newPresence ?? []
        // Auto-create weight rows for each presence x product
        if (presence.length > 0 && refProds.length > 0) {
          const weightInserts = presence.flatMap(p =>
            refProds.map(prod => ({
              channel_presence_id: p.id,
              market_product_id:   prod.id,
              product_name:        prod.name,
            }))
          )
          await supabase.from('competitor_channel_product_weights').insert(weightInserts)
        }
      }
      setCompChanPresence(presence)

      // 4. Init forms
      const forms = {}, wForms = {}
      presence.forEach(p => {
        forms[p.id] = { num_clientes: p.num_clientes ?? '', clientes_ponderado: p.clientes_ponderado ?? '' }
        wForms[p.id] = {}
      })
      setCompChanForms(forms)

      // 5. Load competitor channel product weights
      if (presence.length > 0) {
        const { data: wRows } = await supabase
          .from('competitor_channel_product_weights').select('*')
          .in('channel_presence_id', presence.map(p => p.id))
        ;(wRows ?? []).forEach(w => {
          if (!wForms[w.channel_presence_id]) wForms[w.channel_presence_id] = {}
          wForms[w.channel_presence_id][w.market_product_id ?? w.product_name] = w.weight ?? ''
        })
      }
      setCompChanWeightForms(wForms)
      setCompChansImporting(false)
    }

    // Load competitor positioning for Posicionamiento section
    if (normalize(sec?.name ?? '').includes('posicionamiento')) {
      const { data: pos } = await supabase
        .from('competitor_positioning')
        .select('*')
        .eq('section_id', sectionId)
        .maybeSingle()
      if (pos) {
        setPositioning(pos)
        setPosForm({
          propuesta_valor:      pos.propuesta_valor ?? '',
          segmento_objetivo:    pos.segmento_objetivo ?? '',
          territorio_marca:     pos.territorio_marca ?? '',
          precio_relativo:      pos.precio_relativo ?? 'valor',
          atributos_clave:      pos.atributos_clave ?? '',
          diferenciadores:      pos.diferenciadores ?? '',
          fortalezas:           pos.fortalezas ?? '',
          debilidades:          pos.debilidades ?? '',
          promesa_marca:        pos.promesa_marca ?? '',
          espacios_no_ocupados: pos.espacios_no_ocupados ?? '',
        })
        // Load v2 schema. If row was created before v2 existed, default to
        // empty so the form doesn't crash. We don't auto-migrate the v1
        // text into v2 fields — semantics differ enough that the user
        // should re-curate.
        const v2 = pos.pos_v2 ?? {}
        setPosV2({
          segmentos:                   v2.segmentos ?? '',
          posicionamiento:             v2.posicionamiento ?? '',
          aristas_desempeno:           Array.isArray(v2.aristas_desempeno) ? v2.aristas_desempeno : [],
          aristas_afinidad:            Array.isArray(v2.aristas_afinidad)  ? v2.aristas_afinidad  : [],
          beneficio_funcional:         v2.beneficio_funcional ?? '',
          beneficio_emocional:         v2.beneficio_emocional ?? '',
          reason_to_believe:           v2.reason_to_believe ?? '',
          tom_conocimiento_espontaneo: v2.tom_conocimiento_espontaneo ?? '',
        })
      } else {
        setPositioning(null)
        setPosForm(EMPTY_POSITIONING)
        setPosV2(EMPTY_POS_V2)
      }
    }

    // Load consumer profiles for Segmentación section
    if (normalize(sec?.name ?? '').includes('segmentacion') && phaseId) {
      const { data: profiles } = await supabase
        .from('consumer_profiles')
        .select('*, sections(id, name, status, section_order, tasks(id, status))')
        .eq('phase_id', phaseId)
        .order('profile_order')
      setSegProfiles((profiles ?? []).map(p => ({
        ...p,
        sections: (p.sections ?? []).sort((a, b) => a.section_order - b.section_order),
      })))
    }

    // Load all BUs + phases+sections for the move picker
    if (ph?.consultancy_id) {
      const { data: bus } = await supabase
        .from('business_units')
        .select('id, name, bu_order')
        .eq('consultancy_id', ph.consultancy_id)
        .order('bu_order')
      setAllBUs(bus ?? [])

      const { data: phases } = await supabase
        .from('phases')
        .select('id, name, phase_order, business_unit_id, sections(id, name, section_order)')
        .eq('consultancy_id', ph.consultancy_id)
        .order('phase_order')
      setAllPhases(phases ?? [])
    }

    // Load resultado record
    if (ph?.consultancy_id) {
      const { data: resultRecord } = await supabase
        .from('ai_reports')
        .select('id, content')
        .eq('entity_type', 'section')
        .eq('entity_id', sectionId)
        .eq('report_type', 'resultado')
        .maybeSingle()
      if (resultRecord) {
        setSavedResult(resultRecord.content ?? '')
        setResultText(resultRecord.content ?? '')
        setResultRecordId(resultRecord.id)
      }
    }

    // Load tab conclusions
    const { data: tabConclData } = await supabase.from('section_tab_conclusions').select('*').eq('section_id', sectionId)
    const concMap = {}
    ;(tabConclData ?? []).forEach(c => { concMap[c.tab_id] = { id: c.id, text: c.conclusion_text ?? '' } })
    setTabConclusions(concMap)

    // Load phase lock status
    const { data: phLock } = await supabase.from('phases').select('is_locked').eq('id', phaseId).single()
    if (phLock?.is_locked) setPhaseLocked(true)
    else setPhaseLocked(false)

    // Load change log
    const { data: clData } = await supabase.from('field_change_log').select('*').eq('section_id', sectionId).order('created_at', { ascending: false }).limit(50)
    setChangeLog(clData ?? [])

    // Load industry definition if this is the "Definición de la industria" section
    if (normalize(sec?.name ?? '').includes('definicion') && normalize(sec?.name ?? '').includes('industria')) {
      const { data: indDef } = await supabase
        .from('industry_definition')
        .select('*')
        .eq('section_id', sectionId)
        .maybeSingle()
      setIndustryDef(indDef)
      if (indDef) setIndustryDefForm({ descripcion: indDef.descripcion ?? '', tamano_ventas: indDef.tamano_ventas ?? '', unidad: indDef.unidad ?? 'USD' })
    }

    setLoading(false)
  }

  async function saveResult() {
    if (!resultText.trim()) return
    setSavingResult(true)
    const { data: ph } = await supabase.from('phases').select('consultancy_id').eq('id', phaseId).single()
    if (resultRecordId) {
      await supabase.from('ai_reports').update({ content: resultText }).eq('id', resultRecordId)
    } else {
      const { data } = await supabase.from('ai_reports').insert({
        consultancy_id: ph?.consultancy_id,
        entity_type: 'section',
        entity_id: sectionId,
        report_type: 'resultado',
        content: resultText,
      }).select().single()
      if (data) setResultRecordId(data.id)
    }
    setSavedResult(resultText)
    setEditingResult(false)
    setSavingResult(false)
  }

  // ── AI draft generator (uses ai-report v3 reportType='draft_section') ──
  // Pulls company / industry / peer-section content from the same project
  // so the model produces a draft that fits THIS plan, not a generic
  // template. The output replaces resultText and opens the editor for the
  // user to refine before saving.
  async function generateAIDraft() {
    if (!consultancyId || !section) return
    setGeneratingDraft(true)
    setDraftError('')
    try {
      const [{ data: cons }, { data: ficha }, { data: peerReports }] = await Promise.all([
        supabase.from('consultancies')
          .select('client_company, industry, country')
          .eq('id', consultancyId).single(),
        supabase.from('project_ficha')
          .select('tamano_empresa, descripcion_negocio')
          .eq('consultancy_id', consultancyId).maybeSingle(),
        supabase.from('ai_reports')
          .select('entity_id, content')
          .eq('consultancy_id', consultancyId)
          .eq('entity_type', 'section')
          .in('report_type', ['resultado', 'section_result', 'user_notes'])
          .neq('entity_id', sectionId)
          .limit(20),
      ])

      const peerExtracts = (peerReports ?? [])
        .map(r => (r.content ?? '').trim())
        .filter(c => c.length > 30)
        .map(c => c.length > 240 ? c.slice(0, 240) + '…' : c)
        .slice(0, 8)
        .join('\n\n---\n\n')

      const context = [
        `EMPRESA: ${cons?.client_company ?? '—'}`,
        `INDUSTRIA: ${cons?.industry ?? '—'}`,
        cons?.country ? `PAÍS: ${cons.country}` : null,
        ficha?.tamano_empresa ? `TAMAÑO: ${ficha.tamano_empresa}` : null,
        ficha?.descripcion_negocio ? `DESCRIPCIÓN DEL NEGOCIO: ${ficha.descripcion_negocio}` : null,
        '',
        `SECCIÓN A REDACTAR: ${section.name}`,
        `FASE: ${phaseName}`,
        buName ? `UNIDAD DE NEGOCIO: ${buName}` : null,
        section.description ? `DESCRIPCIÓN DE LA SECCIÓN: ${section.description}` : null,
        '',
        peerExtracts
          ? `EXTRACTOS DE OTRAS SECCIONES YA TRABAJADAS EN ESTE PROYECTO:\n\n${peerExtracts}`
          : 'OTRAS SECCIONES: aún sin contenido. Eres el primero en redactar; declara explícitamente los supuestos que tomas.',
      ].filter(Boolean).join('\n')

      const { data, error } = await supabase.functions.invoke('ai-report', {
        body: { reportType: 'draft_section', context, consultancyId },
      })
      if (error) throw error
      if (data?.error) throw new Error(data.error)
      const draft = data?.report?.trim()
      if (!draft) throw new Error('La IA devolvió un borrador vacío. Intenta de nuevo.')
      setResultText(draft)
      setEditingResult(true)
    } catch (err) {
      console.error('[generateAIDraft] error:', err)
      setDraftError(err.message ?? 'No se pudo generar el borrador. Intenta de nuevo.')
    } finally {
      setGeneratingDraft(false)
    }
  }

  // ── Industry definition save ─────────────────────────────────────────────

  async function saveIndustryDef(e) {
    e.preventDefault()
    setSavingIndustryDef(true)
    const payload = {
      section_id: sectionId,
      descripcion: industryDefForm.descripcion,
      tamano_ventas: industryDefForm.tamano_ventas ? Number(industryDefForm.tamano_ventas) : null,
      unidad: industryDefForm.unidad || 'USD',
    }
    if (industryDef?.id) {
      await supabase.from('industry_definition').update({ ...payload, updated_at: new Date().toISOString() }).eq('id', industryDef.id)
    } else {
      const { data } = await supabase.from('industry_definition').insert(payload).select().single()
      if (data) setIndustryDef(data)
    }
    setEditingIndustryDef(false)
    setSavingIndustryDef(false)
    await fetchData()
  }

  // ── Market matrix functions ──────────────────────────────────────────────

  function openCreateMarket() {
    setEditMarket(null)
    setMarketForm({ name: '', sales_size: '', description: '' })
    setShowMarketModal(true)
  }

  function openEditMarket(market) {
    setEditMarket(market)
    setMarketForm({ name: market.name, sales_size: String(market.sales_size), description: market.description ?? '' })
    setShowMarketModal(true)
  }

  async function saveMarket(e) {
    e.preventDefault()
    setSavingMarket(true)
    const payload = {
      section_id:   sectionId,
      name:         marketForm.name,
      sales_size:   parseFloat(marketForm.sales_size) || 0,
      description:  marketForm.description || null,
      market_order: editMarket ? editMarket.market_order : (markets.length + 1),
    }
    if (editMarket) {
      const { error } = await supabase.from('industry_markets').update(payload).eq('id', editMarket.id)
      if (!error) setMarkets(prev => prev.map(m => m.id === editMarket.id ? { ...m, ...payload } : m))
    } else {
      const { data } = await supabase.from('industry_markets').insert(payload).select().single()
      if (data) setMarkets(prev => [...prev, data])
    }
    setShowMarketModal(false)
    setSavingMarket(false)
  }

  async function deleteMarket(marketId) {
    if (!confirm('¿Eliminar este mercado?')) return
    await supabase.from('industry_markets').delete().eq('id', marketId)
    setMarkets(prev => prev.filter(m => m.id !== marketId))
  }

  async function toggleMatrixFinalized() {
    setFinalizingMatrix(true)
    const newVal = !matrixFinalized
    await supabase.from('sections').update({ matrix_finalized: newVal }).eq('id', sectionId)
    setMatrixFinalized(newVal)
    setSection(s => ({ ...s, matrix_finalized: newVal }))
    setFinalizingMatrix(false)
  }

  function importFromExcel(e) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = async (evt) => {
      const wb = XLSX.read(evt.target.result, { type: 'array' })
      const ws = wb.Sheets[wb.SheetNames[0]]
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' })
      if (!rows.length) return

      // Detect columns flexibly (case-insensitive, accent-insensitive)
      const normalize = s => String(s).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      const headers = Object.keys(rows[0]).reduce((acc, k) => {
        const n = normalize(k)
        if (n.includes('nombre') || n.includes('mercado') || n === 'name') acc.name = k
        if (n.includes('tamano') || n.includes('tamañ') || n.includes('venta') || n.includes('size') || n.includes('monto')) acc.size = k
        if (n.includes('descrip')) acc.description = k
        return acc
      }, {})

      const toInsert = rows
        .filter(r => r[headers.name])
        .map((r, i) => ({
          section_id:   sectionId,
          name:         String(r[headers.name] ?? '').trim(),
          sales_size:   parseFloat(String(r[headers.size] ?? '0').replace(/[^0-9.]/g, '')) || 0,
          description:  headers.description ? String(r[headers.description] ?? '').trim() || null : null,
          market_order: markets.length + i + 1,
        }))
        .filter(r => r.name)

      if (!toInsert.length) { alert('No se encontraron mercados válidos en el archivo.'); return }

      const { data: inserted } = await supabase.from('industry_markets').insert(toInsert).select()
      if (inserted) setMarkets(prev => [...prev, ...inserted])
    }
    reader.readAsArrayBuffer(file)
    e.target.value = ''
  }

  // ── End market matrix functions ──────────────────────────────────────────

  // ── Market selection functions ───────────────────────────────────────────

  async function toggleMarketSelection(marketId) {
    const isSelected = selectedMarketIds.has(marketId)
    if (isSelected) {
      // Check if BU already created for this market
      const rec = selectionRecords.find(r => r.market_id === marketId)
      if (rec?.bu_id) {
        alert('Este mercado ya tiene una unidad de negocio creada. Para deseleccionarlo primero elimina la unidad de negocio correspondiente.')
        return
      }
      await supabase.from('market_selections')
        .delete()
        .eq('selection_section_id', sectionId)
        .eq('market_id', marketId)
      setSelectedMarketIds(prev => { const n = new Set(prev); n.delete(marketId); return n })
      setSelectionRecords(prev => prev.filter(r => r.market_id !== marketId))
    } else {
      const { data } = await supabase.from('market_selections')
        .insert({ selection_section_id: sectionId, market_id: marketId })
        .select('*, bu:business_units(id, name)')
        .single()
      if (data) {
        setSelectedMarketIds(prev => new Set([...prev, marketId]))
        setSelectionRecords(prev => [...prev, data])
      }
    }
  }

  async function createBUsFromSelections() {
    const pending = selectionRecords.filter(r => !r.bu_id)
    if (!pending.length) return
    setCreatingBUs(true)

    // Find HI template BU
    const { data: hiList } = await supabase
      .from('business_units')
      .select('id')
      .ilike('name', '%harinas industriales%')
      .limit(1)
    const hiId = hiList?.[0]?.id

    // Get HI phases+sections+tasks for template
    let hiPhases = []
    if (hiId) {
      const { data: hp } = await supabase
        .from('phases')
        .select(`id, name, description, phase_order,
          sections(id, name, description, section_order,
            tasks(id, name, description, priority, task_order))`)
        .eq('business_unit_id', hiId)
        .order('phase_order')
      hiPhases = hp ?? []
    }

    // Get current BU count for ordering
    const { data: existingBUs } = await supabase
      .from('business_units')
      .select('bu_order')
      .eq('consultancy_id', consultancyId)
      .order('bu_order', { ascending: false })
      .limit(1)
    let nextOrder = (existingBUs?.[0]?.bu_order ?? 0) + 1

    const updatedRecords = [...selectionRecords]

    for (const rec of pending) {
      const market = sourceMarkets.find(m => m.id === rec.market_id)
      if (!market) continue

      // Create BU
      const { data: newBu } = await supabase
        .from('business_units')
        .insert({
          consultancy_id: consultancyId,
          name: market.name,
          description: market.description || null,
          bu_order: nextOrder++,
          status: 'pending',
        })
        .select()
        .single()
      if (!newBu) continue

      // Apply HI template
      if (hiId && hiPhases.length) {
        for (const phase of hiPhases) {
          const { data: newPhase } = await supabase.from('phases').insert({
            business_unit_id: newBu.id, consultancy_id: consultancyId,
            name: phase.name, description: phase.description || null,
            phase_order: phase.phase_order, status: 'pending', progress: 0,
          }).select().single()
          if (!newPhase) continue
          for (const section of (phase.sections ?? []).sort((a, b) => a.section_order - b.section_order)) {
            const { data: newSec } = await supabase.from('sections').insert({
              phase_id: newPhase.id, name: section.name,
              description: section.description || null,
              section_order: section.section_order, status: 'pending',
            }).select().single()
            if (!newSec) continue
            const tasks = (section.tasks ?? []).sort((a, b) => a.task_order - b.task_order)
            if (tasks.length) await supabase.from('tasks').insert(tasks.map(t => ({
              section_id: newSec.id, name: t.name, description: t.description || null,
              priority: t.priority || null, task_order: t.task_order, status: 'pending',
            })))
          }
        }
      }

      // Link BU to selection record
      await supabase.from('market_selections')
        .update({ bu_id: newBu.id })
        .eq('id', rec.id)

      const idx = updatedRecords.findIndex(r => r.id === rec.id)
      if (idx !== -1) updatedRecords[idx] = { ...updatedRecords[idx], bu_id: newBu.id, bu: { id: newBu.id, name: newBu.name } }
    }

    setSelectionRecords(updatedRecords)
    setCreatingBUs(false)
  }

  // ── End market selection functions ───────────────────────────────────────

  // ── Market performance functions ─────────────────────────────────────────
  async function savePerf(e) {
    e.preventDefault()
    setSavingPerf(true)
    const year = parseInt(perfForm.year) || CURRENT_YEAR
    const payload = {
      section_id:        sectionId,
      data_year:         year,
      data_quarter:      null,   // always annual
      market_size:       perfForm.market_size !== '' ? parseFloat(perfForm.market_size) : null,
      size_unit:         perfForm.size_unit || 'USD millones',
      volume:            perfForm.volume !== '' ? parseFloat(perfForm.volume) : null,
      volume_unit:       perfForm.volume_unit || 'toneladas',
      reference_year:    perfForm.reference_year ? parseInt(perfForm.reference_year) : year,
      data_source:       perfForm.data_source || null,
      maturity:          perfForm.maturity || null,
      has_seasonality:   perfForm.has_seasonality,
      seasonality_months: perfForm.has_seasonality ? (perfForm.seasonality_months || null) : null,
      updated_at:        new Date().toISOString(),
    }
    // Check if a record already exists for this section+year, then update; else insert
    const { data: existing } = await supabase.from('market_performance')
      .select('id').eq('section_id', sectionId).eq('data_year', year).is('data_quarter', null).maybeSingle()
    if (existing) {
      await supabase.from('market_performance').update(payload).eq('id', existing.id)
    } else {
      await supabase.from('market_performance').insert(payload)
    }
    await fetchData()
    setEditingPerf(false)
    setSavingPerf(false)
  }

  async function deletePerf(recordId) {
    if (!confirm('¿Eliminar este año del historial?')) return
    await supabase.from('market_performance').delete().eq('id', recordId ?? perfRecord)
    await fetchData()
    setEditingPerf(false)
  }
  // ── End market performance functions ─────────────────────────────────────

  // ── Market products functions ─────────────────────────────────────────────

  function openCreateProduct() {
    setEditProduct(null)
    setProductForm(EMPTY_PRODUCT)
    setShowProductModal(true)
  }

  function openEditProduct(product) {
    setEditProduct(product)
    setProductForm({
      name:             product.name,
      sales_size:       String(product.sales_size),
      size_unit:        product.size_unit ?? 'USD millones',
      volume:           product.volume != null ? String(product.volume) : '',
      volume_unit:      product.volume_unit ?? 'toneladas',
      reference_year:   product.reference_year ?? new Date().getFullYear(),
      data_source:      product.data_source ?? '',
      cagr:             product.cagr !== null && product.cagr !== undefined ? String(product.cagr) : '',
      last_year_growth: product.last_year_growth !== null && product.last_year_growth !== undefined ? String(product.last_year_growth) : '',
      description:      product.description ?? '',
    })
    setShowProductModal(true)
  }

  async function saveProduct(e) {
    e.preventDefault()
    setSavingProduct(true)
    const payload = {
      section_id:       sectionId,
      data_year:        selectedYear,
      data_quarter:     selectedQuarter,
      name:             productForm.name,
      sales_size:       parseFloat(productForm.sales_size) || 0,
      size_unit:        productForm.size_unit || 'USD millones',
      volume:           productForm.volume !== '' ? parseFloat(productForm.volume) : null,
      volume_unit:      productForm.volume_unit || 'toneladas',
      reference_year:   productForm.reference_year ? parseInt(productForm.reference_year) : null,
      data_source:      productForm.data_source || null,
      cagr:             productForm.cagr !== '' ? parseFloat(productForm.cagr) : null,
      last_year_growth: productForm.last_year_growth !== '' ? parseFloat(productForm.last_year_growth) : null,
      description:      productForm.description || null,
      product_order:    editProduct ? editProduct.product_order : (products.length + 1),
    }
    if (editProduct) {
      const { error } = await supabase.from('market_products').update(payload).eq('id', editProduct.id)
      if (!error) setProducts(prev => prev.map(p => p.id === editProduct.id ? { ...p, ...payload } : p))
      else alert(error.message)
    } else {
      const { data, error } = await supabase.from('market_products').insert(payload).select().single()
      if (data) setProducts(prev => [...prev, data])
      else if (error) alert(error.message)
    }
    setShowProductModal(false)
    setSavingProduct(false)
  }

  async function deleteProduct(productId) {
    if (!confirm('¿Eliminar este producto?')) return
    await supabase.from('market_products').delete().eq('id', productId)
    setProducts(prev => prev.filter(p => p.id !== productId))
  }

  // ── Bulk delete (across all years/quarters) ───────────────────────────────
  // Each market_* table stores one row per (section, year[, quarter]) so the
  // same product/channel/competitor name appears multiple times. Per-row
  // deletes leave orphans in other years; this helper wipes every row that
  // matches the chosen name in the current section in a single round-trip.
  async function openBulkDelete(kind) {
    setBulkDeleteKind(kind)
    setBulkDeleteSel('')
    setBulkDeleteErr('')
    setBulkDeleteBusy(false)
    const table =
      kind === 'product'    ? 'market_products' :
      kind === 'channel'    ? 'market_channels' :
      kind === 'competitor' ? 'market_competitors' : null
    if (!table) return
    const { data } = await supabase
      .from(table)
      .select('name')
      .eq('section_id', sectionId)
    const uniq = Array.from(new Set((data ?? []).map(r => r.name).filter(Boolean))).sort()
    setBulkDeleteNames(uniq)
    setBulkDeleteOpen(true)
  }

  async function confirmBulkDelete() {
    if (!bulkDeleteSel || !bulkDeleteKind) return
    setBulkDeleteBusy(true)
    setBulkDeleteErr('')
    const table =
      bulkDeleteKind === 'product'    ? 'market_products' :
      bulkDeleteKind === 'channel'    ? 'market_channels' :
      bulkDeleteKind === 'competitor' ? 'market_competitors' : null
    try {
      const { error } = await supabase.from(table).delete()
        .eq('section_id', sectionId)
        .eq('name', bulkDeleteSel)
      if (error) throw error
      setBulkDeleteOpen(false)
      // Refresh whatever is on screen — fetchData reloads the right slice.
      fetchData()
    } catch (err) {
      setBulkDeleteErr(err.message ?? 'No se pudo eliminar.')
    } finally {
      setBulkDeleteBusy(false)
    }
  }

  // ── End market products functions ─────────────────────────────────────────

  // ── Market competitors functions ──────────────────────────────────────────

  function openCreateCompetitor() {
    setEditCompetitor(null)
    setCompetitorForm({ name: '', description: '', is_own_brand: false })
    // initialise share and price forms with blank values for all sibling products
    const initShares = {}
    const initPrices = {}
    siblingProducts.forEach(p => {
      initShares[p.id] = ''
      initPrices[p.id] = { avg_price: '', price_unit: p.size_unit ?? '' }
    })
    setCompetitorShareForm(initShares)
    setCompetitorPriceForm(initPrices)
    setShowCompetitorModal(true)
  }

  function openEditCompetitor(comp) {
    setEditCompetitor(comp)
    setCompetitorForm({ name: comp.name, description: comp.description ?? '', is_own_brand: comp.is_own_brand ?? false })
    const existing = competitorShares[comp.id] ?? {}
    const existingPrices = competitorPrices[comp.id] ?? {}
    const initShares = {}
    const initPrices = {}
    siblingProducts.forEach(p => {
      initShares[p.id] = existing[p.id] !== undefined ? String(existing[p.id]) : ''
      const ep = existingPrices[p.id]
      initPrices[p.id] = ep
        ? { avg_price: String(ep.avg_price), price_unit: ep.price_unit ?? p.size_unit ?? '' }
        : { avg_price: '', price_unit: p.size_unit ?? '' }
    })
    setCompetitorShareForm(initShares)
    setCompetitorPriceForm(initPrices)
    setShowCompetitorModal(true)
  }

  async function saveCompetitor(e) {
    e.preventDefault()
    setSavingCompetitor(true)

    let compId = editCompetitor?.id
    const compPayload = {
      section_id:       sectionId,
      data_year:        selectedYear,
      data_quarter:     selectedQuarter,
      name:             competitorForm.name,
      description:      competitorForm.description || null,
      is_own_brand:     competitorForm.is_own_brand ?? false,
      competitor_order: editCompetitor ? editCompetitor.competitor_order : (competitors.length + 1),
    }

    // If marking as own brand, unmark any previous own brand in this section
    if (compPayload.is_own_brand) {
      await supabase.from('market_competitors')
        .update({ is_own_brand: false })
        .eq('section_id', sectionId)
        .neq('id', compId ?? '00000000-0000-0000-0000-000000000000')
      setCompetitors(prev => prev.map(c => ({ ...c, is_own_brand: false })))
    }

    if (editCompetitor) {
      const { error } = await supabase.from('market_competitors').update(compPayload).eq('id', compId)
      if (error) { alert(error.message); setSavingCompetitor(false); return }
      setCompetitors(prev => prev.map(c => c.id === compId ? { ...c, ...compPayload } : c))
    } else {
      const { data, error } = await supabase.from('market_competitors').insert(compPayload).select().single()
      if (error || !data) { alert(error?.message ?? 'Error'); setSavingCompetitor(false); return }
      compId = data.id
      setCompetitors(prev => [...prev, data])
    }

    // Upsert shares — only for products that have share OR price value
    const sharesToUpsert = siblingProducts
      .filter(p => {
        const hasShare = competitorShareForm[p.id] !== '' && competitorShareForm[p.id] !== undefined
        const hasPrice = competitorPriceForm[p.id]?.avg_price !== '' && competitorPriceForm[p.id]?.avg_price !== undefined
        return hasShare || hasPrice
      })
      .map(p => {
        const pf = competitorPriceForm[p.id] ?? {}
        return {
          competitor_id: compId,
          product_id:    p.id,
          market_share:  competitorShareForm[p.id] !== '' ? (parseFloat(competitorShareForm[p.id]) || 0) : null,
          avg_price:     pf.avg_price !== '' && pf.avg_price !== undefined ? parseFloat(pf.avg_price) : null,
          price_unit:    pf.price_unit || null,
        }
      })

    if (sharesToUpsert.length > 0) {
      await supabase.from('competitor_product_shares')
        .upsert(sharesToUpsert, { onConflict: 'competitor_id,product_id' })
    }

    // Also delete shares that were cleared (no share AND no price)
    const clearedProductIds = siblingProducts
      .filter(p => {
        const noShare = competitorShareForm[p.id] === '' || competitorShareForm[p.id] === undefined
        const noPrice = !competitorPriceForm[p.id]?.avg_price || competitorPriceForm[p.id]?.avg_price === ''
        return noShare && noPrice
      })
      .map(p => p.id)
    if (clearedProductIds.length > 0) {
      await supabase.from('competitor_product_shares')
        .delete()
        .eq('competitor_id', compId)
        .in('product_id', clearedProductIds)
    }

    // Update local shares and prices maps
    const newSharesForComp = {}
    const newPricesForComp = {}
    sharesToUpsert.forEach(s => {
      if (s.market_share != null) newSharesForComp[s.product_id] = s.market_share
      if (s.avg_price != null) newPricesForComp[s.product_id] = { avg_price: s.avg_price, price_unit: s.price_unit ?? '' }
    })
    setCompetitorShares(prev => ({ ...prev, [compId]: newSharesForComp }))
    setCompetitorPrices(prev => ({ ...prev, [compId]: newPricesForComp }))

    // Auto-register competitor inside the "Análisis Competitivo" phase (only for non-own-brand competitors)
    if (!editCompetitor && buId) {
      const normalizeStr = s => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      const { data: buPhases } = await supabase.from('phases').select('id, name').eq('business_unit_id', buId)

      if (!compPayload.is_own_brand) {
        // External competitor → create Análisis Competitivo sections as before
        const analisisPhase = (buPhases ?? []).find(p => normalizeStr(p.name).includes('analisis competitiv'))
        if (analisisPhase) {
          const { count } = await supabase.from('competitors').select('id', { count: 'exact', head: true }).eq('phase_id', analisisPhase.id)
          const nextOrder = (count ?? 0) + 1
          const { data: newComp } = await supabase.from('competitors').insert({
            phase_id: analisisPhase.id, name: competitorForm.name,
            description: competitorForm.description || null, competitor_order: nextOrder,
          }).select().single()
          if (newComp) {
            const COMPETITOR_SUBSECTIONS = ['Posicionamiento', 'Canales', 'Precio', 'Portafolio', 'Capacidad Industrial', 'Estrategia de Comunicación']
            await supabase.from('sections').insert(
              COMPETITOR_SUBSECTIONS.map((name, i) => ({
                phase_id: analisisPhase.id, competitor_id: newComp.id,
                name, section_order: i + 1, status: 'pending',
              }))
            )
            await supabase.from('market_competitors')
              .update({ competitor_ref_id: newComp.id, analisis_phase_id: analisisPhase.id })
              .eq('id', compId)
            setCompetitors(prev => prev.map(c =>
              c.id === compId ? { ...c, competitor_ref_id: newComp.id, analisis_phase_id: analisisPhase.id } : c
            ))
          }
        }
      } else {
        // Own brand → auto-import products into bu_strategy_portfolio for this BU's strategy portafolio section
        const estrategiaPhase = (buPhases ?? []).find(p => normalizeStr(p.name).includes('estrategia') || normalizeStr(p.name).includes('definicion'))
        if (estrategiaPhase) {
          const { data: stratSecs } = await supabase.from('sections').select('id, name').eq('phase_id', estrategiaPhase.id)
          const portSec = (stratSecs ?? []).find(s => normalizeStr(s.name).includes('portafolio'))
          if (portSec && sharesToUpsert.length > 0) {
            // Build product items from the shares entered in the modal
            const productItems = sharesToUpsert.map(s => {
              const prod = siblingProducts.find(p => p.id === s.product_id)
              return { id: s.product_id, nombre: prod?.name ?? 'Producto', decision: 'Mantener', rationale: '', palancas: [] }
            })
            // Upsert into bu_strategy_portfolio
            const { data: existingPort } = await supabase.from('bu_strategy_portfolio').select('id, products').eq('section_id', portSec.id).maybeSingle()
            if (existingPort) {
              // Merge: keep existing products not in the new list, add new ones
              const existingIds = new Set(productItems.map(p => p.id))
              const merged = [...(existingPort.products ?? []).filter(p => !existingIds.has(p.id)), ...productItems]
              await supabase.from('bu_strategy_portfolio').update({ products: merged, updated_at: new Date().toISOString() }).eq('id', existingPort.id)
            } else {
              await supabase.from('bu_strategy_portfolio').insert({ section_id: portSec.id, products: productItems, new_refs: [] })
            }
          }
        }
      }
    }

    setShowCompetitorModal(false)
    setSavingCompetitor(false)
  }

  async function deleteCompetitor(compId) {
    if (!confirm('¿Eliminar este competidor?')) return
    await supabase.from('market_competitors').delete().eq('id', compId)
    setCompetitors(prev => prev.filter(c => c.id !== compId))
    setCompetitorShares(prev => { const n = { ...prev }; delete n[compId]; return n })
  }

  // ── End market competitors functions ──────────────────────────────────────

  // ── Market channels functions ─────────────────────────────────────────────

  function openCreateChannel() {
    setEditChannel(null)
    setChannelForm({ name: '', description: '', num_clientes: '', clientes_ponderado: '' })
    const init = {}
    channelSiblingProducts.forEach(p => { init[p.id] = '' })
    setChannelWeightForm(init)
    setShowChannelModal(true)
  }

  function openEditChannel(ch) {
    setEditChannel(ch)
    setChannelForm({
      name:                ch.name,
      description:         ch.description ?? '',
      num_clientes:        ch.num_clientes != null ? String(ch.num_clientes) : '',
      clientes_ponderado:  ch.clientes_ponderado != null ? String(ch.clientes_ponderado) : '',
    })
    const existing = channelWeights[ch.id] ?? {}
    const init = {}
    channelSiblingProducts.forEach(p => { init[p.id] = existing[p.id] !== undefined ? String(existing[p.id]) : '' })
    setChannelWeightForm(init)
    setShowChannelModal(true)
  }

  async function saveChannel(e) {
    e.preventDefault()
    setSavingChannel(true)
    let chanId = editChannel?.id
    const payload = {
      section_id:          sectionId,
      data_year:           selectedYear,
      data_quarter:        selectedQuarter,
      name:                channelForm.name,
      description:         channelForm.description || null,
      channel_order:       editChannel ? editChannel.channel_order : (channels.length + 1),
      num_clientes:        channelForm.num_clientes !== '' ? parseInt(channelForm.num_clientes) || null : null,
      clientes_ponderado:  channelForm.clientes_ponderado !== '' ? parseInt(channelForm.clientes_ponderado) || null : null,
    }
    if (editChannel) {
      const { error } = await supabase.from('market_channels').update(payload).eq('id', chanId)
      if (error) { alert(error.message); setSavingChannel(false); return }
      setChannels(prev => prev.map(c => c.id === chanId ? { ...c, ...payload } : c))
    } else {
      const { data, error } = await supabase.from('market_channels').insert(payload).select().single()
      if (error || !data) { alert(error?.message ?? 'Error'); setSavingChannel(false); return }
      chanId = data.id
      setChannels(prev => [...prev, data])
    }
    // Upsert weights for filled products
    const toUpsert = channelSiblingProducts
      .filter(p => channelWeightForm[p.id] !== '' && channelWeightForm[p.id] !== undefined)
      .map(p => ({ channel_id: chanId, product_id: p.id, weight: parseFloat(channelWeightForm[p.id]) || 0 }))
    if (toUpsert.length > 0)
      await supabase.from('channel_product_weights').upsert(toUpsert, { onConflict: 'channel_id,product_id' })
    // Delete cleared
    const cleared = channelSiblingProducts
      .filter(p => channelWeightForm[p.id] === '' || channelWeightForm[p.id] === undefined)
      .map(p => p.id)
    if (cleared.length > 0)
      await supabase.from('channel_product_weights').delete().eq('channel_id', chanId).in('product_id', cleared)
    const newWeights = {}
    toUpsert.forEach(w => { newWeights[w.product_id] = w.weight })
    setChannelWeights(prev => ({ ...prev, [chanId]: newWeights }))
    setShowChannelModal(false)
    setSavingChannel(false)
  }

  async function deleteChannel(chanId) {
    if (!confirm('¿Eliminar este canal?')) return
    await supabase.from('market_channels').delete().eq('id', chanId)
    setChannels(prev => prev.filter(c => c.id !== chanId))
    setChannelWeights(prev => { const n = { ...prev }; delete n[chanId]; return n })
  }

  // ── End market channels functions ─────────────────────────────────────────

  // ── PowerPoint export helpers ─────────────────────────────────────────────
  const pptSectionName = section?.name ?? 'Sección'
  const pptYear = selectedYear

  // Shared slide header bar
  function addSlideHeader(slide, pres, title, subtitle, hexColor) {
    slide.background = { color: 'F9FAFB' }
    // Top color bar
    slide.addShape(pres.shapes.RECTANGLE, {
      x: 0, y: 0, w: 10, h: 1.1,
      fill: { color: hexColor }, line: { color: hexColor, width: 0 },
    })
    // Title
    slide.addText(title.toUpperCase(), {
      x: 0.45, y: 0.12, w: 8.5, h: 0.5,
      fontSize: 20, bold: true, color: 'FFFFFF', fontFace: 'Calibri', margin: 0,
    })
    // Subtitle
    slide.addText(subtitle, {
      x: 0.45, y: 0.62, w: 8.5, h: 0.38,
      fontSize: 10, color: 'FFFFFFCC', fontFace: 'Calibri', italic: true, margin: 0,
    })
    // Footer
    slide.addText(`Next Valley Consultants · ${pptSectionName} · ${pptYear}`, {
      x: 0, y: 5.3, w: 10, h: 0.3,
      fontSize: 8, color: '94A3B8', align: 'center', fontFace: 'Calibri',
    })
  }

  // ── Export: Desempeño del Mercado ─────────────────────────────────────────
  async function exportPerfToPptx() {
    const pres = new pptxgen()
    pres.layout = 'LAYOUT_16x9'
    const COLOR = 'D97706'  // amber-600

    // Slide 1 — KPI summary
    const s1 = pres.addSlide()
    addSlideHeader(s1, pres, 'Desempeño del Mercado', `Año ${pptYear} · ${section?.name ?? ''}`, COLOR)

    // Build KPI list dynamically — include volume + avg_price when available
    const salesVal = perfData.market_size != null && perfData.market_size !== '' ? Number(perfData.market_size) : null
    const volVal   = perfData.volume != null && perfData.volume !== '' ? Number(perfData.volume) : null
    const avgPrice = salesVal != null && volVal != null && volVal > 0 ? salesVal / volVal : null

    const kpis = [
      salesVal != null && { label: 'Ventas del mercado', value: `${salesVal.toLocaleString('es-CO')} ${perfData.size_unit ?? ''}` },
      volVal  != null && { label: 'Volumen', value: `${volVal.toLocaleString('es-CO')} ${perfData.volume_unit ?? ''}` },
      avgPrice != null && { label: 'Precio promedio', value: `${avgPrice < 1 ? avgPrice.toFixed(3) : avgPrice.toLocaleString('es-CO', { maximumFractionDigits: 2 })}`, sub: `${perfData.size_unit} / ${perfData.volume_unit}`, highlight: true },
      (() => {
        const ann = [...perfHistory].filter(h => h.data_quarter == null && h.market_size != null && Number(h.market_size) > 0).sort((a, b) => a.data_year - b.data_year)
        const per = [...perfHistory].filter(h => h.data_quarter === (selectedQuarter ?? null) && h.market_size != null && Number(h.market_size) > 0).sort((a, b) => a.data_year - b.data_year)
        let cagr = null, lyg = null
        if (ann.length >= 2) {
          const n = ann[ann.length - 1].data_year - ann[0].data_year
          if (n > 0) cagr = Math.round((Math.pow(Number(ann[ann.length - 1].market_size) / Number(ann[0].market_size), 1 / n) - 1) * 1000) / 10
        }
        const cur = per.find(h => h.data_year === selectedYear), prev = per.find(h => h.data_year === selectedYear - 1)
        if (cur && prev) lyg = Math.round(((Number(cur.market_size) - Number(prev.market_size)) / Number(prev.market_size)) * 1000) / 10
        const qLabel = selectedQuarter ? `Q${selectedQuarter} ` : ''
        return [
          cagr != null && { label: 'CAGR anual (calculado)', value: `${cagr}%` },
          lyg  != null && { label: `Crec. ${qLabel}${selectedYear - 1}→${qLabel}${selectedYear} (calculado)`, value: `${lyg}%` },
        ].filter(Boolean)
      })(),
      perfData.maturity && { label: 'Madurez', value: perfData.maturity },
    ].filter(Boolean)

    const kpiW = kpis.length <= 4 ? 2.1 : 1.65
    const kpiH = 1.45
    const kpiGap = 0.1
    const totalW = kpis.length * kpiW + (kpis.length - 1) * kpiGap
    const kpiStartX = Math.max((10 - totalW) / 2, 0.3)

    kpis.forEach((k, i) => {
      const x = kpiStartX + i * (kpiW + kpiGap)
      const bgColor = k.highlight ? 'FEF3C7' : 'FFFFFF'
      const borderColor = k.highlight ? 'FCD34D' : 'E5E7EB'
      const valColor = k.highlight ? COLOR : COLOR
      s1.addShape(pres.shapes.RECTANGLE, { x, y: 1.25, w: kpiW, h: kpiH, fill: { color: bgColor }, line: { color: borderColor, width: 1 }, shadow: { type: 'outer', blur: 4, offset: 2, angle: 135, color: '000000', opacity: 0.06 } })
      s1.addText(k.value, { x, y: 1.35, w: kpiW, h: 0.6, fontSize: kpis.length <= 4 ? 20 : 16, bold: true, color: valColor, align: 'center', fontFace: 'Calibri' })
      if (k.sub) s1.addText(k.sub, { x, y: 1.9, w: kpiW, h: 0.22, fontSize: 8, color: '92400E', align: 'center', fontFace: 'Calibri', italic: true })
      s1.addText(k.label + (k.highlight ? ' ✦' : ''), { x, y: k.sub ? 2.1 : 2.0, w: kpiW, h: 0.3, fontSize: 9, color: '6B7280', align: 'center', fontFace: 'Calibri', bold: k.highlight })
    })

    const infoItems = [
      perfData.data_source && `Fuente: ${perfData.data_source}`,
      perfData.has_seasonality ? `Estacionalidad: ${perfData.seasonality_months || 'Sí'}` : 'Sin estacionalidad',
      perfData.reference_year && `Año de referencia: ${perfData.reference_year}`,
    ].filter(Boolean)

    if (infoItems.length) {
      s1.addText(infoItems.join('   ·   '), {
        x: 0.4, y: 2.85, w: 9.2, h: 0.35, fontSize: 10, color: '9CA3AF', align: 'center', fontFace: 'Calibri',
      })
    }

    // Slide 2 — Historical evolution (only if ≥2 years)
    if (perfHistory.length >= 2) {
      const s2 = pres.addSlide()
      const hasVolHist = perfHistory.some(h => h.volume != null && Number(h.volume) > 0)
      addSlideHeader(s2, pres, 'Evolución Histórica', hasVolHist ? 'Ventas, volumen y precio promedio por año' : 'Ventas del mercado por año', COLOR)
      const hlabels = perfHistory.map(h => String(h.data_year))
      const saleVals = perfHistory.map(h => Number(h.market_size) || 0)
      const unit = perfHistory[0]?.size_unit ?? 'USD millones'

      if (hasVolHist) {
        // Two-series bar: ventas + volumen side by side + avg price line would need combo chart
        // Use grouped bar: ventas and volume (normalized to ventas scale via index)
        const volVals = perfHistory.map(h => Number(h.volume) || 0)
        const priceVals = perfHistory.map(h => {
          const s = Number(h.market_size), v = Number(h.volume)
          return s > 0 && v > 0 ? Math.round((s / v) * 100) / 100 : 0
        })
        // Slide 2a: ventas
        s2.addText(`Ventas del mercado (${unit})`, { x: 0.4, y: 1.15, w: 9.2, h: 0.3, fontSize: 11, color: '374151', fontFace: 'Calibri', bold: true })
        s2.addChart(pres.charts.BAR, [{ name: 'Ventas', labels: hlabels, values: saleVals }], {
          x: 0.4, y: 1.5, w: 4.3, h: 3.5, barDir: 'col',
          chartColors: [COLOR],
          chartArea: { fill: { color: 'FFFFFF' } },
          catAxisLabelColor: '6B7280', valAxisLabelColor: '6B7280',
          valGridLine: { color: 'E5E7EB', size: 0.5 }, catGridLine: { style: 'none' },
          showValue: true, dataLabelFontSize: 9, showLegend: false,
        })
        // Volumen
        s2.addText(`Volumen (${perfHistory[0]?.volume_unit ?? ''})`, { x: 5.3, y: 1.15, w: 4.3, h: 0.3, fontSize: 11, color: '374151', fontFace: 'Calibri', bold: true })
        s2.addChart(pres.charts.BAR, [{ name: 'Volumen', labels: hlabels, values: volVals }], {
          x: 5.3, y: 1.5, w: 4.3, h: 3.5, barDir: 'col',
          chartColors: ['3B82F6'],
          chartArea: { fill: { color: 'FFFFFF' } },
          catAxisLabelColor: '6B7280', valAxisLabelColor: '6B7280',
          valGridLine: { color: 'E5E7EB', size: 0.5 }, catGridLine: { style: 'none' },
          showValue: true, dataLabelFontSize: 9, showLegend: false,
        })
        // Slide 3: precio promedio
        const s3 = pres.addSlide()
        addSlideHeader(s3, pres, 'Precio Promedio Histórico', `${unit} / ${perfHistory[0]?.volume_unit ?? ''}`, COLOR)
        s3.addText(`Precio promedio = Ventas ÷ Volumen`, { x: 0.4, y: 1.15, w: 9.2, h: 0.3, fontSize: 11, color: '374151', fontFace: 'Calibri', bold: true })
        s3.addChart(pres.charts.LINE, [{ name: 'Precio promedio', labels: hlabels, values: priceVals }], {
          x: 0.4, y: 1.5, w: 9.2, h: 3.5,
          chartColors: [COLOR],
          lineSize: 3, lineSmooth: true,
          chartArea: { fill: { color: 'FFFFFF' } },
          catAxisLabelColor: '6B7280', valAxisLabelColor: '6B7280',
          valGridLine: { color: 'E5E7EB', size: 0.5 }, catGridLine: { style: 'none' },
          showValue: true, dataLabelFontSize: 10, dataLabelColor: COLOR,
          showLegend: false,
        })
      } else {
        s2.addText(`Ventas del mercado (${unit})`, { x: 0.4, y: 1.15, w: 9.2, h: 0.3, fontSize: 11, color: '374151', fontFace: 'Calibri', bold: true })
        s2.addChart(pres.charts.BAR, [{ name: 'Ventas', labels: hlabels, values: saleVals }], {
          x: 0.4, y: 1.5, w: 9.2, h: 3.5, barDir: 'col',
          chartColors: [COLOR],
          chartArea: { fill: { color: 'FFFFFF' }, roundedCorners: false },
          catAxisLabelColor: '6B7280', valAxisLabelColor: '6B7280',
          valGridLine: { color: 'E5E7EB', size: 0.5 }, catGridLine: { style: 'none' },
          showValue: true, dataLabelColor: '374151', dataLabelFontSize: 10,
          showLegend: false,
        })
      }
    }

    await pres.writeFile({ fileName: `desempeno-mercado-${pptYear}.pptx` })
  }

  // ── Export: Soluciones / Productos ────────────────────────────────────────
  async function exportProductsToPptx() {
    if (!products.length) return
    const pres = new pptxgen()
    pres.layout = 'LAYOUT_16x9'
    const COLOR = '7C3AED'  // violet-700
    const COLORS = ['7C3AED','8B5CF6','A78BFA','C4B5FD','6D28D9','5B21B6','4C1D95','9333EA']

    const sorted = [...products].sort((a, b) => (Number(b.sales_size) || 0) - (Number(a.sales_size) || 0))
    const total  = sorted.reduce((s, p) => s + (Number(p.sales_size) || 0), 0)

    // Slide 1 — Horizontal bar ranking
    const s1 = pres.addSlide()
    addSlideHeader(s1, pres, 'Soluciones — Ranking de Productos', `Tamaño de mercado por producto · Año ${pptYear}`, COLOR)

    const labels = sorted.map(p => p.name)
    const values = sorted.map(p => Number(p.sales_size) || 0)
    const unit   = sorted[0]?.size_unit ?? 'USD millones'

    s1.addText(`Tamaño del mercado por producto (${unit})`, { x: 0.4, y: 1.15, w: 9.2, h: 0.3, fontSize: 11, color: '374151', fontFace: 'Calibri', bold: true })
    s1.addChart(pres.charts.BAR, [{ name: 'Tamaño', labels, values }], {
      x: 0.4, y: 1.5, w: 9.2, h: 3.5, barDir: 'bar',
      chartColors: sorted.map((_, i) => COLORS[i % COLORS.length]),
      chartArea: { fill: { color: 'FFFFFF' }, roundedCorners: false },
      catAxisLabelColor: '374151', valAxisLabelColor: '6B7280',
      valGridLine: { color: 'E5E7EB', size: 0.5 }, catGridLine: { style: 'none' },
      showValue: true, dataLabelColor: '374151', dataLabelFontSize: 10,
      showLegend: false,
    })

    // Slide 2 — % of total
    if (total > 0) {
      const s2 = pres.addSlide()
      addSlideHeader(s2, pres, 'Participación por Producto', `% del total del mercado · Año ${pptYear}`, COLOR)
      s2.addChart(pres.charts.PIE, [{ name: 'Participación', labels: sorted.map(p => p.name), values: sorted.map(p => Math.round(((Number(p.sales_size) || 0) / total) * 1000) / 10) }], {
        x: 1, y: 1.2, w: 5, h: 4,
        chartColors: sorted.map((_, i) => COLORS[i % COLORS.length]),
        chartArea: { fill: { color: 'FFFFFF' } },
        showPercent: true, dataLabelFontSize: 11,
        legendPos: 'r', showLegend: true, legendFontSize: 10,
      })
      s2.addText(`Total: ${total.toLocaleString('es-CO')} ${sorted[0]?.size_unit ?? ''}`, {
        x: 6.2, y: 2.5, w: 3.4, h: 0.5, fontSize: 14, bold: true, color: COLOR, fontFace: 'Calibri',
      })
    }

    // Slide 3 — Precio promedio por producto (only when any product has volume)
    const withPrice = sorted.filter(p => p.volume != null && Number(p.volume) > 0)
    if (withPrice.length > 0) {
      const s3 = pres.addSlide()
      addSlideHeader(s3, pres, 'Precio Promedio por Producto', `Ventas ÷ Volumen · Año ${pptYear}`, COLOR)
      const priceLabels = withPrice.map(p => p.name)
      const priceValues = withPrice.map(p => {
        const pr = Number(p.sales_size) / Number(p.volume)
        return Math.round(pr * 100) / 100
      })
      const priceUnit = `${withPrice[0].size_unit ?? ''} / ${withPrice[0].volume_unit ?? ''}`
      s3.addText(`Precio promedio (${priceUnit})`, { x: 0.4, y: 1.15, w: 9.2, h: 0.3, fontSize: 11, color: '374151', fontFace: 'Calibri', bold: true })
      s3.addChart(pres.charts.BAR, [{ name: 'Precio promedio', labels: priceLabels, values: priceValues }], {
        x: 0.4, y: 1.5, w: 9.2, h: 3.5, barDir: 'bar',
        chartColors: withPrice.map((_, i) => COLORS[i % COLORS.length]),
        chartArea: { fill: { color: 'FFFFFF' }, roundedCorners: false },
        catAxisLabelColor: '374151', valAxisLabelColor: '6B7280',
        valGridLine: { color: 'E5E7EB', size: 0.5 }, catGridLine: { style: 'none' },
        showValue: true, dataLabelColor: '374151', dataLabelFontSize: 10,
        showLegend: false,
      })
    }

    await pres.writeFile({ fileName: `productos-mercado-${pptYear}.pptx` })
  }

  // ── Export: Participaciones (Competidores) ─────────────────────────────────
  async function exportCompetitorsToPptx() {
    if (!competitors.length) return
    const pres = new pptxgen()
    pres.layout = 'LAYOUT_16x9'
    const COLOR = 'BE123C'  // rose-700
    const PROD_COLORS = ['3B82F6','10B981','F59E0B','8B5CF6','EF4444','06B6D4','84CC16','F97316']

    // Slide 1 — Stacked bar per competitor per product
    const s1 = pres.addSlide()
    addSlideHeader(s1, pres, 'Participaciones de Mercado', `Por competidor y producto · Año ${pptYear}`, COLOR)

    const compLabels = competitors.map(c => c.name)
    const chartData  = siblingProducts.map((p, i) => ({
      name:   p.name,
      labels: compLabels,
      values: competitors.map(c => {
        const shares = competitorShares[c.id] ?? {}
        return Number(shares[p.id]) || 0
      }),
    }))

    if (chartData.length > 0) {
      s1.addChart(pres.charts.BAR, chartData, {
        x: 0.4, y: 1.2, w: 9.2, h: 4.0, barDir: 'bar', barGrouping: 'stacked',
        chartColors: siblingProducts.map((_, i) => PROD_COLORS[i % PROD_COLORS.length]),
        chartArea: { fill: { color: 'FFFFFF' }, roundedCorners: false },
        catAxisLabelColor: '374151', valAxisLabelColor: '6B7280',
        valGridLine: { color: 'E5E7EB', size: 0.5 }, catGridLine: { style: 'none' },
        showValue: true, dataLabelFontSize: 9, dataLabelColor: 'FFFFFF',
        showLegend: true, legendPos: 'b', legendFontSize: 9,
        valAxisTitle: '% Participación', showValAxisTitle: true,
      })
    } else {
      // Fallback: total share per competitor (sum across products)
      const totals = competitors.map(c => {
        const shares = competitorShares[c.id] ?? {}
        return Object.values(shares).reduce((s, v) => s + (Number(v) || 0), 0)
      })
      s1.addChart(pres.charts.BAR, [{ name: 'Participación total', labels: compLabels, values: totals }], {
        x: 0.4, y: 1.2, w: 9.2, h: 4.0, barDir: 'bar',
        chartColors: [COLOR],
        chartArea: { fill: { color: 'FFFFFF' }, roundedCorners: false },
        catAxisLabelColor: '374151', valAxisLabelColor: '6B7280',
        valGridLine: { color: 'E5E7EB', size: 0.5 }, catGridLine: { style: 'none' },
        showValue: true, dataLabelFontSize: 10, dataLabelColor: '374151',
        showLegend: false,
      })
    }

    // Slide 2 — Precio promedio por competidor por producto
    // Collect products that have at least one competitor price OR market avg price
    const priceProducts = siblingProducts.filter(p => {
      const hasMkt = p.volume != null && Number(p.volume) > 0
      const hasComp = competitors.some(c => (competitorPrices[c.id] ?? {})[p.id]?.avg_price != null)
      return hasMkt || hasComp
    })

    if (priceProducts.length > 0) {
      const s2 = pres.addSlide()
      addSlideHeader(s2, pres, 'Precio Promedio por Producto', `Competidores vs precio de mercado · Año ${pptYear}`, COLOR)

      const priceLabels = priceProducts.map(p => p.name)
      const priceChartData = []

      // Market avg price series
      const mktPriceValues = priceProducts.map(p => {
        if (p.volume != null && Number(p.volume) > 0) {
          return Math.round((Number(p.sales_size) / Number(p.volume)) * 100) / 100
        }
        return 0
      })
      if (mktPriceValues.some(v => v > 0)) {
        priceChartData.push({ name: 'Mercado', labels: priceLabels, values: mktPriceValues })
      }

      // Competitor price series
      competitors.forEach((c, i) => {
        const compPrices = priceProducts.map(p => {
          const pd = (competitorPrices[c.id] ?? {})[p.id]
          return pd?.avg_price != null ? Math.round(Number(pd.avg_price) * 100) / 100 : 0
        })
        if (compPrices.some(v => v > 0)) {
          priceChartData.push({ name: c.name, labels: priceLabels, values: compPrices })
        }
      })

      if (priceChartData.length > 0) {
        const ALL_COLORS = ['6B7280', ...PROD_COLORS]  // grey for market, colors for competitors
        s2.addChart(pres.charts.BAR, priceChartData, {
          x: 0.4, y: 1.2, w: 9.2, h: 4.0, barDir: 'col', barGrouping: 'clustered',
          chartColors: priceChartData.map((_, i) => ALL_COLORS[i % ALL_COLORS.length]),
          chartArea: { fill: { color: 'FFFFFF' }, roundedCorners: false },
          catAxisLabelColor: '374151', valAxisLabelColor: '6B7280',
          valGridLine: { color: 'E5E7EB', size: 0.5 }, catGridLine: { style: 'none' },
          showValue: true, dataLabelFontSize: 9, dataLabelColor: '374151',
          showLegend: true, legendPos: 'b', legendFontSize: 9,
          valAxisTitle: 'Precio promedio', showValAxisTitle: true,
        })
        s2.addText('Precio de mercado = Ventas ÷ Volumen', {
          x: 0.4, y: 5.35, w: 9.2, h: 0.25, fontSize: 8, color: '9CA3AF', fontFace: 'Calibri', italic: true,
        })
      }
    }

    await pres.writeFile({ fileName: `participaciones-mercado-${pptYear}.pptx` })
  }

  // ── Export: Canales ───────────────────────────────────────────────────────
  async function exportChannelsToPptx() {
    if (!channels.length) return
    const pres = new pptxgen()
    pres.layout = 'LAYOUT_16x9'
    const COLOR = '0369A1'  // sky-700
    const PROD_COLORS = ['3B82F6','10B981','F59E0B','8B5CF6','EF4444','06B6D4','84CC16','F97316']

    // Slide 1 — Stacked bar per channel per product
    const s1 = pres.addSlide()
    addSlideHeader(s1, pres, 'Canales de Distribución', `Peso por producto en cada canal · Año ${pptYear}`, COLOR)

    const chanLabels = channels.map(c => c.name)
    const chartData  = channelSiblingProducts.map((p, i) => ({
      name:   p.name,
      labels: chanLabels,
      values: channels.map(ch => {
        const w = channelWeights[ch.id] ?? {}
        return Number(w[p.id]) || 0
      }),
    }))

    if (chartData.length > 0) {
      s1.addChart(pres.charts.BAR, chartData, {
        x: 0.4, y: 1.2, w: 9.2, h: 4.0, barDir: 'bar', barGrouping: 'clustered',
        chartColors: channelSiblingProducts.map((_, i) => PROD_COLORS[i % PROD_COLORS.length]),
        chartArea: { fill: { color: 'FFFFFF' }, roundedCorners: false },
        catAxisLabelColor: '374151', valAxisLabelColor: '6B7280',
        valGridLine: { color: 'E5E7EB', size: 0.5 }, catGridLine: { style: 'none' },
        showValue: true, dataLabelFontSize: 9, dataLabelColor: '1E293B',
        showLegend: true, legendPos: 'b', legendFontSize: 9,
        valAxisTitle: 'Peso (%)', showValAxisTitle: true,
      })
    } else {
      // No products: just show channel names
      const avg = channels.map(ch => {
        const w = channelWeights[ch.id] ?? {}
        const vals = Object.values(w).map(Number)
        return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : 0
      })
      s1.addChart(pres.charts.BAR, [{ name: 'Peso promedio (%)', labels: chanLabels, values: avg }], {
        x: 0.4, y: 1.2, w: 9.2, h: 4.0, barDir: 'bar',
        chartColors: [COLOR],
        chartArea: { fill: { color: 'FFFFFF' }, roundedCorners: false },
        catAxisLabelColor: '374151', valAxisLabelColor: '6B7280',
        valGridLine: { color: 'E5E7EB', size: 0.5 }, catGridLine: { style: 'none' },
        showValue: true, dataLabelFontSize: 10, dataLabelColor: '374151',
        showLegend: false,
      })
    }

    // Slide 2 — Clientes por canal (only when any channel has client data)
    const channelsWithClients = channels.filter(ch => ch.num_clientes != null || ch.clientes_ponderado != null)
    if (channelsWithClients.length > 0) {
      const s2 = pres.addSlide()
      addSlideHeader(s2, pres, 'Clientes por Canal', `Concentración de clientes · Año ${pptYear}`, COLOR)
      const clientLabels = channelsWithClients.map(ch => ch.name)
      const clientSeries = []
      if (channelsWithClients.some(ch => ch.num_clientes != null)) {
        clientSeries.push({
          name: 'Total clientes',
          labels: clientLabels,
          values: channelsWithClients.map(ch => ch.num_clientes ?? 0),
        })
      }
      if (channelsWithClients.some(ch => ch.clientes_ponderado != null)) {
        clientSeries.push({
          name: 'Clientes ponderados (clave)',
          labels: clientLabels,
          values: channelsWithClients.map(ch => ch.clientes_ponderado ?? 0),
        })
      }
      if (clientSeries.length > 0) {
        s2.addChart(pres.charts.BAR, clientSeries, {
          x: 0.4, y: 1.2, w: 9.2, h: 3.8, barDir: 'col', barGrouping: 'clustered',
          chartColors: ['0369A1', '6366F1'],
          chartArea: { fill: { color: 'FFFFFF' }, roundedCorners: false },
          catAxisLabelColor: '374151', valAxisLabelColor: '6B7280',
          valGridLine: { color: 'E5E7EB', size: 0.5 }, catGridLine: { style: 'none' },
          showValue: true, dataLabelFontSize: 10, dataLabelColor: '374151',
          showLegend: true, legendPos: 'b', legendFontSize: 9,
        })
        s2.addText('Clientes ponderados: aquellos que concentran el volumen/peso del canal', {
          x: 0.4, y: 5.3, w: 9.2, h: 0.25, fontSize: 8, color: '9CA3AF', fontFace: 'Calibri', italic: true,
        })
      }
    }

    await pres.writeFile({ fileName: `canales-mercado-${pptYear}.pptx` })
  }

  // ── Export: Precio ────────────────────────────────────────────────────────
  async function exportPrecioToPptx() {
    const hasData = precioMktHistory.length > 0 || precioProductHistory.length > 0 || precioCompHistory.length > 0
    if (!hasData) return
    const pres = new pptxgen()
    pres.layout = 'LAYOUT_16x9'
    const COLOR = '059669' // emerald-600
    const COMP_PALETTE = ['7C3AED','0369A1','BE123C','D97706','9333EA','0891B2','DC2626','16A34A']
    const COLOR_MKT = 'D97706' // amber

    const allProductNames = Array.from(new Set([
      ...precioProductHistory.map(r => r.name),
      ...precioCompHistory.map(r => r.product_name),
    ]))
    const allCompNames = Array.from(new Set(precioCompHistory.map(r => r.competitor_name)))

    // Helper: build chart data for pptxgen
    function buildPptData(productName) {
      const years = Array.from(new Set([
        ...(productName === '__global__' ? precioMktHistory.map(r => r.year) : []),
        ...precioProductHistory.filter(r => r.name === productName).map(r => r.year),
        ...precioCompHistory.filter(r => r.product_name === productName).map(r => r.year),
      ])).sort((a, b) => a - b)

      const labels = years.map(String)
      const series = []

      // Market line
      const mktKey = productName === '__global__' ? 'Mercado Global' : 'Precio mercado'
      const mktVals = years.map(y => {
        if (productName === '__global__') {
          return precioMktHistory.find(r => r.year === y)?.avg_price ?? null
        }
        return precioProductHistory.find(r => r.name === productName && r.year === y)?.avg_price ?? null
      })
      if (mktVals.some(v => v != null)) {
        series.push({ name: mktKey, labels, values: mktVals.map(v => v ?? 0) })
      }

      // Competitor lines
      allCompNames.forEach(cName => {
        const vals = years.map(y =>
          precioCompHistory.find(r => r.product_name === productName && r.competitor_name === cName && r.year === y)?.avg_price ?? null
        )
        if (vals.some(v => v != null)) {
          series.push({ name: cName, labels, values: vals.map(v => v ?? 0) })
        }
      })

      return { labels, series, years }
    }

    // Slide 1: Global market price (if available)
    if (precioMktHistory.length > 0) {
      const s1 = pres.addSlide()
      addSlideHeader(s1, pres, 'Precio Promedio del Mercado', `Evolución histórica · ${precioMktHistory[0]?.unit ?? ''}`, COLOR)
      const { labels, series } = buildPptData('__global__')
      if (series.length > 0) {
        s1.addChart(pres.charts.LINE, series, {
          x: 0.4, y: 1.2, w: 9.2, h: 4.1,
          chartColors: [COLOR_MKT, ...COMP_PALETTE],
          chartArea: { fill: { color: 'FFFFFF' }, roundedCorners: false },
          catAxisLabelColor: '374151', valAxisLabelColor: '6B7280',
          valGridLine: { color: 'E5E7EB', size: 0.5 },
          showValue: true, dataLabelFontSize: 9, dataLabelColor: '374151',
          lineDataSymbol: 'circle', lineDataSymbolSize: 6,
          showLegend: series.length > 1, legendPos: 'b', legendFontSize: 9,
        })
      }
    }

    // One slide per product
    for (const pName of allProductNames) {
      const { labels, series } = buildPptData(pName)
      if (series.length === 0) continue
      const slide = pres.addSlide()
      const priceUnit = precioProductHistory.find(r => r.name === pName)?.price_unit ?? ''
      addSlideHeader(slide, pres, `Precio — ${pName}`, `Mercado vs competidores${priceUnit ? ` · ${priceUnit}` : ''}`, COLOR)
      slide.addChart(pres.charts.LINE, series, {
        x: 0.4, y: 1.2, w: 9.2, h: 3.8,
        chartColors: [COLOR_MKT, ...COMP_PALETTE],
        chartArea: { fill: { color: 'FFFFFF' }, roundedCorners: false },
        catAxisLabelColor: '374151', valAxisLabelColor: '6B7280',
        valGridLine: { color: 'E5E7EB', size: 0.5 },
        showValue: true, dataLabelFontSize: 9, dataLabelColor: '374151',
        lineDataSymbol: 'circle', lineDataSymbolSize: 6,
        showLegend: true, legendPos: 'b', legendFontSize: 9,
      })

      // Price positioning table for latest year
      const lastYear = Math.max(...precioCompHistory.filter(r => r.product_name === pName).map(r => r.year).concat([0]))
      const mktPrice = precioProductHistory.find(r => r.name === pName && r.year === lastYear)?.avg_price
      if (mktPrice != null && lastYear > 0) {
        const compRows = precioCompHistory.filter(r => r.product_name === pName && r.year === lastYear)
        if (compRows.length > 0) {
          const tableRows = [
            [{ text: 'Competidor', options: { bold: true, fill: '059669', color: 'FFFFFF', fontSize: 9 } },
             { text: 'Precio', options: { bold: true, fill: '059669', color: 'FFFFFF', fontSize: 9 } },
             { text: 'vs Mercado', options: { bold: true, fill: '059669', color: 'FFFFFF', fontSize: 9 } }],
            [{ text: 'Precio de mercado', options: { bold: true, fontSize: 9 } },
             { text: mktPrice.toLocaleString('es-CO', { maximumFractionDigits: 4 }), options: { bold: true, fontSize: 9 } },
             { text: '—', options: { fontSize: 9 } }],
            ...compRows.map(r => {
              const delta = ((r.avg_price - mktPrice) / mktPrice * 100).toFixed(1)
              const isOver = parseFloat(delta) > 0
              return [
                { text: r.competitor_name, options: { fontSize: 9 } },
                { text: r.avg_price.toLocaleString('es-CO', { maximumFractionDigits: 4 }), options: { fontSize: 9 } },
                { text: `${isOver ? '+' : ''}${delta}%`, options: { fontSize: 9, color: isOver ? 'DC2626' : '16A34A', bold: true } },
              ]
            }),
          ]
          slide.addTable(tableRows, { x: 0.4, y: 5.05, w: 9.2, rowH: 0.28, border: { pt: 0.5, color: 'E5E7EB' } })
        }
      }
    }

    await pres.writeFile({ fileName: `precios-mercado-${pptYear}.pptx` })
  }
  // ── End PowerPoint exports ─────────────────────────────────────────────────

  async function updateSectionStatus(status) {
    await supabase.from('sections').update({ status }).eq('id', sectionId)
    setSection(s => ({ ...s, status }))
    const { data: allSections } = await supabase.from('sections').select('status').eq('phase_id', phaseId)
    if (allSections) {
      const completed = allSections.filter(s => s.status === 'completed').length
      const progress  = Math.round((completed / allSections.length) * 100)
      await supabase.from('phases').update({ progress }).eq('id', phaseId)
    }
  }

  function openCreate() {
    setEditTask(null)
    setTaskForm({ name: '', description: '', assignee_id: '', due_date: '', priority: 'medium', status: 'pending' })
    setMoveBuId('')
    setMovePhaseId('')
    setMoveSectionId('')
    setTaskResultText('')
    setTaskSavedResult('')
    setTaskResultRecordId(null)
    setTaskEditingResult(false)
    setShowTaskModal(true)
  }

  async function openEdit(task) {
    setEditTask(task)
    setTaskForm({
      name: task.name, description: task.description ?? '',
      assignee_id: task.assignee_id ?? '', due_date: task.due_date ?? '',
      priority: task.priority ?? 'medium', status: task.status ?? 'pending',
    })
    setMoveBuId(buId ?? '')
    setMovePhaseId(phaseId)
    setMoveSectionId(sectionId)
    setTaskEditingResult(false)
    // Load task resultado
    const { data: rec } = await supabase.from('ai_reports').select('id, content')
      .eq('entity_type', 'task').eq('entity_id', task.id)
      .eq('report_type', 'resultado').maybeSingle()
    if (rec) {
      setTaskSavedResult(rec.content ?? '')
      setTaskResultText(rec.content ?? '')
      setTaskResultRecordId(rec.id)
    } else {
      setTaskSavedResult('')
      setTaskResultText('')
      setTaskResultRecordId(null)
    }
    setShowTaskModal(true)
  }

  async function saveTaskResult() {
    if (!taskResultText.trim() || !editTask) return
    setTaskSavingResult(true)
    const { data: ph } = await supabase.from('phases').select('consultancy_id').eq('id', phaseId).single()
    if (taskResultRecordId) {
      await supabase.from('ai_reports').update({ content: taskResultText }).eq('id', taskResultRecordId)
    } else {
      const { data } = await supabase.from('ai_reports').insert({
        consultancy_id: ph?.consultancy_id,
        entity_type: 'task',
        entity_id: editTask.id,
        report_type: 'resultado',
        content: taskResultText,
      }).select().single()
      if (data) setTaskResultRecordId(data.id)
    }
    setTaskSavedResult(taskResultText)
    setTaskEditingResult(false)
    setTaskSavingResult(false)
  }

  async function logActivity(action, entityType, entityName) {
    const { data: ph } = await supabase.from('phases').select('consultancy_id').eq('id', phaseId).single()
    if (!ph?.consultancy_id) return
    await supabase.from('activity_log').insert({
      consultancy_id: ph.consultancy_id, user_id: profile.id,
      user_name: profile.full_name, action, entity_type: entityType, entity_name: entityName,
    })
  }

  async function saveTask(e) {
    e.preventDefault()
    setSaving(true)
    const targetSection = (editTask && moveSectionId) ? moveSectionId : sectionId
    const moved = editTask && targetSection !== sectionId
    const payload = {
      ...taskForm,
      assignee_id: taskForm.assignee_id || null,
      due_date:    taskForm.due_date    || null,
      section_id:  targetSection,
      created_by:  profile.id,
    }
    if (editTask) {
      await supabase.from('tasks').update(payload).eq('id', editTask.id)
      await logActivity('updated', 'task', taskForm.name)
    } else {
      await supabase.from('tasks').insert(payload)
      await logActivity('created', 'task', taskForm.name)
    }
    if (taskForm.assignee_id) {
      const { data: ph } = await supabase.from('phases').select('consultancy_id').eq('id', phaseId).single()
      if (ph?.consultancy_id) {
        await supabase.from('notifications').insert({
          user_id: taskForm.assignee_id, type: 'task_assigned',
          title: 'Nueva tarea asignada', message: taskForm.name,
          entity_type: 'task', consultancy_id: ph.consultancy_id,
        })
      }
    }
    setShowTaskModal(false)
    if (moved) {
      // Remove task from this section's list — it now lives elsewhere
      setTasks(prev => prev.filter(t => t.id !== editTask.id))
    } else {
      fetchData()
    }
    setSaving(false)
  }

  async function deleteTask(taskId) {
    if (!confirm('¿Eliminar esta tarea?')) return
    const taskName = tasks.find(t => t.id === taskId)?.name ?? ''
    await supabase.from('tasks').delete().eq('id', taskId)
    setTasks(tasks.filter(t => t.id !== taskId))
    await logActivity('deleted', 'task', taskName)
  }

  async function updateTaskStatus(taskId, status) {
    await supabase.from('tasks').update({ status }).eq('id', taskId)
    setTasks(tasks.map(t => t.id === taskId ? { ...t, status } : t))
    if (status === 'completed') {
      const taskName = tasks.find(t => t.id === taskId)?.name ?? ''
      await logActivity('completed', 'task', taskName)
    }
  }

  function onDragStart(taskId) { dragTaskId.current = taskId }
  function onDragOver(e) { e.preventDefault() }
  async function onDrop(status) {
    if (!dragTaskId.current) return
    await updateTaskStatus(dragTaskId.current, status)
    dragTaskId.current = null
  }

  // ── Tab conclusion helpers ────────────────────────────────────────────────
  async function saveTabConclusion(tabId) {
    setSavingConclusion(s => ({ ...s, [tabId]: true }))
    const existing = tabConclusions[tabId]
    const text = existing?.text ?? ''
    const payload = { section_id: sectionId, tab_id: tabId, conclusion_text: text, updated_at: new Date().toISOString() }
    if (existing?.id) {
      await supabase.from('section_tab_conclusions').update(payload).eq('id', existing.id)
    } else {
      const { data: nr } = await supabase.from('section_tab_conclusions').insert(payload).select().single()
      if (nr) setTabConclusions(prev => ({ ...prev, [tabId]: { id: nr.id, text: nr.conclusion_text ?? '' } }))
    }
    setSavingConclusion(s => ({ ...s, [tabId]: false }))
    setConclusionSaved(s => ({ ...s, [tabId]: true }))
    setTimeout(() => setConclusionSaved(s => ({ ...s, [tabId]: false })), 2500)
  }

  async function generateTabConclusion(tabId, dataContext) {
    setGeneratingConclusion(s => ({ ...s, [tabId]: true }))
    try {
      const ctx = `Sección: ${section?.name ?? ''}\nFase: ${phaseName ?? ''}\nDatos:\n${dataContext}`
      const { data } = await supabase.functions.invoke('ai-report', {
        body: { context: ctx, reportType: 'tab_conclusion', entityType: 'section', consultancyId }
      })
      if (data?.report) setTabConclusions(prev => ({ ...prev, [tabId]: { ...(prev[tabId] ?? {}), text: data.report } }))
    } catch(e) { console.error(e) }
    setGeneratingConclusion(s => ({ ...s, [tabId]: false }))
  }

  function renderConclusionBlock(tabId, dataForAI = '') {
    const text = tabConclusions[tabId]?.text ?? ''
    const saving = savingConclusion[tabId] ?? false
    const saved = conclusionSaved[tabId] ?? false
    const generating = generatingConclusion[tabId] ?? false
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-3">
        <div className="flex items-center justify-between border-b border-gray-100 pb-2">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Conclusión Ejecutiva</p>
          {(isAdmin || isNVC) && !phaseLocked && (
            <div className="flex gap-2">
              <button onClick={() => generateTabConclusion(tabId, dataForAI)} disabled={generating}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 hover:bg-violet-100 text-violet-700 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50">
                {generating ? <><Loader size={12} className="animate-spin" />Generando...</> : <><Sparkles size={12} />Generar con IA</>}
              </button>
              <button onClick={() => saveTabConclusion(tabId)} disabled={saving}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 ${saved ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
                {saving ? <><Loader size={12} className="animate-spin" />Guardando...</> : saved ? <><CheckCircle2 size={12} />¡Guardado!</> : <><Save size={12} />Guardar</>}
              </button>
            </div>
          )}
        </div>
        <textarea rows={3} value={text}
          onChange={e => setTabConclusions(prev => ({ ...prev, [tabId]: { ...(prev[tabId] ?? {}), text: e.target.value } }))}
          placeholder="Escribe aquí la conclusión ejecutiva, o genera una automáticamente con IA..."
          disabled={!isAdmin && !isNVC}
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300 resize-none bg-white text-gray-700 disabled:bg-gray-50 disabled:text-gray-500" />
      </div>
    )
  }

  async function logChange(tabId, summary, snapshot = {}) {
    try {
      await supabase.from('field_change_log').insert({
        section_id: sectionId,
        tab_id: tabId,
        changed_by: profile?.id ?? null,
        changed_by_name: profile?.full_name ?? profile?.email ?? 'Usuario',
        summary,
        snapshot,
        created_at: new Date().toISOString(),
      })
    } catch (_) { /* non-blocking */ }
  }

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-500 border-t-transparent" />
    </div>
  )
  if (!section) return null

  const normName = (section.name ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  const isMarketMatrixSection      = normName.includes('matriz de mercados')
  const isMarketSelectionSection   = normName.includes('seleccion de mercados')
  const isMarketPerformanceSection = normName.includes('desempeno') && normName.includes('mercado')
  const isMarketProductsSection    = normName.includes('soluciones') || (normName.includes('producto') && !normName.includes('precio'))
  const isMarketCompetitorsSection = normName.includes('participacion')
  const isMarketChannelsSection    = normName.includes('canal') && !section.competitor_id
  const isCompetitorCanalesSection    = normName.includes('canal') && !!section.competitor_id
  const normPhaseName                  = (phaseName ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  const isBUInternoComunicacionSection = normName.includes('comunicacion') && !section.competitor_id && normPhaseName.includes('interno')
  const isMktComunicacionSection      = normName.includes('comunicacion') && !section.competitor_id && !normPhaseName.includes('interno')
  const isCompComunicacionSection     = normName.includes('comunicacion') && !!section.competitor_id
  const isMarketPrecioSection      = normName.includes('precio') && !section.competitor_id
  const isCompetitorPrecioSection  = normName.includes('precio') && !!section.competitor_id
  const isObjetivoEstrategicoSection = normName.includes('objetivo')
  const isSegmentacionSection      = normName.includes('segmentacion')
  const isPosicionamientoSection   = normName.includes('posicionamiento')
  const isPortafolioSection        = normName.includes('portafolio') && !!section.competitor_id
  const isBuStratPortfolioSection  = normName.includes('portafolio') && !!buId && !section.competitor_id && (normPhaseName.includes('estrategia') || normPhaseName.includes('definicion'))
  const isBuPriceStratSection      = normName.includes('precio') && !!buId && !section.competitor_id && (normPhaseName.includes('estrategia') || normPhaseName.includes('definicion'))
  const isCapacidadIndustrialSection = normName.includes('capacidad') && (!!section.competitor_id || !!buId)
  const isPylSection = (normName.includes('p&l') || normName.includes('pyg') || (normName.includes('estrategia') && normName.includes('desde'))) && !section.competitor_id
  const isNecesidadesTensionesSection = normName.includes('necesidades') && !section.competitor_id
  const isSobreSolucionSection        = normName.includes('sobre') && normName.includes('solucion') && !section.competitor_id
  const isAdopcionCategoriaSection    = normName.includes('adopcion') && normName.includes('categoria') && !section.competitor_id
  const isAdopcionMarcaSection        = normName.includes('adopcion') && normName.includes('marca') && !section.competitor_id
  const isDefinicionIndustriaSection  = normName.includes('definicion') && normName.includes('industria')

  const tabs = [
    ...(!isDefinicionIndustriaSection ? [{ id: 'tasks', label: 'Tareas', icon: CheckSquare, special: false }] : []),
    ...(isMarketMatrixSection
      ? [{ id: 'mercados',   label: 'Mercados',   icon: BarChart2,  special: false, market: true }]
      : []
    ),
    ...(isMarketSelectionSection
      ? [{ id: 'seleccion',  label: 'Selección',  icon: Target,     special: false, selection: true }]
      : []
    ),
    ...(isMarketPerformanceSection
      ? [{ id: 'desempeno',  label: 'Desempeño',  icon: Activity,   special: false, performance: true }]
      : []
    ),
    ...(isMarketProductsSection
      ? [{ id: 'productos',     label: 'Productos',    icon: BarChart2,  special: false, productsTab: true }]
      : []
    ),
    ...(isMarketCompetitorsSection
      ? [{ id: 'competidores',  label: 'Competidores', icon: Building2, special: false, competitorsTab: true }]
      : []
    ),
    ...(isMarketChannelsSection
      ? [{ id: 'canales', label: 'Canales', icon: TrendingUp, special: false, channelsTab: true }]
      : []
    ),
    ...(isMarketPrecioSection
      ? [{ id: 'precio', label: 'Precio', icon: TrendingUp, special: false, precioTab: true }]
      : []
    ),
    ...(isSegmentacionSection
      ? [{ id: 'perfiles', label: 'Perfiles', icon: User, special: false, perfilesTab: true }]
      : []
    ),
    ...(isPosicionamientoSection
      ? [{ id: 'posicionamiento', label: 'Posicionamiento', icon: Target, special: false, posicionamientoTab: true }]
      : []
    ),
    ...(isPortafolioSection
      ? [{ id: 'portafolio', label: 'Portafolio', icon: Database, special: false, portafolioTab: true }]
      : []
    ),
    ...(isBuStratPortfolioSection
      ? [{ id: 'portafolio_estrategia', label: 'Portafolio Estrategia', icon: Database, special: false, buStratPortTab: true }]
      : []
    ),
    ...(isBuPriceStratSection
      ? [{ id: 'precio_estrategia', label: 'Estrategia de Precio', icon: TrendingUp, special: false, buPriceStratTab: true }]
      : []
    ),
    ...(isCompetitorCanalesSection
      ? [{ id: 'canales_comp', label: 'Canales', icon: TrendingUp, special: false, canalesCompTab: true }]
      : []
    ),
    ...(isCompetitorPrecioSection
      ? [{ id: 'precio_comp', label: 'Precio', icon: TrendingUp, special: false, precioCompTab: true }]
      : []
    ),
    ...(isObjetivoEstrategicoSection
      ? [{ id: 'objetivo_est', label: buId ? ((normPhaseName.includes('estrategia') || normPhaseName.includes('definicion')) ? 'Objetivo UN Estrategia' : 'Objetivo UN') : 'Objetivo Estratégico', icon: Target, special: false, objetivoTab: true }]
      : []
    ),
    ...(isMktComunicacionSection
      ? [{ id: 'mix_medios', label: 'Mix de Medios', icon: BarChart2, special: false, mixMediosTab: true }]
      : []
    ),
    ...(isCapacidadIndustrialSection
      ? [{ id: 'cap_industrial', label: 'Capacidad', icon: Database, special: false, capIndustrialTab: true }]
      : []
    ),
    ...(isPylSection
      ? [{ id: 'pyl', label: 'P&L', icon: TrendingUp, special: false, pylTab: true }]
      : []
    ),
    ...(isNecesidadesTensionesSection
      ? [{ id: 'necesidades', label: 'Análisis', icon: User, special: false, necesidadesTab: true }]
      : []
    ),
    ...(isSobreSolucionSection
      ? [{ id: 'sobre_solucion', label: 'Análisis', icon: Target, special: false, sobreSolucionTab: true }]
      : []
    ),
    ...(isAdopcionCategoriaSection
      ? [{ id: 'adopcion_categoria', label: 'Análisis', icon: TrendingUp, special: false, adopcionTab: true }]
      : []
    ),
    ...(isAdopcionMarcaSection
      ? [{ id: 'adopcion_marca', label: 'Análisis', icon: Trophy, special: false, adopcionMarcaTab: true }]
      : []
    ),
    ...(isCompComunicacionSection
      ? [{ id: 'est_comunicacion', label: 'Estrategia', icon: Activity, special: false, commTab: true }]
      : []
    ),
    ...(isBUInternoComunicacionSection
      ? [{ id: 'bu_comm', label: 'Estrategia', icon: Activity, special: false, buCommTab: true }]
      : []
    ),
    ...(isDefinicionIndustriaSection
      ? [{ id: 'def_industria', label: 'Definición y Tamaño de la industria', icon: BarChart2, special: false, industryDefTab: true }]
      : []
    ),
    ...(!isDefinicionIndustriaSection ? [{ id: 'resultado', label: 'Resultado', icon: Trophy, special: true }] : []),
    ...(!isDefinicionIndustriaSection ? [{ id: 'files',     label: 'Archivos',  icon: FileText, special: false }] : []),
    ...(!isDefinicionIndustriaSection ? [{ id: 'comments',  label: 'Comentarios', icon: MessageSquare, special: false }] : []),
  ]
  const hasSpecialTabs = isMarketMatrixSection || isMarketSelectionSection || isMarketPerformanceSection || isMarketProductsSection || isMarketCompetitorsSection || isMarketChannelsSection || isCompetitorCanalesSection || isMktComunicacionSection || isCompComunicacionSection || isBUInternoComunicacionSection || isMarketPrecioSection || isCompetitorPrecioSection || isObjetivoEstrategicoSection || isSegmentacionSection || isPosicionamientoSection || isPortafolioSection || isBuStratPortfolioSection || isBuPriceStratSection || isCapacidadIndustrialSection || isPylSection || isNecesidadesTensionesSection || isSobreSolucionSection || isAdopcionCategoriaSection || isAdopcionMarcaSection || isDefinicionIndustriaSection
  if (hasSpecialTabs) {
    tabs.splice(tabs.length - 3, 0, { id: 'historial', label: 'Historial', icon: Activity, special: false })
  }
  const completedCount     = tasks.filter(t => t.status === 'completed').length
  const totalMarketSize    = markets.reduce((sum, m) => sum + (Number(m.sales_size) || 0), 0)
  const sortedMarkets      = [...markets].sort((a, b) => b.sales_size - a.sales_size)
  const pendingBUs         = selectionRecords.filter(r => !r.bu_id).length
  const totalSrcMarketSize = sourceMarkets.reduce((sum, m) => sum + (Number(m.sales_size) || 0), 0)
  const totalProductsSize  = products.reduce((sum, p) => sum + (Number(p.sales_size) || 0), 0)
  const sortedProducts     = [...products].sort((a, b) => b.sales_size - a.sales_size)

  // Channels chart data
  const channelChartData = (() => {
    if (!channels.length) return []
    if (activeChannelFilter === 'all') {
      return channels.map(c => {
        const weights = channelWeights[c.id] ?? {}
        const vals = Object.values(weights).map(Number).filter(v => !isNaN(v))
        const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0
        return { name: c.name, weight: parseFloat(avg.toFixed(1)), id: c.id }
      }).sort((a, b) => b.weight - a.weight)
    }
    return channels.map(c => ({
      name: c.name,
      weight: parseFloat(channelWeights[c.id]?.[activeChannelFilter] ?? 0),
      id: c.id,
    })).sort((a, b) => b.weight - a.weight)
  })()

  // Competitors chart data
  const competitorChartData = (() => {
    if (!competitors.length) return []
    if (activeProductFilter === 'all') {
      // Average share across all products with data per competitor
      return competitors.map(c => {
        const shares = competitorShares[c.id] ?? {}
        const vals = Object.values(shares).map(Number).filter(v => !isNaN(v))
        const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0
        return { name: c.name, share: parseFloat(avg.toFixed(1)), id: c.id }
      }).sort((a, b) => b.share - a.share)
    }
    return competitors.map(c => {
      const share = parseFloat(competitorShares[c.id]?.[activeProductFilter] ?? 0)
      return { name: c.name, share, id: c.id }
    }).sort((a, b) => b.share - a.share)
  })()

  return (
    <div className="space-y-6">
      {/* Breadcrumb — each crumb labeled with its entity type so the user
          knows what level they're navigating at. The current location
          (the leaf) is rendered with a colored chip + filled icon to make
          it visually distinct from the upstream crumbs. */}
      <div className="flex items-center gap-1.5 text-sm text-gray-400 flex-wrap">
        <button onClick={() => navigate(`/consultancy/${id}`)}
          className="inline-flex items-center gap-1.5 hover:text-brand-600 transition-colors">
          <Building2 size={13} className="opacity-60" />
          <span className="text-[10px] uppercase tracking-wide font-semibold opacity-60">Proyecto:</span>
          <span>{consultancyName || 'Consultoría'}</span>
        </button>
        {!isProjectPhase && (
          <>
            <ChevronRight size={14} className="flex-shrink-0" />
            <button onClick={() => navigate(`/consultancy/${id}/bu/${buId}`)}
              className="inline-flex items-center gap-1.5 hover:text-brand-600 transition-colors truncate max-w-[180px] md:max-w-none">
              <Target size={13} className="opacity-60" />
              <span className="text-[10px] uppercase tracking-wide font-semibold opacity-60">UN:</span>
              <span className="truncate">{buName || 'Unidad de Negocio'}</span>
            </button>
          </>
        )}
        <ChevronRight size={14} className="flex-shrink-0" />
        <button
          onClick={() => navigate(isProjectPhase ? `/consultancy/${id}/phase/${phaseId}` : `/consultancy/${id}/bu/${buId}/phase/${phaseId}`)}
          className="inline-flex items-center gap-1.5 hover:text-brand-600 transition-colors truncate max-w-[180px] md:max-w-none"
        >
          <BarChart2 size={13} className="opacity-60" />
          <span className="text-[10px] uppercase tracking-wide font-semibold opacity-60">Etapa:</span>
          <span className="truncate">{phaseName || 'Etapa'}</span>
        </button>
        <ChevronRight size={14} className="flex-shrink-0" />
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-brand-50 border border-brand-200 text-brand-700 font-semibold">
          <FileText size={13} />
          <span className="text-[10px] uppercase tracking-wide">Sección:</span>
          <span>{section.name}</span>
        </span>
      </div>

      {phaseLocked && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium">
          <Lock size={15} className="flex-shrink-0 text-amber-600" />
          Esta fase está bloqueada — el contenido es de solo lectura. Contacta a un administrador NVC para editar.
        </div>
      )}

      {/* Caja guide panel — only for caja_user role */}
      {isCaja && (
        <CajaGuidePanel
          sectionName={section?.name}
          sectionId={sectionId}
          consultancyId={id}
          existingTasks={tasks}
          onTasksCreated={fetchData}
        />
      )}

      {/* Header card */}
      <div className="card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="text-xl font-bold text-gray-900">{section.name}</h1>
              <StatusBadge status={section.status} />
            </div>
            {section.description && <p className="text-sm text-gray-500 mt-1">{section.description}</p>}
            <p className="text-xs text-gray-400 mt-2">{completedCount}/{tasks.length} tareas completadas</p>
          </div>
          {!isCaja && <AIReportButton consultancyId={id} entityType="section" entityId={sectionId} label="Informe de Sección" />}
        </div>
        <div className="mt-4 flex gap-2 flex-wrap">
          {isCaja ? (
            /* Caja users: simplified — only "Completado" toggle */
            <button
              onClick={() => updateSectionStatus(section.status === 'completed' ? 'in_progress' : 'completed')}
              className={`text-xs px-4 py-1.5 rounded-full border font-medium transition-colors ${
                section.status === 'completed'
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'border-gray-200 text-gray-500 hover:border-emerald-400 hover:text-emerald-600'
              }`}
            >
              {section.status === 'completed' ? '✓ Completada' : 'Marcar como completada'}
            </button>
          ) : (
            STATUSES.map(s => (
              <button key={s} onClick={() => updateSectionStatus(s)}
                className={`text-xs px-3 py-1 rounded-full border transition-colors
                  ${section.status === s ? 'bg-brand-700 text-white border-brand-700' : 'border-gray-200 text-gray-500 hover:border-brand-300'}`}>
                {STATUS_LABEL[s]}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 flex-wrap">
        {tabs.map(({ id: tid, label, icon: Icon, special, market, selection, performance, productsTab, competitorsTab, channelsTab, precioTab, perfilesTab, posicionamientoTab, portafolioTab, canalesCompTab, mixMediosTab, commTab, buCommTab, precioCompTab, objetivoTab, capIndustrialTab, pylTab, necesidadesTab, buStratPortTab, buPriceStratTab, industryDefTab }) => (
          special ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-emerald-500 text-emerald-700 bg-emerald-50'
                  : 'border-transparent text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
              }`}>
              <Icon size={15} /> {label}
              {savedResult && <span className="ml-1 w-2 h-2 rounded-full bg-emerald-500 inline-block" />}
            </button>
          ) : market ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-blue-600 text-blue-700 bg-blue-50'
                  : 'border-transparent text-blue-600 hover:text-blue-700 hover:bg-blue-50'
              }`}>
              <Icon size={15} /> {label}
              {markets.length > 0 && <span className="ml-1 bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded-full font-medium">{markets.length}</span>}
              {matrixFinalized && <Lock size={11} className="ml-0.5 text-blue-500" />}
            </button>
          ) : productsTab ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-violet-600 text-violet-700 bg-violet-50'
                  : 'border-transparent text-violet-600 hover:text-violet-700 hover:bg-violet-50'
              }`}>
              <Icon size={15} /> {label}
              {products.length > 0 && <span className="ml-1 bg-violet-100 text-violet-700 text-xs px-1.5 py-0.5 rounded-full font-medium">{products.length}</span>}
            </button>
          ) : competitorsTab ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-rose-600 text-rose-700 bg-rose-50'
                  : 'border-transparent text-rose-600 hover:text-rose-700 hover:bg-rose-50'
              }`}>
              <Icon size={15} /> {label}
              {competitors.length > 0 && <span className="ml-1 bg-rose-100 text-rose-700 text-xs px-1.5 py-0.5 rounded-full font-medium">{competitors.length}</span>}
            </button>
          ) : channelsTab ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-sky-600 text-sky-700 bg-sky-50'
                  : 'border-transparent text-sky-600 hover:text-sky-700 hover:bg-sky-50'
              }`}>
              <Icon size={15} /> {label}
              {channels.length > 0 && <span className="ml-1 bg-sky-100 text-sky-700 text-xs px-1.5 py-0.5 rounded-full font-medium">{channels.length}</span>}
            </button>
          ) : precioTab ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-emerald-600 text-emerald-700 bg-emerald-50'
                  : 'border-transparent text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
              }`}>
              <Icon size={15} /> {label}
              {(precioMktHistory.length > 0 || precioProductHistory.length > 0) && <span className="ml-1 w-2 h-2 rounded-full bg-emerald-500 inline-block" />}
            </button>
          ) : perfilesTab ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-violet-600 text-violet-700 bg-violet-50'
                  : 'border-transparent text-violet-600 hover:text-violet-700 hover:bg-violet-50'
              }`}>
              <Icon size={15} /> {label}
              {segProfiles.length > 0 && <span className="ml-1 bg-violet-100 text-violet-700 text-xs px-1.5 py-0.5 rounded-full font-medium">{segProfiles.length}</span>}
            </button>
          ) : posicionamientoTab ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-indigo-600 text-indigo-700 bg-indigo-50'
                  : 'border-transparent text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50'
              }`}>
              <Icon size={15} /> {label}
              {positioning && <span className="ml-1 w-2 h-2 rounded-full bg-indigo-500 inline-block" />}
            </button>
          ) : portafolioTab ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-orange-600 text-orange-700 bg-orange-50'
                  : 'border-transparent text-orange-600 hover:text-orange-700 hover:bg-orange-50'
              }`}>
              <Icon size={15} /> {label}
              {portfolioProducts.length > 0 && <span className="ml-1 bg-orange-100 text-orange-700 text-xs px-1.5 py-0.5 rounded-full font-medium">{portfolioProducts.length}</span>}
            </button>
          ) : buStratPortTab ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-violet-600 text-violet-700 bg-violet-50'
                  : 'border-transparent text-violet-600 hover:text-violet-700 hover:bg-violet-50'
              }`}>
              <Icon size={15} /> {label}
              {(stratPortProducts.length > 0 || stratPortNewRefs.length > 0) && (
                <span className="ml-1 bg-violet-100 text-violet-700 text-xs px-1.5 py-0.5 rounded-full font-medium">
                  {stratPortProducts.length + stratPortNewRefs.length}
                </span>
              )}
            </button>
          ) : buPriceStratTab ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-teal-600 text-teal-700 bg-teal-50'
                  : 'border-transparent text-teal-600 hover:text-teal-700 hover:bg-teal-50'
              }`}>
              <Icon size={15} /> {label}
              {priceStratProducts.length > 0 && <span className="ml-1 bg-teal-100 text-teal-700 text-xs px-1.5 py-0.5 rounded-full font-medium">{priceStratProducts.length}</span>}
            </button>
          ) : canalesCompTab ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-teal-600 text-teal-700 bg-teal-50'
                  : 'border-transparent text-teal-600 hover:text-teal-700 hover:bg-teal-50'
              }`}>
              <Icon size={15} /> {label}
              {compChanPresence.length > 0 && <span className="ml-1 bg-teal-100 text-teal-700 text-xs px-1.5 py-0.5 rounded-full font-medium">{compChanPresence.length}</span>}
            </button>
          ) : objetivoTab ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-blue-700 text-blue-800 bg-blue-50'
                  : 'border-transparent text-blue-600 hover:text-blue-700 hover:bg-blue-50'
              }`}>
              <Icon size={15} /> {label}
              {stratObj && <span className="ml-1 w-2 h-2 rounded-full bg-blue-600 inline-block" />}
            </button>
          ) : precioCompTab ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-emerald-600 text-emerald-700 bg-emerald-50'
                  : 'border-transparent text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
              }`}>
              <Icon size={15} /> {label}
              {compPrecioData.length > 0 && <span className="ml-1 bg-emerald-100 text-emerald-700 text-xs px-1.5 py-0.5 rounded-full font-medium">{compPrecioData.length}</span>}
            </button>
          ) : mixMediosTab ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-pink-600 text-pink-700 bg-pink-50'
                  : 'border-transparent text-pink-600 hover:text-pink-700 hover:bg-pink-50'
              }`}>
              <Icon size={15} /> {label}
              {mktMediaMix.length > 0 && <span className="ml-1 bg-pink-100 text-pink-700 text-xs px-1.5 py-0.5 rounded-full font-medium">{mktMediaMix.length}</span>}
            </button>
          ) : necesidadesTab ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-purple-600 text-purple-700 bg-purple-50'
                  : 'border-transparent text-purple-600 hover:text-purple-700 hover:bg-purple-50'
              }`}>
              <Icon size={15} /> {label}
              {needsProfiles.length > 0 && <span className="ml-1 bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 rounded-full font-medium">{needsProfiles.length}</span>}
            </button>
          ) : pylTab ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-green-600 text-green-700 bg-green-50'
                  : 'border-transparent text-green-600 hover:text-green-700 hover:bg-green-50'
              }`}>
              <Icon size={15} /> {label}
              {pylRecord && <span className="ml-1 w-2 h-2 rounded-full bg-green-500 inline-block" />}
            </button>
          ) : capIndustrialTab ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-amber-600 text-amber-700 bg-amber-50'
                  : 'border-transparent text-amber-600 hover:text-amber-700 hover:bg-amber-50'
              }`}>
              <Icon size={15} /> {label}
              {capIndustrial && <span className="ml-1 w-2 h-2 rounded-full bg-amber-500 inline-block" />}
            </button>
          ) : commTab ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-pink-600 text-pink-700 bg-pink-50'
                  : 'border-transparent text-pink-600 hover:text-pink-700 hover:bg-pink-50'
              }`}>
              <Icon size={15} /> {label}
              {commStrategy && <span className="ml-1 w-2 h-2 rounded-full bg-pink-500 inline-block" />}
            </button>
          ) : tid === 'bu_comm' ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-violet-600 text-violet-700 bg-violet-50'
                  : 'border-transparent text-violet-600 hover:text-violet-700 hover:bg-violet-50'
              }`}>
              <Icon size={15} /> {label}
              {buCommStrategy && <span className="ml-1 w-2 h-2 rounded-full bg-violet-500 inline-block" />}
            </button>
          ) : selection ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-teal-600 text-teal-700 bg-teal-50'
                  : 'border-transparent text-teal-600 hover:text-teal-700 hover:bg-teal-50'
              }`}>
              <Icon size={15} /> {label}
              {selectedMarketIds.size > 0 && <span className="ml-1 bg-teal-100 text-teal-700 text-xs px-1.5 py-0.5 rounded-full font-medium">{selectedMarketIds.size}</span>}
            </button>
          ) : industryDefTab ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-cyan-600 text-cyan-700 bg-cyan-50'
                  : 'border-transparent text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50'
              }`}>
              <Icon size={15} /> {label}
              {industryDef && (industryDef.descripcion || industryDef.tamano_ventas) && <span className="ml-1 w-2 h-2 rounded-full bg-cyan-500 inline-block" />}
            </button>
          ) : performance ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tid
                  ? 'border-amber-500 text-amber-700 bg-amber-50'
                  : 'border-transparent text-amber-600 hover:text-amber-700 hover:bg-amber-50'
              }`}>
              <Icon size={15} /> {label}
              {perfRecord && <span className="ml-1 w-2 h-2 rounded-full bg-amber-500 inline-block" />}
            </button>
          ) : (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors
                ${activeTab === tid ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              <Icon size={15} /> {label}
            </button>
          )
        ))}
      </div>

      {/* Tasks tab */}
      {/* ── Market Matrix Tab ─────────────────────────────────── */}
      {activeTab === 'mercados' && (
        <div className="space-y-5">
          {/* Header */}
          <div className="rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <BarChart2 size={18} className="text-white" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-base">Matriz de Mercados de la Industria</h2>
                <p className="text-blue-100 text-xs mt-0.5">
                  {markets.length} mercado{markets.length !== 1 ? 's' : ''} · Total industria:{' '}
                  {totalMarketSize > 0 ? `$${totalMarketSize.toLocaleString('es-CO')}` : '—'}
                </p>
              </div>
            </div>
            {(isAdmin || isNVC) && !phaseLocked && (
              <button
                onClick={toggleMatrixFinalized}
                disabled={finalizingMatrix || (!matrixFinalized && markets.length === 0)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 ${
                  matrixFinalized
                    ? 'bg-white/20 hover:bg-white/30 text-white'
                    : 'bg-white text-blue-700 hover:bg-blue-50'
                }`}
              >
                {matrixFinalized ? <><Unlock size={14} /> Editar</> : <><Lock size={14} /> Finalizar Matriz</>}
              </button>
            )}
          </div>

          {/* Editable mode */}
          {!matrixFinalized && (
            <div className="space-y-4">
              {/* Action buttons */}
              {(isAdmin || isNVC) && !phaseLocked && (
                <div className="flex gap-2 flex-wrap">
                  <button onClick={openCreateMarket} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors">
                    <Plus size={15} /> Agregar mercado
                  </button>
                  <label className="flex items-center gap-2 px-4 py-2 rounded-lg border border-blue-300 text-blue-700 hover:bg-blue-50 text-sm font-medium cursor-pointer transition-colors">
                    <Upload size={15} /> Importar desde Excel
                    <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={importFromExcel} />
                  </label>
                </div>
              )}

              {/* Markets table */}
              {markets.length > 0 ? (
                <div className="card overflow-hidden p-0">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-blue-50 border-b border-blue-100">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-blue-700 uppercase tracking-wide">Mercado</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-blue-700 uppercase tracking-wide">Tamaño en Ventas</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-blue-700 uppercase tracking-wide">% del Total</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-blue-700 uppercase tracking-wide">Descripción</th>
                        {(isAdmin || isNVC || isCaja) && <th className="px-4 py-3 text-right text-xs font-semibold text-violet-700 uppercase tracking-wide">Acciones</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {sortedMarkets.map(m => {
                        const pct = totalMarketSize > 0 ? ((m.sales_size / totalMarketSize) * 100).toFixed(1) : '0.0'
                        return (
                          <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 font-medium text-gray-800">{m.name}</td>
                            <td className="px-4 py-3 text-right text-gray-700">${Number(m.sales_size).toLocaleString('es-CO')}</td>
                            <td className="px-4 py-3 text-right">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">{pct}%</span>
                            </td>
                            <td className="px-4 py-3 text-gray-500 text-xs max-w-xs truncate">{m.description || '—'}</td>
                            {(isAdmin || isNVC) && !phaseLocked && (
                              <td className="px-4 py-3">
                                <div className="flex gap-1 justify-end">
                                  <button onClick={() => openEditMarket(m)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><Pencil size={13} /></button>
                                  <button onClick={() => deleteMarket(m.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={13} /></button>
                                </div>
                              </td>
                            )}
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-blue-50 border-t-2 border-blue-200">
                        <td className="px-4 py-3 text-sm font-bold text-blue-800">Total Industria</td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-blue-800">${totalMarketSize.toLocaleString('es-CO')}</td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-blue-800">100%</td>
                        <td colSpan={2} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="text-center py-14 text-gray-400 card">
                  <BarChart2 size={40} className="mx-auto mb-3 opacity-20" />
                  <p className="font-medium">Sin mercados agregados</p>
                  <p className="text-sm mt-1">Agrega mercados manualmente o importa desde Excel</p>
                  <p className="text-xs mt-3 text-blue-400">El Excel debe tener columnas: Nombre, Tamaño en Ventas, Descripción</p>
                </div>
              )}
            </div>
          )}

          {/* Finalized matrix view */}
          {matrixFinalized && markets.length > 0 && (
            <div className="card space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-gray-900">Ranking de Mercados por Tamaño</h3>
                <span className="text-xs text-gray-400 flex items-center gap-1"><Lock size={11} /> Matriz finalizada</span>
              </div>

              <div className="space-y-3">
                {sortedMarkets.map((m, idx) => {
                  const pct = totalMarketSize > 0 ? (m.sales_size / totalMarketSize) * 100 : 0
                  const COLORS = ['bg-blue-700','bg-blue-600','bg-blue-500','bg-blue-400','bg-blue-300','bg-blue-200','bg-blue-100']
                  const barColor = COLORS[Math.min(idx, COLORS.length - 1)]
                  const textColor = idx < 3 ? 'text-white' : 'text-blue-800'
                  return (
                    <div key={m.id} className="space-y-1">
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-0.5">
                        <span className="font-semibold text-gray-800">
                          <span className="text-blue-400 mr-1.5">#{idx + 1}</span>{m.name}
                        </span>
                        <span className="text-gray-500">${Number(m.sales_size).toLocaleString('es-CO')} · <span className="font-semibold text-blue-700">{pct.toFixed(1)}%</span></span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-8 overflow-hidden relative">
                        <div
                          className={`h-full rounded-full ${barColor} transition-all flex items-center px-3`}
                          style={{ width: `${Math.max(pct, 2)}%` }}
                        >
                          {pct > 8 && <span className={`text-xs font-semibold ${textColor}`}>{pct.toFixed(1)}%</span>}
                        </div>
                      </div>
                      {m.description && <p className="text-xs text-gray-400 pl-1">{m.description}</p>}
                    </div>
                  )
                })}
              </div>

              {/* Summary footer */}
              <div className="border-t border-gray-100 pt-4 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-700">{markets.length}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Mercados identificados</p>
                </div>
                <div className="text-center border-x border-gray-100">
                  <p className="text-2xl font-bold text-blue-700">${totalMarketSize.toLocaleString('es-CO')}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Tamaño total de la industria</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-700">{sortedMarkets[0]?.name?.split(' ')[0] ?? '—'}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Mercado líder</p>
                </div>
              </div>
            </div>
          )}

          {matrixFinalized && markets.length === 0 && (
            <div className="text-center py-10 text-gray-400">
              <p>Matriz finalizada pero sin mercados. Usa "Editar" para agregar.</p>
            </div>
          )}
        </div>
      )}

      {/* Market modal */}
      <Modal open={showMarketModal} onClose={() => setShowMarketModal(false)} title={editMarket ? 'Editar mercado' : 'Nuevo mercado'}>
        <form onSubmit={saveMarket} className="space-y-4">
          <div>
            <label className="label">Nombre del mercado *</label>
            <input className="input" required value={marketForm.name}
              onChange={e => setMarketForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Ej: Harinas para panificación industrial" />
          </div>
          <div>
            <label className="label">Tamaño en ventas *</label>
            <input className="input" required type="number" min="0" step="any" value={marketForm.sales_size}
              onChange={e => setMarketForm(f => ({ ...f, sales_size: e.target.value }))}
              placeholder="Ej: 500000000" />
            <p className="text-xs text-gray-400 mt-1">Ingresa el valor numérico (sin separadores de miles)</p>
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea className="input resize-none" rows={3} value={marketForm.description}
              onChange={e => setMarketForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Breve descripción del mercado..." />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setShowMarketModal(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={savingMarket}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors disabled:opacity-50">
              {savingMarket ? 'Guardando...' : editMarket ? 'Guardar cambios' : 'Agregar mercado'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Market Selection Tab ──────────────────────────────── */}
      {activeTab === 'seleccion' && (
        <div className="space-y-5">
          {/* Header */}
          <div className="rounded-xl bg-gradient-to-r from-teal-600 to-teal-700 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <Target size={18} className="text-white" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-base">Selección de Mercados Objetivo</h2>
                <p className="text-teal-100 text-xs mt-0.5">
                  {selectedMarketIds.size} mercado{selectedMarketIds.size !== 1 ? 's' : ''} seleccionado{selectedMarketIds.size !== 1 ? 's' : ''}
                  {selectionRecords.filter(r => r.bu_id).length > 0 && ` · ${selectionRecords.filter(r => r.bu_id).length} UdN creada${selectionRecords.filter(r => r.bu_id).length !== 1 ? 's' : ''}`}
                </p>
              </div>
            </div>
            {(isAdmin || isNVC) && pendingBUs > 0 && (
              <button
                onClick={createBUsFromSelections}
                disabled={creatingBUs}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-teal-700 hover:bg-teal-50 text-sm font-semibold transition-colors disabled:opacity-60"
              >
                {creatingBUs ? <><Loader size={14} className="animate-spin" /> Creando...</> : <><Building2 size={14} /> Crear {pendingBUs} Unidad{pendingBUs !== 1 ? 'es' : ''} de Negocio</>}
              </button>
            )}
          </div>

          {/* No source matrix yet */}
          {sourceMarkets.length === 0 && (
            <div className="text-center py-14 card text-gray-400">
              <BarChart2 size={40} className="mx-auto mb-3 opacity-20" />
              <p className="font-medium">La Matriz de Mercados aún no está disponible</p>
              <p className="text-sm mt-1">Primero completa y finaliza la sección <span className="font-semibold text-gray-500">"Matriz de Mercados de la Industria"</span> en esta misma etapa.</p>
            </div>
          )}

          {/* Markets list for selection */}
          {sourceMarkets.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600 font-medium">Selecciona los mercados objetivo de la industria</p>
                <p className="text-xs text-gray-400">Total industria: <span className="font-semibold text-gray-600">${totalSrcMarketSize.toLocaleString('es-CO')}</span></p>
              </div>

              {sourceMarkets.map((m, idx) => {
                const isSelected   = selectedMarketIds.has(m.id)
                const rec          = selectionRecords.find(r => r.market_id === m.id)
                const hasBU        = !!rec?.bu_id
                const pct          = totalSrcMarketSize > 0 ? ((m.sales_size / totalSrcMarketSize) * 100).toFixed(1) : '0.0'
                const COLORS       = ['bg-blue-700','bg-blue-600','bg-blue-500','bg-blue-400','bg-blue-300']
                const barColor     = COLORS[Math.min(idx, COLORS.length - 1)]

                return (
                  <div
                    key={m.id}
                    onClick={() => (isAdmin || isNVC) && toggleMarketSelection(m.id)}
                    className={`card p-4 cursor-pointer transition-all border-2 ${
                      isSelected
                        ? 'border-teal-400 bg-teal-50/50'
                        : 'border-gray-200 hover:border-teal-200 hover:bg-gray-50'
                    } ${!(isAdmin || isNVC) ? 'cursor-default' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      {(isAdmin || isNVC) && !phaseLocked && (
                        <div className={`mt-0.5 w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                          isSelected ? 'bg-teal-500 border-teal-500' : 'border-gray-300'
                        }`}>
                          {isSelected && <svg viewBox="0 0 12 12" className="w-3 h-3 fill-none stroke-white stroke-2"><polyline points="2,6 5,9 10,3" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 font-medium">#{idx + 1}</span>
                            <span className="font-semibold text-gray-800 text-sm">{m.name}</span>
                            {isSelected && !hasBU && (
                              <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-medium">Seleccionado</span>
                            )}
                            {hasBU && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                                <CheckCircle2 size={10} /> UdN creada: {rec.bu?.name}
                              </span>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <span className="text-sm font-bold text-gray-700">${Number(m.sales_size).toLocaleString('es-CO')}</span>
                            <span className="ml-2 text-xs font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">{pct}%</span>
                          </div>
                        </div>

                        {/* Mini bar */}
                        <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                          <div className={`h-full rounded-full ${isSelected ? 'bg-teal-500' : barColor} transition-all`}
                            style={{ width: `${Math.max(parseFloat(pct), 1)}%` }} />
                        </div>

                        {m.description && <p className="text-xs text-gray-400 mt-1.5">{m.description}</p>}
                      </div>
                    </div>
                  </div>
                )
              })}

              {/* Create BUs summary */}
              {selectedMarketIds.size > 0 && (isAdmin || isNVC) && (
                <div className="card border border-teal-200 bg-teal-50/30 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-teal-800">
                        {selectedMarketIds.size} mercado{selectedMarketIds.size !== 1 ? 's' : ''} seleccionado{selectedMarketIds.size !== 1 ? 's' : ''}
                      </p>
                      <p className="text-xs text-teal-600 mt-0.5">
                        {selectionRecords.filter(r => r.bu_id).length > 0 && `${selectionRecords.filter(r => r.bu_id).length} con UdN creada · `}
                        {pendingBUs > 0 ? `${pendingBUs} pendiente${pendingBUs !== 1 ? 's' : ''} de crear` : 'Todas las UdN creadas ✓'}
                      </p>
                    </div>
                    {pendingBUs > 0 && (
                      <button
                        onClick={createBUsFromSelections}
                        disabled={creatingBUs}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold transition-colors disabled:opacity-60"
                      >
                        {creatingBUs
                          ? <><Loader size={14} className="animate-spin" /> Creando unidades...</>
                          : <><Building2 size={14} /> Crear {pendingBUs} Unidad{pendingBUs !== 1 ? 'es' : ''} de Negocio</>
                        }
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Market Performance Tab ────────────────────────────── */}
      {activeTab === 'desempeno' && (
        <div className="space-y-5">
          {/* Header */}
          <div className="rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <Activity size={18} className="text-white" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-base">Desempeño del Mercado</h2>
                <p className="text-amber-100 text-xs mt-0.5">Indicadores clave de tamaño, crecimiento y características del mercado</p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {perfRecord && !editingPerf && (
                <button onClick={exportPerfToPptx}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors"
                  title="Descargar gráfica en PowerPoint">
                  <Download size={14} /> PPT
                </button>
              )}
              {(isAdmin || isNVC) && !phaseLocked && (
                <button onClick={() => {
                  const years = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i)
                  const periods = [
                    { label: 'Anual', quarter: null },
                    { label: 'Q1', quarter: 1 }, { label: 'Q2', quarter: 2 },
                    { label: 'Q3', quarter: 3 }, { label: 'Q4', quarter: 4 },
                  ]
                  const rows = [['Año', 'Período', 'Ventas / Tamaño del mercado', 'Unidad de ventas', 'Volumen', 'Unidad de volumen', 'Madurez del mercado', 'Fuente de información', 'Estacionalidad (Sí/No)', 'Meses de estacionalidad']]
                  years.forEach(y => periods.forEach(p => rows.push([y, p.label, '', 'USD millones', '', 'toneladas', '', '', 'No', ''])))
                  const ws = XLSX.utils.aoa_to_sheet(rows)
                  ws['!cols'] = [{ wch: 8 }, { wch: 8 }, { wch: 28 }, { wch: 18 }, { wch: 12 }, { wch: 18 }, { wch: 18 }, { wch: 28 }, { wch: 18 }, { wch: 22 }]
                  const wb = XLSX.utils.book_new()
                  XLSX.utils.book_append_sheet(wb, ws, 'Desempeño del Mercado')
                  XLSX.writeFile(wb, 'plantilla_desempeno_mercado.xlsx')
                }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors"
                  title="Descargar plantilla Excel">
                  <Download size={14} /> Plantilla
                </button>
              )}
              {(isAdmin || isNVC) && !phaseLocked && (
                <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors" title="Subir datos desde Excel">
                  <Upload size={14} /> Subir Excel
                  <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={async e => {
                    const file = e.target.files?.[0]; if (!file) return
                    const reader = new FileReader()
                    reader.onload = async evt => {
                      const wb2 = XLSX.read(evt.target.result, { type: 'array' })
                      const ws2 = wb2.Sheets[wb2.SheetNames[0]]
                      const rows2 = XLSX.utils.sheet_to_json(ws2, { defval: '' })
                      const norm3 = s => String(s).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                      const qMap = { 'anual': null, 'q1': 1, 'q2': 2, 'q3': 3, 'q4': 4 }
                      let imported = 0
                      for (const row of rows2) {
                        const yr = parseInt(row['Año'] || row['Year'] || row['año'] || '')
                        if (!yr || isNaN(yr)) continue
                        const perStr = norm3(String(row['Período'] || row['Periodo'] || row['Period'] || 'anual')).trim()
                        const qtr = qMap[perStr] ?? null
                        const mktSize = parseFloat(String(row['Ventas / Tamaño del mercado'] || row['Ventas'] || '').replace(/[^0-9.-]/g, '')) || null
                        const vol = parseFloat(String(row['Volumen'] || '').replace(/[^0-9.-]/g, '')) || null
                        const sUnit = String(row['Unidad de ventas'] || 'USD millones').trim() || 'USD millones'
                        const vUnit = String(row['Unidad de volumen'] || 'toneladas').trim() || 'toneladas'
                        const maturity = String(row['Madurez del mercado'] || '').trim() || null
                        const source = String(row['Fuente de información'] || row['Fuente'] || '').trim() || null
                        const hasSeason = norm3(String(row['Estacionalidad (Sí/No)'] || 'no')).includes('s')
                        const seasonMonths = String(row['Meses de estacionalidad'] || '').trim() || null
                        if (!mktSize && !vol) continue
                        const payload = { section_id: sectionId, data_year: yr, data_quarter: qtr, market_size: mktSize, size_unit: sUnit, volume: vol, volume_unit: vUnit, maturity, data_source: source, has_seasonality: hasSeason, seasonality_months: hasSeason ? seasonMonths : null, reference_year: yr, updated_at: new Date().toISOString() }
                        // Upsert: check if record exists
                        let existQ = supabase.from('market_performance').select('id').eq('section_id', sectionId).eq('data_year', yr)
                        existQ = qtr == null ? existQ.is('data_quarter', null) : existQ.eq('data_quarter', qtr)
                        const { data: existing } = await existQ.maybeSingle()
                        if (existing) { await supabase.from('market_performance').update(payload).eq('id', existing.id) }
                        else { await supabase.from('market_performance').insert(payload) }
                        imported++
                      }
                      alert(`${imported} registro${imported !== 1 ? 's' : ''} importado${imported !== 1 ? 's' : ''} correctamente.`)
                      fetchData()
                    }
                    reader.readAsArrayBuffer(file)
                    e.target.value = ''
                  }} />
                </label>
              )}
              {!editingPerf && (isAdmin || isNVC) && (
                <button onClick={() => { setPerfForm({ ...EMPTY_PERF, year: CURRENT_YEAR }); setEditingPerf(true) }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-amber-700 hover:bg-amber-50 text-sm font-semibold transition-colors">
                  <Plus size={14} /> Agregar año
                </button>
              )}
            </div>
          </div>

          {/* Edit / Add-year form */}
          {editingPerf && (
            <form onSubmit={savePerf} className="card border border-amber-200 space-y-5">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <Edit3 size={14} className="text-amber-500" /> Agregar / editar año
              </h3>

              {/* Año selector */}
              <div className="grid grid-cols-4 gap-3 items-end">
                <div>
                  <label className="label">Año del dato</label>
                  <input type="number" className="input font-semibold" min="2000" max="2050"
                    value={perfForm.year}
                    onChange={e => setPerfForm(f => ({ ...f, year: e.target.value }))} />
                </div>
                <div className="col-span-3 text-xs text-gray-400 pb-2">
                  {perfHistory.filter(h => h.data_quarter == null).map(h => h.data_year).includes(parseInt(perfForm.year))
                    ? <span className="text-amber-600 font-medium">⚠️ Ya existe un dato para {perfForm.year} — se actualizará</span>
                    : <span className="text-green-600">Nuevo año — se creará un registro</span>
                  }
                </div>
              </div>

              {/* Row 1: Tamaño + Unidad */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <label className="label">Ventas / Tamaño del mercado</label>
                  <input type="number" step="any" min="0" className="input" placeholder="Ej: 450"
                    value={perfForm.market_size}
                    onChange={e => setPerfForm(f => ({ ...f, market_size: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Unidad de ventas</label>
                  <select className="input" value={perfForm.size_unit}
                    onChange={e => setPerfForm(f => ({ ...f, size_unit: e.target.value }))}>
                    {['USD millones','USD miles de millones','COP millones','COP miles de millones','EUR millones'].map(u =>
                      <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Año de referencia</label>
                  <input type="number" className="input" placeholder={new Date().getFullYear()}
                    value={perfForm.reference_year}
                    onChange={e => setPerfForm(f => ({ ...f, reference_year: e.target.value }))} />
                </div>
              </div>

              {/* Row 1b: Volumen + Unidad de volumen + Precio promedio calculado */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="label">Volumen (unidades)</label>
                  <input type="number" step="any" min="0" className="input" placeholder="Ej: 120000"
                    value={perfForm.volume}
                    onChange={e => setPerfForm(f => ({ ...f, volume: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Unidad de volumen</label>
                  <select className="input" value={perfForm.volume_unit}
                    onChange={e => setPerfForm(f => ({ ...f, volume_unit: e.target.value }))}>
                    {['toneladas','miles de toneladas','millones de toneladas','miles de unidades','millones de unidades','litros','miles de litros','millones de litros','kg','barriles'].map(u =>
                      <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Precio promedio (calculado)</label>
                  <div className="input bg-amber-50 border-amber-200 flex items-center gap-2 text-amber-800 font-semibold cursor-default select-none">
                    {(() => {
                      const sales = parseFloat(perfForm.market_size)
                      const vol   = parseFloat(perfForm.volume)
                      if (sales > 0 && vol > 0) {
                        const price = sales / vol
                        return <><span className="flex-1">{price < 1 ? price.toFixed(4) : price.toLocaleString('es-CO', { maximumFractionDigits: 2 })}</span><span className="text-xs text-amber-600 font-normal">{perfForm.size_unit} / {perfForm.volume_unit}</span></>
                      }
                      return <span className="text-gray-400 font-normal text-sm">Ingresa ventas y volumen</span>
                    })()}
                  </div>
                </div>
              </div>

              {/* Row 2: Madurez + Fuente */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Madurez del mercado</label>
                  <select className="input" value={perfForm.maturity}
                    onChange={e => setPerfForm(f => ({ ...f, maturity: e.target.value }))}>
                    <option value="">— Seleccionar —</option>
                    {['Emergente','En crecimiento','Maduro','En declive'].map(m =>
                      <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Fuente de información</label>
                  <input type="text" className="input" placeholder="Ej: Nielsen, Euromonitor, datos propios..."
                    value={perfForm.data_source}
                    onChange={e => setPerfForm(f => ({ ...f, data_source: e.target.value }))} />
                </div>
              </div>

              {/* Row 4: Estacionalidad */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className="label mb-0">¿Tiene estacionalidad?</label>
                  <div className="flex gap-2">
                    {[{v: false, l: 'No'}, {v: true, l: 'Sí'}].map(({v, l}) => (
                      <button key={l} type="button"
                        onClick={() => setPerfForm(f => ({ ...f, has_seasonality: v }))}
                        className={`px-4 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                          perfForm.has_seasonality === v
                            ? 'bg-amber-500 border-amber-500 text-white'
                            : 'border-gray-200 text-gray-500 hover:border-amber-300'
                        }`}>{l}</button>
                    ))}
                  </div>
                </div>
                {perfForm.has_seasonality && (
                  <div>
                    <label className="label">¿Cuáles meses o períodos son de alta temporada?</label>
                    <input type="text" className="input"
                      placeholder="Ej: Enero, Febrero, Diciembre — o Q4"
                      value={perfForm.seasonality_months}
                      onChange={e => setPerfForm(f => ({ ...f, seasonality_months: e.target.value }))} />
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setEditingPerf(false)} className="btn-secondary flex-1">Cancelar</button>
                <button type="submit" disabled={savingPerf}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold transition-colors disabled:opacity-50">
                  <Save size={14} /> {savingPerf ? 'Guardando...' : 'Guardar datos'}
                </button>
              </div>
            </form>
          )}

          {/* ── Trend visualization by variable ── */}
          {!editingPerf && (() => {
            const annualData = [...perfHistory]
              .filter(h => h.data_quarter == null)
              .sort((a, b) => a.data_year - b.data_year)
            const latest = annualData[annualData.length - 1]
            const sizeUnit = latest?.size_unit ?? 'USD millones'
            const volUnit  = latest?.volume_unit ?? 'toneladas'

            if (annualData.length === 0) return (
              <div className="text-center py-14 card text-gray-400">
                <Activity size={40} className="mx-auto mb-3 opacity-20" />
                <p className="font-medium">Sin datos registrados</p>
                {(isAdmin || isNVC) && <p className="text-sm mt-1">Usa "Agregar año" para registrar los indicadores del mercado.</p>}
              </div>
            )

            const chartData = annualData.map(h => ({
              year: String(h.data_year),
              ventas:  h.market_size != null ? Number(h.market_size) : null,
              volumen: h.volume      != null ? Number(h.volume)      : null,
              precio:  h.market_size > 0 && h.volume > 0 ? parseFloat((Number(h.market_size) / Number(h.volume)).toFixed(6)) : null,
              id: h.id, data_year: h.data_year, maturity: h.maturity, data_source: h.data_source,
              has_seasonality: h.has_seasonality, seasonality_months: h.seasonality_months,
              market_size: h.market_size, volume: h.volume, size_unit: h.size_unit, volume_unit: h.volume_unit,
              reference_year: h.reference_year,
            }))

            const variables = [
              { key: 'ventas',  label: 'Ventas del Mercado', unit: sizeUnit, color: '#D97706', type: 'bar' },
              { key: 'volumen', label: 'Volumen',             unit: volUnit,  color: '#3B82F6', type: 'bar' },
              { key: 'precio',  label: 'Precio Promedio',     unit: `${sizeUnit} / ${volUnit}`, color: '#10B981', type: 'line', note: 'calculado' },
            ]
            const activeVar = variables.find(v => v.key === selectedVariable) ?? variables[0]
            const activeData = chartData.filter(d => d[selectedVariable] != null && d[selectedVariable] > 0)

            // CAGR
            let cagr = null
            if (activeData.length >= 2) {
              const fv = activeData[0][selectedVariable], lv = activeData[activeData.length - 1][selectedVariable]
              const n  = activeData[activeData.length - 1].data_year - activeData[0].data_year
              if (n > 0 && fv > 0) cagr = Math.round((Math.pow(lv / fv, 1 / n) - 1) * 1000) / 10
            }

            const maturityColor = { 'Emergente': 'bg-violet-100 text-violet-700', 'En crecimiento': 'bg-green-100 text-green-700', 'Maduro': 'bg-blue-100 text-blue-700', 'En declive': 'bg-red-100 text-red-700' }[latest?.maturity] ?? 'bg-gray-100 text-gray-600'
            const fmtVal = v => v == null ? '—' : (v < 1 ? v.toFixed(4) : v.toLocaleString('es-CO', { maximumFractionDigits: 2 }))

            return (
              <div className="space-y-4">
                {/* Variable selector */}
                <div className="flex gap-2 flex-wrap">
                  {variables.map(v => {
                    const hasValues = chartData.some(d => d[v.key] != null && d[v.key] > 0)
                    if (!hasValues && v.key !== 'ventas') return null
                    return (
                      <button key={v.key} type="button" onClick={() => setSelectedVariable(v.key)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${
                          selectedVariable === v.key
                            ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:text-amber-700'
                        }`}>
                        {v.label}
                        {v.note && <span className="text-[10px] opacity-70 font-normal">({v.note})</span>}
                      </button>
                    )
                  })}
                </div>

                {/* Chart */}
                {activeData.length >= 2 ? (
                  <div className="card border border-amber-100 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{activeVar.label}</p>
                        <p className="text-xs text-gray-400">{activeVar.unit}</p>
                      </div>
                      {cagr != null && (
                        <div className="text-right">
                          <p className="text-[10px] text-gray-400 uppercase tracking-wide">CAGR anual</p>
                          <p className={`text-lg font-bold ${cagr >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {cagr > 0 ? '+' : ''}{cagr}%
                          </p>
                          <p className="text-[10px] text-gray-400">{activeData[0].year}–{activeData[activeData.length-1].year}</p>
                        </div>
                      )}
                    </div>
                    <ResponsiveContainer width="100%" height={210}>
                      {activeVar.type === 'line' ? (
                        <LineChart data={activeData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                          <XAxis dataKey="year" tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                          <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} width={65} tickFormatter={v => v < 1 ? v.toFixed(3) : v.toLocaleString('es-CO')} />
                          <Tooltip formatter={v => [fmtVal(v), activeVar.label]} labelFormatter={l => `Año ${l}`}
                            contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
                          <Line type="monotone" dataKey={selectedVariable} stroke={activeVar.color} strokeWidth={2.5}
                            dot={{ fill: activeVar.color, r: 4 }} activeDot={{ r: 6 }} connectNulls />
                        </LineChart>
                      ) : (
                        <BarChart data={activeData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                          <XAxis dataKey="year" tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                          <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} width={65} tickFormatter={v => v.toLocaleString('es-CO')} />
                          <Tooltip formatter={v => [v.toLocaleString('es-CO'), activeVar.label]} labelFormatter={l => `Año ${l}`}
                            contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
                          <Bar dataKey={selectedVariable} radius={[4, 4, 0, 0]}>
                            {activeData.map((_, idx) => (
                              <Cell key={idx} fill={idx === activeData.length - 1 ? activeVar.color : `${activeVar.color}88`} />
                            ))}
                          </Bar>
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                ) : activeData.length === 1 ? (
                  <div className="card border border-amber-100 bg-amber-50/30 text-center py-8">
                    <p className="text-4xl font-bold text-gray-900">{fmtVal(activeData[0][selectedVariable])}</p>
                    <p className="text-xs text-gray-500 mt-1">{activeVar.unit}</p>
                    <p className="text-xs font-semibold text-amber-600 mt-2">{activeVar.label} · {activeData[0].year}</p>
                    <p className="text-xs text-gray-400 mt-1">Agrega más años para ver la tendencia</p>
                  </div>
                ) : (
                  <div className="text-center py-6 card text-gray-400 text-xs">Sin datos disponibles para "{activeVar.label}"</div>
                )}

                {/* Latest values summary */}
                {latest && (
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: 'Ventas', value: latest.market_size, unit: sizeUnit },
                      { label: 'Volumen', value: latest.volume,      unit: volUnit  },
                      { label: 'Precio Prom.', value: latest.market_size > 0 && latest.volume > 0 ? Number(latest.market_size) / Number(latest.volume) : null, unit: `${sizeUnit} / ${volUnit}`, calc: true },
                    ].map(({ label, value, unit, calc }) => (
                      <div key={label} className="card border border-amber-100 bg-amber-50/20 text-center py-3">
                        {value != null && Number(value) > 0 ? (
                          <>
                            <p className="text-lg font-bold text-gray-900">{fmtVal(Number(value))}</p>
                            <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{unit}</p>
                          </>
                        ) : <p className="text-lg text-gray-300 font-bold">—</p>}
                        <p className="text-xs font-semibold text-amber-600 mt-1">{label}</p>
                        {calc && value != null && Number(value) > 0 && <span className="text-[9px] bg-amber-200 text-amber-700 font-semibold px-1.5 py-0.5 rounded-full inline-block mt-1">calculado</span>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Qualitative badges */}
                {latest && (
                  <div className="flex flex-wrap gap-2">
                    {latest.maturity && <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${maturityColor}`}><Activity size={11} /> {latest.maturity}</span>}
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${latest.has_seasonality ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                      <CalendarIcon size={11} /> {latest.has_seasonality ? `Estacional: ${latest.seasonality_months || 'Sí'}` : 'Sin estacionalidad'}
                    </span>
                    {latest.data_source && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-gray-100 text-gray-600"><FileText size={11} /> {latest.data_source}</span>}
                  </div>
                )}

                {/* Data table */}
                <div className="card border border-gray-100 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="px-3 py-2.5 text-left font-semibold text-gray-500">Año</th>
                        <th className="px-3 py-2.5 text-right font-semibold text-gray-500">Ventas</th>
                        <th className="px-3 py-2.5 text-right font-semibold text-gray-500">Volumen</th>
                        <th className="px-3 py-2.5 text-right font-semibold text-amber-600">Precio Prom.</th>
                        <th className="px-3 py-2.5 text-left font-semibold text-gray-500">Madurez</th>
                        <th className="px-3 py-2.5 text-left font-semibold text-gray-500">Fuente</th>
                        {(isAdmin || isNVC) && <th className="px-2 py-2.5" />}
                      </tr>
                    </thead>
                    <tbody>
                      {[...chartData].reverse().map((row, i) => {
                        const priceVal = row.ventas > 0 && row.volumen > 0 ? row.ventas / row.volumen : null
                        return (
                          <tr key={row.id ?? i} className="border-b border-gray-50 hover:bg-amber-50/30 transition-colors">
                            <td className="px-3 py-2.5 font-semibold text-gray-800">{row.year}</td>
                            <td className="px-3 py-2.5 text-right text-gray-700">{row.ventas != null ? row.ventas.toLocaleString('es-CO') : '—'}</td>
                            <td className="px-3 py-2.5 text-right text-gray-700">{row.volumen != null ? row.volumen.toLocaleString('es-CO') : '—'}</td>
                            <td className="px-3 py-2.5 text-right font-medium text-amber-700">{priceVal != null ? fmtVal(priceVal) : '—'}</td>
                            <td className="px-3 py-2.5 text-gray-500">{row.maturity ?? '—'}</td>
                            <td className="px-3 py-2.5 text-gray-400">{row.data_source ?? '—'}</td>
                            {(isAdmin || isNVC) && (
                              <td className="px-2 py-2.5">
                                <div className="flex items-center gap-1">
                                  <button onClick={() => { setPerfForm({ ...EMPTY_PERF, year: row.data_year, market_size: row.market_size ?? '', size_unit: row.size_unit ?? 'USD millones', volume: row.volume ?? '', volume_unit: row.volume_unit ?? 'toneladas', reference_year: row.reference_year ?? row.data_year, data_source: row.data_source ?? '', maturity: row.maturity ?? '', has_seasonality: row.has_seasonality ?? false, seasonality_months: row.seasonality_months ?? '' }); setEditingPerf(true) }}
                                    className="p-1 rounded hover:bg-amber-100 text-amber-500 transition-colors" title="Editar">
                                    <Pencil size={11} />
                                  </button>
                                  <button onClick={() => deletePerf(row.id)}
                                    className="p-1 rounded hover:bg-red-50 text-red-400 transition-colors" title="Eliminar año">
                                    <Trash2 size={11} />
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          })()}

          {false && perfRecord && (() => {
            const maturityColor = null

            // ── Calcular CAGR y crecimiento vs período anterior ──
            // Anual: agrega Q1+Q2+Q3+Q4 por año. Trimestral: filtra por quarter específico.
            const sumByYear = {}
            perfHistory.filter(h => h.data_quarter != null && h.market_size != null && Number(h.market_size) > 0)
              .forEach(h => {
                if (!sumByYear[h.data_year]) sumByYear[h.data_year] = { data_year: h.data_year, market_size: 0, volume: 0, size_unit: h.size_unit, volume_unit: h.volume_unit }
                sumByYear[h.data_year].market_size += Number(h.market_size) || 0
                sumByYear[h.data_year].volume += Number(h.volume) || 0
              })
            const annualHist = Object.values(sumByYear).sort((a, b) => a.data_year - b.data_year)

            let calcCagr = null
            if (annualHist.length >= 2) {
              const first = annualHist[0], last = annualHist[annualHist.length - 1]
              const n = last.data_year - first.data_year
              if (n > 0) calcCagr = Math.round((Math.pow(Number(last.market_size) / Number(first.market_size), 1 / n) - 1) * 1000) / 10
            }

            // Crecimiento: compara mismo período del año anterior
            let calcLastYearGrowth = null
            if (selectedQuarter === null) {
              // Anual: compara suma de todos los trimestres del año vs año anterior
              const curAnn  = annualHist.find(h => h.data_year === selectedYear)
              const prevAnn = annualHist.find(h => h.data_year === selectedYear - 1)
              if (curAnn && prevAnn && Number(prevAnn.market_size) > 0) {
                calcLastYearGrowth = Math.round(((Number(curAnn.market_size) - Number(prevAnn.market_size)) / Number(prevAnn.market_size)) * 1000) / 10
              }
            } else {
              // Trimestral: compara mismo quarter del año anterior
              const periodHist = perfHistory
                .filter(h => h.data_quarter === selectedQuarter && h.market_size != null && Number(h.market_size) > 0)
                .sort((a, b) => a.data_year - b.data_year)
              const curRec  = periodHist.find(h => h.data_year === selectedYear)
              const prevRec = periodHist.find(h => h.data_year === selectedYear - 1)
              if (curRec && prevRec && Number(prevRec.market_size) > 0) {
                calcLastYearGrowth = Math.round(((Number(curRec.market_size) - Number(prevRec.market_size)) / Number(prevRec.market_size)) * 1000) / 10
              }
            }
            const histSorted = annualHist // alias para las labels del CAGR card
            const growthPositive = calcLastYearGrowth == null ? true : calcLastYearGrowth >= 0
            return (
              <div className="space-y-4">
                {/* KPI cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {perfData.market_size !== '' && perfData.market_size !== null && (
                    <div className="card border border-amber-100 bg-amber-50/30 text-center">
                      <Database size={16} className="mx-auto mb-1 text-amber-500" />
                      <p className="text-2xl font-bold text-gray-900">{Number(perfData.market_size).toLocaleString('es-CO')}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{perfData.size_unit}</p>
                      <p className="text-xs font-semibold text-amber-600 mt-1">Ventas del mercado</p>
                      {perfData.reference_year && <p className="text-xs text-gray-400">Año {perfData.reference_year}</p>}
                    </div>
                  )}
                  {perfData.volume !== '' && perfData.volume !== null && (
                    <div className="card border border-amber-100 bg-amber-50/30 text-center">
                      <BarChart2 size={16} className="mx-auto mb-1 text-amber-500" />
                      <p className="text-2xl font-bold text-gray-900">{Number(perfData.volume).toLocaleString('es-CO')}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{perfData.volume_unit}</p>
                      <p className="text-xs font-semibold text-amber-600 mt-1">Volumen del mercado</p>
                    </div>
                  )}
                  {perfData.market_size !== '' && perfData.market_size !== null && perfData.volume !== '' && perfData.volume !== null && (() => {
                    const price = Number(perfData.market_size) / Number(perfData.volume)
                    if (!isFinite(price) || price <= 0) return null
                    const priceStr = price < 1 ? price.toFixed(4) : price.toLocaleString('es-CO', { maximumFractionDigits: 2 })
                    return (
                      <div className="card border border-amber-300 bg-amber-100/50 text-center relative overflow-hidden">
                        <div className="absolute top-1.5 right-1.5">
                          <span className="text-[9px] bg-amber-200 text-amber-700 font-semibold px-1.5 py-0.5 rounded-full">calculado</span>
                        </div>
                        <Target size={16} className="mx-auto mb-1 text-amber-600" />
                        <p className="text-2xl font-bold text-amber-900">{priceStr}</p>
                        <p className="text-xs text-amber-700 mt-0.5">{perfData.size_unit} / {perfData.volume_unit}</p>
                        <p className="text-xs font-semibold text-amber-700 mt-1">Precio Promedio</p>
                        <p className="text-xs text-amber-600 mt-0.5">Ventas ÷ Volumen</p>
                      </div>
                    )
                  })()}
                  {calcCagr !== null && (
                    <div className="card border border-amber-300 bg-amber-100/50 text-center relative overflow-hidden">
                      <div className="absolute top-1.5 right-1.5">
                        <span className="text-[9px] bg-amber-200 text-amber-700 font-semibold px-1.5 py-0.5 rounded-full">calculado</span>
                      </div>
                      <TrendingUp size={16} className="mx-auto mb-1 text-amber-500" />
                      <p className="text-2xl font-bold text-gray-900">{calcCagr > 0 ? '+' : ''}{calcCagr}%</p>
                      <p className="text-xs font-semibold text-amber-600 mt-1">CAGR</p>
                      <p className="text-xs text-gray-400">{histSorted[0]?.data_year}–{histSorted[histSorted.length - 1]?.data_year}</p>
                    </div>
                  )}
                  {calcLastYearGrowth !== null && (
                    <div className="card border border-amber-300 bg-amber-100/50 text-center relative overflow-hidden">
                      <div className="absolute top-1.5 right-1.5">
                        <span className="text-[9px] bg-amber-200 text-amber-700 font-semibold px-1.5 py-0.5 rounded-full">calculado</span>
                      </div>
                      {growthPositive ? <TrendingUp size={16} className="mx-auto mb-1 text-green-500" /> : <TrendingDown size={16} className="mx-auto mb-1 text-red-500" />}
                      <p className={`text-2xl font-bold ${growthPositive ? 'text-green-700' : 'text-red-600'}`}>{calcLastYearGrowth > 0 ? '+' : ''}{calcLastYearGrowth}%</p>
                      <p className="text-xs font-semibold text-amber-600 mt-1">
                        {selectedQuarter ? `Q${selectedQuarter} ` : ''}{selectedYear - 1}→{selectedQuarter ? `Q${selectedQuarter} ` : ''}{selectedYear}
                      </p>
                    </div>
                  )}
                  {perfData.maturity && (
                    <div className="card border border-amber-100 bg-amber-50/30 text-center">
                      <Activity size={16} className="mx-auto mb-1 text-amber-500" />
                      <span className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-semibold ${maturityColor}`}>{perfData.maturity}</span>
                      <p className="text-xs font-semibold text-amber-600 mt-2">Madurez del mercado</p>
                    </div>
                  )}
                  <div className="card border border-amber-100 bg-amber-50/30 text-center">
                    <CalendarIcon size={16} className="mx-auto mb-1 text-amber-500" />
                    <p className={`text-lg font-bold mt-1 ${perfData.has_seasonality ? 'text-amber-600' : 'text-gray-500'}`}>
                      {perfData.has_seasonality ? 'Sí' : 'No'}
                    </p>
                    <p className="text-xs font-semibold text-amber-600 mt-1">Estacionalidad</p>
                    {perfData.has_seasonality && perfData.seasonality_months && (
                      <p className="text-xs text-gray-500 mt-0.5">{perfData.seasonality_months}</p>
                    )}
                  </div>
                  {perfData.data_source && (
                    <div className="card border border-amber-100 bg-amber-50/30 text-center">
                      <FileText size={16} className="mx-auto mb-1 text-amber-500" />
                      <p className="text-sm font-semibold text-gray-700 mt-1">{perfData.data_source}</p>
                      <p className="text-xs font-semibold text-amber-600 mt-1">Fuente</p>
                    </div>
                  )}
                </div>
              </div>
            )
          })()}

          {!editingPerf && !perfRecord && (
            <div className="text-center py-14 card text-gray-400">
              <Activity size={40} className="mx-auto mb-3 opacity-20" />
              <p className="font-medium">Sin datos registrados para {selectedYear}</p>
              {(isAdmin || isNVC) && <p className="text-sm mt-1">Haz clic en "Registrar datos" para agregar los indicadores del mercado.</p>}
            </div>
          )}

          {/* ── Evolución Histórica ── */}
          {perfHistory.length > 1 && (
            <div className="card space-y-4 border border-amber-100">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <TrendingUp size={15} className="text-amber-500" /> Evolución Histórica del Mercado
                {selectedQuarter === null && <span className="text-xs font-normal text-amber-600 ml-1">(suma Q1+Q2+Q3+Q4)</span>}
              </h3>
              <div className="space-y-3">
                {(() => {
                  // Aggregate: Anual = sum of quarters per year; Trimestral = filter by quarter
                  let chartData
                  if (selectedQuarter === null) {
                    const byYear = {}
                    perfHistory.filter(p => p.data_quarter != null).forEach(p => {
                      if (!byYear[p.data_year]) byYear[p.data_year] = { data_year: p.data_year, market_size: 0, volume: 0, size_unit: p.size_unit, volume_unit: p.volume_unit }
                      byYear[p.data_year].market_size += Number(p.market_size) || 0
                      byYear[p.data_year].volume += Number(p.volume) || 0
                    })
                    chartData = Object.values(byYear).sort((a, b) => a.data_year - b.data_year)
                  } else {
                    chartData = perfHistory.filter(p => p.data_quarter === selectedQuarter).sort((a, b) => a.data_year - b.data_year)
                  }
                  const hasVolume = chartData.some(p => p.volume != null && Number(p.volume) > 0)
                  const maxSize = Math.max(...chartData.map(p => Number(p.market_size) || 0), 1)
                  const maxVol  = Math.max(...chartData.map(p => Number(p.volume) || 0), 1)
                  return chartData.map(p => {
                    const size  = Number(p.market_size) || 0
                    const vol   = Number(p.volume) || 0
                    const pct   = (size / maxSize) * 100
                    const vPct  = (vol / maxVol) * 100
                    const price = size > 0 && vol > 0 ? size / vol : null
                    const isActive = p.data_year === selectedYear
                    return (
                      <div key={p.data_year} className="space-y-1.5 cursor-pointer" onClick={() => setSelectedYear(p.data_year)}>
                        <div className="flex items-center justify-between text-xs">
                          <span className={`font-semibold ${isActive ? 'text-amber-700' : 'text-gray-600'}`}>
                            {p.data_year} {isActive && '← actual'}
                          </span>
                          <span className="text-gray-500 flex items-center gap-3">
                            {size > 0 && <span>{size.toLocaleString('es-CO')} {p.size_unit ?? ''}</span>}
                            {vol > 0 && <span className="text-blue-500">{vol.toLocaleString('es-CO')} {p.volume_unit ?? ''}</span>}
                            {price != null && <span className="text-amber-600 font-medium">P̄ {price < 1 ? price.toFixed(3) : price.toLocaleString('es-CO', { maximumFractionDigits: 2 })}</span>}
                            {p.cagr != null && <span className="text-green-600 font-medium">CAGR {p.cagr}%</span>}
                          </span>
                        </div>
                        {/* Ventas bar */}
                        <div className="w-full bg-gray-100 rounded-full h-5 overflow-hidden">
                          <div className={`h-full rounded-full transition-all flex items-center px-2 ${isActive ? 'bg-amber-500' : 'bg-amber-200'}`}
                            style={{ width: `${Math.max(pct, 2)}%` }}>
                            {pct > 15 && <span className={`text-xs font-semibold ${isActive ? 'text-white' : 'text-amber-700'}`}>{size.toLocaleString('es-CO')}</span>}
                          </div>
                        </div>
                        {/* Volume bar — only when history has volume */}
                        {hasVolume && vol > 0 && (
                          <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${isActive ? 'bg-blue-400' : 'bg-blue-200'}`}
                              style={{ width: `${Math.max(vPct, 2)}%` }} />
                          </div>
                        )}
                      </div>
                    )
                  })
                })()}
              </div>
              <p className="text-xs text-gray-400">Haz clic en un año para ver sus datos</p>
            </div>
          )}
        </div>
      )}

      {/* ── Market Channels Tab ──────────────────────────────── */}
      {activeTab === 'canales' && (
        <div className="space-y-5">
          {/* Header */}
          <div className="rounded-xl bg-gradient-to-r from-sky-600 to-sky-700 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <TrendingUp size={18} className="text-white" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-base">Mix de Canales de Distribución</h2>
                <p className="text-sky-100 text-xs mt-0.5">
                  {channels.length} canal{channels.length !== 1 ? 'es' : ''} registrado{channels.length !== 1 ? 's' : ''}
                  {channelSiblingProducts.length > 0 && ` · ${channelSiblingProducts.length} producto${channelSiblingProducts.length !== 1 ? 's' : ''} del mercado`}
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {channels.length > 0 && (
                <button onClick={exportChannelsToPptx}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors"
                  title="Descargar gráfica en PowerPoint">
                  <Download size={14} /> PPT
                </button>
              )}
              {(isAdmin || isNVC) && !phaseLocked && (
                /* ── Plantilla Clientes ── */
                <button
                  title="Descargar plantilla Excel de clientes por canal"
                  onClick={() => {
                    const wb = XLSX.utils.book_new()
                    const sheetNames = channels.length > 0
                      ? channels.map(ch => ch.name.substring(0, 31)) // Excel max 31 chars
                      : ['Canal 1', 'Canal 2']
                    sheetNames.forEach((shName, idx) => {
                      const existingClients = channels[idx]?.clients_data ?? []
                      const rows = [['Nombre del cliente', 'Ventas del cliente']]
                      if (existingClients.length > 0) {
                        existingClients.forEach(c => rows.push([c.name, c.ventas]))
                      } else {
                        // placeholder rows
                        rows.push(['Ejemplo: Distribuidor ABC', 450000])
                        rows.push(['Ejemplo: Cadena XYZ', 230000])
                        rows.push(['Ejemplo: Cliente directo 1', 85000])
                      }
                      const ws = XLSX.utils.aoa_to_sheet(rows)
                      ws['!cols'] = [{ wch: 35 }, { wch: 22 }]
                      // Bold header style hint
                      XLSX.utils.book_append_sheet(wb, ws, shName)
                    })
                    XLSX.writeFile(wb, `plantilla_clientes_canales_${selectedYear}${selectedQuarter ? `_Q${selectedQuarter}` : ''}.xlsx`)
                  }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors">
                  <Download size={14} /> Plantilla Clientes
                </button>
              )}
              {(isAdmin || isNVC) && !phaseLocked && (
                /* ── Subir Clientes ── */
                <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors"
                  title="Subir Excel de clientes por canal">
                  {uploadingClients ? <><Loader size={14} className="animate-spin" /> Procesando...</> : <><Upload size={14} /> Subir Clientes</>}
                  <input type="file" accept=".xlsx,.xls" className="hidden" disabled={uploadingClients} onChange={async e => {
                    const file = e.target.files?.[0]; if (!file) return
                    setUploadingClients(true)
                    const norm4 = s => String(s).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim()
                    const reader = new FileReader()
                    reader.onload = async evt => {
                      try {
                        const wb2 = XLSX.read(evt.target.result, { type: 'array' })
                        let updatedCount = 0
                        for (const sheetName of wb2.SheetNames) {
                          // Match sheet name to a channel (accent/case insensitive)
                          const matchedCh = channels.find(ch => norm4(ch.name) === norm4(sheetName))
                          if (!matchedCh) continue
                          const ws2 = wb2.Sheets[sheetName]
                          const rowsRaw = XLSX.utils.sheet_to_json(ws2, { defval: '' })
                          if (!rowsRaw.length) continue
                          // Detect columns
                          const firstRow = rowsRaw[0]
                          const keys = Object.keys(firstRow)
                          const nameKey = keys.find(k => norm4(k).includes('nombre') || norm4(k).includes('client') || norm4(k) === 'name') ?? keys[0]
                          const ventasKey = keys.find(k => norm4(k).includes('venta') || norm4(k).includes('sales') || norm4(k).includes('ingre')) ?? keys[1]
                          // Parse clients
                          const clients = rowsRaw
                            .filter(r => String(r[nameKey] ?? '').trim() && String(r[nameKey]).trim() !== '' && !String(r[nameKey]).toLowerCase().startsWith('ejemplo'))
                            .map(r => ({
                              name:   String(r[nameKey]).trim(),
                              ventas: parseFloat(String(r[ventasKey] ?? '0').replace(/[^0-9.-]/g, '')) || 0,
                            }))
                            .filter(c => c.name && c.ventas > 0)
                          if (!clients.length) continue
                          // Sort desc by ventas
                          clients.sort((a, b) => b.ventas - a.ventas)
                          const totalVentas = clients.reduce((s, c) => s + c.ventas, 0)
                          // Annotate with pct
                          const clientsWithPct = clients.map(c => ({ ...c, pct: Math.round((c.ventas / totalVentas) * 1000) / 10 }))
                          // Pareto 80%: count clients needed to reach 80% of channel sales
                          let cumPct = 0, paretoCount = 0
                          for (const c of clientsWithPct) {
                            cumPct += c.pct
                            paretoCount++
                            if (cumPct >= 80) break
                          }
                          const numClientes = clients.length
                          const clientesPonderado = paretoCount
                          // Update channel
                          await supabase.from('market_channels').update({
                            num_clientes:       numClientes,
                            clientes_ponderado: clientesPonderado,
                            clients_data:       clientsWithPct,
                            updated_at:         new Date().toISOString(),
                          }).eq('id', matchedCh.id)
                          updatedCount++
                        }
                        alert(`${updatedCount} canal${updatedCount !== 1 ? 'es' : ''} actualizado${updatedCount !== 1 ? 's' : ''} con datos de clientes.`)
                        fetchData()
                      } catch (err) {
                        alert('Error al procesar el archivo: ' + err.message)
                      } finally {
                        setUploadingClients(false)
                      }
                    }
                    reader.readAsArrayBuffer(file)
                    e.target.value = ''
                  }} />
                </label>
              )}
              {(isAdmin || isNVC || isCaja) && !phaseLocked && (
                <>
                  <button
                    onClick={() => openBulkDelete('channel')}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/90 hover:bg-red-500 text-white text-sm font-semibold transition-colors"
                    title="Eliminar un canal y toda su data histórica"
                  >
                    <Trash2 size={14} /> Eliminar canal
                  </button>
                  <button onClick={openCreateChannel}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-sky-700 hover:bg-sky-50 text-sm font-semibold transition-colors">
                    <Plus size={14} /> Agregar canal
                  </button>
                </>
              )}
            </div>
          </div>

          {/* ── Tendencia de Canales ── */}
          {channelsTrendData.length > 0 && (() => {
            const allChanNames = [...new Set(channelsTrendData.flatMap(yd => Object.keys(yd.channels)))]
            const CHAN_COLORS = ['#0284C7','#38BDF8','#0369A1','#7DD3FC','#075985','#BAE6FD','#0EA5E9','#E0F2FE']
            // Normalize per year so bars stack to 100%
            const chartData = channelsTrendData.map(yd => {
              const row = { year: String(yd.year) }
              const totalWeight = allChanNames.reduce((s, n) => s + (yd.channels[n]?.totalWeight ?? 0), 0)
              allChanNames.forEach(name => {
                const w = yd.channels[name]?.totalWeight ?? 0
                row[name] = totalWeight > 0 ? Math.round((w / totalWeight) * 1000) / 10 : 0
              })
              return row
            })
            const hasMultiYear = channelsTrendData.length >= 2
            return (
              <div className="card space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="text-base font-bold text-gray-900">Evolución del Mix de Canales</h3>
                  <span className="text-xs text-gray-400 bg-gray-50 border border-gray-200 px-2 py-1 rounded-lg">% participación en el mix</span>
                </div>
                {!hasMultiYear ? (
                  <p className="text-sm text-gray-400 text-center py-4">Agrega datos de más años para ver la tendencia</p>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                      <XAxis dataKey="year" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={36}
                        tickFormatter={v => `${v}%`} domain={[0, 100]} />
                      <Tooltip formatter={(v, n) => [`${v}%`, n]} labelStyle={{ fontWeight: 600, color: '#374151' }} />
                      <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                      {allChanNames.map((name, i) => (
                        <Bar key={name} dataKey={name} stackId="channels"
                          fill={CHAN_COLORS[i % CHAN_COLORS.length]}
                          radius={i === allChanNames.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            )
          })()}

          {/* Year + Quarter selector */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500 font-medium w-14">Año:</span>
              {Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i).map(y => (
                <button key={y} type="button" onClick={() => setSelectedYear(y)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                    selectedYear === y ? 'bg-sky-600 text-white border-sky-600' : 'border-gray-200 text-gray-600 hover:border-sky-300 hover:text-sky-700'
                  }`}>{y}</button>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500 font-medium w-14">Período:</span>
              {[{ label: 'Anual', value: null }, { label: 'Q1', value: 1 }, { label: 'Q2', value: 2 }, { label: 'Q3', value: 3 }, { label: 'Q4', value: 4 }].map(({ label, value }) => (
                <button key={label} type="button" onClick={() => setSelectedQuarter(value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                    selectedQuarter === value ? 'bg-sky-600 text-white border-sky-600' : 'border-gray-200 text-gray-600 hover:border-sky-300 hover:text-sky-700'
                  }`}>{label}</button>
              ))}
            </div>
          </div>

          {/* Channel cards */}
          {channels.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {channels.map(ch => {
                const weights = channelWeights[ch.id] ?? {}
                const productsList = channelSiblingProducts.filter(p => weights[p.id] !== undefined)
                return (
                  <div key={ch.id} className="card border border-sky-100 hover:border-sky-200 transition-colors">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 text-base">{ch.name}</h3>
                        {ch.description && <p className="text-xs text-gray-400 mt-0.5">{ch.description}</p>}
                        {/* Clientes KPIs */}
                        {(ch.num_clientes != null || ch.clientes_ponderado != null) && (
                          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                            {ch.num_clientes != null && (
                              <span className="inline-flex items-center gap-1 text-xs text-sky-700 font-semibold bg-sky-50 border border-sky-200 px-2 py-0.5 rounded-full">
                                <User size={10} /> {ch.num_clientes.toLocaleString('es-CO')} clientes
                              </span>
                            )}
                            {ch.clientes_ponderado != null && (
                              <span className="inline-flex items-center gap-1 text-xs text-indigo-700 font-semibold bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-full"
                                title="Clientes clave que concentran el peso del canal">
                                <Target size={10} /> {ch.clientes_ponderado.toLocaleString('es-CO')} ponderados
                              </span>
                            )}
                            {ch.num_clientes != null && ch.clientes_ponderado != null && ch.num_clientes > 0 && (
                              <span className="text-xs text-gray-400">
                                ({((ch.clientes_ponderado / ch.num_clientes) * 100).toFixed(0)}% concentra el canal)
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      {(isAdmin || isNVC || isCaja) && !phaseLocked && (
                        <div className="flex gap-1.5 flex-shrink-0">
                          <button onClick={() => openEditChannel(ch)}
                            title="Editar canal"
                            className="p-1.5 text-sky-600 hover:bg-sky-100 rounded-lg transition-colors border border-sky-200">
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => deleteChannel(ch.id)}
                            title="Eliminar canal"
                            className="p-1.5 text-red-500 hover:bg-red-100 rounded-lg transition-colors border border-red-200">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                    {productsList.length > 0 ? (
                      <div className="space-y-2">
                        {productsList.map(p => {
                          const w = parseFloat(weights[p.id]) || 0
                          return (
                            <div key={p.id}>
                              <div className="flex items-center justify-between text-xs mb-0.5">
                                <span className="text-gray-600 font-medium truncate mr-2">{p.name}</span>
                                <span className="font-bold text-sky-700 flex-shrink-0">{w}%</span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                                <div className="h-full rounded-full bg-sky-500 transition-all" style={{ width: `${Math.min(w, 100)}%` }} />
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic">
                        {channelSiblingProducts.length === 0 ? 'Agrega productos en "Soluciones" para registrar pesos' : 'Sin pesos registrados'}
                      </p>
                    )}

                    {/* ── Listado de clientes (expandible) ── */}
                    {ch.clients_data?.length > 0 && (
                      <div className="mt-3 border-t border-sky-100 pt-3">
                        <button
                          onClick={() => setExpandedClientChannelId(prev => prev === ch.id ? null : ch.id)}
                          className="flex items-center justify-between w-full text-xs font-semibold text-sky-600 hover:text-sky-800 transition-colors">
                          <span className="flex items-center gap-1.5">
                            <User size={11} /> {ch.clients_data.length} clientes · Top 5 concentran {
                              ch.clients_data.slice(0, 5).reduce((s, c) => s + (c.pct || 0), 0).toFixed(1)
                            }%
                          </span>
                          <span className="text-gray-400 text-[10px]">{expandedClientChannelId === ch.id ? '▲ Cerrar' : '▼ Ver detalle'}</span>
                        </button>

                        {expandedClientChannelId === ch.id && (
                          <div className="mt-2 overflow-hidden rounded-lg border border-sky-100">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-sky-50 text-sky-700">
                                  <th className="text-left px-2.5 py-1.5 font-semibold">#</th>
                                  <th className="text-left px-2.5 py-1.5 font-semibold">Cliente</th>
                                  <th className="text-right px-2.5 py-1.5 font-semibold">Ventas</th>
                                  <th className="text-right px-2.5 py-1.5 font-semibold">% canal</th>
                                  <th className="text-right px-2.5 py-1.5 font-semibold">% acum.</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-sky-50">
                                {(() => {
                                  let cumPct = 0
                                  const paretoMark = ch.clientes_ponderado ?? 0
                                  return ch.clients_data.map((c, idx) => {
                                    cumPct += c.pct || 0
                                    const isPareto = idx < paretoMark
                                    return (
                                      <tr key={idx} className={`transition-colors ${isPareto ? 'bg-sky-50/60' : 'bg-white hover:bg-gray-50'}`}>
                                        <td className="px-2.5 py-1.5 text-gray-400">{idx + 1}</td>
                                        <td className="px-2.5 py-1.5 font-medium text-gray-800 max-w-[140px] truncate">
                                          {isPareto && <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-500 mr-1 mb-0.5" />}
                                          {c.name}
                                        </td>
                                        <td className="px-2.5 py-1.5 text-right text-gray-700">{Number(c.ventas).toLocaleString('es-CO')}</td>
                                        <td className="px-2.5 py-1.5 text-right font-semibold text-sky-700">{c.pct}%</td>
                                        <td className="px-2.5 py-1.5 text-right text-gray-500">{Math.min(cumPct, 100).toFixed(1)}%</td>
                                      </tr>
                                    )
                                  })
                                })()}
                              </tbody>
                              <tfoot>
                                <tr className="bg-sky-50 border-t border-sky-200">
                                  <td colSpan={2} className="px-2.5 py-1.5 font-bold text-sky-800 text-xs">Total</td>
                                  <td className="px-2.5 py-1.5 text-right font-bold text-sky-800">
                                    {ch.clients_data.reduce((s, c) => s + (c.ventas || 0), 0).toLocaleString('es-CO')}
                                  </td>
                                  <td className="px-2.5 py-1.5 text-right font-bold text-sky-800">100%</td>
                                  <td />
                                </tr>
                                <tr>
                                  <td colSpan={5} className="px-2.5 py-1.5 text-[10px] text-gray-400">
                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-500 mr-1 mb-0.5" />
                                    Los primeros {ch.clientes_ponderado} clientes (ponderados) concentran el 80% de las ventas del canal
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {channels.length === 0 && (
            <div className="text-center py-14 card text-gray-400">
              <TrendingUp size={40} className="mx-auto mb-3 opacity-20" />
              {selectedQuarter === null ? (
                <>
                  <p className="font-medium">Selecciona un trimestre para ver los canales</p>
                  <p className="text-sm mt-1">Los datos de canales se registran por trimestre (Q1, Q2, Q3, Q4)</p>
                </>
              ) : (
                <>
                  <p className="font-medium">Sin canales registrados</p>
                  <p className="text-sm mt-1">Agrega los canales de distribución para ver el mix por producto</p>
                  {(isAdmin || isNVC) && !phaseLocked && (
                    <button onClick={openCreateChannel} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold transition-colors">
                      <Plus size={15} /> Agregar primer canal
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {channels.length > 0 && channelSiblingProducts.length === 0 && (
            <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-xs text-amber-700 flex items-center gap-2">
              <BarChart2 size={14} className="flex-shrink-0" />
              Para registrar pesos por producto, agrega productos en la sección <span className="font-semibold ml-1">"Soluciones (Producto)"</span> de esta etapa.
            </div>
          )}

          {/* Chart */}
          {channels.length > 0 && (
            <div className="card space-y-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <h3 className="text-base font-bold text-gray-900">Peso de Canales por Producto</h3>
                <div className="flex gap-1.5 flex-wrap">
                  <button onClick={() => setActiveChannelFilter('all')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${activeChannelFilter === 'all' ? 'bg-sky-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-sky-50 hover:text-sky-700'}`}>
                    Promedio general
                  </button>
                  {channelSiblingProducts.map(p => (
                    <button key={p.id} onClick={() => setActiveChannelFilter(p.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${activeChannelFilter === p.id ? 'bg-sky-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-sky-50 hover:text-sky-700'}`}>
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>

              {activeChannelFilter !== 'all' && (() => {
                const prod = channelSiblingProducts.find(p => p.id === activeChannelFilter)
                return prod ? <p className="text-xs text-gray-500 -mt-2">Peso de canales en <span className="font-semibold text-sky-700">{prod.name}</span></p> : null
              })()}

              <div className="space-y-3">
                {channelChartData.map((item, idx) => {
                  const COLORS = ['bg-sky-700','bg-sky-600','bg-sky-500','bg-sky-400','bg-sky-300','bg-sky-200']
                  const barColor = COLORS[Math.min(idx, COLORS.length - 1)]
                  const textColor = idx < 3 ? 'text-white' : 'text-sky-800'
                  return (
                    <div key={item.id} className="space-y-1">
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-0.5">
                        <span className="font-semibold text-gray-800">
                          <span className="text-sky-400 mr-1.5">#{idx + 1}</span>{item.name}
                        </span>
                        <span className="font-bold text-sky-700">{item.weight}%</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-8 overflow-hidden">
                        <div className={`h-full rounded-full ${barColor} transition-all flex items-center px-3`}
                          style={{ width: `${Math.max(item.weight, 1)}%` }}>
                          {item.weight > 8 && <span className={`text-xs font-semibold ${textColor}`}>{item.weight}%</span>}
                        </div>
                      </div>
                    </div>
                  )
                })}

                {/* Otros bar */}
                {activeChannelFilter !== 'all' && (() => {
                  const total = channelChartData.reduce((s, d) => s + d.weight, 0)
                  const otros = Math.max(0, parseFloat((100 - total).toFixed(1)))
                  if (otros <= 0) return null
                  return (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-0.5">
                        <span className="font-semibold text-gray-500 italic">Otros / No especificado</span>
                        <span className="font-bold text-gray-500">{otros}%</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-8 overflow-hidden">
                        <div className="h-full rounded-full bg-gray-300 transition-all flex items-center px-3"
                          style={{ width: `${Math.max(otros, 1)}%` }}>
                          {otros > 8 && <span className="text-xs font-semibold text-gray-600">{otros}%</span>}
                        </div>
                      </div>
                    </div>
                  )
                })()}
              </div>

              <div className="border-t border-gray-100 pt-4 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-sky-700">{channels.length}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Canales identificados</p>
                </div>
                <div className="text-center border-x border-gray-100">
                  <p className="text-2xl font-bold text-sky-700">{channelChartData[0]?.weight ?? 0}%</p>
                  <p className="text-xs text-gray-500 mt-0.5">Canal principal</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-sky-700">{channelChartData[0]?.name?.split(' ')[0] ?? '—'}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Canal #1</p>
                </div>
              </div>
            </div>
          )}

          {/* ── Evolución Histórica de Canales ── */}
          {channelsHistory.length > 1 && (
            <div className="card space-y-4 border border-sky-100">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <TrendingUp size={15} className="text-sky-500" /> Evolución Histórica de Canales
              </h3>
              <div className="space-y-3">
                {(() => {
                  const maxCount = Math.max(...channelsHistory.map(h => h.count), 1)
                  return channelsHistory.map(h => {
                    const pct = (h.count / maxCount) * 100
                    const isActive = h.year === selectedYear
                    return (
                      <div key={h.year} className="space-y-1 cursor-pointer" onClick={() => setSelectedYear(h.year)}>
                        <div className="flex items-center justify-between text-xs">
                          <span className={`font-semibold ${isActive ? 'text-sky-700' : 'text-gray-600'}`}>
                            {h.year} {isActive && '← actual'}
                          </span>
                          <span className="text-gray-500">{h.count} canal{h.count !== 1 ? 'es' : ''}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-6 overflow-hidden">
                          <div className={`h-full rounded-full transition-all flex items-center px-2 ${isActive ? 'bg-sky-600' : 'bg-sky-200'}`}
                            style={{ width: `${Math.max(pct, 8)}%` }}>
                            <span className={`text-xs font-semibold ${isActive ? 'text-white' : 'text-sky-700'}`}>{h.count}</span>
                          </div>
                        </div>
                      </div>
                    )
                  })
                })()}
              </div>
              <p className="text-xs text-gray-400">Haz clic en un año para ver sus canales</p>
            </div>
          )}
        </div>
      )}

      {/* Channel modal */}
      <Modal open={showChannelModal} onClose={() => setShowChannelModal(false)} title={editChannel ? 'Editar canal' : 'Nuevo canal'}>
        <form onSubmit={saveChannel} className="space-y-4">
          <div>
            <label className="label">Nombre del canal *</label>
            <input className="input" required value={channelForm.name}
              onChange={e => setChannelForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Ej: Distribuidores mayoristas, E-commerce, Venta directa..." />
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea className="input resize-none" rows={2} value={channelForm.description}
              onChange={e => setChannelForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Breve descripción del canal..." />
          </div>
          {/* Clientes */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">N° clientes del canal</label>
              <input type="number" min="0" step="1" className="input"
                placeholder="Ej: 450"
                value={channelForm.num_clientes}
                onChange={e => setChannelForm(f => ({ ...f, num_clientes: e.target.value }))} />
              <p className="text-xs text-gray-400 mt-1">Total de clientes que compran por este canal</p>
            </div>
            <div>
              <label className="label">Clientes ponderados</label>
              <input type="number" min="0" step="1" className="input"
                placeholder="Ej: 15"
                value={channelForm.clientes_ponderado}
                onChange={e => setChannelForm(f => ({ ...f, clientes_ponderado: e.target.value }))} />
              <p className="text-xs text-gray-400 mt-1">Clientes clave que concentran el peso del canal</p>
            </div>
          </div>

          {channelSiblingProducts.length > 0 && (
            <div>
              <label className="label">Peso por producto (%)</label>
              <p className="text-xs text-gray-400 mb-2">Porcentaje de las ventas de cada producto que pasa por este canal. Deja vacío si no aplica.</p>
              <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                {channelSiblingProducts.map(p => (
                  <div key={p.id} className="flex items-center gap-3">
                    <span className="text-sm text-gray-700 flex-1 truncate">{p.name}</span>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <input type="number" min="0" max="100" step="0.1"
                        className="input w-24 text-right" placeholder="—"
                        value={channelWeightForm[p.id] ?? ''}
                        onChange={e => setChannelWeightForm(f => ({ ...f, [p.id]: e.target.value }))} />
                      <span className="text-sm text-gray-400">%</span>
                    </div>
                  </div>
                ))}
              </div>
              {channelSiblingProducts.length === 0 && (
                <p className="text-xs text-gray-400 italic">No hay productos registrados en "Soluciones (Producto)" aún.</p>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setShowChannelModal(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={savingChannel}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold transition-colors disabled:opacity-50">
              {savingChannel ? 'Guardando...' : editChannel ? 'Guardar cambios' : 'Agregar canal'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Market Competitors Tab ───────────────────────────── */}
      {activeTab === 'competidores' && (
        <div className="space-y-5">
          {/* Header */}
          <div className="rounded-xl bg-gradient-to-r from-rose-600 to-rose-700 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <Building2 size={18} className="text-white" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-base">Participaciones de Mercado por Competidor</h2>
                <p className="text-rose-100 text-xs mt-0.5">
                  {competitors.length} competidor{competitors.length !== 1 ? 'es' : ''} registrado{competitors.length !== 1 ? 's' : ''}
                  {siblingProducts.length > 0 && ` · ${siblingProducts.length} producto${siblingProducts.length !== 1 ? 's' : ''} del mercado`}
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {competitors.length > 0 && (
                <button onClick={exportCompetitorsToPptx}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors"
                  title="Descargar gráfica en PowerPoint">
                  <Download size={14} /> PPT
                </button>
              )}
              {(isAdmin || isNVC) && !phaseLocked && (
                <button
                  title="Descargar plantilla Excel de competidores"
                  onClick={() => {
                    const prodHeaders = siblingProducts.flatMap(p => [`${p.name} - Part %`, `${p.name} - Precio`, `${p.name} - Unidad`])
                    const header = ['Competidor', 'Descripción', 'Propia marca (Sí/No)', ...prodHeaders]
                    const exampleRow = ['Nombre del competidor', 'Descripción opcional', 'No', ...siblingProducts.flatMap(() => ['', '', ''])]
                    const ownRow = ['Mi Marca S.A.', 'Esta es mi empresa', 'Sí', ...siblingProducts.flatMap(p => ['25', '1500', 'COP/kg'])]
                    const rows = [header, exampleRow, ownRow]
                    const ws = XLSX.utils.aoa_to_sheet(rows)
                    ws['!cols'] = [{ wch: 28 }, { wch: 28 }, { wch: 22 }, ...siblingProducts.flatMap(() => [{ wch: 16 }, { wch: 14 }, { wch: 12 }])]
                    const wb = XLSX.utils.book_new()
                    XLSX.utils.book_append_sheet(wb, ws, 'Competidores')
                    XLSX.writeFile(wb, `plantilla_competidores_${selectedYear}${selectedQuarter ? `_Q${selectedQuarter}` : ''}.xlsx`)
                  }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors">
                  <Download size={14} /> Plantilla
                </button>
              )}
              {(isAdmin || isNVC) && !phaseLocked && (
                <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors" title="Subir competidores desde Excel">
                  <Upload size={14} /> Subir Excel
                  <input type="file" accept=".xlsx,.xls" className="hidden" onChange={async e => {
                    const file = e.target.files?.[0]; if (!file) return
                    const reader = new FileReader()
                    reader.onload = async evt => {
                      const wb2 = XLSX.read(evt.target.result, { type: 'array' })
                      const ws2 = wb2.Sheets[wb2.SheetNames[0]]
                      const rows2 = XLSX.utils.sheet_to_json(ws2, { defval: '' })
                      const normH = s => String(s).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim()
                      const isOwnBrandVal = v => normH(String(v)).startsWith('s') // Sí, si, yes, s
                      // Group rows by competitor name
                      const compMap = {}
                      for (const row of rows2) {
                        const keys = Object.keys(row)
                        const nameKey = keys.find(k => normH(k).includes('competidor') || normH(k) === 'nombre' || normH(k) === 'name') ?? keys[0]
                        const ownKey = keys.find(k => normH(k).includes('propia') || normH(k).includes('marca') || normH(k).includes('soy'))
                        const descKey = keys.find(k => normH(k).includes('descrip'))
                        const compName = String(row[nameKey] ?? '').trim()
                        if (!compName) continue
                        if (!compMap[compName]) compMap[compName] = { description: descKey ? String(row[descKey] ?? '').trim() : '', is_own_brand: ownKey ? isOwnBrandVal(row[ownKey]) : false, products: {} }
                        // Parse product columns
                        for (const k of keys) {
                          const nk = normH(k)
                          const matchProd = siblingProducts.find(p => nk.startsWith(normH(p.name)))
                          if (!matchProd) continue
                          if (nk.includes('part') || nk.includes('%')) {
                            const val = parseFloat(String(row[k]).replace(',', '.'))
                            if (!isNaN(val)) compMap[compName].products[matchProd.id] = { ...( compMap[compName].products[matchProd.id] ?? {}), share: val }
                          } else if (nk.includes('precio') || nk.includes('price')) {
                            const val = parseFloat(String(row[k]).replace(',', '.'))
                            if (!isNaN(val)) compMap[compName].products[matchProd.id] = { ...(compMap[compName].products[matchProd.id] ?? {}), avg_price: val }
                          } else if (nk.includes('unidad') || nk.includes('unit')) {
                            const val = String(row[k]).trim()
                            if (val) compMap[compName].products[matchProd.id] = { ...(compMap[compName].products[matchProd.id] ?? {}), price_unit: val }
                          }
                        }
                      }
                      // Insert competitors
                      for (const [cname, cdata] of Object.entries(compMap)) {
                        // Mark own brand: unmark others first if needed
                        if (cdata.is_own_brand) {
                          await supabase.from('market_competitors').update({ is_own_brand: false }).eq('section_id', sectionId)
                          setCompetitors(prev => prev.map(c => ({ ...c, is_own_brand: false })))
                        }
                        const { data: newC } = await supabase.from('market_competitors').insert({
                          section_id: sectionId, data_year: selectedYear, data_quarter: selectedQuarter,
                          name: cname, description: cdata.description || null,
                          is_own_brand: cdata.is_own_brand,
                          competitor_order: competitors.length + 1,
                        }).select().single()
                        if (!newC) continue
                        setCompetitors(prev => [...prev, newC])
                        // Insert shares/prices
                        const sharesToInsert = Object.entries(cdata.products)
                          .filter(([, v]) => v.share != null || v.avg_price != null)
                          .map(([pid, v]) => ({ competitor_id: newC.id, product_id: pid, market_share: v.share ?? null, avg_price: v.avg_price ?? null, price_unit: v.price_unit ?? null }))
                        if (sharesToInsert.length > 0) {
                          await supabase.from('competitor_product_shares').upsert(sharesToInsert, { onConflict: 'competitor_id,product_id' })
                          const sharesMap = {}; const pricesMap = {}
                          sharesToInsert.forEach(s => { if (s.market_share != null) sharesMap[s.product_id] = s.market_share; if (s.avg_price != null) pricesMap[s.product_id] = { avg_price: s.avg_price, price_unit: s.price_unit ?? '' } })
                          setCompetitorShares(prev => ({ ...prev, [newC.id]: sharesMap }))
                          setCompetitorPrices(prev => ({ ...prev, [newC.id]: pricesMap }))
                        }
                      }
                    }
                    reader.readAsArrayBuffer(file)
                    e.target.value = ''
                  }} />
                </label>
              )}
              {(isAdmin || isNVC || isCaja) && !phaseLocked && (
                <>
                  <button
                    onClick={() => openBulkDelete('competitor')}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/90 hover:bg-red-500 text-white text-sm font-semibold transition-colors"
                    title="Eliminar un competidor y toda su data histórica"
                  >
                    <Trash2 size={14} /> Eliminar competidor
                  </button>
                  <button
                    onClick={openCreateCompetitor}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-rose-700 hover:bg-rose-50 text-sm font-semibold transition-colors"
                  >
                    <Plus size={14} /> Agregar competidor
                  </button>
                </>
              )}
            </div>
          </div>

          {/* ── Tendencia de Participaciones ── */}
          {competitorsTrendData.length > 0 && (() => {
            const allCompNames = [...new Set(competitorsTrendData.flatMap(yd => Object.keys(yd.competitors)))]
            // Own brand first, others sorted alphabetically
            const ownNames   = allCompNames.filter(n => competitorsTrendData.some(yd => yd.competitors[n]?.isOwn))
            const otherNames = allCompNames.filter(n => !ownNames.includes(n))
            const orderedNames = [...ownNames, ...otherNames]
            const COMP_COLORS = ['#10B981','#F43F5E','#FB923C','#FBBF24','#60A5FA','#A78BFA','#34D399','#F87171','#FCD34D','#818CF8']
            // Normalize per year to 100% — each line shows that competitor's % of total share pool
            const chartData = competitorsTrendData.map(yd => {
              const row = { year: String(yd.year) }
              const total = orderedNames.reduce((s, n) => s + (yd.competitors[n]?.totalShare ?? 0), 0)
              orderedNames.forEach(name => {
                const share = yd.competitors[name]?.totalShare ?? 0
                row[name] = total > 0 ? Math.round((share / total) * 1000) / 10 : null
              })
              return row
            })
            const hasMultiYear = competitorsTrendData.length >= 2
            return (
              <div className="card space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="text-base font-bold text-gray-900">Evolución de Participaciones de Mercado</h3>
                  <span className="text-xs text-gray-400 bg-gray-50 border border-gray-200 px-2 py-1 rounded-lg">% participación por competidor</span>
                </div>
                {!hasMultiYear ? (
                  <p className="text-sm text-gray-400 text-center py-4">Agrega datos de más años para ver la tendencia</p>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                      <XAxis dataKey="year" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={38}
                        tickFormatter={v => `${v}%`} domain={[0, 'auto']} />
                      <Tooltip
                        formatter={(v, n) => v != null ? [`${v}%`, n] : ['—', n]}
                        labelStyle={{ fontWeight: 600, color: '#374151' }}
                        contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }}
                      />
                      <Legend wrapperStyle={{ fontSize: 11, paddingTop: 10 }} />
                      {orderedNames.map((name, i) => {
                        const isOwn = ownNames.includes(name)
                        const color = COMP_COLORS[i % COMP_COLORS.length]
                        return (
                          <Line
                            key={name}
                            type="monotone"
                            dataKey={name}
                            stroke={color}
                            strokeWidth={isOwn ? 3 : 2}
                            strokeDasharray={isOwn ? undefined : undefined}
                            dot={{ r: 4, fill: color, strokeWidth: 0 }}
                            activeDot={{ r: 6, strokeWidth: 0 }}
                            connectNulls
                          />
                        )
                      })}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            )
          })()}

          {/* Competitor cards — year selector */}
          {/* Year + Quarter selector */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500 font-medium w-14">Año:</span>
              {Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i).map(y => (
                <button key={y} type="button" onClick={() => setSelectedYear(y)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                    selectedYear === y ? 'bg-rose-600 text-white border-rose-600' : 'border-gray-200 text-gray-600 hover:border-rose-300 hover:text-rose-700'
                  }`}>{y}</button>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500 font-medium w-14">Período:</span>
              {[{ label: 'Anual', value: null }, { label: 'Q1', value: 1 }, { label: 'Q2', value: 2 }, { label: 'Q3', value: 3 }, { label: 'Q4', value: 4 }].map(({ label, value }) => (
                <button key={label} type="button" onClick={() => setSelectedQuarter(value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                    selectedQuarter === value ? 'bg-rose-600 text-white border-rose-600' : 'border-gray-200 text-gray-600 hover:border-rose-300 hover:text-rose-700'
                  }`}>{label}</button>
              ))}
            </div>
          </div>

          {competitors.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {competitors.map(comp => {
                const shares = competitorShares[comp.id] ?? {}
                const productsList = siblingProducts.filter(p => shares[p.id] !== undefined)
                return (
                  <div key={comp.id} className={`card border transition-colors ${comp.is_own_brand ? 'border-emerald-200 bg-emerald-50/30 hover:border-emerald-300' : 'border-rose-100 hover:border-rose-200'}`}>
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-gray-900 text-base">{comp.name}</h3>
                          {comp.is_own_brand && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-300 text-emerald-700 text-xs font-bold">
                              <CheckCircle2 size={10} /> Mi marca
                            </span>
                          )}
                        </div>
                        {comp.description && <p className="text-xs text-gray-400 mt-0.5">{comp.description}</p>}
                        {comp.analisis_phase_id && buId && !comp.is_own_brand && (
                          <button
                            onClick={() => navigate(`/consultancy/${id}/bu/${buId}/phase/${comp.analisis_phase_id}`)}
                            className="mt-1 inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700 font-medium"
                          >
                            <Building2 size={11} /> Ver en Análisis Competitivo →
                          </button>
                        )}
                      </div>
                      {(isAdmin || isNVC || isCaja) && !phaseLocked && (
                        <div className="flex gap-1.5 flex-shrink-0">
                          <button onClick={() => openEditCompetitor(comp)}
                            title="Editar competidor"
                            className="p-1.5 text-rose-600 hover:bg-rose-100 rounded-lg transition-colors border border-rose-200">
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => deleteCompetitor(comp.id)}
                            title="Eliminar competidor"
                            className="p-1.5 text-red-500 hover:bg-red-100 rounded-lg transition-colors border border-red-200">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </div>

                    {productsList.length > 0 ? (
                      <div className="space-y-3">
                        {productsList.map(p => {
                          const share = parseFloat(shares[p.id]) || 0
                          const priceData = (competitorPrices[comp.id] ?? {})[p.id]
                          const compPrice = priceData?.avg_price != null ? Number(priceData.avg_price) : null
                          const priceUnit = priceData?.price_unit ?? p.size_unit ?? ''
                          // Market avg price: sales_size / volume from sibling product
                          const mktVol = p.volume != null ? Number(p.volume) : null
                          const mktAvgPrice = mktVol != null && mktVol > 0 ? Number(p.sales_size) / mktVol : null
                          // Price delta vs market
                          const priceDelta = compPrice != null && mktAvgPrice != null
                            ? ((compPrice - mktAvgPrice) / mktAvgPrice * 100).toFixed(1)
                            : null
                          const fmtPrice = v => v < 1 ? v.toFixed(4) : v.toLocaleString('es-CO', { maximumFractionDigits: 2 })
                          return (
                            <div key={p.id}>
                              <div className="flex items-center justify-between text-xs mb-0.5">
                                <span className="text-gray-600 font-medium truncate mr-2">{p.name}</span>
                                <span className="font-bold text-rose-700 flex-shrink-0">{share}%</span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden mb-1.5">
                                <div className="h-full rounded-full bg-rose-500 transition-all"
                                  style={{ width: `${Math.min(share, 100)}%` }} />
                              </div>
                              {/* Price row */}
                              {compPrice != null && (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold">
                                    {fmtPrice(compPrice)} <span className="font-normal text-rose-400">{priceUnit}</span>
                                  </span>
                                  {mktAvgPrice != null && (
                                    <span className="text-xs text-gray-400">
                                      vs mercado: <span className="font-medium text-gray-600">{fmtPrice(mktAvgPrice)}</span>
                                    </span>
                                  )}
                                  {priceDelta != null && (
                                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                                      parseFloat(priceDelta) > 0
                                        ? 'bg-red-50 text-red-600'
                                        : parseFloat(priceDelta) < 0
                                          ? 'bg-green-50 text-green-600'
                                          : 'bg-gray-100 text-gray-500'
                                    }`}>
                                      {parseFloat(priceDelta) > 0 ? '+' : ''}{priceDelta}%
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic">
                        {siblingProducts.length === 0 ? 'Agrega productos en "Soluciones" para registrar participaciones' : 'Sin participaciones registradas'}
                      </p>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {competitors.length === 0 && (
            <div className="text-center py-14 card text-gray-400">
              <Building2 size={40} className="mx-auto mb-3 opacity-20" />
              {selectedQuarter === null ? (
                <>
                  <p className="font-medium">Selecciona un trimestre para ver las participaciones</p>
                  <p className="text-sm mt-1">Los datos de participación se registran por trimestre (Q1, Q2, Q3, Q4)</p>
                </>
              ) : (
                <>
                  <p className="font-medium">Sin competidores agregados</p>
                  <p className="text-sm mt-1">Agrega los competidores del mercado para ver sus participaciones</p>
                  {(isAdmin || isNVC) && !phaseLocked && (
                    <button onClick={openCreateCompetitor} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold transition-colors">
                      <Plus size={15} /> Agregar primer competidor
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* No sibling products info (non-blocking) */}
          {competitors.length > 0 && siblingProducts.length === 0 && (
            <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-xs text-amber-700 flex items-center gap-2">
              <BarChart2 size={14} className="flex-shrink-0" />
              Para registrar participaciones por producto, agrega productos en la sección <span className="font-semibold">"Soluciones (Producto)"</span> de esta etapa.
            </div>
          )}

          {/* Chart section */}
          {competitors.length > 0 && (
            <div className="card space-y-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <h3 className="text-base font-bold text-gray-900">Participación de Mercado por Competidor</h3>
                <div className="flex gap-1.5 flex-wrap">
                  <button
                    onClick={() => setActiveProductFilter('all')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                      activeProductFilter === 'all'
                        ? 'bg-rose-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-rose-50 hover:text-rose-700'
                    }`}
                  >
                    Promedio general
                  </button>
                  {siblingProducts.map(p => (
                    <button
                      key={p.id}
                      onClick={() => setActiveProductFilter(p.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                        activeProductFilter === p.id
                          ? 'bg-rose-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-rose-50 hover:text-rose-700'
                      }`}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Selected product label */}
              {activeProductFilter !== 'all' && (() => {
                const prod = siblingProducts.find(p => p.id === activeProductFilter)
                return prod ? (
                  <p className="text-xs text-gray-500 -mt-2">
                    Mostrando participación en <span className="font-semibold text-rose-700">{prod.name}</span>
                  </p>
                ) : null
              })()}

              {/* Bars */}
              <div className="space-y-3">
                {competitorChartData.map((item, idx) => {
                  const COLORS = ['bg-rose-700','bg-rose-600','bg-rose-500','bg-rose-400','bg-rose-300','bg-rose-200']
                  const barColor = COLORS[Math.min(idx, COLORS.length - 1)]
                  const textColor = idx < 3 ? 'text-white' : 'text-rose-800'
                  return (
                    <div key={item.id} className="space-y-1">
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-0.5">
                        <span className="font-semibold text-gray-800">
                          <span className="text-rose-400 mr-1.5">#{idx + 1}</span>{item.name}
                        </span>
                        <span className="font-bold text-rose-700">{item.share}%</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-8 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${barColor} transition-all flex items-center px-3`}
                          style={{ width: `${Math.max(item.share, 1)}%` }}
                        >
                          {item.share > 8 && <span className={`text-xs font-semibold ${textColor}`}>{item.share}%</span>}
                        </div>
                      </div>
                    </div>
                  )
                })}

                {/* Otros bar */}
                {activeProductFilter !== 'all' && (() => {
                  const totalShown = competitorChartData.reduce((s, d) => s + d.share, 0)
                  const otros = Math.max(0, parseFloat((100 - totalShown).toFixed(1)))
                  if (otros <= 0) return null
                  return (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-0.5">
                        <span className="font-semibold text-gray-500 italic">Otros / No identificados</span>
                        <span className="font-bold text-gray-500">{otros}%</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-8 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gray-300 transition-all flex items-center px-3"
                          style={{ width: `${Math.max(otros, 1)}%` }}
                        >
                          {otros > 8 && <span className="text-xs font-semibold text-gray-600">{otros}%</span>}
                        </div>
                      </div>
                    </div>
                  )
                })()}
              </div>

              {/* Footer summary */}
              <div className="border-t border-gray-100 pt-4 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-rose-700">{competitors.length}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Competidores</p>
                </div>
                <div className="text-center border-x border-gray-100">
                  <p className="text-2xl font-bold text-rose-700">
                    {competitorChartData[0]?.share ?? 0}%
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">Participación líder</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-rose-700">{competitorChartData[0]?.name?.split(' ')[0] ?? '—'}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Competidor #1</p>
                </div>
              </div>
            </div>
          )}

          {/* ── Evolución Histórica de Competidores ── */}
          {competitorsHistory.length > 1 && (
            <div className="card space-y-4 border border-rose-100">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <TrendingUp size={15} className="text-rose-500" /> Evolución Histórica de Competidores
              </h3>
              <div className="space-y-3">
                {(() => {
                  const maxCount = Math.max(...competitorsHistory.map(h => h.count), 1)
                  return competitorsHistory.map(h => {
                    const pct = (h.count / maxCount) * 100
                    const isActive = h.year === selectedYear
                    return (
                      <div key={h.year} className="space-y-1 cursor-pointer" onClick={() => setSelectedYear(h.year)}>
                        <div className="flex items-center justify-between text-xs">
                          <span className={`font-semibold ${isActive ? 'text-rose-700' : 'text-gray-600'}`}>
                            {h.year} {isActive && '← actual'}
                          </span>
                          <span className="text-gray-500">{h.count} competidor{h.count !== 1 ? 'es' : ''} identificado{h.count !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-6 overflow-hidden">
                          <div className={`h-full rounded-full transition-all flex items-center px-2 ${isActive ? 'bg-rose-600' : 'bg-rose-200'}`}
                            style={{ width: `${Math.max(pct, 8)}%` }}>
                            <span className={`text-xs font-semibold ${isActive ? 'text-white' : 'text-rose-700'}`}>{h.count}</span>
                          </div>
                        </div>
                      </div>
                    )
                  })
                })()}
              </div>
              <p className="text-xs text-gray-400">Haz clic en un año para ver sus competidores</p>
            </div>
          )}
        </div>
      )}

      {/* Competitor modal */}
      <Modal open={showCompetitorModal} onClose={() => setShowCompetitorModal(false)} title={editCompetitor ? 'Editar competidor' : 'Nuevo competidor'}>
        <form onSubmit={saveCompetitor} className="space-y-4">
          <div>
            <label className="label">Nombre del competidor *</label>
            <input className="input" required value={competitorForm.name}
              onChange={e => setCompetitorForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Ej: Harinera del Valle" />
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea className="input resize-none" rows={2} value={competitorForm.description}
              onChange={e => setCompetitorForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Breve descripción del competidor..." />
          </div>
          {/* Own brand toggle */}
          <div
            onClick={() => setCompetitorForm(f => ({ ...f, is_own_brand: !f.is_own_brand }))}
            className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all select-none ${
              competitorForm.is_own_brand
                ? 'border-emerald-500 bg-emerald-50'
                : 'border-gray-200 bg-gray-50 hover:border-gray-300'
            }`}>
            <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 border-2 transition-all ${
              competitorForm.is_own_brand ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300 bg-white'
            }`}>
              {competitorForm.is_own_brand && <CheckCircle2 size={12} className="text-white" />}
            </div>
            <div>
              <p className={`text-sm font-semibold ${competitorForm.is_own_brand ? 'text-emerald-700' : 'text-gray-700'}`}>
                Esta es mi marca / Esta soy yo
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                Marca este competidor como la propia marca del cliente. Sus productos se importarán automáticamente al Portafolio Estratégico. Solo puede haber una marca propia por sección.
              </p>
            </div>
          </div>

          {siblingProducts.length > 0 && (
            <div>
              <label className="label">Participación y precio por producto</label>
              <p className="text-xs text-gray-400 mb-2">Ingresa la participación (%) y/o el precio promedio por unidad de este competidor en cada producto.</p>
              {/* Column headers */}
              <div className="flex items-center gap-2 mb-1 px-1">
                <span className="text-xs text-gray-400 flex-1">Producto</span>
                <span className="text-xs text-gray-400 w-20 text-right">Part. %</span>
                <span className="text-xs text-gray-400 w-28 text-right">Precio prom.</span>
                <span className="text-xs text-gray-400 w-20">Unidad</span>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {siblingProducts.map(p => {
                  const pf = competitorPriceForm[p.id] ?? { avg_price: '', price_unit: p.size_unit ?? '' }
                  return (
                    <div key={p.id} className="flex items-center gap-2">
                      <span className="text-sm text-gray-700 flex-1 truncate">{p.name}</span>
                      {/* Share % */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <input
                          type="number" min="0" max="100" step="0.1"
                          className="input w-20 text-right text-xs py-1.5"
                          placeholder="—"
                          value={competitorShareForm[p.id] ?? ''}
                          onChange={e => setCompetitorShareForm(f => ({ ...f, [p.id]: e.target.value }))}
                        />
                        <span className="text-xs text-gray-400">%</span>
                      </div>
                      {/* Price */}
                      <input
                        type="number" min="0" step="any"
                        className="input w-28 text-right text-xs py-1.5 border-rose-200 focus:ring-rose-300"
                        placeholder="—"
                        value={pf.avg_price}
                        onChange={e => setCompetitorPriceForm(f => ({ ...f, [p.id]: { ...pf, avg_price: e.target.value } }))}
                      />
                      {/* Unit */}
                      <input
                        type="text"
                        className="input w-20 text-xs py-1.5 border-rose-200 focus:ring-rose-300"
                        placeholder="COP/kg"
                        value={pf.price_unit}
                        onChange={e => setCompetitorPriceForm(f => ({ ...f, [p.id]: { ...pf, price_unit: e.target.value } }))}
                      />
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setShowCompetitorModal(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={savingCompetitor}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold transition-colors disabled:opacity-50">
              {savingCompetitor ? 'Guardando...' : editCompetitor ? 'Guardar cambios' : 'Agregar competidor'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Posicionamiento Competidor Tab ───────────────────── */}
      {activeTab === 'posicionamiento' && (() => {

        async function savePositioning(e) {
          e?.preventDefault()
          setSavingPos(true)
          setPosSaved(false)
          const payload = {
            section_id:           sectionId,
            propuesta_valor:      posForm.propuesta_valor || null,
            segmento_objetivo:    posForm.segmento_objetivo || null,
            territorio_marca:     posForm.territorio_marca || null,
            precio_relativo:      posForm.precio_relativo || 'valor',
            atributos_clave:      posForm.atributos_clave || null,
            diferenciadores:      posForm.diferenciadores || null,
            fortalezas:           posForm.fortalezas || null,
            debilidades:          posForm.debilidades || null,
            promesa_marca:        posForm.promesa_marca || null,
            espacios_no_ocupados: posForm.espacios_no_ocupados || null,
            // v2 schema lives in JSONB next to the legacy columns. Both are
            // saved every time so the row stays internally consistent.
            pos_v2:               posV2,
            updated_at:           new Date().toISOString(),
          }
          if (positioning) {
            await supabase.from('competitor_positioning').update(payload).eq('id', positioning.id)
          } else {
            const { data: newPos } = await supabase.from('competitor_positioning').insert(payload).select().single()
            if (newPos) setPositioning(newPos)
          }
          setSavingPos(false)
          await logChange('posicionamiento', `Posicionamiento actualizado: ${posForm.propuesta_valor?.slice(0,60) ?? ''}`, posForm)
          setPosSaved(true)
          setTimeout(() => setPosSaved(false), 2500)
        }

        // ── Excel template download (AOA-based, 9 V1 fields, sin precio) ──
        // Precio NO se incluye porque se calcula desde "Participaciones de Mercado" (ventas / dosis).
        function downloadPosTemplate() {
          const competitorName = section?.name ?? 'Competidor'
          const FIELDS = [
            { label: 'Propuesta de Valor', field: 'propuesta_valor',
              hint: 'Resumen de una frase del valor único que ofrece este competidor a su segmento objetivo.',
              example: 'Soluciones nutricionales premium para porcinos con asistencia técnica especializada.' },
            { label: 'Segmento Objetivo', field: 'segmento_objetivo',
              hint: '¿A quién le habla este competidor? Tipo de cliente, tamaño, rol o industria.',
              example: 'Porcicultores tecnificados de >500 madres en el Valle del Cauca.' },
            { label: 'Territorio de Marca', field: 'territorio_marca',
              hint: 'Espacio mental que ocupa la marca: el "lugar" donde el consumidor la coloca.',
              example: 'Innovación nutricional respaldada por ciencia veterinaria.' },
            { label: 'Promesa de Marca', field: 'promesa_marca',
              hint: 'Compromiso explícito que la marca le hace al consumidor (qué obtendrá si la elige).',
              example: 'Mayor conversión alimenticia y menor mortalidad en lechones.' },
            { label: 'Atributos Clave', field: 'atributos_clave',
              hint: 'Características funcionales que el competidor comunica como propias (separadas por comas).',
              example: 'Alta digestibilidad, premezcla con probióticos, asistencia técnica 24/7.' },
            { label: 'Diferenciadores Principales', field: 'diferenciadores',
              hint: '¿Qué hace este competidor mejor o distinto que el resto del mercado?',
              example: 'Único proveedor con planta certificada GMP+ en la región sur.' },
            { label: 'Fortalezas Percibidas', field: 'fortalezas',
              hint: 'Ventajas competitivas reales que los clientes reconocen y valoran.',
              example: 'Marca reconocida, fuerza de ventas técnica, distribución nacional.' },
            { label: 'Debilidades / Vulnerabilidades', field: 'debilidades',
              hint: 'Aspectos donde este competidor es débil o vulnerable a un ataque competitivo.',
              example: 'Precio alto, lentitud en entregas, portafolio limitado en aves.' },
            { label: 'Espacios No Ocupados', field: 'espacios_no_ocupados',
              hint: 'Necesidades del segmento que este competidor NO está atendiendo (oportunidad para nosotros).',
              example: 'No ofrece línea económica para pequeños productores; sin presencia digital.' },
          ]
          const aoa = [
            ['NEXT VALLEY CONSULTANTS · Plantilla de Posicionamiento Competitivo'],
            ['Análisis Competitivo · Sección: Posicionamiento Estratégico'],
            ['Competidor:', competitorName],
            [''],
            ['INSTRUCCIONES'],
            ['1) Complete la columna "Tu Respuesta" con texto libre. No modifique los nombres de los campos.'],
            ['2) El precio se calcula automáticamente desde "Participaciones de Mercado" (ventas ÷ dosis), por eso NO aparece en esta plantilla.'],
            ['3) Cuando termine, suba este archivo desde la sección Posicionamiento del competidor para autocompletar el formulario.'],
            [''],
            ['Campo', 'Tu Respuesta ✏️', '¿Qué significa este campo?', 'Ejemplo orientativo'],
            ...FIELDS.map(f => [f.label, posForm[f.field] ?? '', f.hint, f.example]),
            [''],
            ['Next Valley Consultants · Información confidencial · Solo para uso del cliente.'],
          ]
          const ws = XLSX.utils.aoa_to_sheet(aoa)
          ws['!cols'] = [{ wch: 32 }, { wch: 50 }, { wch: 60 }, { wch: 60 }]
          ws['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: 3 } },
            { s: { r: 4, c: 0 }, e: { r: 4, c: 3 } },
            { s: { r: 5, c: 0 }, e: { r: 5, c: 3 } },
            { s: { r: 6, c: 0 }, e: { r: 6, c: 3 } },
            { s: { r: 7, c: 0 }, e: { r: 7, c: 3 } },
            { s: { r: 21, c: 0 }, e: { r: 21, c: 3 } }, // last row index = 9 (header) + 9 fields + 2 (blank+footer offset). Adjust if rows change.
          ]
          const wb = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(wb, ws, 'Posicionamiento')
          XLSX.writeFile(wb, `posicionamiento_${competitorName.replace(/\s+/g,'_')}.xlsx`)
        }

        // ── Excel import (9 V1 fields, sin precio) ──
        function importPosExcel(e) {
          const file = e.target.files?.[0]
          if (!file) return
          const reader = new FileReader()
          reader.onload = ev => {
            const wb  = XLSX.read(ev.target.result, { type: 'array' })
            const ws  = wb.Sheets[wb.SheetNames[0]]
            const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' })
            const MAP = {
              'Propuesta de Valor':           'propuesta_valor',
              'Segmento Objetivo':            'segmento_objetivo',
              'Territorio de Marca':          'territorio_marca',
              'Promesa de Marca':             'promesa_marca',
              'Atributos Clave':              'atributos_clave',
              'Diferenciadores Principales':  'diferenciadores',
              'Fortalezas Percibidas':        'fortalezas',
              'Debilidades / Vulnerabilidades': 'debilidades',
              'Espacios No Ocupados':         'espacios_no_ocupados',
            }
            const updates = {}
            for (const row of rows) {
              const label = String(row[0] || '').trim()
              const val   = String(row[1] || '').trim()
              if (MAP[label] && val) updates[MAP[label]] = val
            }
            if (Object.keys(updates).length > 0) {
              setPosForm(f => ({ ...f, ...updates }))
            }
          }
          reader.readAsArrayBuffer(file)
          e.target.value = ''
        }


        // Counter now reflects the v2 schema: text fields + a "has at least
        // one arista" check. The aristas TOM% is not counted (it's just an
        // attribute of an existing arista).
        const v2TextFields = ['segmentos', 'posicionamiento', 'beneficio_funcional', 'beneficio_emocional', 'reason_to_believe', 'tom_conocimiento_espontaneo']
        const v2TextFilled = v2TextFields.filter(k => (posV2[k] ?? '').trim() !== '').length
        const aristasFilled = (posV2.aristas_desempeno.length > 0 ? 1 : 0) + (posV2.aristas_afinidad.length > 0 ? 1 : 0)
        const completedFields = v2TextFilled + aristasFilled
        const totalFields = v2TextFields.length + 2  // +2 = both aristas lists
        const pct = Math.round((completedFields / totalFields) * 100)

        // Inline editor components for the aristas lists
        const AristasEditor = ({ title, accent, aristas, onChange, placeholder }) => {
          const [draft, setDraft] = useState('')
          const accentBg   = accent === 'violet' ? 'bg-violet-50 border-violet-200 text-violet-700'   : 'bg-emerald-50 border-emerald-200 text-emerald-700'
          const accentBtn  = accent === 'violet' ? 'bg-violet-600 hover:bg-violet-700' : 'bg-emerald-600 hover:bg-emerald-700'
          const add = () => {
            const name = draft.trim()
            if (!name) return
            if (aristas.some(a => a.name.toLowerCase() === name.toLowerCase())) return
            onChange([...aristas, { name, tom_pct: '' }])
            setDraft('')
          }
          const remove = i => onChange(aristas.filter((_, idx) => idx !== i))
          return (
            <div className={`rounded-xl border p-3 ${accentBg.replace('text-', 'text-').replace(/text-\w+-700/, '')}`}>
              <p className="text-xs font-bold uppercase tracking-wide mb-2 opacity-80">{title}</p>
              <div className="flex gap-1.5 mb-3">
                <input type="text" value={draft}
                  onChange={e => setDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
                  placeholder={placeholder}
                  className="flex-1 input text-sm" />
                <button type="button" onClick={add}
                  className={`px-3 rounded-lg text-white text-xs font-semibold ${accentBtn}`}>
                  Añadir
                </button>
              </div>
              {aristas.length === 0 ? (
                <p className="text-xs text-gray-400 italic">Sin aristas todavía.</p>
              ) : (
                <ul className="space-y-1.5">
                  {aristas.map((a, i) => (
                    <li key={i} className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-white border border-gray-100 text-sm">
                      <span className="truncate">{a.name}</span>
                      <button type="button" onClick={() => remove(i)} className="text-gray-300 hover:text-red-500 transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )
        }

        const AristasTomEditor = ({ title, accent, aristas, onChange }) => {
          if (!aristas.length) return null
          const accentBg = accent === 'violet' ? 'bg-violet-50 border-violet-200 text-violet-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'
          const updatePct = (i, pctStr) => {
            const next = aristas.map((a, idx) => idx === i ? { ...a, tom_pct: pctStr } : a)
            onChange(next)
          }
          return (
            <div className={`rounded-xl border p-3 ${accentBg}`}>
              <p className="text-xs font-bold uppercase tracking-wide mb-2 opacity-80">{title}</p>
              <ul className="space-y-1.5">
                {aristas.map((a, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-white border border-gray-100 text-sm">
                    <span className="flex-1 truncate text-gray-700">{a.name}</span>
                    <div className="flex items-center gap-1">
                      <input
                        type="number" step="0.1" min="0" max="100"
                        value={a.tom_pct ?? ''}
                        onChange={e => updatePct(i, e.target.value)}
                        placeholder="0"
                        className="w-16 text-right border border-gray-200 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-300"
                      />
                      <span className="text-xs text-gray-400">%</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )
        }

        const PosField = ({ label, fieldKey, placeholder, rows = 3, hint }) => (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700">{label}</label>
              {hint && <span className="text-xs text-gray-400 italic">{hint}</span>}
            </div>
            <textarea
              rows={rows}
              className="input resize-none text-sm leading-relaxed"
              placeholder={placeholder}
              value={posForm[fieldKey]}
              onChange={e => setPosForm(f => ({ ...f, [fieldKey]: e.target.value }))}
            />
          </div>
        )

        return (
          <div className="space-y-5">
            {/* Header */}
            <div className="rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-700 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Target size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-base">Análisis de Posicionamiento</h2>
                  <p className="text-indigo-100 text-xs mt-0.5">
                    {completedFields}/{totalFields} variables completadas · {pct}%
                  </p>
                </div>
              </div>
              {(isAdmin || isNVC) && !phaseLocked && (
                <div className="flex flex-wrap gap-2">
                  {/* Excel template download */}
                  <button onClick={downloadPosTemplate}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors">
                    <Download size={14} /> Plantilla Excel
                  </button>
                  {/* Excel import */}
                  <input ref={posExcelInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={importPosExcel} />
                  <button onClick={() => posExcelInputRef.current?.click()}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors">
                    <Upload size={14} /> Importar Excel
                  </button>
                  {posForm.propuesta_valor && (
                    <button onClick={() => {
                      const pres = new pptxgen()
                      pres.layout = 'LAYOUT_WIDE'
                      const IBLUE = '3730A3'
                      // Slide 1 — Identidad estratégica
                      const s1 = pres.addSlide()
                      s1.background = { color: IBLUE }
                      s1.addText('Análisis de Posicionamiento', { x:0.5, y:0.4, w:12, h:0.6, fontSize:24, bold:true, color:'FFFFFF' })
                      s1.addText(section?.name ?? '', { x:0.5, y:1.0, w:12, h:0.4, fontSize:14, color:'C7D2FE' })
                      const fields1 = [
                        ['Propuesta de Valor', posForm.propuesta_valor],
                        ['Segmento Objetivo', posForm.segmento_objetivo],
                        ['Territorio de Marca', posForm.territorio_marca],
                        ['Promesa de Marca', posForm.promesa_marca],
                      ].filter(([, v]) => v)
                      let y1 = 1.8
                      fields1.forEach(([label, val]) => {
                        s1.addText(label, { x:0.5, y:y1, w:12, h:0.3, fontSize:11, bold:true, color:'C7D2FE' })
                        s1.addText(val, { x:0.5, y:y1+0.3, w:12, h:0.5, fontSize:13, color:'FFFFFF', wrap:true })
                        y1 += 0.95
                      })
                      // Slide 2 — Precio y análisis estratégico
                      const s2 = pres.addSlide()
                      s2.addText('Precio y Análisis Estratégico', { x:0.5, y:0.3, w:12, h:0.5, fontSize:20, bold:true, color:'1E1B4B' })
                      const fields2 = [
                        ['Posicionamiento de Precio', posForm.precio_relativo ? posForm.precio_relativo.charAt(0).toUpperCase() + posForm.precio_relativo.slice(1) : ''],
                        ['Atributos Clave', posForm.atributos_clave],
                        ['Diferenciadores Principales', posForm.diferenciadores],
                        ['Fortalezas Percibidas', posForm.fortalezas],
                        ['Debilidades / Vulnerabilidades', posForm.debilidades],
                        ['Espacios No Ocupados', posForm.espacios_no_ocupados],
                      ].filter(([, v]) => v)
                      const tableData2 = [
                        [{ text:'Variable', options:{bold:true,fill:'EEF2FF',color:'3730A3'} }, { text:'Análisis', options:{bold:true,fill:'EEF2FF',color:'3730A3'} }],
                        ...fields2.map(([label, val]) => [label, val])
                      ]
                      s2.addTable(tableData2, { x:0.5, y:0.9, w:12.3, colW:[3.5, 8.8], fontSize:11, border:{type:'solid',color:'E0E7FF'}, rowH:0.55 })
                      // Conclusion slide
                      const concText = tabConclusions['posicionamiento']?.text
                      if (concText) {
                        const sc = pres.addSlide()
                        sc.addText('Conclusión Ejecutiva', { x:0.5, y:0.3, w:12, h:0.5, fontSize:20, bold:true, color:'1E1B4B' })
                        sc.addText(concText, { x:0.5, y:1.0, w:12, h:5, fontSize:13, color:'374151', wrap:true })
                      }
                      pres.writeFile({ fileName: `posicionamiento_${section?.name ?? ''}.pptx` })
                    }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors">
                      <Download size={14} /> PPT
                    </button>
                  )}
                  <button onClick={savePositioning} disabled={savingPos}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                      posSaved
                        ? 'bg-emerald-500 text-white'
                        : 'bg-white text-indigo-700 hover:bg-indigo-50'
                    } disabled:opacity-50`}>
                    {savingPos ? <><Loader size={13} className="animate-spin" /> Guardando...</>
                      : posSaved ? <><CheckCircle2 size={13} /> Guardado</>
                      : <><Save size={13} /> Guardar</>}
                  </button>
                </div>
              )}
            </div>

            {/* Progress bar */}
            <div className="card py-3">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="font-medium text-gray-600">Completitud del análisis</span>
                <span className={`font-bold ${pct === 100 ? 'text-emerald-600' : 'text-indigo-600'}`}>{pct}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-500 ${pct === 100 ? 'bg-emerald-500' : 'bg-indigo-500'}`}
                  style={{ width: `${pct}%` }} />
              </div>
            </div>

            <form onSubmit={savePositioning} className="space-y-4">
              {/* The Posicionamiento section appears in two places:
                    • Inside each competitor (Phase 3 Análisis Competitivo)
                      → section has a competitor_id → V1 form (the
                        per-competitor schema the user already validated:
                        Propuesta de Valor, Segmento, Territorio, etc.)
                    • In Análisis Interno (Phase 4) at the project level
                      → section has no competitor_id → V2 form (segmentos,
                        aristas, beneficios funcional/emocional, RTB, TOM)
                  We toggle between forms based on section.competitor_id so
                  both flows coexist on the same competitor_positioning
                  table (legacy columns vs pos_v2 JSONB). The V1 template
                  download omits "precio" because that value lives in
                  Participaciones de Mercado (ventas / dosis), not here. */}
              {section?.competitor_id ? (
                /* ─── V1 form: per-competitor positioning ─────────────
                   Same fields as the original "Posicionamiento" form
                   (Propuesta de Valor, Segmento Objetivo, etc.) MINUS
                   the price selector — precio is calculated from
                   Participaciones de Mercado (ventas / dosis) and
                   shouldn't be re-entered here. */
                <>
                  <div className="card space-y-4">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-1 h-5 rounded-full bg-indigo-500" />
                      <h3 className="text-sm font-bold text-gray-800">Identidad Estratégica</h3>
                    </div>
                    <PosField label="Propuesta de Valor"  fieldKey="propuesta_valor"  rows={2}
                      placeholder="¿Qué promesa central le hace este competidor a su cliente?"
                      hint="1-2 oraciones" />
                    <PosField label="Segmento Objetivo"   fieldKey="segmento_objetivo" rows={2}
                      placeholder="¿A quién le habla principalmente?" />
                    <PosField label="Territorio de Marca" fieldKey="territorio_marca"  rows={2}
                      placeholder="¿Qué espacio mental/emocional ocupa en el consumidor?"
                      hint="Intangible, no funcional" />
                    <PosField label="Promesa de Marca"    fieldKey="promesa_marca"     rows={2}
                      placeholder="El compromiso explícito o implícito con el consumidor"
                      hint="Tagline conceptual" />
                  </div>

                  <div className="card space-y-4">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-1 h-5 rounded-full bg-amber-500" />
                      <h3 className="text-sm font-bold text-gray-800">Atributos</h3>
                    </div>
                    <PosField label="Atributos Clave" fieldKey="atributos_clave" rows={3}
                      placeholder="3-5 atributos funcionales o emocionales que comunican"
                      hint="Separa con · o comas" />
                  </div>

                  <div className="card space-y-4">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-1 h-5 rounded-full bg-rose-500" />
                      <h3 className="text-sm font-bold text-gray-800">Análisis Estratégico</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <PosField label="Diferenciadores Principales"   fieldKey="diferenciadores"      rows={4}
                        placeholder="¿Qué los hace únicos frente al mercado?" />
                      <PosField label="Fortalezas Percibidas"          fieldKey="fortalezas"           rows={4}
                        placeholder="Lo que el mercado reconoce positivamente de este competidor" />
                      <PosField label="Debilidades / Vulnerabilidades" fieldKey="debilidades"          rows={4}
                        placeholder="Gaps o puntos débiles en su propuesta" />
                      <PosField label="Espacios No Ocupados"           fieldKey="espacios_no_ocupados" rows={4}
                        placeholder="Territorios o segmentos que NO están cubriendo"
                        hint="Whitespace" />
                    </div>
                  </div>

                  {(posForm.propuesta_valor || posForm.territorio_marca) && (
                    <div className="rounded-xl border-2 border-indigo-100 bg-indigo-50/50 p-4 space-y-3">
                      <p className="text-xs font-bold text-indigo-700 uppercase tracking-wide">Vista Resumen</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { label: 'Propuesta',  value: posForm.propuesta_valor },
                          { label: 'Segmento',   value: posForm.segmento_objetivo },
                          { label: 'Territorio', value: posForm.territorio_marca },
                          { label: 'Promesa',    value: posForm.promesa_marca },
                        ].filter(item => item.value).map(item => (
                          <div key={item.label} className="bg-white rounded-lg border border-indigo-100 p-3">
                            <p className="text-xs text-indigo-400 font-medium mb-1">{item.label}</p>
                            <p className="text-xs text-gray-700 line-clamp-3">{item.value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* ─── V2 form: Análisis Interno positioning ─────────── */
                <>
              {/* Bloque 1: Segmentación & Posicionamiento */}
              <div className="card space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-1 h-5 rounded-full bg-indigo-500" />
                  <h3 className="text-sm font-bold text-gray-800">Segmentación & Posicionamiento</h3>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700">Segmentos</label>
                  <textarea rows={3} className="input resize-none text-sm leading-relaxed"
                    placeholder="¿Qué segmentos de consumidor / mercado se atienden? Lista los principales con un descriptor breve cada uno."
                    value={posV2.segmentos} onChange={e => setPosV2(v => ({ ...v, segmentos: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700">Posicionamiento</label>
                  <textarea rows={3} className="input resize-none text-sm leading-relaxed"
                    placeholder="Declaración estructurada. Ej: 'Para [target], [marca] es la única [categoría] que [beneficio clave] porque [RTB].'"
                    value={posV2.posicionamiento} onChange={e => setPosV2(v => ({ ...v, posicionamiento: e.target.value }))} />
                </div>
              </div>

              {/* Bloque 2: Aristas del posicionamiento */}
              <div className="card space-y-5">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-1 h-5 rounded-full bg-emerald-500" />
                  <h3 className="text-sm font-bold text-gray-800">Aristas del Posicionamiento</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AristasEditor
                    title="Aristas de Desempeño"
                    accent="emerald"
                    aristas={posV2.aristas_desempeno}
                    onChange={items => setPosV2(v => ({ ...v, aristas_desempeno: items }))}
                    placeholder="Ej: Calidad, Velocidad, Precio competitivo"
                  />
                  <AristasEditor
                    title="Aristas de Afinidad"
                    accent="violet"
                    aristas={posV2.aristas_afinidad}
                    onChange={items => setPosV2(v => ({ ...v, aristas_afinidad: items }))}
                    placeholder="Ej: Tradición, Confianza, Cercanía"
                  />
                </div>
              </div>

              {/* Bloque 3: Beneficios + RTB */}
              <div className="card space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-1 h-5 rounded-full bg-amber-500" />
                  <h3 className="text-sm font-bold text-gray-800">Beneficios y Razón para Creer</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700">Beneficio funcional principal</label>
                    <textarea rows={3} className="input resize-none text-sm"
                      placeholder="¿Qué problema concreto resuelve? Tangible, medible."
                      value={posV2.beneficio_funcional} onChange={e => setPosV2(v => ({ ...v, beneficio_funcional: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700">Beneficio emocional</label>
                    <textarea rows={3} className="input resize-none text-sm"
                      placeholder="¿Qué siente el consumidor? Intangible, aspiracional."
                      value={posV2.beneficio_emocional} onChange={e => setPosV2(v => ({ ...v, beneficio_emocional: e.target.value }))} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700">Reason to Believe (RTB)</label>
                  <textarea rows={3} className="input resize-none text-sm leading-relaxed"
                    placeholder="¿Por qué te debería creer el consumidor? Evidencia: certificaciones, ingredientes, herencia, casos."
                    value={posV2.reason_to_believe} onChange={e => setPosV2(v => ({ ...v, reason_to_believe: e.target.value }))} />
                </div>
              </div>

              {/* Bloque 4: TOM */}
              <div className="card space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-1 h-5 rounded-full bg-sky-500" />
                  <h3 className="text-sm font-bold text-gray-800">TOM — Top of Mind</h3>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700">Conocimiento espontáneo</label>
                  <textarea rows={3} className="input resize-none text-sm"
                    placeholder="¿Qué nivel de conocimiento espontáneo tiene la marca? Si tienes el dato cuantitativo (ej. estudio de mercado), inclúyelo. Si es cualitativo, descríbelo."
                    value={posV2.tom_conocimiento_espontaneo} onChange={e => setPosV2(v => ({ ...v, tom_conocimiento_espontaneo: e.target.value }))} />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-2">Nivel de asociación con las aristas</label>
                  <p className="text-xs text-gray-500 mb-3">A cada arista que definiste arriba se le asigna un % que mide qué tan asociada está al nombre de la marca en la mente del consumidor.</p>
                  {(posV2.aristas_desempeno.length === 0 && posV2.aristas_afinidad.length === 0) ? (
                    <p className="text-sm text-gray-400 italic">Define primero al menos una arista arriba para asignarle un %.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <AristasTomEditor
                        title="Aristas de Desempeño"
                        accent="emerald"
                        aristas={posV2.aristas_desempeno}
                        onChange={items => setPosV2(v => ({ ...v, aristas_desempeno: items }))}
                      />
                      <AristasTomEditor
                        title="Aristas de Afinidad"
                        accent="violet"
                        aristas={posV2.aristas_afinidad}
                        onChange={items => setPosV2(v => ({ ...v, aristas_afinidad: items }))}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Summary card — visible cuando hay datos del v2 */}
              {(posV2.posicionamiento || posV2.beneficio_funcional || posV2.beneficio_emocional) && (
                <div className="rounded-xl border-2 border-indigo-100 bg-indigo-50/50 p-4 space-y-3">
                  <p className="text-xs font-bold text-indigo-700 uppercase tracking-wide">Vista Resumen</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { label: 'Posicionamiento',     value: posV2.posicionamiento },
                      { label: 'Segmentos',           value: posV2.segmentos },
                      { label: 'Beneficio funcional', value: posV2.beneficio_funcional },
                      { label: 'Beneficio emocional', value: posV2.beneficio_emocional },
                      { label: 'RTB',                 value: posV2.reason_to_believe },
                      { label: 'TOM espontáneo',      value: posV2.tom_conocimiento_espontaneo },
                    ].filter(item => item.value && item.value.trim()).map(item => (
                      <div key={item.label} className="bg-white rounded-lg border border-indigo-100 p-3">
                        <p className="text-xs text-indigo-400 font-medium mb-1">{item.label}</p>
                        <p className="text-xs text-gray-700 line-clamp-3">{item.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
                </>
              )}

              {(isAdmin || isNVC) && !phaseLocked && (
                <button type="submit" disabled={savingPos}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
                    posSaved ? 'bg-emerald-500 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  } disabled:opacity-50`}>
                  {savingPos ? <><Loader size={14} className="animate-spin" /> Guardando...</>
                    : posSaved ? <><CheckCircle2 size={14} /> ¡Guardado!</>
                    : <><Save size={14} /> Guardar Posicionamiento</>}
                </button>
              )}
            </form>

            {renderConclusionBlock('posicionamiento', JSON.stringify({posicionamiento: posForm}))}
          </div>
        )
      })()}

      {/* ── Portafolio Tab ─────────────────────────────────────── */}
      {activeTab === 'portafolio' && (() => {
        const ORIGEN_OPTS   = ['Nacional', 'Importado', 'Mixto']
        const CALIDAD_OPTS  = ['Premium', 'Valor', 'Economía']
        const CICLO_OPTS    = ['Introducción', 'Crecimiento', 'Madurez', 'Declive']

        async function savePortfolioProduct(productId) {
          setSavingPortfolio(s => ({ ...s, [productId]: true }))
          const form = portfolioForms[productId] ?? {}
          await supabase.from('competitor_portfolio_products').update({
            product_name:         form.product_name || null,
            participacion_pct:    form.participacion_pct !== '' ? Number(form.participacion_pct) : null,
            precio_promedio:      form.precio_promedio !== '' ? Number(form.precio_promedio) : null,
            precio_unidad:        form.precio_unidad || null,
            presentaciones:       form.presentaciones || null,
            origen:               form.origen || null,
            precio_min:           form.precio_min !== '' ? Number(form.precio_min) : null,
            precio_max:           form.precio_max !== '' ? Number(form.precio_max) : null,
            nivel_calidad:        form.nivel_calidad || null,
            etapa_ciclo_vida:     form.etapa_ciclo_vida || null,
            segmento_objetivo:    form.segmento_objetivo || null,
            canales_distribucion: form.canales_distribucion || null,
            diferenciadores:      form.diferenciadores || null,
            fortalezas:           form.fortalezas || null,
            debilidades:          form.debilidades || null,
            observaciones:        form.observaciones || null,
            updated_at:           new Date().toISOString(),
          }).eq('id', productId)
          // Refresh local product name in list
          setPortfolioProducts(prev => prev.map(p =>
            p.id === productId
              ? { ...p, product_name: form.product_name || p.product_name,
                  participacion_pct: form.participacion_pct !== '' ? Number(form.participacion_pct) : p.participacion_pct,
                  precio_promedio:   form.precio_promedio !== '' ? Number(form.precio_promedio) : p.precio_promedio,
                  precio_unidad:     form.precio_unidad || p.precio_unidad }
              : p
          ))
          setSavingPortfolio(s => ({ ...s, [productId]: false }))
          setPortfolioSavedId(productId)
          setTimeout(() => setPortfolioSavedId(null), 2000)
        }

        async function deletePortfolioProduct(productId) {
          if (!window.confirm('¿Eliminar este producto del portafolio?')) return
          await supabase.from('competitor_portfolio_products').delete().eq('id', productId)
          setPortfolioProducts(prev => prev.filter(p => p.id !== productId))
          setPortfolioForms(prev => { const n = { ...prev }; delete n[productId]; return n })
          if (portfolioExpandedId === productId) setPortfolioExpandedId(null)
        }

        async function addPortfolioProduct() {
          const name = window.prompt('Nombre del producto:')
          if (!name?.trim()) return
          const nextOrder = portfolioProducts.length + 1
          const { data: newProd } = await supabase
            .from('competitor_portfolio_products')
            .insert({
              competitor_id: section.competitor_id,
              section_id:    sectionId,
              product_name:  name.trim(),
              product_order: nextOrder,
            }).select().single()
          if (newProd) {
            setPortfolioProducts(prev => [...prev, newProd])
            setPortfolioForms(prev => ({
              ...prev,
              [newProd.id]: { product_name: newProd.product_name, participacion_pct: '', precio_promedio: '', precio_unidad: '', presentaciones: '', origen: '', precio_min: '', precio_max: '', nivel_calidad: '', etapa_ciclo_vida: '', segmento_objetivo: '', canales_distribucion: '', diferenciadores: '', fortalezas: '', debilidades: '', observaciones: '' },
            }))
            setPortfolioExpandedId(newProd.id)
          }
        }

        function updateForm(productId, field, value) {
          setPortfolioForms(prev => ({
            ...prev,
            [productId]: { ...(prev[productId] ?? {}), [field]: value },
          }))
        }

        const filledCount = (productId) => {
          const f = portfolioForms[productId] ?? {}
          const SKIP = ['product_name', 'precio_unidad']
          return Object.entries(f).filter(([k, v]) => !SKIP.includes(k) && v !== '' && v != null).length
        }

        return (
          <div className="space-y-5">
            {/* Header */}
            <div className="rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Database size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-base">Portafolio del Competidor</h2>
                  <p className="text-orange-100 text-xs mt-0.5">
                    {portfolioProducts.length} producto{portfolioProducts.length !== 1 ? 's' : ''} · Datos base importados desde Análisis de Mercado
                  </p>
                </div>
              </div>
              {(isAdmin || isNVC) && !phaseLocked && (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const header = ['Producto', 'Part. %', 'Precio promedio', 'Unidad precio', 'Presentaciones', 'Origen (Nacional/Importado/Mixto)', 'Precio mínimo', 'Precio máximo', 'Nivel calidad (Premium/Valor/Economía)', 'Etapa ciclo vida', 'Segmento objetivo', 'Canales distribución', 'Diferenciadores', 'Fortalezas', 'Debilidades', 'Observaciones']
                      const exampleRows = portfolioProducts.length > 0
                        ? portfolioProducts.map(p => [p.product_name, p.participacion_pct ?? '', p.precio_promedio ?? '', p.precio_unidad ?? '', '', '', '', '', '', '', '', '', '', '', '', ''])
                        : [['Producto 1', '25', '1500', 'COP/kg', '', 'Nacional', '', '', 'Premium', 'Madurez', '', '', '', '', '', '']]
                      const wb = XLSX.utils.book_new()
                      const ws = XLSX.utils.aoa_to_sheet([header, ...exampleRows])
                      ws['!cols'] = header.map(() => ({ wch: 22 }))
                      XLSX.utils.book_append_sheet(wb, ws, 'Portafolio')
                      XLSX.writeFile(wb, `plantilla_portafolio_${section?.name ?? 'competidor'}.xlsx`)
                    }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors">
                    <Download size={14} /> Plantilla
                  </button>
                  <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors">
                    <Upload size={14} /> Subir Excel
                    <input type="file" accept=".xlsx,.xls" className="hidden" onChange={async e => {
                      const file = e.target.files?.[0]; if (!file) return
                      const reader = new FileReader()
                      reader.onload = async evt => {
                        const wb2 = XLSX.read(evt.target.result, { type: 'array' })
                        const ws2 = wb2.Sheets[wb2.SheetNames[0]]
                        const rows2 = XLSX.utils.sheet_to_json(ws2, { defval: '' })
                        const normH = s => String(s).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim()
                        for (const row of rows2) {
                          const keys = Object.keys(row)
                          const nameKey = keys.find(k => normH(k) === 'producto' || normH(k) === 'nombre' || normH(k) === 'name') ?? keys[0]
                          const prodName = String(row[nameKey] ?? '').trim()
                          if (!prodName) continue
                          const prod = portfolioProducts.find(p => normH(p.product_name) === normH(prodName))
                          if (!prod) continue
                          const get = (...hints) => { const k = keys.find(k2 => hints.some(h => normH(k2).includes(h))); return k ? String(row[k] ?? '').trim() : '' }
                          const getNum = (...hints) => { const v = get(...hints); const n = parseFloat(v.replace(',', '.')); return isNaN(n) ? '' : n }
                          const updates = {
                            participacion_pct:    getNum('part', '%'),
                            precio_promedio:      getNum('precio prom', 'precio_prom', 'price'),
                            precio_unidad:        get('unidad precio', 'unidad_precio', 'unit'),
                            presentaciones:       get('presentaciones'),
                            origen:               get('origen'),
                            precio_min:           getNum('min'),
                            precio_max:           getNum('max'),
                            nivel_calidad:        get('nivel', 'calidad'),
                            etapa_ciclo_vida:     get('etapa', 'ciclo'),
                            segmento_objetivo:    get('segmento'),
                            canales_distribucion: get('canal'),
                            diferenciadores:      get('diferenc'),
                            fortalezas:           get('fortalez'),
                            debilidades:          get('debilid'),
                            observaciones:        get('observ'),
                            updated_at:           new Date().toISOString(),
                          }
                          await supabase.from('competitor_portfolio_products').update(updates).eq('id', prod.id)
                          setPortfolioForms(prev => ({ ...prev, [prod.id]: { ...(prev[prod.id] ?? {}), ...Object.fromEntries(Object.entries(updates).filter(([k]) => k !== 'updated_at').map(([k,v]) => [k, v ?? ''])) } }))
                        }
                      }
                      reader.readAsArrayBuffer(file)
                      e.target.value = ''
                    }} />
                  </label>
                  <button onClick={addPortfolioProduct}
                    className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-semibold transition-colors">
                    <Plus size={15} /> Agregar producto
                  </button>
                </div>
              )}
            </div>

            {portfolioImporting && (
              <div className="flex items-center gap-3 p-4 bg-orange-50 border border-orange-200 rounded-xl text-orange-700 text-sm">
                <Loader size={16} className="animate-spin flex-shrink-0" />
                Importando productos desde Análisis de Mercado...
              </div>
            )}

            {!portfolioImporting && portfolioProducts.length === 0 && (
              <div className="text-center py-14 text-gray-400">
                <Database size={36} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium text-gray-500">Sin productos en el portafolio</p>
                <p className="text-sm mt-1 max-w-sm mx-auto">Los productos se importan automáticamente desde Participaciones de Mercado, o puedes agregarlos manualmente con el botón de arriba.</p>
              </div>
            )}

            {portfolioProducts.map(prod => {
              const isOpen   = portfolioExpandedId === prod.id
              const form     = portfolioForms[prod.id] ?? {}
              const filled   = filledCount(prod.id)
              const isSaving = savingPortfolio[prod.id]
              const isSaved  = portfolioSavedId === prod.id
              // Display name: prefer form value if expanded, else stored value
              const displayName = form.product_name || prod.product_name

              return (
                <div key={prod.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  {/* Product header row */}
                  <div className="flex items-center gap-3 px-5 py-3">
                    <button
                      onClick={() => setPortfolioExpandedId(isOpen ? null : prod.id)}
                      className="flex items-center gap-3 flex-1 min-w-0 text-left"
                    >
                      <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-orange-700 font-bold text-sm">{displayName?.[0]?.toUpperCase()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 text-sm">{displayName}</p>
                        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                          {prod.participacion_pct != null && (
                            <span className="text-xs text-gray-500">Part.: <strong className="text-orange-600">{prod.participacion_pct}%</strong></span>
                          )}
                          {prod.precio_promedio != null && (
                            <span className="text-xs text-gray-500">Precio prom.: <strong className="text-orange-600">{Number(prod.precio_promedio).toLocaleString('es-CO')} {prod.precio_unidad ?? ''}</strong></span>
                          )}
                          {filled > 0 && (
                            <span className="text-xs bg-orange-50 text-orange-600 border border-orange-200 px-2 py-0.5 rounded-full">{filled} campos</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight size={16} className={`text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                    </button>
                    {(isAdmin || isNVC) && !phaseLocked && (
                      <button onClick={() => deletePortfolioProduct(prod.id)}
                        className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                        title="Eliminar producto">
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>

                  {/* Expanded form */}
                  {isOpen && (
                    <div className="border-t border-gray-100 px-5 pb-5 pt-4 space-y-5 bg-gray-50/50">

                      {/* ─── Bloque 0: Identificación (editable) ─ */}
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Identificación</p>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-2">
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Nombre del producto</label>
                            <input type="text" value={form.product_name ?? prod.product_name}
                              onChange={e => updateForm(prod.id, 'product_name', e.target.value)}
                              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white font-medium" />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">
                              Participación de mercado (%)
                              {prod.market_product_id && <span className="ml-1 text-blue-500 font-normal">· importado</span>}
                            </label>
                            <input type="number" value={form.participacion_pct ?? (prod.participacion_pct ?? '')}
                              onChange={e => updateForm(prod.id, 'participacion_pct', e.target.value)}
                              placeholder="0.0"
                              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white" />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">
                              Precio promedio
                              {prod.market_product_id && <span className="ml-1 text-blue-500 font-normal">· importado</span>}
                            </label>
                            <div className="flex gap-2">
                              <input type="number" value={form.precio_promedio ?? (prod.precio_promedio ?? '')}
                                onChange={e => updateForm(prod.id, 'precio_promedio', e.target.value)}
                                placeholder="Valor"
                                className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white" />
                              <input type="text" value={form.precio_unidad ?? (prod.precio_unidad ?? '')}
                                onChange={e => updateForm(prod.id, 'precio_unidad', e.target.value)}
                                placeholder="Unidad"
                                className="w-28 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ─── Bloque 1: Producto ─────────────── */}
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Producto</p>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-2">
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Presentaciones / SKUs</label>
                            <textarea rows={2} value={form.presentaciones ?? ''}
                              onChange={e => updateForm(prod.id, 'presentaciones', e.target.value)}
                              placeholder="Ej: 500g, 1kg, 5kg, presentación institucional..."
                              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none bg-white" />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Origen</label>
                            <div className="flex gap-2 flex-wrap">
                              {ORIGEN_OPTS.map(o => (
                                <button key={o} type="button"
                                  onClick={() => updateForm(prod.id, 'origen', form.origen === o ? '' : o)}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${form.origen === o ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'}`}>
                                  {o}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ─── Bloque 2: Precio extendido ─────── */}
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Rango de precio</p>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Precio mínimo observado</label>
                            <input type="number" value={form.precio_min ?? ''}
                              onChange={e => updateForm(prod.id, 'precio_min', e.target.value)}
                              placeholder={form.precio_unidad || prod.precio_unidad || 'Valor'}
                              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white" />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Precio máximo observado</label>
                            <input type="number" value={form.precio_max ?? ''}
                              onChange={e => updateForm(prod.id, 'precio_max', e.target.value)}
                              placeholder={form.precio_unidad || prod.precio_unidad || 'Valor'}
                              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white" />
                          </div>
                        </div>
                      </div>

                      {/* ─── Bloque 3: Posicionamiento ────────── */}
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Posicionamiento estratégico</p>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Nivel de calidad percibida</label>
                            <div className="flex gap-2">
                              {CALIDAD_OPTS.map(c => (
                                <button key={c} type="button"
                                  onClick={() => updateForm(prod.id, 'nivel_calidad', form.nivel_calidad === c ? '' : c)}
                                  className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${form.nivel_calidad === c
                                    ? c === 'Premium' ? 'bg-amber-500 text-white border-amber-500'
                                      : c === 'Valor' ? 'bg-blue-500 text-white border-blue-500'
                                      : 'bg-emerald-500 text-white border-emerald-500'
                                    : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'}`}>
                                  {c}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Etapa del ciclo de vida</label>
                            <div className="flex gap-2 flex-wrap">
                              {CICLO_OPTS.map(c => (
                                <button key={c} type="button"
                                  onClick={() => updateForm(prod.id, 'etapa_ciclo_vida', form.etapa_ciclo_vida === c ? '' : c)}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${form.etapa_ciclo_vida === c ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'}`}>
                                  {c}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="col-span-2">
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Segmento objetivo</label>
                            <input type="text" value={form.segmento_objetivo ?? ''}
                              onChange={e => updateForm(prod.id, 'segmento_objetivo', e.target.value)}
                              placeholder="¿A quién va dirigido este producto?"
                              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white" />
                          </div>
                          <div className="col-span-2">
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Canales de distribución</label>
                            <input type="text" value={form.canales_distribucion ?? ''}
                              onChange={e => updateForm(prod.id, 'canales_distribucion', e.target.value)}
                              placeholder="Ej: Retail, mayoristas, e-commerce, canal institucional..."
                              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white" />
                          </div>
                        </div>
                      </div>

                      {/* ─── Bloque 4: Análisis competitivo ─── */}
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Análisis competitivo</p>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-2">
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Diferenciadores clave</label>
                            <textarea rows={2} value={form.diferenciadores ?? ''}
                              onChange={e => updateForm(prod.id, 'diferenciadores', e.target.value)}
                              placeholder="¿Qué hace diferente a este producto?"
                              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none bg-white" />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Fortalezas</label>
                            <textarea rows={3} value={form.fortalezas ?? ''}
                              onChange={e => updateForm(prod.id, 'fortalezas', e.target.value)}
                              placeholder="Ventajas competitivas del producto..."
                              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none bg-white" />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Debilidades</label>
                            <textarea rows={3} value={form.debilidades ?? ''}
                              onChange={e => updateForm(prod.id, 'debilidades', e.target.value)}
                              placeholder="Puntos débiles del producto..."
                              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none bg-white" />
                          </div>
                          <div className="col-span-2">
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Observaciones generales</label>
                            <textarea rows={2} value={form.observaciones ?? ''}
                              onChange={e => updateForm(prod.id, 'observaciones', e.target.value)}
                              placeholder="Notas adicionales sobre este producto..."
                              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none bg-white" />
                          </div>
                        </div>
                      </div>

                      {/* Save / Delete row */}
                      {(isAdmin || isNVC) && !phaseLocked && (
                        <div className="flex items-center justify-between pt-1">
                          <button onClick={() => deletePortfolioProduct(prod.id)}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-red-600 hover:bg-red-50 border border-red-200 transition-colors">
                            <Trash2 size={14} /> Eliminar producto
                          </button>
                          <button onClick={() => savePortfolioProduct(prod.id)} disabled={isSaving}
                            className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 ${
                              isSaved ? 'bg-emerald-500 text-white' : 'bg-orange-500 hover:bg-orange-600 text-white'
                            }`}>
                            {isSaving ? <><Loader size={14} className="animate-spin" /> Guardando...</>
                              : isSaved ? <><CheckCircle2 size={14} /> ¡Guardado!</>
                              : <><Save size={14} /> Guardar</>}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}

            {renderConclusionBlock('portafolio', JSON.stringify({productos: portfolioProducts.map(p=>({nombre: p.product_name, participacion: p.participacion_pct, precio: p.precio_promedio}))}))}
          </div>
        )
      })()}

      {/* ── Objetivo Estratégico Tab ──────────────────────────── */}
      {activeTab === 'objetivo_est' && (() => {
        function updateMetric(year, field, value) {
          const key = `${year}_real`
          setStratMetrics(prev => ({ ...prev, [key]: { ...(prev[key] ?? {}), [field]: value } }))
        }
        function getMetric(year, field) {
          return stratMetrics[`${year}_real`]?.[field] ?? ''
        }
        function updateGoal(year, field, value) {
          const key = `${year}_objetivo`
          setStratMetrics(prev => ({ ...prev, [key]: { ...(prev[key] ?? {}), [field]: value } }))
        }
        function getGoal(year, field) {
          return stratMetrics[`${year}_objetivo`]?.[field] ?? ''
        }
        function cumplimiento(year) {
          const m = stratMetrics[`${year}_real`] ?? {}
          const real = parseFloat(m.ventas)
          const obj  = parseFloat(m.ventas_objetivo)
          if (!real || !obj || obj === 0) return null
          return Math.round((real / obj) * 100)
        }

        async function saveAll() {
          setSavingStratObj(true)
          const payload = {
            section_id:     sectionId,
            consultancy_id: consultancyId,   // ← required for RLS access via consultancy
            vision:         stratObjForm.vision || null,
            mision:         stratObjForm.mision || null,
            declaracion:    stratObjForm.declaracion || null,
            updated_at:     new Date().toISOString(),
          }
          let soId = stratObj?.id
          if (stratObj) {
            await supabase.from('strategic_objectives').update(payload).eq('id', stratObj.id)
          } else {
            const { data: newSo } = await supabase.from('strategic_objectives').insert(payload).select().single()
            if (newSo) { setStratObj(newSo); soId = newSo.id }
          }
          if (soId) {
            await supabase.from('strategic_objective_metrics').delete().eq('strategic_objective_id', soId)
            const realInserts = OBJ_REAL_YEARS.map(year => {
              const m = stratMetrics[`${year}_real`] ?? {}
              return {
                strategic_objective_id: soId, year, type: 'real',
                ventas:                 m.ventas !== '' ? Number(m.ventas) : null,
                ventas_unit:            m.ventas_unit || 'USD millones',
                participacion_pct:      m.participacion_pct !== '' ? Number(m.participacion_pct) : null,
                ventas_objetivo:        m.ventas_objetivo !== '' ? Number(m.ventas_objetivo) : null,
                participacion_objetivo: m.participacion_objetivo !== '' ? Number(m.participacion_objetivo) : null,
                notes:                  m.notes || null,
              }
            }).filter(r => r.ventas != null || r.ventas_objetivo != null || r.participacion_pct != null)
            const goalInserts = OBJ_GOAL_YEARS.map(year => {
              const m = stratMetrics[`${year}_objetivo`] ?? {}
              return {
                strategic_objective_id: soId, year, type: 'objetivo',
                ventas:                 m.ventas !== '' ? Number(m.ventas) : null,
                ventas_unit:            m.ventas_unit || 'USD millones',
                participacion_pct:      null,
                ventas_objetivo:        null,
                participacion_objetivo: m.participacion_objetivo !== '' ? Number(m.participacion_objetivo) : null,
                notes:                  null,
              }
            }).filter(r => r.ventas != null || r.participacion_objetivo != null)
            const allMetricInserts = [...realInserts, ...goalInserts]
            if (allMetricInserts.length > 0) await supabase.from('strategic_objective_metrics').insert(allMetricInserts)
          }
          // Update KPIs for existing pillars
          if (pillars.length > 0) {
            await Promise.all(pillars.map(p => supabase.from('strategic_pillars').update({ kpi: p.kpi || null }).eq('id', p.id)))
          }
          setSavingStratObj(false)
          await logChange('objetivo_est', `Objetivo estratégico actualizado: "${stratObjForm.declaracion?.slice(0,60) ?? ''}"`, { objetivo: stratObjForm, metricas: stratMetrics, pilares: pillars })
          setStratObjSaved(true)
          setTimeout(() => setStratObjSaved(false), 2500)
        }

        async function addPilar() {
          if (!newPillarForm.name.trim()) return
          setSavingPillar(true)
          const { data: np } = await supabase.from('strategic_pillars').insert({
            section_id: sectionId,
            consultancy_id: consultancyId,   // ← required for RLS access via consultancy
            name: newPillarForm.name.trim(),
            description: newPillarForm.description.trim() || null,
            kpi: newPillarForm.kpi.trim() || null,
            pilar_order: pillars.length,
          }).select().single()
          if (np) setPillars(prev => [...prev, np])
          setNewPillarForm({ name: '', description: '', kpi: '' })
          setSavingPillar(false)
        }

        function updatePillarKpi(pid, val) {
          setPillars(prev => prev.map(p => p.id === pid ? { ...p, kpi: val } : p))
        }

        async function deletePilar(pid) {
          await supabase.from('strategic_pillars').delete().eq('id', pid)
          setPillars(prev => prev.filter(p => p.id !== pid))
        }

        return (
          <div className="space-y-5">
            {/* Header */}
            <div className="rounded-xl bg-gradient-to-r from-indigo-700 to-indigo-900 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Target size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-base">
                    {buId ? 'Objetivo Estratégico de la Unidad de Negocio' : 'Evaluación del Objetivo Estratégico'}
                  </h2>
                  <p className="text-indigo-200 text-xs mt-0.5">
                    {buId
                      ? 'Objetivo · Pilares de la BU · Ventas históricas'
                      : 'Visión · Misión · Declaración · Pilares corporativos · Métricas históricas'}
                  </p>
                </div>
              </div>
              {(isAdmin || isNVC) && !phaseLocked && (
                <div className="flex gap-2">
                  {(stratObj || pillars.length > 0) && (
                    <button onClick={() => {
                      const pres = new pptxgen()
                      pres.layout = 'LAYOUT_WIDE'
                      const IBLUE = '3730A3'
                      // Slide 1 — Objetivo
                      const s1 = pres.addSlide()
                      s1.background = { color: IBLUE }
                      s1.addText(buId ? 'Objetivo Estratégico UN' : 'Objetivo Estratégico Corporativo', { x:0.5, y:0.4, w:12, h:0.6, fontSize:24, bold:true, color:'FFFFFF' })
                      s1.addText(section?.name ?? '', { x:0.5, y:1.0, w:12, h:0.4, fontSize:14, color:'C7D2FE' })
                      if (stratObjForm.declaracion) {
                        s1.addText('Declaración del Objetivo', { x:0.5, y:1.8, w:12, h:0.4, fontSize:14, bold:true, color:'FFFFFF' })
                        s1.addText(stratObjForm.declaracion, { x:0.5, y:2.3, w:12, h:2.5, fontSize:13, color:'E0E7FF', wrap:true })
                      }
                      // Slide 2 — Pillars
                      if (pillars.length > 0) {
                        const s2 = pres.addSlide()
                        s2.addText('Pilares Estratégicos', { x:0.5, y:0.3, w:12, h:0.5, fontSize:20, bold:true, color:'1E1B4B' })
                        const tableData = [
                          [{ text:'#', options:{bold:true,fill:'EEF2FF',color:'3730A3'} }, { text:'Pilar', options:{bold:true,fill:'EEF2FF',color:'3730A3'} }, { text:'Descripción', options:{bold:true,fill:'EEF2FF',color:'3730A3'} }, { text:'KPI', options:{bold:true,fill:'EEF2FF',color:'3730A3'} }],
                          ...pillars.map((p, i) => [String(i+1), p.name ?? '', p.description ?? '', p.kpi ?? '—'])
                        ]
                        s2.addTable(tableData, { x:0.5, y:0.9, w:12.3, colW:[0.5, 3, 4.5, 4.3], fontSize:11, border:{type:'solid',color:'E0E7FF'}, rowH:0.45 })
                      }
                      // Slide 3 — Historical metrics
                      const realYearsWithData = OBJ_REAL_YEARS.filter(y => stratMetrics[`${y}_real`]?.ventas !== '')
                      if (realYearsWithData.length > 0) {
                        const s3 = pres.addSlide()
                        s3.addText('Ventas y Participación Histórica', { x:0.5, y:0.3, w:12, h:0.5, fontSize:20, bold:true, color:'1E1B4B' })
                        const hd = [{ text:'Año', options:{bold:true,fill:'EEF2FF',color:'3730A3'} }, { text:'Ventas reales', options:{bold:true,fill:'EEF2FF',color:'3730A3'} }, { text:'Objetivo pasado', options:{bold:true,fill:'EEF2FF',color:'3730A3'} }, { text:'Cumplimiento', options:{bold:true,fill:'EEF2FF',color:'3730A3'} }, { text:'Part. real %', options:{bold:true,fill:'EEF2FF',color:'3730A3'} }]
                        const rows = [hd, ...OBJ_REAL_YEARS.map(y => {
                          const m = stratMetrics[`${y}_real`] ?? {}
                          const real = parseFloat(m.ventas); const obj = parseFloat(m.ventas_objetivo)
                          const cump = real && obj && obj > 0 ? Math.round(real/obj*100)+'%' : '—'
                          return [String(y), m.ventas ? `${m.ventas} ${m.ventas_unit ?? ''}` : '—', m.ventas_objetivo ? String(m.ventas_objetivo) : '—', cump, m.participacion_pct ? `${m.participacion_pct}%` : '—']
                        })]
                        s3.addTable(rows, { x:0.5, y:0.9, w:12.3, fontSize:12, border:{type:'solid',color:'E0E7FF'}, rowH:0.5 })
                      }
                      // Conclusion slide
                      const concText = tabConclusions['objetivo_est']?.text
                      if (concText) {
                        const sc = pres.addSlide()
                        sc.addText('Conclusión Ejecutiva', { x:0.5, y:0.3, w:12, h:0.5, fontSize:20, bold:true, color:'1E1B4B' })
                        sc.addText(concText, { x:0.5, y:1.0, w:12, h:5, fontSize:13, color:'374151', wrap:true })
                      }
                      pres.writeFile({ fileName: `objetivo_estrategico_${section?.name ?? ''}.pptx` })
                    }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors">
                      <Download size={14} /> PPT
                    </button>
                  )}
                  <button onClick={saveAll} disabled={savingStratObj}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 ${stratObjSaved ? 'bg-emerald-400 text-white' : 'bg-white text-indigo-800 hover:bg-indigo-50'}`}>
                    {savingStratObj ? <><Loader size={14} className="animate-spin" />Guardando...</> : stratObjSaved ? <><CheckCircle2 size={14} />¡Guardado!</> : <><Save size={14} />Guardar</>}
                  </button>
                </div>
              )}
            </div>

            {/* Bloque 1 — Visión y Misión (solo nivel corporativo) */}
            {!buId && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-2">1 · Visión y Misión</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-600">Visión</label>
                    <textarea rows={4} value={stratObjForm.vision} onChange={e => setStratObjForm(f => ({ ...f, vision: e.target.value }))}
                      placeholder="¿Qué quiere ser la unidad de negocio en el futuro? ¿Cuál es su sueño de largo plazo?"
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none bg-white" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-600">Misión</label>
                    <textarea rows={4} value={stratObjForm.mision} onChange={e => setStratObjForm(f => ({ ...f, mision: e.target.value }))}
                      placeholder="¿Para qué existe esta unidad de negocio? ¿A quién sirve y cómo?"
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none bg-white" />
                  </div>
                </div>
              </div>
            )}

            {/* Bloque 1/2 — Declaración del Objetivo */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-3">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-2">
                {buId ? '1 · Objetivo Estratégico de la Unidad de Negocio' : '2 · Declaración del Objetivo de la Unidad de Negocio'}
              </p>
              <p className="text-xs text-gray-400">
                {buId
                  ? '¿Qué quiere lograr esta unidad de negocio? Exprésalo en una frase clara con horizonte de tiempo.'
                  : '¿Qué quiere lograr esta unidad de negocio? Exprésalo en una frase clara con horizonte de tiempo.'}
              </p>
              <textarea rows={3} value={stratObjForm.declaracion} onChange={e => setStratObjForm(f => ({ ...f, declaracion: e.target.value }))}
                placeholder="Ej: Para 2028, liderar el segmento industrial en la región Andina con 30% de participación."
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none bg-white" />
            </div>

            {/* Bloque 2/3 — Pilares */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-2">
                {buId ? '2 · Pilares de la Unidad de Negocio' : '3 · Pilares Corporativos'}
              </p>
              {pillars.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">
                  {buId ? 'Aún no hay pilares. Agrega los pilares estratégicos de esta unidad de negocio.' : 'Aún no hay pilares corporativos. Agrega los que guiarán la consecución del objetivo.'}
                </p>
              )}
              <div className="space-y-3">
                {pillars.map((p, idx) => (
                  <div key={p.id} className="flex gap-3 items-start p-3 rounded-lg border border-gray-100 bg-gray-50/50 group">
                    <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">{idx + 1}</div>
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <p className="text-sm font-semibold text-gray-800">{p.name}</p>
                      {p.description && <p className="text-xs text-gray-500">{p.description}</p>}
                      {(isAdmin || isNVC) ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-indigo-500 font-semibold whitespace-nowrap">KPI:</span>
                          <input type="text" value={p.kpi ?? ''} onChange={e => updatePillarKpi(p.id, e.target.value)}
                            placeholder="Ej: Crecimiento de ventas ≥ 15% anual"
                            className="flex-1 text-xs border border-indigo-100 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-300 bg-white" />
                        </div>
                      ) : (p.kpi && (
                        <p className="text-xs text-indigo-600 font-medium">KPI: {p.kpi}</p>
                      ))}
                    </div>
                    {(isAdmin || isNVC) && !phaseLocked && (
                      <button onClick={() => deletePilar(p.id)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 flex-shrink-0">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {(isAdmin || isNVC) && !phaseLocked && (
                <div className="border border-dashed border-indigo-200 rounded-lg p-3 space-y-2 bg-indigo-50/30">
                  <p className="text-xs font-semibold text-indigo-600">+ Agregar pilar</p>
                  <input type="text" value={newPillarForm.name} onChange={e => setNewPillarForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Nombre del pilar (ej: Innovación en portafolio)"
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white" />
                  <input type="text" value={newPillarForm.description} onChange={e => setNewPillarForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Descripción breve (opcional)"
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white" />
                  <input type="text" value={newPillarForm.kpi} onChange={e => setNewPillarForm(f => ({ ...f, kpi: e.target.value }))}
                    placeholder="KPI del pilar (ej: Crecimiento de ventas ≥ 15% anual)"
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white" />
                  <button onClick={addPilar} disabled={savingPillar || !newPillarForm.name.trim()}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50">
                    {savingPillar ? <><Loader size={13} className="animate-spin" />Guardando...</> : <><Plus size={13} />Agregar pilar</>}
                  </button>
                </div>
              )}
            </div>

            {/* Bloque 3/4 — Ventas y participación con cumplimiento */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {buId ? '3 · Ventas y Participación Histórica de la BU' : '4 · Ventas y Participación Histórica'}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">Real vs. objetivo pasado y cumplimiento — últimos 3 años</p>
                </div>
                <button onClick={async () => {
                  setImportingPyl(true)
                  try {
                    const { data: pylSecs } = await supabase
                      .from('sections')
                      .select('id, name')
                      .eq('phase_id', phaseId)
                      .ilike('name', '%p&l%')

                    let pylSecList = pylSecs ?? []
                    if (!pylSecList.length) {
                      const { data: allSecs } = await supabase.from('sections').select('id, name').eq('phase_id', phaseId)
                      const pylSec = allSecs?.find(s => {
                        const n = s.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'')
                        return n.includes('p&l') || n.includes('pyg') || (n.includes('estrategia') && n.includes('desde'))
                      })
                      if (!pylSec) { alert('No se encontró sección P&L en esta fase'); setImportingPyl(false); return }
                      pylSecList = [pylSec]
                    }

                    const { data: pylData } = await supabase.from('bu_pyl').select('*').eq('section_id', pylSecList[0].id).maybeSingle()
                    if (!pylData?.pyl_rows?.length) { alert('La sección P&L no tiene datos aún'); setImportingPyl(false); return }

                    const ventasRow = pylData.pyl_rows.find(r => {
                      const lbl = (r.label ?? '').toLowerCase()
                      return lbl.includes('venta') || lbl.includes('ingreso') || lbl.includes('revenue')
                    })
                    if (!ventasRow) { alert('No se encontró fila de Ventas en el P&L'); setImportingPyl(false); return }

                    const unit = pylData.unit ?? 'USD millones'
                    const newMetrics = { ...stratMetrics }
                    OBJ_REAL_YEARS.forEach(year => {
                      const yearGroup = pylData.pyl_columns?.find(g => g.year === year)
                      if (!yearGroup) return
                      const firstCol = yearGroup.colNames?.[0]
                      if (!firstCol) return
                      const key = `${year}___${firstCol}`
                      const val = ventasRow.values?.[key]
                      if (val !== undefined && val !== '') {
                        newMetrics[`${year}_real`] = {
                          ...(newMetrics[`${year}_real`] ?? {}),
                          ventas: String(val),
                          ventas_unit: unit,
                        }
                      }
                    })
                    setStratMetrics(newMetrics)
                    alert(`Datos importados del P&L: ${pylData.pyl_rows.length} filas disponibles. Revisa los valores y guarda.`)
                  } catch (e) {
                    console.error(e)
                    alert('Error al importar datos del P&L')
                  }
                  setImportingPyl(false)
                }}
                disabled={importingPyl}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50 transition-colors disabled:opacity-50">
                  {importingPyl ? <><Loader size={11} className="animate-spin" />Importando...</> : <><Download size={11} />Importar desde P&amp;L</>}
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-xs text-gray-500 font-semibold border-b border-gray-100 bg-gray-50/50">
                      <th className="text-left py-2.5 px-4 w-20">Año</th>
                      <th className="text-left py-2.5 px-2">Ventas reales</th>
                      <th className="text-left py-2.5 px-2">Objetivo pasado</th>
                      <th className="text-center py-2.5 px-2 w-24">Cumplimiento</th>
                      <th className="text-left py-2.5 px-2">Part. real %</th>
                      <th className="text-left py-2.5 px-2">Part. objetivo %</th>
                      <th className="text-left py-2.5 px-2">Notas</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {OBJ_REAL_YEARS.map(y => {
                      const cump = cumplimiento(y)
                      const cumpColor = cump == null ? '' : cump >= 100 ? 'text-emerald-600' : cump >= 80 ? 'text-amber-600' : 'text-red-500'
                      return (
                        <tr key={y} className="hover:bg-gray-50 transition-colors">
                          <td className="py-2.5 px-4 text-sm font-bold text-gray-700">{y}</td>
                          <td className="py-2 px-2">
                            <div className="flex gap-1">
                              <input type="number" value={getMetric(y, 'ventas')} onChange={e => updateMetric(y, 'ventas', e.target.value)}
                                placeholder="0" className="w-20 text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-center" />
                              <input type="text" value={getMetric(y, 'ventas_unit') || 'USD M'} onChange={e => updateMetric(y, 'ventas_unit', e.target.value)}
                                className="w-16 text-xs border border-gray-200 rounded-lg px-1.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-gray-500" />
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            <input type="number" value={getMetric(y, 'ventas_objetivo')} onChange={e => updateMetric(y, 'ventas_objetivo', e.target.value)}
                              placeholder="0" className="w-24 text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-center" />
                          </td>
                          <td className="py-2 px-2 text-center">
                            {cump != null
                              ? <span className={`text-sm font-bold ${cumpColor}`}>{cump}%</span>
                              : <span className="text-xs text-gray-300">—</span>}
                          </td>
                          <td className="py-2 px-2">
                            <div className="relative w-20">
                              <input type="number" min="0" max="100" step="0.1" value={getMetric(y, 'participacion_pct')} onChange={e => updateMetric(y, 'participacion_pct', e.target.value)}
                                placeholder="0" className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 pr-5 focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-center" />
                              <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            <div className="relative w-20">
                              <input type="number" min="0" max="100" step="0.1" value={getMetric(y, 'participacion_objetivo')} onChange={e => updateMetric(y, 'participacion_objetivo', e.target.value)}
                                placeholder="0" className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 pr-5 focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-center" />
                              <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            <input type="text" value={getMetric(y, 'notes')} onChange={e => updateMetric(y, 'notes', e.target.value)}
                              placeholder="Observación" className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-gray-500" />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50">
                <p className="text-xs text-gray-400">Cumplimiento = Ventas reales ÷ Objetivo pasado × 100. Verde ≥ 100% · Ámbar ≥ 80% · Rojo &lt; 80%</p>
              </div>
            </div>

            {/* Bloque 4 — Objetivos próximos 5 años (solo en fase de estrategia con BU) */}
            {buId && (normPhaseName.includes('estrategia') || normPhaseName.includes('definicion')) && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">4 · Objetivos de Ventas y Participación — Próximos 5 Años</p>
                  <p className="text-xs text-gray-400 mt-0.5">Metas hacia adelante · {OBJ_GOAL_YEARS[0]}–{OBJ_GOAL_YEARS[OBJ_GOAL_YEARS.length - 1]}</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-xs text-gray-500 font-semibold border-b border-gray-100 bg-gray-50/50">
                        <th className="text-left py-2.5 px-4 w-20">Año</th>
                        <th className="text-left py-2.5 px-2">Ventas objetivo</th>
                        <th className="text-left py-2.5 px-2 w-20">Unidad</th>
                        <th className="text-left py-2.5 px-2">Part. objetivo %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {OBJ_GOAL_YEARS.map(y => (
                        <tr key={y} className="hover:bg-indigo-50/30 transition-colors">
                          <td className="py-2.5 px-4 text-sm font-bold text-indigo-700">{y}</td>
                          <td className="py-2 px-2">
                            <input type="number" value={getGoal(y, 'ventas')} onChange={e => updateGoal(y, 'ventas', e.target.value)}
                              placeholder="0" className="w-24 text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-center" />
                          </td>
                          <td className="py-2 px-2">
                            <input type="text" value={getGoal(y, 'ventas_unit') || 'USD M'} onChange={e => updateGoal(y, 'ventas_unit', e.target.value)}
                              className="w-16 text-xs border border-gray-200 rounded-lg px-1.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-gray-500" />
                          </td>
                          <td className="py-2 px-2">
                            <div className="relative w-24">
                              <input type="number" min="0" max="100" step="0.1" value={getGoal(y, 'participacion_objetivo')} onChange={e => updateGoal(y, 'participacion_objetivo', e.target.value)}
                                placeholder="0" className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 pr-5 focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-center" />
                              <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50">
                  <p className="text-xs text-gray-400">Define las metas de ventas y participación de mercado para cada uno de los próximos 5 años. Guarda con el botón de arriba.</p>
                </div>
              </div>
            )}

            {renderConclusionBlock('objetivo_est', JSON.stringify({objetivo: stratObjForm.declaracion, pilares: pillars.map(p=>({nombre: p.name, kpi: p.kpi})), estrategia: stratObjForm.estrategia}))}
          </div>
        )
      })()}

      {/* ── BU Price Strategy Tab ─────────────────────────────── */}
      {activeTab === 'precio_estrategia' && (() => {
        const POSICION_OPTS  = ['Premium', 'Paridad', 'Valor']
        const MONEDAS        = ['USD', 'COP', 'EUR', 'MXN', 'PEN', 'CLP', 'BRL', 'ARS', 'BOB', 'PYG']
        const POSICION_COLOR = { Premium: 'bg-indigo-100 text-indigo-700', Paridad: 'bg-gray-100 text-gray-600', Valor: 'bg-amber-100 text-amber-700' }

        function updateProd(pid, field, val) {
          setPriceStratProducts(prev => prev.map(p => p.id === pid ? { ...p, [field]: val } : p))
        }
        function updateObjetivo(pid, año, field, val) {
          setPriceStratProducts(prev => prev.map(p => {
            if (p.id !== pid) return p
            const obj = (p.objetivos ?? []).map(o => o.año === año ? { ...o, [field]: val } : o)
            return { ...p, objetivos: obj }
          }))
        }
        function getObjetivo(prod, año, field) {
          return (prod.objetivos ?? []).find(o => o.año === año)?.[field] ?? ''
        }
        function addProductManualPrice() {
          const nombre = window.prompt('Nombre del producto:')
          if (!nombre?.trim()) return
          setPriceStratProducts(prev => [...prev, {
            id: Date.now().toString(), nombre: nombre.trim(),
            precio_actual: '', moneda: 'USD', posicion: 'Paridad',
            objetivos: PRICE_GOAL_YEARS.map(año => ({ año, precio: '', moneda: 'USD', posicion: 'Paridad' })),
          }])
        }
        function removeProduct(pid) { setPriceStratProducts(prev => prev.filter(p => p.id !== pid)) }

        // Weighted average (simple avg since no volume weights available here)
        function avgPrice(getter) {
          const vals = priceStratProducts.map(p => parseFloat(getter(p))).filter(v => !isNaN(v) && v > 0)
          if (!vals.length) return null
          return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2)
        }

        async function savePriceStrat() {
          setSavingPriceStrat(true)
          const payload = { section_id: sectionId, products: priceStratProducts, updated_at: new Date().toISOString() }
          if (priceStratRecord) {
            await supabase.from('bu_price_strategy').update(payload).eq('id', priceStratRecord.id)
          } else {
            const { data: nr } = await supabase.from('bu_price_strategy').insert(payload).select().single()
            if (nr) setPriceStratRecord(nr)
          }
          setSavingPriceStrat(false)
          await logChange('precio_estrategia', `Estrategia de precio: ${priceStratProducts.length} productos configurados`, { productos: priceStratProducts })
          setPriceStratSaved(true)
          setTimeout(() => setPriceStratSaved(false), 2500)
        }

        return (
          <div className="space-y-5">
            {/* Header */}
            <div className="rounded-xl bg-gradient-to-r from-teal-600 to-teal-800 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <TrendingUp size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-base">Estrategia de Precio</h2>
                  <p className="text-teal-100 text-xs mt-0.5">Posición de precio actual y objetivos por producto — próximos 3 años</p>
                </div>
              </div>
              {(isAdmin || isNVC) && !phaseLocked && (
                <div className="flex gap-2">
                  {priceStratProducts.length > 0 && (
                    <button onClick={() => {
                      const pres = new pptxgen()
                      pres.layout = 'LAYOUT_WIDE'
                      const TEAL = '0D9488'
                      const s1 = pres.addSlide()
                      s1.background = { color: TEAL }
                      s1.addText('Estrategia de Precio', { x:0.5, y:0.4, w:12, h:0.6, fontSize:24, bold:true, color:'FFFFFF' })
                      s1.addText(section?.name ?? '', { x:0.5, y:1.1, w:12, h:0.4, fontSize:14, color:'CCFBF1' })
                      // Table slide
                      const s2 = pres.addSlide()
                      s2.addText('Precio Actual y Objetivos por Producto', { x:0.5, y:0.3, w:12, h:0.5, fontSize:18, bold:true, color:'1E1B4B' })
                      const hd = [
                        { text:'Producto', options:{bold:true,fill:'F0FDFA',color:'0D9488'} },
                        { text:'Precio actual', options:{bold:true,fill:'F0FDFA',color:'0D9488'} },
                        { text:'Posición', options:{bold:true,fill:'F0FDFA',color:'0D9488'} },
                        ...PRICE_GOAL_YEARS.flatMap(y => [{ text:`${y} objetivo`, options:{bold:true,fill:'F0FDFA',color:'0D9488'} }])
                      ]
                      const rows = [hd, ...priceStratProducts.map(p => [
                        p.nombre,
                        p.precio_actual ? `${p.precio_actual} ${p.moneda}` : '—',
                        p.posicion ?? '—',
                        ...PRICE_GOAL_YEARS.map(y => { const o = (p.objetivos??[]).find(ob=>ob.año===y); return o?.precio ? `${o.precio} ${o.moneda??p.moneda}` : '—' })
                      ])]
                      s2.addTable(rows, { x:0.5, y:0.9, w:12.3, fontSize:11, border:{type:'solid',color:'CCFBF1'}, rowH:0.45 })
                      const concText = tabConclusions['precio_estrategia']?.text
                      if (concText) {
                        const sc = pres.addSlide()
                        sc.addText('Conclusión', { x:0.5, y:0.3, w:12, h:0.5, fontSize:18, bold:true, color:'1E1B4B' })
                        sc.addText(concText, { x:0.5, y:1.0, w:12, h:5, fontSize:13, color:'374151', wrap:true })
                      }
                      pres.writeFile({ fileName: `precio_estrategia_${section?.name ?? ''}.pptx` })
                    }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors">
                      <Download size={14} /> PPT
                    </button>
                  )}
                  <button onClick={savePriceStrat} disabled={savingPriceStrat}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 ${priceStratSaved ? 'bg-emerald-400 text-white' : 'bg-white text-teal-800 hover:bg-teal-50'}`}>
                    {savingPriceStrat ? <><Loader size={14} className="animate-spin" />Guardando...</> : priceStratSaved ? <><CheckCircle2 size={14} />¡Guardado!</> : <><Save size={14} />Guardar</>}
                  </button>
                </div>
              )}
            </div>

            {/* Products table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Precio por Producto — Actual y Objetivos</p>
                  <p className="text-xs text-gray-400 mt-0.5">Ingresa el precio actual y define los objetivos para {PRICE_GOAL_YEARS.join(', ')}</p>
                </div>
                {(isAdmin || isNVC) && !phaseLocked && (
                  <button onClick={addProductManualPrice}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 hover:bg-teal-100 text-teal-700 rounded-lg text-xs font-semibold transition-colors">
                    <Plus size={13} />Agregar producto
                  </button>
                )}
              </div>

              {priceStratProducts.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <TrendingUp size={32} className="mx-auto mb-2 opacity-20" />
                  <p className="text-sm text-gray-500">Sin productos. Agrega productos o completa el Portafolio Estratégico primero.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-xs text-gray-500 font-semibold border-b border-gray-100 bg-gray-50/50">
                        <th className="text-left py-2.5 px-4 min-w-36">Producto</th>
                        <th className="text-center py-2.5 px-3 min-w-44 border-r border-gray-100">Precio actual</th>
                        {PRICE_GOAL_YEARS.map(y => (
                          <th key={y} className="text-center py-2.5 px-3 min-w-44 text-teal-600">{y}</th>
                        ))}
                        {(isAdmin || isNVC) && <th className="w-8" />}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {priceStratProducts.map(prod => (
                        <tr key={prod.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="py-2.5 px-4">
                            <input type="text" value={prod.nombre} onChange={e => updateProd(prod.id, 'nombre', e.target.value)}
                              className="w-full text-sm font-medium border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-teal-200 bg-white" />
                          </td>
                          {/* Precio actual */}
                          <td className="py-2 px-3 border-r border-gray-100">
                            <div className="space-y-1.5">
                              <div className="flex gap-1.5">
                                <input type="number" value={prod.precio_actual} onChange={e => updateProd(prod.id, 'precio_actual', e.target.value)}
                                  placeholder="0" className="w-20 text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-teal-200 bg-white text-center" />
                                <select value={prod.moneda} onChange={e => updateProd(prod.id, 'moneda', e.target.value)}
                                  className="text-xs border border-gray-200 rounded-lg px-1.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-200 bg-white text-gray-500 w-16">
                                  {MONEDAS.map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                              </div>
                              <div className="flex gap-1">
                                {POSICION_OPTS.map(pos => (
                                  <button key={pos} type="button" onClick={() => updateProd(prod.id, 'posicion', pos)}
                                    className={`text-xs px-2 py-0.5 rounded-full border transition-all flex-1 ${prod.posicion === pos ? POSICION_COLOR[pos] + ' border-current' : 'bg-white text-gray-400 border-gray-200'}`}>
                                    {pos}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </td>
                          {/* Objetivo por año */}
                          {PRICE_GOAL_YEARS.map(y => (
                            <td key={y} className="py-2 px-3">
                              <div className="space-y-1.5">
                                <div className="flex gap-1.5">
                                  <input type="number" value={getObjetivo(prod, y, 'precio')} onChange={e => updateObjetivo(prod.id, y, 'precio', e.target.value)}
                                    placeholder="0" className="w-20 text-sm border border-teal-100 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-teal-200 bg-teal-50/30 text-center" />
                                  <select value={getObjetivo(prod, y, 'moneda') || prod.moneda} onChange={e => updateObjetivo(prod.id, y, 'moneda', e.target.value)}
                                    className="text-xs border border-teal-100 rounded-lg px-1.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-200 bg-teal-50/30 text-gray-500 w-16">
                                    {MONEDAS.map(m => <option key={m} value={m}>{m}</option>)}
                                  </select>
                                </div>
                                <div className="flex gap-1">
                                  {POSICION_OPTS.map(pos => (
                                    <button key={pos} type="button" onClick={() => updateObjetivo(prod.id, y, 'posicion', pos)}
                                      className={`text-xs px-2 py-0.5 rounded-full border transition-all flex-1 ${getObjetivo(prod, y, 'posicion') === pos ? POSICION_COLOR[pos] + ' border-current' : 'bg-white text-gray-400 border-gray-200'}`}>
                                      {pos}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </td>
                          ))}
                          {(isAdmin || isNVC) && !phaseLocked && (
                            <td className="py-2 px-2">
                              <button onClick={() => removeProduct(prod.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors">
                                <Trash2 size={14} />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    {/* Summary row */}
                    {priceStratProducts.length > 1 && (
                      <tfoot>
                        <tr className="bg-teal-50/40 border-t-2 border-teal-100">
                          <td className="py-2.5 px-4 text-xs font-bold text-teal-700">Promedio de línea</td>
                          <td className="py-2.5 px-3 text-center border-r border-gray-100">
                            <span className="text-sm font-bold text-teal-700">
                              {avgPrice(p => p.precio_actual) ?? '—'}
                            </span>
                          </td>
                          {PRICE_GOAL_YEARS.map(y => (
                            <td key={y} className="py-2.5 px-3 text-center">
                              <span className="text-sm font-bold text-teal-700">
                                {avgPrice(p => getObjetivo(p, y, 'precio')) ?? '—'}
                              </span>
                            </td>
                          ))}
                          <td />
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </div>

            {/* Legend */}
            <div className="bg-gray-50 rounded-xl border border-gray-100 p-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Posición de precio</p>
              <div className="flex gap-4 flex-wrap text-xs">
                <span className="flex items-center gap-1.5"><span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium">Premium</span> Precio por encima del mercado — mayor propuesta de valor percibida</span>
                <span className="flex items-center gap-1.5"><span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">Paridad</span> Precio alineado con el promedio del mercado</span>
                <span className="flex items-center gap-1.5"><span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Valor</span> Precio por debajo del mercado — competencia por precio</span>
              </div>
            </div>

            {renderConclusionBlock('precio_estrategia', JSON.stringify({productos: priceStratProducts}))}
          </div>
        )
      })()}

      {/* ── BU Strategy Portfolio Tab ─────────────────────────── */}
      {activeTab === 'portafolio_estrategia' && (() => {
        const DECISION_OPTS  = ['Mantener', 'Potenciar', 'Transformar', 'Descontinuar']
        const PALANCA_TIPOS  = ['Fórmula', 'Empaque', 'Costo', 'Precio', 'Canal', 'Posicionamiento', 'Servicio', 'Otro']
        const PRIORIDAD_OPTS = ['Alta', 'Media', 'Baja']
        const TIPO_REF_OPTS  = ['Extensión de línea', 'Nueva referencia']

        const DECISION_COLOR = {
          Mantener:     'bg-blue-100 text-blue-700 border-blue-200',
          Potenciar:    'bg-emerald-100 text-emerald-700 border-emerald-200',
          Transformar:  'bg-amber-100 text-amber-700 border-amber-200',
          Descontinuar: 'bg-red-100 text-red-700 border-red-200',
        }
        const PRIO_COLOR = {
          Alta:  'bg-red-100 text-red-700',
          Media: 'bg-amber-100 text-amber-700',
          Baja:  'bg-gray-100 text-gray-500',
        }

        // ── Existing products helpers ─────────────────────────
        function updateProduct(pid, field, val) {
          setStratPortProducts(prev => prev.map(p => p.id === pid ? { ...p, [field]: val } : p))
        }
        function removeProduct(pid) {
          setStratPortProducts(prev => prev.filter(p => p.id !== pid))
        }
        function addProductManual() {
          const nombre = window.prompt('Nombre del producto existente:')
          if (!nombre?.trim()) return
          setStratPortProducts(prev => [...prev, { id: Date.now().toString(), nombre: nombre.trim(), decision: 'Mantener', rationale: '', palancas: [] }])
        }
        // palancas per existing product
        function addPalanca(pid) {
          setStratPortProducts(prev => prev.map(p => p.id === pid
            ? { ...p, palancas: [...(p.palancas ?? []), { id: Date.now().toString(), tipo: '', descripcion: '', prioridad: 'Media' }] }
            : p))
        }
        function updatePalanca(pid, lid, field, val) {
          setStratPortProducts(prev => prev.map(p => p.id === pid
            ? { ...p, palancas: (p.palancas ?? []).map(l => l.id === lid ? { ...l, [field]: val } : l) }
            : p))
        }
        function removePalanca(pid, lid) {
          setStratPortProducts(prev => prev.map(p => p.id === pid
            ? { ...p, palancas: (p.palancas ?? []).filter(l => l.id !== lid) }
            : p))
        }

        // ── New references helpers ────────────────────────────
        function addNewRef() {
          setStratPortNewRefs(prev => [...prev, { id: Date.now().toString(), nombre: '', tipo_ref: 'Nueva referencia', rationale: '', palancas: [] }])
        }
        function updateNewRef(rid, field, val) {
          setStratPortNewRefs(prev => prev.map(r => r.id === rid ? { ...r, [field]: val } : r))
        }
        function removeNewRef(rid) {
          setStratPortNewRefs(prev => prev.filter(r => r.id !== rid))
        }
        function addNewRefPalanca(rid) {
          setStratPortNewRefs(prev => prev.map(r => r.id === rid
            ? { ...r, palancas: [...(r.palancas ?? []), { id: Date.now().toString(), tipo: '', descripcion: '', prioridad: 'Media' }] }
            : r))
        }
        function updateNewRefPalanca(rid, lid, field, val) {
          setStratPortNewRefs(prev => prev.map(r => r.id === rid
            ? { ...r, palancas: (r.palancas ?? []).map(l => l.id === lid ? { ...l, [field]: val } : l) }
            : r))
        }
        function removeNewRefPalanca(rid, lid) {
          setStratPortNewRefs(prev => prev.map(r => r.id === rid
            ? { ...r, palancas: (r.palancas ?? []).filter(l => l.id !== lid) }
            : r))
        }

        // ── Save ─────────────────────────────────────────────
        async function saveStratPort() {
          setSavingStratPort(true)
          const payload = {
            section_id: sectionId,
            products:   stratPortProducts,
            new_refs:   stratPortNewRefs,
            updated_at: new Date().toISOString(),
          }
          if (stratPortRecord) {
            await supabase.from('bu_strategy_portfolio').update(payload).eq('id', stratPortRecord.id)
          } else {
            const { data: nr } = await supabase.from('bu_strategy_portfolio').insert(payload).select().single()
            if (nr) setStratPortRecord(nr)
          }
          setSavingStratPort(false)
          await logChange('portafolio_estrategia', `Portafolio estratégico: ${stratPortProducts.length} productos, ${stratPortNewRefs.length} referencias nuevas`, { productos: stratPortProducts, nuevasReferencias: stratPortNewRefs })
          setStratPortSaved(true)
          setTimeout(() => setStratPortSaved(false), 2500)
        }

        // ── Render ───────────────────────────────────────────
        return (
          <div className="space-y-5">
            {/* Header */}
            <div className="rounded-xl bg-gradient-to-r from-violet-700 to-violet-900 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Database size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-base">Portafolio Estratégico</h2>
                  <p className="text-violet-200 text-xs mt-0.5">
                    Decisiones por producto · Palancas de mejora · Nuevas referencias para este mercado
                  </p>
                </div>
              </div>
              {(isAdmin || isNVC) && !phaseLocked && (
                <div className="flex gap-2">
                  {(stratPortProducts.length > 0 || stratPortNewRefs.length > 0) && (
                    <button onClick={() => {
                      const pres = new pptxgen()
                      pres.layout = 'LAYOUT_WIDE'
                      const VIOL = '5B21B6'
                      // Slide 1 — Cover
                      const s1 = pres.addSlide()
                      s1.background = { color: VIOL }
                      s1.addText('Portafolio Estratégico', { x:0.5, y:0.4, w:12, h:0.6, fontSize:24, bold:true, color:'FFFFFF' })
                      s1.addText(section?.name ?? '', { x:0.5, y:1.0, w:12, h:0.4, fontSize:14, color:'DDD6FE' })
                      s1.addText(`${stratPortProducts.length} productos · ${stratPortNewRefs.length} referencias nuevas`, { x:0.5, y:1.6, w:12, h:0.4, fontSize:13, color:'C4B5FD' })
                      // Slide 2 — Existing products decisions
                      if (stratPortProducts.length > 0) {
                        const s2 = pres.addSlide()
                        s2.addText('Decisiones Estratégicas por Producto', { x:0.5, y:0.3, w:12, h:0.5, fontSize:20, bold:true, color:'1E1B4B' })
                        const hdr = [
                          { text:'Producto', options:{bold:true,fill:'EDE9FE',color:'5B21B6'} },
                          { text:'Decisión', options:{bold:true,fill:'EDE9FE',color:'5B21B6'} },
                          { text:'Rationale', options:{bold:true,fill:'EDE9FE',color:'5B21B6'} },
                          { text:'Palancas', options:{bold:true,fill:'EDE9FE',color:'5B21B6'} },
                        ]
                        const rows2 = [hdr, ...stratPortProducts.map(p => [
                          p.nombre ?? '',
                          p.decision ?? '',
                          p.rationale ?? '',
                          (p.palancas ?? []).map(l => `· ${l.tipo}${l.descripcion ? ': '+l.descripcion : ''}`).join('\n') || '—',
                        ])]
                        s2.addTable(rows2, { x:0.5, y:0.9, w:12.3, colW:[2.8, 1.8, 4, 3.7], fontSize:10, border:{type:'solid',color:'EDE9FE'}, rowH:0.5 })
                      }
                      // Slide 3 — New references
                      if (stratPortNewRefs.length > 0) {
                        const s3 = pres.addSlide()
                        s3.addText('Nuevas Referencias', { x:0.5, y:0.3, w:12, h:0.5, fontSize:20, bold:true, color:'1E1B4B' })
                        const hdr3 = [
                          { text:'Nombre', options:{bold:true,fill:'EDE9FE',color:'5B21B6'} },
                          { text:'Tipo', options:{bold:true,fill:'EDE9FE',color:'5B21B6'} },
                          { text:'Rationale', options:{bold:true,fill:'EDE9FE',color:'5B21B6'} },
                          { text:'Palancas', options:{bold:true,fill:'EDE9FE',color:'5B21B6'} },
                        ]
                        const rows3 = [hdr3, ...stratPortNewRefs.map(r => [
                          r.nombre ?? '',
                          r.tipo_ref ?? '',
                          r.rationale ?? '',
                          (r.palancas ?? []).map(l => `· ${l.tipo}${l.descripcion ? ': '+l.descripcion : ''}`).join('\n') || '—',
                        ])]
                        s3.addTable(rows3, { x:0.5, y:0.9, w:12.3, colW:[3, 2.2, 4, 3.1], fontSize:10, border:{type:'solid',color:'EDE9FE'}, rowH:0.5 })
                      }
                      // Conclusion slide
                      const concText = tabConclusions['portafolio_estrategia']?.text
                      if (concText) {
                        const sc = pres.addSlide()
                        sc.addText('Conclusión Ejecutiva', { x:0.5, y:0.3, w:12, h:0.5, fontSize:20, bold:true, color:'1E1B4B' })
                        sc.addText(concText, { x:0.5, y:1.0, w:12, h:5, fontSize:13, color:'374151', wrap:true })
                      }
                      pres.writeFile({ fileName: `portafolio_estrategia_${section?.name ?? ''}.pptx` })
                    }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors">
                      <Download size={14} /> PPT
                    </button>
                  )}
                  <button onClick={saveStratPort} disabled={savingStratPort}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 ${stratPortSaved ? 'bg-emerald-400 text-white' : 'bg-white text-violet-800 hover:bg-violet-50'}`}>
                    {savingStratPort ? <><Loader size={14} className="animate-spin" />Guardando...</> : stratPortSaved ? <><CheckCircle2 size={14} />¡Guardado!</> : <><Save size={14} />Guardar</>}
                  </button>
                </div>
              )}
            </div>

            {/* Bloque 1 — Productos existentes */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">1 · Portafolio Actual — Decisiones Estratégicas</p>
                {(isAdmin || isNVC) && !phaseLocked && (
                  <button onClick={addProductManual}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 hover:bg-violet-100 text-violet-700 rounded-lg text-xs font-semibold transition-colors">
                    <Plus size={13} />Agregar producto
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-400">
                Para cada producto define la decisión estratégica y las palancas de acción concretas. Los productos se importan automáticamente del Análisis Interno si están disponibles.
              </p>

              {stratPortProducts.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  <Database size={32} className="mx-auto mb-2 opacity-20" />
                  <p className="text-sm text-gray-500 font-medium">Sin productos</p>
                  <p className="text-xs mt-1">Agrega los productos actuales de esta unidad de negocio para definir su estrategia.</p>
                </div>
              )}

              <div className="space-y-4">
                {stratPortProducts.map((prod, idx) => (
                  <div key={prod.id} className="border border-gray-100 rounded-xl p-4 space-y-3 bg-gray-50/40 group">
                    {/* Product header row */}
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">{idx + 1}</div>
                      <div className="flex-1 min-w-0 space-y-2">
                        {/* Name */}
                        <input type="text" value={prod.nombre} onChange={e => updateProduct(prod.id, 'nombre', e.target.value)}
                          placeholder="Nombre del producto"
                          className="w-full text-sm font-semibold border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-violet-300 bg-white" />
                        {/* Decision selector */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-gray-500 font-medium">Decisión:</span>
                          {DECISION_OPTS.map(opt => (
                            <button key={opt} onClick={() => updateProduct(prod.id, 'decision', opt)}
                              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${prod.decision === opt ? DECISION_COLOR[opt] : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300'}`}>
                              {opt}
                            </button>
                          ))}
                        </div>
                        {/* Rationale */}
                        <textarea rows={2} value={prod.rationale} onChange={e => updateProduct(prod.id, 'rationale', e.target.value)}
                          placeholder="¿Por qué esta decisión? Justificación estratégica breve..."
                          className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-200 resize-none bg-white text-gray-600" />
                      </div>
                      {(isAdmin || isNVC) && !phaseLocked && (
                        <button onClick={() => removeProduct(prod.id)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 flex-shrink-0 mt-0.5">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>

                    {/* Palancas */}
                    <div className="ml-10 space-y-2">
                      <p className="text-xs font-semibold text-gray-500">Palancas estratégicas</p>
                      {(prod.palancas ?? []).map(pal => (
                        <div key={pal.id} className="flex gap-2 items-start bg-white border border-gray-100 rounded-lg p-2.5 group/pal">
                          <select value={pal.tipo} onChange={e => updatePalanca(prod.id, pal.id, 'tipo', e.target.value)}
                            className="text-xs border border-gray-200 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-violet-300 bg-white text-gray-600 flex-shrink-0 w-36">
                            <option value="">Tipo...</option>
                            {PALANCA_TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <input type="text" value={pal.descripcion} onChange={e => updatePalanca(prod.id, pal.id, 'descripcion', e.target.value)}
                            placeholder="Describe el cambio o mejora concreta..."
                            className="flex-1 text-xs border border-gray-200 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-violet-300 bg-white" />
                          <select value={pal.prioridad} onChange={e => updatePalanca(prod.id, pal.id, 'prioridad', e.target.value)}
                            className={`text-xs border rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-violet-300 flex-shrink-0 w-20 font-medium ${PRIO_COLOR[pal.prioridad] ?? ''}`}>
                            {PRIORIDAD_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                          {(isAdmin || isNVC) && !phaseLocked && (
                            <button onClick={() => removePalanca(prod.id, pal.id)} className="opacity-0 group-hover/pal:opacity-100 transition-opacity p-1 rounded hover:bg-red-50 text-red-400 flex-shrink-0">
                              <X size={12} />
                            </button>
                          )}
                        </div>
                      ))}
                      {(isAdmin || isNVC) && !phaseLocked && (
                        <button onClick={() => addPalanca(prod.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-violet-600 hover:bg-violet-50 rounded-lg border border-dashed border-violet-200 transition-colors font-medium">
                          <Plus size={12} />Agregar palanca
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bloque 2 — Nuevas referencias */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">2 · Nuevas Referencias en Este Mercado</p>
                  <p className="text-xs text-gray-400 mt-1">Solo extensiones de línea y nuevas referencias dentro de esta categoría / mercado. Los lanzamientos a mercados nuevos pertenecen a otras unidades de negocio.</p>
                </div>
                {(isAdmin || isNVC) && !phaseLocked && (
                  <button onClick={addNewRef}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-xs font-semibold transition-colors flex-shrink-0 ml-4">
                    <Plus size={13} />Agregar referencia
                  </button>
                )}
              </div>

              {stratPortNewRefs.length === 0 && (
                <div className="text-center py-6 text-gray-400">
                  <Plus size={28} className="mx-auto mb-2 opacity-20" />
                  <p className="text-sm text-gray-500">Sin nuevas referencias definidas aún.</p>
                </div>
              )}

              <div className="space-y-4">
                {stratPortNewRefs.map((ref, idx) => (
                  <div key={ref.id} className="border border-gray-100 rounded-xl p-4 space-y-3 bg-emerald-50/20 group">
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">{idx + 1}</div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex gap-2">
                          <input type="text" value={ref.nombre} onChange={e => updateNewRef(ref.id, 'nombre', e.target.value)}
                            placeholder="Nombre de la nueva referencia o extensión"
                            className="flex-1 text-sm font-semibold border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-300 bg-white" />
                          <select value={ref.tipo_ref} onChange={e => updateNewRef(ref.id, 'tipo_ref', e.target.value)}
                            className="text-xs border border-emerald-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-300 bg-emerald-50 text-emerald-700 font-medium flex-shrink-0">
                            {TIPO_REF_OPTS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <textarea rows={2} value={ref.rationale} onChange={e => updateNewRef(ref.id, 'rationale', e.target.value)}
                          placeholder="¿Por qué esta referencia? Oportunidad de mercado, tendencia, gap identificado..."
                          className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-200 resize-none bg-white text-gray-600" />
                      </div>
                      {(isAdmin || isNVC) && !phaseLocked && (
                        <button onClick={() => removeNewRef(ref.id)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 flex-shrink-0 mt-0.5">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    {/* Palancas for new ref */}
                    <div className="ml-10 space-y-2">
                      <p className="text-xs font-semibold text-gray-500">Palancas / atributos clave</p>
                      {(ref.palancas ?? []).map(pal => (
                        <div key={pal.id} className="flex gap-2 items-start bg-white border border-gray-100 rounded-lg p-2.5 group/pal">
                          <select value={pal.tipo} onChange={e => updateNewRefPalanca(ref.id, pal.id, 'tipo', e.target.value)}
                            className="text-xs border border-gray-200 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-300 bg-white text-gray-600 flex-shrink-0 w-36">
                            <option value="">Tipo...</option>
                            {PALANCA_TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <input type="text" value={pal.descripcion} onChange={e => updateNewRefPalanca(ref.id, pal.id, 'descripcion', e.target.value)}
                            placeholder="Descripción del atributo o desarrollo necesario..."
                            className="flex-1 text-xs border border-gray-200 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-300 bg-white" />
                          <select value={pal.prioridad} onChange={e => updateNewRefPalanca(ref.id, pal.id, 'prioridad', e.target.value)}
                            className={`text-xs border rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-300 flex-shrink-0 w-20 font-medium ${PRIO_COLOR[pal.prioridad] ?? ''}`}>
                            {PRIORIDAD_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                          {(isAdmin || isNVC) && !phaseLocked && (
                            <button onClick={() => removeNewRefPalanca(ref.id, pal.id)} className="opacity-0 group-hover/pal:opacity-100 transition-opacity p-1 rounded hover:bg-red-50 text-red-400 flex-shrink-0">
                              <X size={12} />
                            </button>
                          )}
                        </div>
                      ))}
                      {(isAdmin || isNVC) && !phaseLocked && (
                        <button onClick={() => addNewRefPalanca(ref.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-emerald-600 hover:bg-emerald-50 rounded-lg border border-dashed border-emerald-200 transition-colors font-medium">
                          <Plus size={12} />Agregar palanca
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Legend */}
            <div className="bg-gray-50 rounded-xl border border-gray-100 p-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Guía de decisiones</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { d: 'Mantener', desc: 'Producto estable. Sin cambios mayores, solo eficiencia operativa.' },
                  { d: 'Potenciar', desc: 'Alto potencial. Inversión en crecimiento, penetración o expansión.' },
                  { d: 'Transformar', desc: 'Requiere cambios significativos para seguir siendo competitivo.' },
                  { d: 'Descontinuar', desc: 'Sin viabilidad estratégica en este mercado. Salida planificada.' },
                ].map(({ d, desc }) => (
                  <div key={d} className={`rounded-lg border px-3 py-2 ${DECISION_COLOR[d]}`}>
                    <p className="text-xs font-bold mb-0.5">{d}</p>
                    <p className="text-xs opacity-75">{desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {renderConclusionBlock('portafolio_estrategia', JSON.stringify({productos: stratPortProducts, nuevasReferencias: stratPortNewRefs}))}
          </div>
        )
      })()}

      {/* ── Competitor Precio — Comparativo automático ────────── */}
      {activeTab === 'precio_comp' && (() => {
        const hasMktRef = compPrecioData.some(d => d.mkt_price != null)

        // Build recharts data
        const chartData = compPrecioData.map(d => ({
          name:       d.product_name,
          Mercado:    d.mkt_price,
          Competidor: d.comp_price,
        }))

        return (
          <div className="space-y-5">
            {/* Header */}
            <div className="rounded-xl bg-gradient-to-r from-emerald-600 to-teal-700 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <TrendingUp size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-base">Comparativo de Precios por Producto</h2>
                  <p className="text-emerald-100 text-xs mt-0.5">
                    Datos importados automáticamente desde Portafolio y Análisis de Mercado · Sin re-ingreso de información
                  </p>
                </div>
              </div>
            </div>

            {compPrecioData.length === 0 && (
              <div className="text-center py-16 text-gray-400">
                <TrendingUp size={36} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium text-gray-500">Sin datos disponibles</p>
                <p className="text-sm mt-1 max-w-sm mx-auto">
                  Para ver el comparativo, ingresa los precios promedio por producto en la sección <strong>Portafolio</strong> de este competidor.
                </p>
              </div>
            )}

            {compPrecioData.length > 0 && (
              <>
                {/* Bar Chart */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm font-semibold text-gray-700">Precio promedio por producto</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-blue-400 inline-block" /> Mercado (ref.)</span>
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-orange-400 inline-block" /> Competidor</span>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                        formatter={(val, name) => [val != null ? val.toLocaleString('es-CO') : '—', name]}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {hasMktRef && <Line type="monotone" dataKey="Mercado" stroke="#60a5fa" strokeWidth={2} dot={{ r: 5, fill: '#60a5fa' }} strokeDasharray="5 5" />}
                      <Line type="monotone" dataKey="Competidor" stroke="#fb923c" strokeWidth={2.5} dot={{ r: 5, fill: '#fb923c' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Detail table */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Detalle por producto</p>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {compPrecioData.map((d, i) => {
                      const above = d.delta_pct != null && d.delta_pct > 0
                      const below = d.delta_pct != null && d.delta_pct < 0
                      const equal = d.delta_pct != null && d.delta_pct === 0
                      return (
                        <div key={i} className="grid grid-cols-4 gap-4 px-5 py-3.5 items-center hover:bg-gray-50 transition-colors">
                          <div>
                            <p className="text-sm font-semibold text-gray-800">{d.product_name}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-400 mb-0.5">Precio mercado</p>
                            <p className="text-sm font-medium text-blue-700">
                              {d.mkt_price != null ? d.mkt_price.toLocaleString('es-CO') : <span className="text-gray-300">—</span>}
                              {d.mkt_price != null && <span className="text-xs text-gray-400 ml-1">{d.mkt_unit}</span>}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-400 mb-0.5">Precio competidor</p>
                            <p className="text-sm font-bold text-orange-600">
                              {d.comp_price.toLocaleString('es-CO')}
                              <span className="text-xs text-gray-400 ml-1 font-normal">{d.comp_unit}</span>
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-400 mb-0.5">vs Mercado</p>
                            {d.delta_pct != null ? (
                              <span className={`inline-flex items-center gap-1 text-sm font-bold px-2.5 py-0.5 rounded-full ${
                                above ? 'bg-red-50 text-red-600'
                                : below ? 'bg-emerald-50 text-emerald-600'
                                : 'bg-gray-100 text-gray-500'
                              }`}>
                                {above ? <TrendingUp size={13} /> : below ? <TrendingDown size={13} /> : null}
                                {above ? '+' : ''}{d.delta_pct}%
                              </span>
                            ) : (
                              <span className="text-xs text-gray-300">Sin ref.</span>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Summary insight */}
                {hasMktRef && (() => {
                  const withDelta = compPrecioData.filter(d => d.delta_pct != null)
                  const avgDelta  = withDelta.length
                    ? Math.round(withDelta.reduce((s, d) => s + d.delta_pct, 0) / withDelta.length * 10) / 10
                    : null
                  if (avgDelta == null) return null
                  const aboveMkt = avgDelta > 3
                  const belowMkt = avgDelta < -3
                  return (
                    <div className={`rounded-xl p-4 border text-sm ${
                      aboveMkt ? 'bg-red-50 border-red-200 text-red-800'
                      : belowMkt ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                      : 'bg-blue-50 border-blue-200 text-blue-800'
                    }`}>
                      <p className="font-semibold mb-0.5">
                        {aboveMkt ? '📈 Precio superior al mercado'
                          : belowMkt ? '📉 Precio por debajo del mercado'
                          : '〰️ Precio en línea con el mercado'}
                      </p>
                      <p className="text-xs opacity-80">
                        Delta promedio vs mercado: <strong>{avgDelta > 0 ? '+' : ''}{avgDelta}%</strong> sobre {withDelta.length} producto{withDelta.length !== 1 ? 's' : ''} con referencia disponible.
                        {aboveMkt ? ' Este competidor tiene una estrategia de precio premium.' : belowMkt ? ' Este competidor compite agresivamente por precio.' : ' Los precios están alineados con la media del mercado.'}
                      </p>
                    </div>
                  )
                })()}
              </>
            )}

            {renderConclusionBlock('precio_comp', JSON.stringify({comparativo: compPrecioData}))}
          </div>
        )
      })()}

      {/* ── Market Comunicación — Mix de Medios Tab ──────────── */}
      {activeTab === 'mix_medios' && (() => {
        const totalPct = mktMediaForm.reduce((s, r) => s + (Number(r.weight_pct) || 0), 0)
        const totalOk  = Math.round(totalPct * 10) / 10 === 100

        function updateRow(idx, field, value) {
          setMktMediaForm(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r))
        }
        function addRow() {
          setMktMediaForm(prev => [...prev, { id: null, medium_name: '', weight_pct: '', investment_ref: '', notes: '' }])
        }
        function removeRow(idx) {
          setMktMediaForm(prev => prev.filter((_, i) => i !== idx))
        }

        async function saveMktMedia() {
          setSavingMktMedia(true)
          const toDelete = mktMediaMix.filter(r => !mktMediaForm.find(f => f.id === r.id)).map(r => r.id)
          if (toDelete.length > 0) await supabase.from('market_media_mix').delete().in('id', toDelete)
          const newRows = [], existRows = []
          mktMediaForm.forEach((f, i) => {
            const payload = { section_id: sectionId, medium_name: f.medium_name || 'Medio', weight_pct: f.weight_pct !== '' ? Number(f.weight_pct) : null, investment_ref: f.investment_ref || null, notes: f.notes || null, medium_order: i + 1, updated_at: new Date().toISOString() }
            if (f.id) existRows.push({ id: f.id, ...payload })
            else newRows.push(payload)
          })
          const results = []
          if (newRows.length > 0) { const { data } = await supabase.from('market_media_mix').insert(newRows).select(); results.push(...(data ?? [])) }
          for (const r of existRows) { await supabase.from('market_media_mix').update(r).eq('id', r.id); results.push(r) }
          setMktMediaMix(results)
          setMktMediaForm(results.map(r => ({ id: r.id, medium_name: r.medium_name, weight_pct: r.weight_pct ?? '', investment_ref: r.investment_ref ?? '', notes: r.notes ?? '' })))
          setSavingMktMedia(false)
          setMktMediaSaved(true)
          setTimeout(() => setMktMediaSaved(false), 2500)
        }

        return (
          <div className="space-y-5">
            {/* Header */}
            <div className="rounded-xl bg-gradient-to-r from-pink-600 to-rose-600 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <BarChart2 size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-base">Mix de Medios del Mercado</h2>
                  <p className="text-pink-100 text-xs mt-0.5">Define la distribución de medios a nivel industria · Sirve de referencia en el análisis competitivo</p>
                </div>
              </div>
              {(isAdmin || isNVC) && !phaseLocked && (
                <div className="flex gap-2">
                  <button onClick={() => {
                    const header = ['Medio', '% del Mix', 'Referencia Inversión', 'Notas']
                    const exampleRows = mktMediaForm.length > 0
                      ? mktMediaForm.map(r => [r.medium_name, r.weight_pct, r.investment_ref, r.notes])
                      : [
                          ['TV abierta', 25, 'USD 500K', ''],
                          ['Digital / Redes Sociales', 35, 'USD 700K', ''],
                          ['Radio', 15, 'USD 300K', ''],
                          ['OOH / Exterior', 10, 'USD 200K', ''],
                          ['Punto de venta', 15, 'USD 300K', 'Trade marketing'],
                        ]
                    const ws = XLSX.utils.aoa_to_sheet([header, ...exampleRows])
                    ws['!cols'] = [{ wch: 22 }, { wch: 14 }, { wch: 22 }, { wch: 22 }]
                    const wb = XLSX.utils.book_new()
                    XLSX.utils.book_append_sheet(wb, ws, 'Mix de Medios')
                    XLSX.writeFile(wb, `plantilla_mix_medios_${section?.name ?? 'mercado'}.xlsx`)
                  }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-xs font-semibold transition-colors">
                    <Download size={13} /> Excel
                  </button>
                  <button onClick={saveMktMedia} disabled={savingMktMedia}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 ${mktMediaSaved ? 'bg-emerald-400 text-white' : 'bg-white text-pink-700 hover:bg-pink-50'}`}>
                    {savingMktMedia ? <><Loader size={14} className="animate-spin" />Guardando...</> : mktMediaSaved ? <><CheckCircle2 size={14} />¡Guardado!</> : <><Save size={14} />Guardar</>}
                  </button>
                </div>
              )}
            </div>

            {/* Progress bar */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-600">Total del mix</p>
                <span className={`text-sm font-bold ${totalOk ? 'text-emerald-600' : totalPct > 100 ? 'text-red-600' : 'text-amber-600'}`}>{Math.round(totalPct * 10) / 10}%{totalOk ? ' ✓' : ''}</span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden flex gap-0.5">
                {mktMediaForm.filter(r => Number(r.weight_pct) > 0).map((r, i) => {
                  const COLORS = ['bg-pink-500','bg-rose-500','bg-fuchsia-500','bg-purple-500','bg-violet-500','bg-indigo-500','bg-blue-500','bg-cyan-500','bg-teal-500','bg-emerald-500']
                  return <div key={i} className={`${COLORS[i % COLORS.length]} h-full transition-all`} style={{ width: `${Math.min(Number(r.weight_pct), 100)}%` }} title={`${r.medium_name}: ${r.weight_pct}%`} />
                })}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {mktMediaForm.filter(r => r.medium_name && Number(r.weight_pct) > 0).map((r, i) => {
                  const COLORS = ['bg-pink-500','bg-rose-500','bg-fuchsia-500','bg-purple-500','bg-violet-500','bg-indigo-500','bg-blue-500','bg-cyan-500','bg-teal-500','bg-emerald-500']
                  return <span key={i} className="flex items-center gap-1 text-xs text-gray-600"><span className={`w-2.5 h-2.5 rounded-full ${COLORS[i % COLORS.length]} inline-block`}/>{r.medium_name} <strong>{r.weight_pct}%</strong></span>
                })}
              </div>
            </div>

            {/* Rows table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="grid grid-cols-12 gap-0 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 px-4 py-2.5">
                <div className="col-span-4">Medio</div>
                <div className="col-span-2 text-center">% del mix</div>
                <div className="col-span-3">Referencia inversión</div>
                <div className="col-span-2">Notas</div>
                <div className="col-span-1" />
              </div>
              <div className="divide-y divide-gray-100">
                {mktMediaForm.map((row, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center hover:bg-gray-50 transition-colors">
                    <div className="col-span-4">
                      <input type="text" value={row.medium_name} onChange={e => updateRow(idx, 'medium_name', e.target.value)}
                        className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-pink-300 bg-white font-medium" />
                    </div>
                    <div className="col-span-2">
                      <div className="relative">
                        <input type="number" min="0" max="100" value={row.weight_pct} onChange={e => updateRow(idx, 'weight_pct', e.target.value)}
                          placeholder="0" className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 pr-6 text-center focus:outline-none focus:ring-2 focus:ring-pink-300 bg-white font-semibold" />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                      </div>
                    </div>
                    <div className="col-span-3">
                      <input type="text" value={row.investment_ref} onChange={e => updateRow(idx, 'investment_ref', e.target.value)}
                        placeholder="Ej: USD 1.2M" className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-pink-300 bg-white" />
                    </div>
                    <div className="col-span-2">
                      <input type="text" value={row.notes} onChange={e => updateRow(idx, 'notes', e.target.value)}
                        placeholder="Opcional" className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-pink-300 bg-white" />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      {(isAdmin || isNVC) && !phaseLocked && (
                        <button onClick={() => removeRow(idx)} className="p-1 text-gray-300 hover:text-red-500 rounded transition-colors"><Trash2 size={14} /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {(isAdmin || isNVC) && !phaseLocked && (
                <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
                  <button onClick={addRow} className="flex items-center gap-1.5 text-sm text-pink-600 hover:text-pink-700 font-semibold transition-colors">
                    <Plus size={15} /> Agregar medio
                  </button>
                </div>
              )}
            </div>

            {renderConclusionBlock('comunicacion', '')}
          </div>
        )
      })()}

      {/* ── Competitor Estrategia de Comunicación Tab ─────────── */}
      {activeTab === 'est_comunicacion' && (() => {
        const mediaTotalPct = compMediaMix.reduce((s, r) => s + (Number(compMediaForms[r.id]) || 0), 0)
        const mediaTotalOk  = Math.round(mediaTotalPct * 10) / 10 === 100

        async function saveAll() {
          setSavingComm(true)
          // 1. Save comm strategy record (upsert)
          const payload = {
            section_id: sectionId, competitor_id: section.competitor_id,
            tono_voz: commForm.tono_voz || null, mensajes_clave: commForm.mensajes_clave || null,
            propuesta_valor_comunicada: commForm.propuesta_valor_comunicada || null,
            nivel_inversion: commForm.nivel_inversion || null, tipo_contenido: commForm.tipo_contenido || null,
            tematicas: commForm.tematicas || null, influencers: commForm.influencers || null,
            campanas_destacadas: commForm.campanas_destacadas || null,
            share_of_voice: commForm.share_of_voice !== '' ? Number(commForm.share_of_voice) : null,
            fortalezas_comunicacion: commForm.fortalezas_comunicacion || null,
            debilidades_comunicacion: commForm.debilidades_comunicacion || null,
            updated_at: new Date().toISOString(),
          }
          if (commStrategy) { await supabase.from('competitor_comm_strategy').update(payload).eq('id', commStrategy.id) }
          else { const { data: nc } = await supabase.from('competitor_comm_strategy').insert(payload).select().single(); if (nc) setCommStrategy(nc) }
          // 2. Save media mix weights
          await Promise.all(compMediaMix.map(r =>
            supabase.from('competitor_media_mix').update({ weight_pct: compMediaForms[r.id] !== '' ? Number(compMediaForms[r.id]) : null }).eq('id', r.id)
          ))
          // 3. Save digital presence
          await Promise.all(digitalPresence.map(r => {
            const f = digitalForms[r.id] ?? {}
            return supabase.from('competitor_digital_presence').update({
              followers: f.followers !== '' ? Number(f.followers) : null,
              engagement_rate: f.engagement_rate !== '' ? Number(f.engagement_rate) : null,
              posts_per_week: f.posts_per_week !== '' ? Number(f.posts_per_week) : null,
              updated_at: new Date().toISOString(),
            }).eq('id', r.id)
          }))
          setSavingComm(false)
          setCommSaved(true)
          setTimeout(() => setCommSaved(false), 2500)
        }

        const TextField = ({ label, field, rows = 3, placeholder }) => (
          <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-600">{label}</label>
            {rows === 1
              ? <input type="text" value={commForm[field] ?? ''} onChange={e => setCommForm(f => ({ ...f, [field]: e.target.value }))} placeholder={placeholder}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-300 bg-white" />
              : <textarea rows={rows} value={commForm[field] ?? ''} onChange={e => setCommForm(f => ({ ...f, [field]: e.target.value }))} placeholder={placeholder}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-300 resize-none bg-white" />
            }
          </div>
        )

        return (
          <div className="space-y-5">
            {/* Header */}
            <div className="rounded-xl bg-gradient-to-r from-pink-600 to-rose-600 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Activity size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-base">Estrategia de Comunicación</h2>
                  <p className="text-pink-100 text-xs mt-0.5">Análisis a nivel unidad de negocio · Referencia de mercado en azul</p>
                </div>
              </div>
              {(isAdmin || isNVC) && !phaseLocked && (
                <button onClick={saveAll} disabled={savingComm}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 ${commSaved ? 'bg-emerald-400 text-white' : 'bg-white text-pink-700 hover:bg-pink-50'}`}>
                  {savingComm ? <><Loader size={14} className="animate-spin" />Guardando...</> : commSaved ? <><CheckCircle2 size={14} />¡Guardado!</> : <><Save size={14} />Guardar todo</>}
                </button>
              )}
            </div>

            {commImporting && (
              <div className="flex items-center gap-3 p-4 bg-pink-50 border border-pink-200 rounded-xl text-pink-700 text-sm">
                <Loader size={16} className="animate-spin flex-shrink-0" /> Preparando datos...
              </div>
            )}

            {/* ─ Bloque 1: Identidad comunicacional ─ */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-2">Identidad comunicacional</p>
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-2">Tono de voz</label>
                <div className="flex gap-2 flex-wrap">
                  {TONO_OPTS.map(t => (
                    <button key={t} type="button" onClick={() => setCommForm(f => ({ ...f, tono_voz: f.tono_voz === t ? '' : t }))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${commForm.tono_voz === t ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-gray-600 border-gray-200 hover:border-pink-300'}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <TextField label="Mensajes clave" field="mensajes_clave" rows={3} placeholder="¿Qué narrativa construyen?" />
                <TextField label="Propuesta de valor comunicada" field="propuesta_valor_comunicada" rows={3} placeholder="¿Qué prometen en su comunicación?" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-2">Nivel de inversión estimado</label>
                <div className="flex gap-2">
                  {INVERSION_OPTS.map(o => (
                    <button key={o} type="button" onClick={() => setCommForm(f => ({ ...f, nivel_inversion: f.nivel_inversion === o ? '' : o }))}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${commForm.nivel_inversion === o
                        ? o === 'Bajo' ? 'bg-emerald-500 text-white border-emerald-500'
                          : o === 'Medio' ? 'bg-blue-500 text-white border-blue-500'
                          : o === 'Alto' ? 'bg-orange-500 text-white border-orange-500'
                          : 'bg-red-500 text-white border-red-500'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-pink-300'}`}>
                      {o}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* ─ Bloque 2: Mix de medios ─ */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Mix de medios</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => {
                    const header = ['Medio', '% Competidor', '% Mercado (ref)']
                    const rows = compMediaMix.map(row => {
                      const mktRef = mktMediaRef.find(m => m.id === row.market_medium_id)
                      return [row.medium_name, compMediaForms[row.id] ?? '', mktRef?.weight_pct ?? '']
                    })
                    const ws = XLSX.utils.aoa_to_sheet([header, ...rows])
                    ws['!cols'] = [{ wch: 22 }, { wch: 18 }, { wch: 18 }]
                    const wb = XLSX.utils.book_new()
                    XLSX.utils.book_append_sheet(wb, ws, 'Mix de Medios')
                    XLSX.writeFile(wb, `mix_medios_${section?.name ?? 'competidor'}.xlsx`)
                  }}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
                    <Download size={11} /> Excel
                  </button>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${mediaTotalOk ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                    Total: {Math.round(mediaTotalPct * 10) / 10}% {mediaTotalOk ? '✓' : ''}
                  </span>
                </div>
              </div>
              {mktMediaRef.length === 0 && (
                <p className="text-xs text-gray-400 italic">Sin referencia de mercado disponible. Crea el mix de medios en la sección Comunicación del Análisis de Mercado.</p>
              )}
              <div className="space-y-2">
                {compMediaMix.map(row => {
                  const mktRef = mktMediaRef.find(m => m.id === row.market_medium_id)
                  return (
                    <div key={row.id} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                      <span className="text-sm font-medium text-gray-700 flex-1">{row.medium_name}</span>
                      {mktRef?.weight_pct != null && (
                        <span className="text-xs text-blue-600 font-semibold bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">Mercado: {mktRef.weight_pct}%</span>
                      )}
                      <div className="relative w-28">
                        <input type="number" min="0" max="100" value={compMediaForms[row.id] ?? ''} onChange={e => setCompMediaForms(p => ({ ...p, [row.id]: e.target.value }))}
                          placeholder="0"
                          className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 pr-7 text-center focus:outline-none focus:ring-2 focus:ring-pink-300 bg-white font-semibold" />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* ─ Bloque 3: Presencia digital ─ */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-2">Presencia digital por plataforma</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 font-semibold border-b border-gray-100">
                      <th className="text-left py-2 pr-4">Plataforma</th>
                      <th className="text-center py-2 px-2">Seguidores</th>
                      <th className="text-center py-2 px-2">Engagement %</th>
                      <th className="text-center py-2 px-2">Posts / semana</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {digitalPresence.map(row => {
                      const f = digitalForms[row.id] ?? {}
                      return (
                        <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                          <td className="py-2 pr-4 font-medium text-gray-700">{row.platform}</td>
                          <td className="py-2 px-2">
                            <input type="number" value={f.followers ?? ''} onChange={e => setDigitalForms(p => ({ ...p, [row.id]: { ...f, followers: e.target.value } }))}
                              placeholder="—" className="w-full text-center text-sm border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-pink-300 bg-white" />
                          </td>
                          <td className="py-2 px-2">
                            <div className="relative">
                              <input type="number" min="0" max="100" step="0.1" value={f.engagement_rate ?? ''} onChange={e => setDigitalForms(p => ({ ...p, [row.id]: { ...f, engagement_rate: e.target.value } }))}
                                placeholder="—" className="w-full text-center text-sm border border-gray-200 rounded-lg px-2 py-1 pr-5 focus:outline-none focus:ring-2 focus:ring-pink-300 bg-white" />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            <input type="number" step="0.5" value={f.posts_per_week ?? ''} onChange={e => setDigitalForms(p => ({ ...p, [row.id]: { ...f, posts_per_week: e.target.value } }))}
                              placeholder="—" className="w-full text-center text-sm border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-pink-300 bg-white" />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ─ Bloque 4: Contenido y creatividad ─ */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-2">Contenido y creatividad</p>
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-2">Tipo de contenido predominante</label>
                <div className="flex gap-2 flex-wrap">
                  {CONTENIDO_OPTS.map(c => (
                    <button key={c} type="button" onClick={() => setCommForm(f => ({ ...f, tipo_contenido: f.tipo_contenido === c ? '' : c }))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${commForm.tipo_contenido === c ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-gray-600 border-gray-200 hover:border-pink-300'}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <TextField label="Temáticas principales" field="tematicas" rows={2} placeholder="Temas recurrentes en su comunicación..." />
                <TextField label="Influencers / Embajadores" field="influencers" rows={2} placeholder="¿Usan influencers? ¿Quiénes?" />
              </div>
            </div>

            {/* ─ Bloque 5: Análisis estratégico ─ */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-2">Análisis estratégico</p>
              <TextField label="Campañas o iniciativas destacadas" field="campanas_destacadas" rows={2} placeholder="Campañas relevantes, activaciones, etc..." />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1">Share of Voice estimado (%)</label>
                  <div className="relative">
                    <input type="number" min="0" max="100" value={commForm.share_of_voice ?? ''} onChange={e => setCommForm(f => ({ ...f, share_of_voice: e.target.value }))}
                      placeholder="0" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-pink-300 bg-white" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-medium">%</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <TextField label="Fortalezas de comunicación" field="fortalezas_comunicacion" rows={3} placeholder="¿Qué hacen bien en comunicación?" />
                <TextField label="Debilidades / oportunidades" field="debilidades_comunicacion" rows={3} placeholder="¿Qué gaps o debilidades se identifican?" />
              </div>
            </div>

            {renderConclusionBlock('est_comunicacion', '')}
          </div>
        )
      })()}

      {/* ── BU Internal Comunicación — identical to competitor comm ── */}
      {activeTab === 'bu_comm' && (() => {
        const mediaTotalPct = buMediaMix.reduce((s, r) => s + (Number(buMediaForms[r.id]) || 0), 0)
        const mediaTotalOk  = Math.round(mediaTotalPct * 10) / 10 === 100

        async function saveAll() {
          setSavingBuComm(true)
          const payload = {
            section_id: sectionId,
            tono_voz: buCommForm.tono_voz || null, mensajes_clave: buCommForm.mensajes_clave || null,
            propuesta_valor_comunicada: buCommForm.propuesta_valor_comunicada || null,
            nivel_inversion: buCommForm.nivel_inversion || null, tipo_contenido: buCommForm.tipo_contenido || null,
            tematicas: buCommForm.tematicas || null, influencers: buCommForm.influencers || null,
            campanas_destacadas: buCommForm.campanas_destacadas || null,
            share_of_voice: buCommForm.share_of_voice !== '' ? Number(buCommForm.share_of_voice) : null,
            fortalezas_comunicacion: buCommForm.fortalezas_comunicacion || null,
            debilidades_comunicacion: buCommForm.debilidades_comunicacion || null,
            updated_at: new Date().toISOString(),
          }
          if (buCommStrategy) { await supabase.from('bu_comm_strategy').update(payload).eq('id', buCommStrategy.id) }
          else { const { data: nb } = await supabase.from('bu_comm_strategy').insert(payload).select().single(); if (nb) setBuCommStrategy(nb) }
          await Promise.all(buMediaMix.map(r =>
            supabase.from('bu_media_mix').update({ weight_pct: buMediaForms[r.id] !== '' ? Number(buMediaForms[r.id]) : null }).eq('id', r.id)
          ))
          await Promise.all(buDigitalPresence.map(r => {
            const f = buDigitalForms[r.id] ?? {}
            return supabase.from('bu_digital_presence').update({
              followers: f.followers !== '' ? Number(f.followers) : null,
              engagement_rate: f.engagement_rate !== '' ? Number(f.engagement_rate) : null,
              posts_per_week: f.posts_per_week !== '' ? Number(f.posts_per_week) : null,
              updated_at: new Date().toISOString(),
            }).eq('id', r.id)
          }))
          setSavingBuComm(false)
          setBuCommSaved(true)
          setTimeout(() => setBuCommSaved(false), 2500)
        }

        const TextField = ({ label, field, rows = 3, placeholder }) => (
          <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-600">{label}</label>
            {rows === 1
              ? <input type="text" value={buCommForm[field] ?? ''} onChange={e => setBuCommForm(f => ({ ...f, [field]: e.target.value }))} placeholder={placeholder}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-300 bg-white" />
              : <textarea rows={rows} value={buCommForm[field] ?? ''} onChange={e => setBuCommForm(f => ({ ...f, [field]: e.target.value }))} placeholder={placeholder}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-300 resize-none bg-white" />}
          </div>
        )

        return (
          <div className="space-y-5">
            {/* Header */}
            <div className="rounded-xl bg-gradient-to-r from-violet-600 to-purple-700 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Activity size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-base">Estrategia de Comunicación</h2>
                  <p className="text-violet-100 text-xs mt-0.5">Análisis a nivel unidad de negocio · Referencia de mercado en azul</p>
                </div>
              </div>
              {(isAdmin || isNVC) && !phaseLocked && (
                <button onClick={saveAll} disabled={savingBuComm}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 ${buCommSaved ? 'bg-emerald-400 text-white' : 'bg-white text-violet-700 hover:bg-violet-50'}`}>
                  {savingBuComm ? <><Loader size={14} className="animate-spin" />Guardando...</> : buCommSaved ? <><CheckCircle2 size={14} />¡Guardado!</> : <><Save size={14} />Guardar todo</>}
                </button>
              )}
            </div>

            {/* ─ Bloque 1: Identidad comunicacional ─ */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-2">Identidad comunicacional</p>
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-2">Tono de voz</label>
                <div className="flex gap-2 flex-wrap">
                  {TONO_OPTS.map(t => (
                    <button key={t} type="button" onClick={() => setBuCommForm(f => ({ ...f, tono_voz: f.tono_voz === t ? '' : t }))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${buCommForm.tono_voz === t ? 'bg-violet-500 text-white border-violet-500' : 'bg-white text-gray-600 border-gray-200 hover:border-violet-300'}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <TextField label="Mensajes clave" field="mensajes_clave" rows={3} placeholder="¿Qué narrativa construyen?" />
                <TextField label="Propuesta de valor comunicada" field="propuesta_valor_comunicada" rows={3} placeholder="¿Qué prometen en su comunicación?" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-2">Nivel de inversión estimado</label>
                <div className="flex gap-2">
                  {INVERSION_OPTS.map(o => (
                    <button key={o} type="button" onClick={() => setBuCommForm(f => ({ ...f, nivel_inversion: f.nivel_inversion === o ? '' : o }))}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${buCommForm.nivel_inversion === o
                        ? o === 'Bajo' ? 'bg-emerald-500 text-white border-emerald-500'
                          : o === 'Medio' ? 'bg-blue-500 text-white border-blue-500'
                          : o === 'Alto' ? 'bg-orange-500 text-white border-orange-500'
                          : 'bg-red-500 text-white border-red-500'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-violet-300'}`}>
                      {o}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* ─ Bloque 2: Mix de medios ─ */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Mix de medios</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => {
                    const header = ['Medio', '% Nuestra Marca', '% Mercado (ref)']
                    const rows = buMediaMix.map(row => {
                      const mktRef = mktMediaRef.find(m => m.medium_name?.toLowerCase() === row.medium_name?.toLowerCase())
                      return [row.medium_name, buMediaForms[row.id] ?? '', mktRef?.weight_pct ?? '']
                    })
                    const ws = XLSX.utils.aoa_to_sheet([header, ...rows])
                    ws['!cols'] = [{ wch: 22 }, { wch: 20 }, { wch: 18 }]
                    const wb = XLSX.utils.book_new()
                    XLSX.utils.book_append_sheet(wb, ws, 'Mix de Medios')
                    XLSX.writeFile(wb, `mix_medios_${section?.name ?? 'bu'}.xlsx`)
                  }}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
                    <Download size={11} /> Excel
                  </button>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${mediaTotalOk ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                    Total: {Math.round(mediaTotalPct * 10) / 10}% {mediaTotalOk ? '✓' : ''}
                  </span>
                </div>
              </div>
              {mktMediaRef.length === 0 && (
                <p className="text-xs text-gray-400 italic">Sin referencia de mercado disponible. Crea el mix de medios en la sección Comunicación del Análisis de Mercado.</p>
              )}
              <div className="space-y-2">
                {buMediaMix.map(row => {
                  const mktRef = mktMediaRef.find(m => m.medium_name?.toLowerCase() === row.medium_name?.toLowerCase())
                  return (
                    <div key={row.id} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                      <span className="text-sm font-medium text-gray-700 flex-1">{row.medium_name}</span>
                      {mktRef?.weight_pct != null && (
                        <span className="text-xs text-blue-600 font-semibold bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">Mercado: {mktRef.weight_pct}%</span>
                      )}
                      <div className="relative w-28">
                        <input type="number" min="0" max="100" value={buMediaForms[row.id] ?? ''} onChange={e => setBuMediaForms(p => ({ ...p, [row.id]: e.target.value }))}
                          placeholder="0"
                          className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 pr-7 text-center focus:outline-none focus:ring-2 focus:ring-violet-300 bg-white font-semibold" />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* ─ Bloque 3: Presencia digital ─ */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-2">Presencia digital por plataforma</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 font-semibold border-b border-gray-100">
                      <th className="text-left py-2 pr-4">Plataforma</th>
                      <th className="text-center py-2 px-2">Seguidores</th>
                      <th className="text-center py-2 px-2">Engagement %</th>
                      <th className="text-center py-2 px-2">Posts / semana</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {buDigitalPresence.map(row => {
                      const f = buDigitalForms[row.id] ?? {}
                      return (
                        <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                          <td className="py-2 pr-4 font-medium text-gray-700">{row.platform}</td>
                          <td className="py-2 px-2">
                            <input type="number" value={f.followers ?? ''} onChange={e => setBuDigitalForms(p => ({ ...p, [row.id]: { ...f, followers: e.target.value } }))}
                              placeholder="—" className="w-full text-center text-sm border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-violet-300 bg-white" />
                          </td>
                          <td className="py-2 px-2">
                            <div className="relative">
                              <input type="number" min="0" max="100" step="0.1" value={f.engagement_rate ?? ''} onChange={e => setBuDigitalForms(p => ({ ...p, [row.id]: { ...f, engagement_rate: e.target.value } }))}
                                placeholder="—" className="w-full text-center text-sm border border-gray-200 rounded-lg px-2 py-1 pr-5 focus:outline-none focus:ring-2 focus:ring-violet-300 bg-white" />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            <input type="number" step="0.5" value={f.posts_per_week ?? ''} onChange={e => setBuDigitalForms(p => ({ ...p, [row.id]: { ...f, posts_per_week: e.target.value } }))}
                              placeholder="—" className="w-full text-center text-sm border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-violet-300 bg-white" />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ─ Bloque 4: Contenido y creatividad ─ */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-2">Contenido y creatividad</p>
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-2">Tipo de contenido predominante</label>
                <div className="flex gap-2 flex-wrap">
                  {CONTENIDO_OPTS.map(c => (
                    <button key={c} type="button" onClick={() => setBuCommForm(f => ({ ...f, tipo_contenido: f.tipo_contenido === c ? '' : c }))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${buCommForm.tipo_contenido === c ? 'bg-violet-500 text-white border-violet-500' : 'bg-white text-gray-600 border-gray-200 hover:border-violet-300'}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <TextField label="Temáticas principales" field="tematicas" rows={2} placeholder="Temas recurrentes en su comunicación..." />
                <TextField label="Influencers / Embajadores" field="influencers" rows={2} placeholder="¿Usan influencers? ¿Quiénes?" />
              </div>
            </div>

            {/* ─ Bloque 5: Análisis estratégico ─ */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-2">Análisis estratégico</p>
              <TextField label="Campañas o iniciativas destacadas" field="campanas_destacadas" rows={2} placeholder="Campañas relevantes, activaciones, etc..." />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1">Share of Voice estimado (%)</label>
                  <div className="relative">
                    <input type="number" min="0" max="100" value={buCommForm.share_of_voice ?? ''} onChange={e => setBuCommForm(f => ({ ...f, share_of_voice: e.target.value }))}
                      placeholder="0" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-violet-300 bg-white" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-medium">%</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <TextField label="Fortalezas de comunicación" field="fortalezas_comunicacion" rows={3} placeholder="¿Qué hacen bien en comunicación?" />
                <TextField label="Debilidades / oportunidades" field="debilidades_comunicacion" rows={3} placeholder="¿Qué gaps o debilidades se identifican?" />
              </div>
            </div>

            {renderConclusionBlock('bu_comunicacion', '')}
          </div>
        )
      })()}

      {/* ── Consumer Needs & Tensions Tab ────────────────────── */}
      {activeTab === 'necesidades' && (() => {
        const activeForm = needsForms[activeNeedsId] ?? NEEDS_EMPTY_FORM
        const setForm = updater => setNeedsForms(prev => ({ ...prev, [activeNeedsId]: typeof updater === 'function' ? updater(prev[activeNeedsId] ?? NEEDS_EMPTY_FORM) : updater }))

        async function saveProfile() {
          if (!activeNeedsId) return
          setSavingNeeds(true)
          const f = activeForm
          const payload = { profile_name: f.profile_name, bloque1_aplica: f.bloque1_aplica, necesidades_funcionales: f.necesidades_funcionales, bloque2_aplica: f.bloque2_aplica, necesidades_emocionales: f.necesidades_emocionales, bloque3_aplica: f.bloque3_aplica, tensiones: f.tensiones, bloque4_aplica: f.bloque4_aplica, jtbd_descripcion: f.jtbd_descripcion || null, jtbd_cuando: f.jtbd_cuando || null, jtbd_alternativas: f.jtbd_alternativas || null, bloque5_aplica: f.bloque5_aplica, insight_clave: f.insight_clave || null, updated_at: new Date().toISOString() }
          await supabase.from('consumer_needs_tensions').update(payload).eq('id', activeNeedsId)
          setNeedsProfiles(p => p.map(x => x.id === activeNeedsId ? { ...x, profile_name: f.profile_name } : x))
          setSavingNeeds(false); setNeedsSaved(true); setTimeout(() => setNeedsSaved(false), 2500)
        }

        function addNeed(tipo) {
          const item = { id: Date.now().toString(), descripcion: '', importancia: 'Media' }
          const key = tipo === 'funcional' ? 'necesidades_funcionales' : 'necesidades_emocionales'
          setForm(f => ({ ...f, [key]: [...(f[key] ?? []), item] }))
        }
        function updateNeed(tipo, id, field, val) {
          const key = tipo === 'funcional' ? 'necesidades_funcionales' : 'necesidades_emocionales'
          setForm(f => ({ ...f, [key]: (f[key] ?? []).map(x => x.id === id ? { ...x, [field]: val } : x) }))
        }
        function removeNeed(tipo, id) {
          const key = tipo === 'funcional' ? 'necesidades_funcionales' : 'necesidades_emocionales'
          setForm(f => ({ ...f, [key]: (f[key] ?? []).filter(x => x.id !== id) }))
        }
        function addTension() {
          setForm(f => ({ ...f, tensiones: [...(f.tensiones ?? []), { id: Date.now().toString(), descripcion: '', origen: '', intensidad: 'Media' }] }))
        }
        function updateTension(id, field, val) {
          setForm(f => ({ ...f, tensiones: (f.tensiones ?? []).map(x => x.id === id ? { ...x, [field]: val } : x) }))
        }
        function removeTension(id) {
          setForm(f => ({ ...f, tensiones: (f.tensiones ?? []).filter(x => x.id !== id) }))
        }

        const importanciaColor = i => i === 'Alta' ? 'bg-red-100 text-red-700 border-red-300' : i === 'Media' ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-gray-100 text-gray-600 border-gray-300'
        const intensidadColor = i => i === 'Alta' ? 'bg-red-500 text-white border-red-500' : i === 'Media' ? 'bg-amber-500 text-white border-amber-500' : 'bg-gray-400 text-white border-gray-400'

        const BloqueHeader = ({ label, bloqueKey }) => (
          <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-4">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">{label}</p>
            <div className="flex gap-1">
              {['Aplica', 'No aplica'].map(opt => (
                <button key={opt} type="button" onClick={() => setForm(f => ({ ...f, [bloqueKey]: opt === 'Aplica' }))}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${(activeForm[bloqueKey] ? 'Aplica' : 'No aplica') === opt ? opt === 'Aplica' ? 'bg-purple-500 text-white border-purple-500' : 'bg-gray-400 text-white border-gray-400' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`}>
                  {opt}
                </button>
              ))}
            </div>
          </div>
        )

        const NeedsList = ({ tipo }) => {
          const key = tipo === 'funcional' ? 'necesidades_funcionales' : 'necesidades_emocionales'
          const items = activeForm[key] ?? []
          return (
            <div className="space-y-2">
              {items.map(item => (
                <div key={item.id} className="flex items-start gap-2 p-2.5 rounded-lg bg-gray-50 border border-gray-100 group">
                  <input type="text" value={item.descripcion} onChange={e => updateNeed(tipo, item.id, 'descripcion', e.target.value)}
                    placeholder="Describe la necesidad..." className="flex-1 text-sm bg-transparent focus:outline-none text-gray-700" />
                  <div className="flex gap-1 flex-shrink-0">
                    {IMPORTANCIA_OPTS.map(o => (
                      <button key={o} type="button" onClick={() => updateNeed(tipo, item.id, 'importancia', o)}
                        className={`px-2 py-0.5 rounded text-xs font-semibold border transition-colors ${item.importancia === o ? importanciaColor(o) : 'bg-white text-gray-400 border-gray-200 hover:border-gray-400'}`}>
                        {o}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => removeNeed(tipo, item.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all flex-shrink-0 mt-0.5">
                    <X size={13} />
                  </button>
                </div>
              ))}
              <button onClick={() => addNeed(tipo)} className="flex items-center gap-1.5 text-xs font-semibold text-purple-600 hover:text-purple-700 transition-colors mt-1">
                <Plus size={13} /> Agregar necesidad
              </button>
            </div>
          )
        }

        return !activeNeedsId ? null : (
          <div className="space-y-4">
            {/* Profile header */}
            <div className="rounded-xl bg-gradient-to-r from-purple-600 to-violet-700 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <User size={18} className="text-white" />
                </div>
                <span className="text-white font-semibold text-base">{activeForm.profile_name || section?.name || 'Perfil'}</span>
              </div>
              {(isAdmin || isNVC) && !phaseLocked && (
                <button onClick={saveProfile} disabled={savingNeeds}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 flex-shrink-0 ${needsSaved ? 'bg-emerald-400 text-white' : 'bg-white text-purple-700 hover:bg-purple-50'}`}>
                  {savingNeeds ? <><Loader size={14} className="animate-spin" />Guardando...</> : needsSaved ? <><CheckCircle2 size={14} />¡Guardado!</> : <><Save size={14} />Guardar</>}
                </button>
              )}
            </div>

                  {/* Bloque 1: Necesidades funcionales */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <BloqueHeader label="Necesidades funcionales" bloqueKey="bloque1_aplica" />
                    {activeForm.bloque1_aplica ? <NeedsList tipo="funcional" /> : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
                  </div>

                  {/* Bloque 2: Necesidades emocionales */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <BloqueHeader label="Necesidades emocionales" bloqueKey="bloque2_aplica" />
                    {activeForm.bloque2_aplica ? <NeedsList tipo="emocional" /> : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
                  </div>

                  {/* Bloque 3: Tensiones */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <BloqueHeader label="Tensiones" bloqueKey="bloque3_aplica" />
                    {activeForm.bloque3_aplica ? (
                      <div className="space-y-2">
                        {(activeForm.tensiones ?? []).map(t => (
                          <div key={t.id} className="p-3 rounded-lg bg-gray-50 border border-gray-100 group space-y-2">
                            <div className="flex items-start gap-2">
                              <input type="text" value={t.descripcion} onChange={e => updateTension(t.id, 'descripcion', e.target.value)}
                                placeholder="Describe la tensión..." className="flex-1 text-sm bg-transparent focus:outline-none text-gray-700" />
                              <button onClick={() => removeTension(t.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all flex-shrink-0">
                                <X size={13} />
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <div className="flex gap-1 flex-wrap">
                                {TENSION_ORIGEN_OPTS.map(o => (
                                  <button key={o} type="button" onClick={() => updateTension(t.id, 'origen', t.origen === o ? '' : o)}
                                    className={`px-2 py-0.5 rounded text-xs font-semibold border transition-colors ${t.origen === o ? 'bg-purple-500 text-white border-purple-500' : 'bg-white text-gray-500 border-gray-200 hover:border-purple-300'}`}>
                                    {o}
                                  </button>
                                ))}
                              </div>
                              <div className="flex gap-1 ml-auto">
                                {['Alta', 'Media', 'Baja'].map(o => (
                                  <button key={o} type="button" onClick={() => updateTension(t.id, 'intensidad', o)}
                                    className={`px-2 py-0.5 rounded text-xs font-semibold border transition-colors ${t.intensidad === o ? intensidadColor(o) : 'bg-white text-gray-400 border-gray-200 hover:border-gray-400'}`}>
                                    {o}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                        <button onClick={addTension} className="flex items-center gap-1.5 text-xs font-semibold text-purple-600 hover:text-purple-700 transition-colors mt-1">
                          <Plus size={13} /> Agregar tensión
                        </button>
                      </div>
                    ) : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
                  </div>

                  {/* Bloque 4: Jobs to be done */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <BloqueHeader label="Jobs to be done" bloqueKey="bloque4_aplica" />
                    {activeForm.bloque4_aplica ? (
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs font-semibold text-gray-600 block mb-1">¿Qué trabajo le contrata al producto/servicio?</label>
                          <textarea rows={2} value={activeForm.jtbd_descripcion} onChange={e => setForm(f => ({ ...f, jtbd_descripcion: e.target.value }))}
                            placeholder='Ej: "Necesito sentirme seguro de que mi familia está bien alimentada"'
                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-300 resize-none bg-white" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">¿Cuándo / en qué contexto?</label>
                            <textarea rows={2} value={activeForm.jtbd_cuando} onChange={e => setForm(f => ({ ...f, jtbd_cuando: e.target.value }))}
                              placeholder="Situación de uso, momento del día, contexto..." className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-300 resize-none bg-white" />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">¿Con qué alternativas compite?</label>
                            <textarea rows={2} value={activeForm.jtbd_alternativas} onChange={e => setForm(f => ({ ...f, jtbd_alternativas: e.target.value }))}
                              placeholder="Sustitutos, workarounds que usa el consumidor..." className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-300 resize-none bg-white" />
                          </div>
                        </div>
                      </div>
                    ) : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
                  </div>

                  {/* Bloque 5: Insight clave */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <BloqueHeader label="Insight clave" bloqueKey="bloque5_aplica" />
                    {activeForm.bloque5_aplica ? (
                      <div>
                        <label className="text-xs font-semibold text-gray-600 block mb-1">Verdad humana subyacente</label>
                        <textarea rows={3} value={activeForm.insight_clave} onChange={e => setForm(f => ({ ...f, insight_clave: e.target.value }))}
                          placeholder='"El consumidor X siente Y cuando Z porque W..."'
                          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-300 resize-none bg-white" />
                      </div>
                    ) : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
                  </div>

            {renderConclusionBlock('necesidades_tensiones', '')}
          </div>
        )
      })()}

      {/* ── Adopción de Marca y Nivel de Lealtad Tab ─────────── */}
      {activeTab === 'adopcion_marca' && (() => {
        const activeForm = brandForms[activeBrandId] ?? BRAND_ADOPTION_EMPTY_FORM
        const setForm = updater => setBrandForms(prev => ({ ...prev, [activeBrandId]: typeof updater === 'function' ? updater(prev[activeBrandId] ?? BRAND_ADOPTION_EMPTY_FORM) : updater }))

        async function saveProfile() {
          if (!activeBrandId) return
          setSavingBrand(true)
          const f = activeForm
          const payload = { profile_name: f.profile_name, bloque1_aplica: f.bloque1_aplica, atributos_marca: f.atributos_marca ?? [], velocidad_adopcion: f.velocidad_adopcion || null, bloque2_aplica: f.bloque2_aplica, nivel_lealtad: f.nivel_lealtad || null, razones_fidelidad: f.razones_fidelidad || null, bloque3_aplica: f.bloque3_aplica, drivers_cambio: f.drivers_cambio || null, frecuencia_cambio: f.frecuencia_cambio || null, bloque4_aplica: f.bloque4_aplica, influenciadores: f.influenciadores || null, updated_at: new Date().toISOString() }
          await supabase.from('consumer_brand_adoption').update(payload).eq('id', activeBrandId)
          setBrandProfiles(p => p.map(x => x.id === activeBrandId ? { ...x, profile_name: f.profile_name } : x))
          setSavingBrand(false); setBrandSaved(true); setTimeout(() => setBrandSaved(false), 2500)
        }

        const lealtadColor = n => ({ 'Muy leal': 'bg-indigo-700 text-white border-indigo-700', 'Leal': 'bg-indigo-500 text-white border-indigo-500', 'Indiferente': 'bg-gray-400 text-white border-gray-400', 'Propenso a cambiar': 'bg-orange-400 text-white border-orange-400', 'Cambia frecuentemente': 'bg-red-500 text-white border-red-500' }[n] ?? '')
        const atributoImportanciaColor = n => n === 'Alta' ? 'bg-indigo-600 text-white border-indigo-600' : n === 'Media' ? 'bg-amber-400 text-white border-amber-400' : 'bg-gray-300 text-gray-600 border-gray-300'

        function addAtributo() { setForm(f => ({ ...f, atributos_marca: [...(f.atributos_marca ?? []), { id: Date.now().toString(), tipo: '', descripcion: '', importancia: 'Media' }] })) }
        function updateAtributo(id, field, val) { setForm(f => ({ ...f, atributos_marca: (f.atributos_marca ?? []).map(x => x.id === id ? { ...x, [field]: val } : x) })) }
        function removeAtributo(id) { setForm(f => ({ ...f, atributos_marca: (f.atributos_marca ?? []).filter(x => x.id !== id) })) }

        const BloqueHeader = ({ label, bloqueKey }) => (
          <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-4">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">{label}</p>
            <div className="flex gap-1">
              {['Aplica', 'No aplica'].map(opt => (
                <button key={opt} type="button" onClick={() => setForm(f => ({ ...f, [bloqueKey]: opt === 'Aplica' }))}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${(activeForm[bloqueKey] ? 'Aplica' : 'No aplica') === opt ? opt === 'Aplica' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-gray-400 text-white border-gray-400' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`}>
                  {opt}
                </button>
              ))}
            </div>
          </div>
        )

        const Selector = ({ field, opts, colorFn }) => (
          <div className="flex gap-2 flex-wrap">
            {opts.map(o => (
              <button key={o} type="button" onClick={() => setForm(f => ({ ...f, [field]: f[field] === o ? '' : o }))}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${activeForm[field] === o ? (colorFn ? colorFn(o) : 'bg-indigo-600 text-white border-indigo-600') : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:text-indigo-700'}`}>
                {o}
              </button>
            ))}
          </div>
        )

        const TextArea = ({ field, label, placeholder, rows = 3 }) => (
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">{label}</label>
            <textarea rows={rows} value={activeForm[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
              placeholder={placeholder}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none bg-white" />
          </div>
        )

        return !activeBrandId ? null : (
          <div className="space-y-4">
            {/* Header */}
            <div className="rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Trophy size={18} className="text-white" />
                </div>
                <span className="text-white font-semibold text-base">{activeForm.profile_name || section?.name || 'Perfil'}</span>
              </div>
              {(isAdmin || isNVC) && !phaseLocked && (
                <button onClick={saveProfile} disabled={savingBrand}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 flex-shrink-0 ${brandSaved ? 'bg-emerald-400 text-white' : 'bg-white text-indigo-700 hover:bg-indigo-50'}`}>
                  {savingBrand ? <><Loader size={14} className="animate-spin" />Guardando...</> : brandSaved ? <><CheckCircle2 size={14} />¡Guardado!</> : <><Save size={14} />Guardar</>}
                </button>
              )}
            </div>

                  {/* Bloque 1: Patrón de adopción de marca */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <BloqueHeader label="Patrón de adopción de marca" bloqueKey="bloque1_aplica" />
                    {activeForm.bloque1_aplica ? (
                      <div className="space-y-4">
                        {/* Atributos de marca que valora al elegir */}
                        <div>
                          <label className="text-xs font-semibold text-gray-600 block mb-2">Atributos que valora al elegir una marca</label>
                          <div className="space-y-2">
                            {(activeForm.atributos_marca ?? []).map(item => (
                              <div key={item.id} className="p-3 rounded-lg bg-gray-50 border border-gray-100 group space-y-2">
                                <div className="flex items-start gap-2">
                                  <input type="text" value={item.descripcion} onChange={e => updateAtributo(item.id, 'descripcion', e.target.value)}
                                    placeholder="¿Qué le importa de este atributo al elegir la marca?..."
                                    className="flex-1 text-sm bg-transparent focus:outline-none text-gray-700" />
                                  <button onClick={() => removeAtributo(item.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all flex-shrink-0">
                                    <X size={13} />
                                  </button>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <div className="flex gap-1 flex-wrap flex-1">
                                    {ATRIBUTO_MARCA_TIPO_OPTS.map(o => (
                                      <button key={o} type="button" onClick={() => updateAtributo(item.id, 'tipo', item.tipo === o ? '' : o)}
                                        className={`px-2 py-0.5 rounded text-xs font-semibold border transition-colors ${item.tipo === o ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-500 border-gray-200 hover:border-indigo-300 hover:text-indigo-700'}`}>
                                        {o}
                                      </button>
                                    ))}
                                  </div>
                                  <div className="flex gap-1 flex-shrink-0">
                                    {['Alta', 'Media', 'Baja'].map(o => (
                                      <button key={o} type="button" onClick={() => updateAtributo(item.id, 'importancia', o)}
                                        className={`px-2 py-0.5 rounded text-xs font-semibold border transition-colors ${item.importancia === o ? atributoImportanciaColor(o) : 'bg-white text-gray-400 border-gray-200 hover:border-gray-400'}`}>
                                        {o}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ))}
                            <button onClick={addAtributo} className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors mt-1">
                              <Plus size={13} /> Agregar atributo
                            </button>
                          </div>
                        </div>
                        {/* Velocidad de adopción */}
                        <div>
                          <label className="text-xs font-semibold text-gray-600 block mb-2">Velocidad de adopción</label>
                          <Selector field="velocidad_adopcion" opts={VELOCIDAD_OPTS} />
                        </div>
                      </div>
                    ) : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
                  </div>

                  {/* Bloque 2: Nivel de lealtad */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <BloqueHeader label="Nivel de lealtad una vez elige" bloqueKey="bloque2_aplica" />
                    {activeForm.bloque2_aplica ? (
                      <div className="space-y-4">
                        <div>
                          <label className="text-xs font-semibold text-gray-600 block mb-2">Nivel de lealtad</label>
                          <Selector field="nivel_lealtad" opts={LEALTAD_OPTS} colorFn={lealtadColor} />
                        </div>
                        <TextArea field="razones_fidelidad" label="¿Qué lo mantiene fiel a una marca?" placeholder="Hábito, confianza, relación calidad-precio, experiencia acumulada, identidad, comunidad..." />
                      </div>
                    ) : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
                  </div>

                  {/* Bloque 3: Drivers de cambio de marca */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <BloqueHeader label="Drivers de cambio de marca" bloqueKey="bloque3_aplica" />
                    {activeForm.bloque3_aplica ? (
                      <div className="space-y-4">
                        <TextArea field="drivers_cambio" label="¿Qué lo hace abandonar una marca que ya usa?" placeholder="Mala experiencia, precio, falta de disponibilidad, nueva opción superior, cambio de contexto..." rows={3} />
                        <div>
                          <label className="text-xs font-semibold text-gray-600 block mb-2">¿Con qué frecuencia cambia de marca?</label>
                          <Selector field="frecuencia_cambio" opts={FREC_CAMBIO_OPTS} />
                        </div>
                      </div>
                    ) : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
                  </div>

                  {/* Bloque 4: Influenciadores en la decisión */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <BloqueHeader label="Influenciadores en la decisión de marca" bloqueKey="bloque4_aplica" />
                    {activeForm.bloque4_aplica ? (
                      <TextArea field="influenciadores" label="¿Quién o qué influye más en su elección de marca?" placeholder="Familia, pares, redes sociales, expertos, publicidad, experiencia propia, reviews, punto de venta..." rows={3} />
                    ) : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
                  </div>

            {renderConclusionBlock('adopcion_marca', '')}
          </div>
        )
      })()}

      {/* ── Adopción de la Categoría Tab ─────────────────────── */}
      {activeTab === 'adopcion_categoria' && (() => {
        const activeForm = adoptForms[activeAdoptId] ?? ADOPTION_EMPTY_FORM
        const setForm = updater => setAdoptForms(prev => ({ ...prev, [activeAdoptId]: typeof updater === 'function' ? updater(prev[activeAdoptId] ?? ADOPTION_EMPTY_FORM) : updater }))

        async function saveProfile() {
          if (!activeAdoptId) return
          setSavingAdopt(true)
          const f = activeForm
          const payload = { profile_name: f.profile_name, bloque1_aplica: f.bloque1_aplica, penetracion_pct: f.penetracion_pct !== '' ? parseFloat(f.penetracion_pct) || null : null, frecuencia_compra: f.frecuencia_compra || null, volumen_por_ocasion: f.volumen_por_ocasion || null, bloque2_aplica: f.bloque2_aplica, satisfaccion_nivel: f.satisfaccion_nivel || null, satisfaccion_gaps: f.satisfaccion_gaps || null, bloque3_aplica: f.bloque3_aplica, riesgos_abandono: f.riesgos_abandono || null, bloque4_aplica: f.bloque4_aplica, influenciadores: f.influenciadores ?? [], updated_at: new Date().toISOString() }
          await supabase.from('consumer_category_adoption').update(payload).eq('id', activeAdoptId)
          setAdoptProfiles(p => p.map(x => x.id === activeAdoptId ? { ...x, profile_name: f.profile_name } : x))
          setSavingAdopt(false); setAdoptSaved(true); setTimeout(() => setAdoptSaved(false), 2500)
        }

        const satisfaccionColor = n => ({ 'Muy alta': 'bg-emerald-600 text-white border-emerald-600', 'Alta': 'bg-emerald-400 text-white border-emerald-400', 'Media': 'bg-amber-400 text-white border-amber-400', 'Baja': 'bg-red-400 text-white border-red-400', 'Muy baja': 'bg-red-600 text-white border-red-600' }[n] ?? '')
        const influImportanciaColor = n => n === 'Alta' ? 'bg-emerald-600 text-white border-emerald-600' : n === 'Media' ? 'bg-amber-400 text-white border-amber-400' : 'bg-gray-300 text-gray-600 border-gray-300'

        function addInfluenciador() { setForm(f => ({ ...f, influenciadores: [...(f.influenciadores ?? []), { id: Date.now().toString(), tipo: '', descripcion: '', importancia: 'Media' }] })) }
        function updateInfluenciador(id, field, val) { setForm(f => ({ ...f, influenciadores: (f.influenciadores ?? []).map(x => x.id === id ? { ...x, [field]: val } : x) })) }
        function removeInfluenciador(id) { setForm(f => ({ ...f, influenciadores: (f.influenciadores ?? []).filter(x => x.id !== id) })) }

        const BloqueHeader = ({ label, bloqueKey }) => (
          <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-4">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">{label}</p>
            <div className="flex gap-1">
              {['Aplica', 'No aplica'].map(opt => (
                <button key={opt} type="button" onClick={() => setForm(f => ({ ...f, [bloqueKey]: opt === 'Aplica' }))}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${(activeForm[bloqueKey] ? 'Aplica' : 'No aplica') === opt ? opt === 'Aplica' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-gray-400 text-white border-gray-400' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`}>
                  {opt}
                </button>
              ))}
            </div>
          </div>
        )

        return !activeAdoptId ? null : (
          <div className="space-y-4">
            {/* Header */}
            <div className="rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <TrendingUp size={18} className="text-white" />
                </div>
                <span className="text-white font-semibold text-base">{activeForm.profile_name || section?.name || 'Perfil'}</span>
              </div>
              {(isAdmin || isNVC) && !phaseLocked && (
                <button onClick={saveProfile} disabled={savingAdopt}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 flex-shrink-0 ${adoptSaved ? 'bg-emerald-400 text-white' : 'bg-white text-emerald-700 hover:bg-emerald-50'}`}>
                  {savingAdopt ? <><Loader size={14} className="animate-spin" />Guardando...</> : adoptSaved ? <><CheckCircle2 size={14} />¡Guardado!</> : <><Save size={14} />Guardar</>}
                </button>
              )}
            </div>

                  {/* Bloque 1: Nivel de adopción */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <BloqueHeader label="Nivel de adopción" bloqueKey="bloque1_aplica" />
                    {activeForm.bloque1_aplica ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Penetración estimada del perfil (%)</label>
                            <div className="relative">
                              <input type="number" min="0" max="100" value={activeForm.penetracion_pct} onChange={e => setForm(f => ({ ...f, penetracion_pct: e.target.value }))}
                                placeholder="0" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-emerald-300 bg-white" />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-medium">%</span>
                            </div>
                            <p className="text-xs text-gray-400 mt-1">¿Qué % del perfil usa la categoría?</p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Volumen de compra por ocasión</label>
                            <input type="text" value={activeForm.volumen_por_ocasion} onChange={e => setForm(f => ({ ...f, volumen_por_ocasion: e.target.value }))}
                              placeholder="Ej: 1-2 unidades, 500g, 1 litro..."
                              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300 bg-white" />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600 block mb-2">Frecuencia de compra</label>
                          <div className="flex gap-2 flex-wrap">
                            {FRECUENCIA_OPTS.map(o => (
                              <button key={o} type="button" onClick={() => setForm(f => ({ ...f, frecuencia_compra: f.frecuencia_compra === o ? '' : o }))}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${activeForm.frecuencia_compra === o ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300 hover:text-emerald-700'}`}>
                                {o}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
                  </div>

                  {/* Bloque 2: Satisfacción con la categoría */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <BloqueHeader label="Satisfacción con la categoría" bloqueKey="bloque2_aplica" />
                    {activeForm.bloque2_aplica ? (
                      <div className="space-y-4">
                        <div>
                          <label className="text-xs font-semibold text-gray-600 block mb-2">Nivel de satisfacción general</label>
                          <div className="flex gap-2 flex-wrap">
                            {SATISFACCION_OPTS.map(o => (
                              <button key={o} type="button" onClick={() => setForm(f => ({ ...f, satisfaccion_nivel: f.satisfaccion_nivel === o ? '' : o }))}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${activeForm.satisfaccion_nivel === o ? satisfaccionColor(o) : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>
                                {o}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600 block mb-1">¿Qué le falta a la categoría para satisfacerlo plenamente?</label>
                          <textarea rows={3} value={activeForm.satisfaccion_gaps} onChange={e => setForm(f => ({ ...f, satisfaccion_gaps: e.target.value }))}
                            placeholder="Gaps, necesidades no cubiertas, expectativas no resueltas por la categoría..."
                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300 resize-none bg-white" />
                        </div>
                      </div>
                    ) : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
                  </div>

                  {/* Bloque 3: Riesgos de abandono */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <BloqueHeader label="Riesgos de abandono" bloqueKey="bloque3_aplica" />
                    {activeForm.bloque3_aplica ? (
                      <textarea rows={4} value={activeForm.riesgos_abandono} onChange={e => setForm(f => ({ ...f, riesgos_abandono: e.target.value }))}
                        placeholder="¿Qué podría hacer que el perfil abandone o reduzca el uso de la categoría? Considera precio, sustitutos, cambios de hábito, nuevas tendencias..."
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300 resize-none bg-white" />
                    ) : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
                  </div>

                  {/* Bloque 4: Influenciadores de adopción */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <BloqueHeader label="Influenciadores de adopción" bloqueKey="bloque4_aplica" />
                    {activeForm.bloque4_aplica ? (
                      <div className="space-y-2">
                        {(activeForm.influenciadores ?? []).map(item => (
                          <div key={item.id} className="p-3 rounded-lg bg-gray-50 border border-gray-100 group space-y-2">
                            <div className="flex items-start gap-2">
                              <input type="text" value={item.descripcion} onChange={e => updateInfluenciador(item.id, 'descripcion', e.target.value)}
                                placeholder="¿Quién o qué influye en que el perfil adopte la categoría?..."
                                className="flex-1 text-sm bg-transparent focus:outline-none text-gray-700" />
                              <button onClick={() => removeInfluenciador(item.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all flex-shrink-0">
                                <X size={13} />
                              </button>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="flex gap-1 flex-wrap flex-1">
                                {INFLUENCIADOR_TIPO_OPTS.map(o => (
                                  <button key={o} type="button" onClick={() => updateInfluenciador(item.id, 'tipo', item.tipo === o ? '' : o)}
                                    className={`px-2 py-0.5 rounded text-xs font-semibold border transition-colors ${item.tipo === o ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-500 border-gray-200 hover:border-emerald-300 hover:text-emerald-700'}`}>
                                    {o}
                                  </button>
                                ))}
                              </div>
                              <div className="flex gap-1 flex-shrink-0">
                                {['Alta', 'Media', 'Baja'].map(o => (
                                  <button key={o} type="button" onClick={() => updateInfluenciador(item.id, 'importancia', o)}
                                    className={`px-2 py-0.5 rounded text-xs font-semibold border transition-colors ${item.importancia === o ? influImportanciaColor(o) : 'bg-white text-gray-400 border-gray-200 hover:border-gray-400'}`}>
                                    {o}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                        <button onClick={addInfluenciador} className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 hover:text-emerald-700 transition-colors mt-1">
                          <Plus size={13} /> Agregar influenciador
                        </button>
                      </div>
                    ) : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
                  </div>

            {renderConclusionBlock('adopcion_categoria', '')}
          </div>
        )
      })()}

      {/* ── Sobre la Solución Tab ────────────────────────────── */}
      {activeTab === 'sobre_solucion' && (() => {
        const activeForm = solForms[activeSolId] ?? SOLUTION_EMPTY_FORM
        const setForm = updater => setSolForms(prev => ({ ...prev, [activeSolId]: typeof updater === 'function' ? updater(prev[activeSolId] ?? SOLUTION_EMPTY_FORM) : updater }))

        async function saveProfile() {
          if (!activeSolId) return
          setSavingSol(true)
          const f = activeForm
          const payload = { profile_name: f.profile_name, bloque1_aplica: f.bloque1_aplica, drivers: f.drivers, bloque2_aplica: f.bloque2_aplica, barreras: f.barreras, bloque3_aplica: f.bloque3_aplica, motivadores: f.motivadores || null, bloque4_aplica: f.bloque4_aplica, proceso_decision: f.proceso_decision || null, bloque5_aplica: f.bloque5_aplica, ocasiones_consumo: f.ocasiones_consumo || null, updated_at: new Date().toISOString() }
          await supabase.from('consumer_solution').update(payload).eq('id', activeSolId)
          setSolProfiles(p => p.map(x => x.id === activeSolId ? { ...x, profile_name: f.profile_name } : x))
          setSavingSol(false); setSolSaved(true); setTimeout(() => setSolSaved(false), 2500)
        }

        // ── Item helpers ──
        function addDriver() { setForm(f => ({ ...f, drivers: [...(f.drivers ?? []), { id: Date.now().toString(), descripcion: '', importancia: 'Media' }] })) }
        function updateDriver(id, field, val) { setForm(f => ({ ...f, drivers: (f.drivers ?? []).map(x => x.id === id ? { ...x, [field]: val } : x) })) }
        function removeDriver(id) { setForm(f => ({ ...f, drivers: (f.drivers ?? []).filter(x => x.id !== id) })) }

        function addBarrera() { setForm(f => ({ ...f, barreras: [...(f.barreras ?? []), { id: Date.now().toString(), descripcion: '', intensidad: 'Media' }] })) }
        function updateBarrera(id, field, val) { setForm(f => ({ ...f, barreras: (f.barreras ?? []).map(x => x.id === id ? { ...x, [field]: val } : x) })) }
        function removeBarrera(id) { setForm(f => ({ ...f, barreras: (f.barreras ?? []).filter(x => x.id !== id) })) }

        const nivelColor = (n, type) => {
          if (type === 'driver') return n === 'Alta' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : n === 'Media' ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-gray-100 text-gray-500 border-gray-300'
          return n === 'Alta' ? 'bg-red-500 text-white border-red-500' : n === 'Media' ? 'bg-amber-500 text-white border-amber-500' : 'bg-gray-400 text-white border-gray-400'
        }

        const BloqueHeader = ({ label, bloqueKey }) => (
          <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-4">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">{label}</p>
            <div className="flex gap-1">
              {['Aplica', 'No aplica'].map(opt => (
                <button key={opt} type="button" onClick={() => setForm(f => ({ ...f, [bloqueKey]: opt === 'Aplica' }))}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${(activeForm[bloqueKey] ? 'Aplica' : 'No aplica') === opt ? opt === 'Aplica' ? 'bg-amber-500 text-white border-amber-500' : 'bg-gray-400 text-white border-gray-400' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`}>
                  {opt}
                </button>
              ))}
            </div>
          </div>
        )

        const TextBlock = ({ bloqueKey, field, label, placeholder, rows = 3 }) => (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <BloqueHeader label={label} bloqueKey={bloqueKey} />
            {activeForm[bloqueKey] ? (
              <textarea rows={rows} value={activeForm[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                placeholder={placeholder}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none bg-white" />
            ) : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
          </div>
        )

        return !activeSolId ? null : (
          <div className="space-y-4">
            {/* Header */}
            <div className="rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Target size={18} className="text-white" />
                </div>
                <span className="text-white font-semibold text-base">{activeForm.profile_name || section?.name || 'Perfil'}</span>
              </div>
              {(isAdmin || isNVC) && !phaseLocked && (
                <button onClick={saveProfile} disabled={savingSol}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 flex-shrink-0 ${solSaved ? 'bg-emerald-400 text-white' : 'bg-white text-amber-700 hover:bg-amber-50'}`}>
                  {savingSol ? <><Loader size={14} className="animate-spin" />Guardando...</> : solSaved ? <><CheckCircle2 size={14} />¡Guardado!</> : <><Save size={14} />Guardar</>}
                </button>
              )}
            </div>

                  {/* Bloque 1: Drivers de compra */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <BloqueHeader label="Drivers de compra" bloqueKey="bloque1_aplica" />
                    {activeForm.bloque1_aplica ? (
                      <div className="space-y-2">
                        {(activeForm.drivers ?? []).map(item => (
                          <div key={item.id} className="flex items-start gap-2 p-2.5 rounded-lg bg-gray-50 border border-gray-100 group">
                            <input type="text" value={item.descripcion} onChange={e => updateDriver(item.id, 'descripcion', e.target.value)}
                              placeholder="¿Qué impulsa la compra de esta categoría/producto?..."
                              className="flex-1 text-sm bg-transparent focus:outline-none text-gray-700" />
                            <div className="flex gap-1 flex-shrink-0">
                              {NIVEL_OPTS.map(o => (
                                <button key={o} type="button" onClick={() => updateDriver(item.id, 'importancia', o)}
                                  className={`px-2 py-0.5 rounded text-xs font-semibold border transition-colors ${item.importancia === o ? nivelColor(o, 'driver') : 'bg-white text-gray-400 border-gray-200 hover:border-gray-400'}`}>
                                  {o}
                                </button>
                              ))}
                            </div>
                            <button onClick={() => removeDriver(item.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all flex-shrink-0 mt-0.5">
                              <X size={13} />
                            </button>
                          </div>
                        ))}
                        <button onClick={addDriver} className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 hover:text-amber-700 transition-colors mt-1">
                          <Plus size={13} /> Agregar driver
                        </button>
                      </div>
                    ) : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
                  </div>

                  {/* Bloque 2: Barreras */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <BloqueHeader label="Barreras" bloqueKey="bloque2_aplica" />
                    {activeForm.bloque2_aplica ? (
                      <div className="space-y-2">
                        {(activeForm.barreras ?? []).map(item => (
                          <div key={item.id} className="flex items-start gap-2 p-2.5 rounded-lg bg-gray-50 border border-gray-100 group">
                            <input type="text" value={item.descripcion} onChange={e => updateBarrera(item.id, 'descripcion', e.target.value)}
                              placeholder="¿Qué frena o dificulta la compra?..."
                              className="flex-1 text-sm bg-transparent focus:outline-none text-gray-700" />
                            <div className="flex gap-1 flex-shrink-0">
                              {NIVEL_OPTS.map(o => (
                                <button key={o} type="button" onClick={() => updateBarrera(item.id, 'intensidad', o)}
                                  className={`px-2 py-0.5 rounded text-xs font-semibold border transition-colors ${item.intensidad === o ? nivelColor(o, 'barrera') : 'bg-white text-gray-400 border-gray-200 hover:border-gray-400'}`}>
                                  {o}
                                </button>
                              ))}
                            </div>
                            <button onClick={() => removeBarrera(item.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all flex-shrink-0 mt-0.5">
                              <X size={13} />
                            </button>
                          </div>
                        ))}
                        <button onClick={addBarrera} className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 hover:text-amber-700 transition-colors mt-1">
                          <Plus size={13} /> Agregar barrera
                        </button>
                      </div>
                    ) : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
                  </div>

                  {/* Bloque 3: Motivadores */}
                  <TextBlock bloqueKey="bloque3_aplica" field="motivadores" label="Motivadores"
                    placeholder="¿Qué motiva emocionalmente al perfil a elegir esta solución? ¿Qué beneficio busca más allá de lo funcional?..." rows={3} />

                  {/* Bloque 4: Proceso de decisión de compra */}
                  <TextBlock bloqueKey="bloque4_aplica" field="proceso_decision" label="Proceso de decisión de compra"
                    placeholder="¿Cómo decide comprar? ¿Qué pasos sigue desde que identifica la necesidad hasta que adquiere el producto?..." rows={4} />

                  {/* Bloque 5: Ocasiones de consumo */}
                  <TextBlock bloqueKey="bloque5_aplica" field="ocasiones_consumo" label="Ocasiones de consumo"
                    placeholder="¿Cuándo, dónde y en qué contextos consume el producto? ¿Existen rituales de uso o momentos clave?..." rows={3} />

            {renderConclusionBlock('sobre_solucion', '')}
          </div>
        )
      })()}

      {/* ── BU P&L Tab ───────────────────────────────────────── */}
      {activeTab === 'pyl' && (() => {

        // PYL_CANONICAL, matchPylCanonical, enrichPylRows are module-level —
        // see top of file. The parser below matches uploaded Excel rows
        // against the canonical schema for hierarchy + bold detection.

        function handleExcelUpload(e) {
          const file = e.target.files[0]
          if (!file) return
          const reader = new FileReader()
          reader.onload = evt => {
            const wb = XLSX.read(evt.target.result, { type: 'binary' })
            const ws = wb.Sheets[wb.SheetNames[0]]
            const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' })
            parseExcelPyl(raw)
          }
          reader.readAsBinaryString(file)
        }

        function parseExcelPyl(raw) {
          if (!raw || raw.length < 2) return
          const isYearLike = v => { const n = Number(v); return n >= 2015 && n <= 2035 }
          const isYearLabel = v => isYearLike(v) || (String(v).toLowerCase().includes('acum') && String(v).trim().length < 20) || String(v).toLowerCase().includes('ppto')
          const isColTypeRow = row => row.some(c => { const s = String(c).toLowerCase(); return s === 'real' || s.includes('var') || s.includes('vta') || s.includes('ppto') })
          let yearRowIdx = 0, colRowIdx = 1
          for (let i = 0; i < Math.min(8, raw.length); i++) {
            if (raw[i].some(isYearLabel)) { yearRowIdx = i; break }
          }
          for (let i = yearRowIdx + 1; i < Math.min(yearRowIdx + 4, raw.length); i++) {
            if (isColTypeRow(raw[i])) { colRowIdx = i; break }
          }
          const yearRow = raw[yearRowIdx] ?? []
          const colRow  = raw[colRowIdx]  ?? []
          // Fill-forward years (merged cells in Excel only show value in the
          // first cell of the merged range)
          const filledYears = []
          let lastYear = ''
          for (let c = 0; c < colRow.length; c++) {
            const y = yearRow[c]
            if (y !== '' && y !== null && isYearLabel(y)) {
              lastYear = isYearLike(y) ? String(Math.round(Number(y))) : String(y).trim()
            }
            filledYears.push(lastYear)
          }
          // Find which column has the labels — the user's template had labels
          // in column 2 (after some empty columns). Detect by looking for the
          // column with the most non-empty text in the rows below the header.
          let labelCol = 0
          let bestScore = -1
          for (let c = 0; c < 6; c++) {
            let score = 0
            for (let r = colRowIdx + 1; r < Math.min(colRowIdx + 30, raw.length); r++) {
              const v = raw[r]?.[c]
              if (typeof v === 'string' && v.trim().length > 3 && isNaN(Number(v))) score++
            }
            if (score > bestScore) { bestScore = score; labelCol = c }
          }
          const groups = []
          const colKeys = []
          for (let c = 0; c < colRow.length; c++) {
            if (c <= labelCol) { colKeys.push(null); continue }
            const colName = String(colRow[c] ?? '').trim()
            if (!colName) { colKeys.push(null); continue }
            const year = filledYears[c] || `Y${c}`
            let group = groups.find(g => g.year === year)
            if (!group) { group = { year, colNames: [] }; groups.push(group) }
            if (!group.colNames.includes(colName)) group.colNames.push(colName)
            colKeys.push(`${year}___${colName}`)
          }
          // Parse rows preserving the ORIGINAL Excel order. Match canonical
          // only to inherit hierarchy (level/bold/sign/isTotal) and to keep the
          // user-facing label consistent — but never reorder.
          const rows = []
          for (let r = colRowIdx + 1; r < raw.length; r++) {
            const row = raw[r] || []
            const rawLabel = String(row[labelCol] ?? '').trim()
            if (!rawLabel) continue
            const values = {}
            for (let c = labelCol + 1; c < row.length; c++) {
              const key = colKeys[c]
              if (!key) continue
              const v = row[c]
              if (v !== '' && v !== null && v !== undefined && !isNaN(Number(v))) values[key] = Number(v)
            }
            const canonical = matchPylCanonical(rawLabel)
            const id = `r${r}_${Math.random().toString(36).slice(2, 6)}`
            // Use the user's original label verbatim (don't replace with the
            // canonical match string) so the table mirrors the Excel exactly.
            rows.push({
              id,
              label:   rawLabel,
              bold:    canonical?.bold    ?? false,
              level:   canonical?.level   ?? 0,
              isTotal: canonical?.isTotal ?? false,
              sign:    canonical?.sign,
              values,
            })
          }
          setPylCols(groups)
          setPylRows(rows)
        }

        function addRow() { setPylRows(p => [...p, { id: `new_${Date.now()}`, label: '', bold: false, values: {} }]) }
        function deleteRow(id) { setPylRows(p => p.filter(r => r.id !== id)) }
        function toggleBold(id) { setPylRows(p => p.map(r => r.id === id ? { ...r, bold: !r.bold } : r)) }
        function updateLabel(id, val) { setPylRows(p => p.map(r => r.id === id ? { ...r, label: val } : r)) }
        function updateCell(id, key, val) { setPylRows(p => p.map(r => r.id === id ? { ...r, values: { ...r.values, [key]: val } } : r)) }
        function moveRow(id, dir) {
          setPylRows(p => {
            const idx = p.findIndex(r => r.id === id)
            if (idx === -1) return p
            const target = dir === 'up' ? idx - 1 : idx + 1
            if (target < 0 || target >= p.length) return p
            const next = [...p]
            ;[next[idx], next[target]] = [next[target], next[idx]]
            return next
          })
        }

        async function savePyl() {
          setSavingPyl(true)
          const payload = { section_id: sectionId, unit: pylUnit, pyl_columns: pylCols, pyl_rows: pylRows, updated_at: new Date().toISOString() }
          if (pylRecord) { await supabase.from('bu_pyl').update(payload).eq('id', pylRecord.id) }
          else { const { data: nb } = await supabase.from('bu_pyl').insert(payload).select().single(); if (nb) setPylRecord(nb) }
          setSavingPyl(false)
          await logChange('pyl', `P&L actualizado: ${pylRows.length} filas, unidad: ${pylUnit}`, { columnas: pylCols, filas: pylRows.map(r => ({ label: r.label, bold: r.bold })) })
          setPylSaved(true); setTimeout(() => setPylSaved(false), 2500)
        }

        const flatCols = pylCols.flatMap(g => g.colNames.map(n => ({ year: g.year, colName: n, key: `${g.year}___${n}` })))

        return (
          <div className="space-y-4">
            {/* Header */}
            <div className="rounded-xl bg-gradient-to-r from-green-600 to-emerald-700 px-5 py-4 flex items-center justify-between gap-3 shadow-sm flex-wrap gap-y-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <TrendingUp size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-base">Estrategia vista desde el P&L</h2>
                  <p className="text-green-100 text-xs mt-0.5">Sube un Excel o edita manualmente · Usa <strong>B</strong> para negrita en cada fila</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-white/20 text-white hover:bg-white/30 transition-colors">
                  <Upload size={14} /> Subir Excel
                  <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleExcelUpload} />
                </label>
                {(isAdmin || isNVC) && !phaseLocked && (
                  <button onClick={savePyl} disabled={savingPyl}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 ${pylSaved ? 'bg-emerald-400 text-white' : 'bg-white text-green-700 hover:bg-green-50'}`}>
                    {savingPyl ? <><Loader size={14} className="animate-spin" />Guardando...</> : pylSaved ? <><CheckCircle2 size={14} />¡Guardado!</> : <><Save size={14} />Guardar</>}
                  </button>
                )}
              </div>
            </div>

            {/* Unit + visualization legend */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-500">Unidad:</span>
                <input type="text" value={pylUnit} onChange={e => setPylUnit(e.target.value)}
                  className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 w-44 focus:outline-none focus:ring-2 focus:ring-green-300 bg-white" />
              </div>
              <p className="text-[11px] text-gray-400 italic max-w-md">
                Los valores grandes se abrevian: <strong>k</strong> = miles · <strong>M</strong> = millones · <strong>B</strong> = mil millones.
                Las columnas de % muestran el dato como porcentaje (ej. 0.073 → 7.3%). Click en cualquier celda para ver y editar el valor exacto.
              </p>
            </div>

            {/* Empty state */}
            {pylCols.length === 0 ? (
              <div className="bg-white rounded-xl border-2 border-dashed border-gray-200 p-12 text-center">
                <Upload size={32} className="mx-auto text-gray-300 mb-3" />
                <p className="text-sm font-semibold text-gray-500">Sube tu Excel o agrega filas manualmente</p>
                <p className="text-xs text-gray-400 mt-1">El esquema se adapta automáticamente al Excel que subas</p>
                <button onClick={addRow} className="mt-4 px-4 py-2 rounded-lg text-sm font-semibold bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors">
                  + Agregar fila
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="text-xs border-collapse" style={{ minWidth: '100%' }}>
                    <thead>
                      {/* Year headers */}
                      <tr className="bg-gray-800 text-white">
                        <th className="sticky left-0 z-10 bg-gray-800 text-left px-3 py-2.5 font-semibold border-r border-gray-600" style={{ minWidth: 220 }}>Línea P&L</th>
                        {pylCols.map(group => (
                          <th key={group.year} colSpan={group.colNames.length} className="text-center px-3 py-2.5 font-bold border-l border-gray-600 whitespace-nowrap">
                            {group.year}
                          </th>
                        ))}
                        <th className="w-6 bg-gray-800 border-l border-gray-600"></th>
                      </tr>
                      {/* Column type headers */}
                      <tr className="bg-gray-700 text-gray-200">
                        <th className="sticky left-0 z-10 bg-gray-700 px-3 py-1.5 border-r border-gray-600"></th>
                        {flatCols.map(({ year, colName, key }, ci) => {
                          const isFirst = ci === 0 || flatCols[ci - 1].year !== year
                          return (
                            <th key={key} className={`text-center px-2 py-1.5 font-medium whitespace-nowrap ${isFirst ? 'border-l border-gray-500' : ''}`} style={{ minWidth: 75 }}>
                              {colName}
                            </th>
                          )
                        })}
                        <th className="w-6 bg-gray-700 border-l border-gray-600"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pylRows.map((row, rowIdx) => {
                        const level = row.level ?? 0
                        const indent = level * 16
                        const rowBg = row.isTotal ? 'bg-green-50/60' : (row.bold ? 'bg-gray-50' : '')
                        const isFirst = rowIdx === 0
                        const isLast  = rowIdx === pylRows.length - 1
                        return (
                        <tr key={row.id} className={`border-b border-gray-100 hover:bg-green-50/40 group ${rowBg}`}>
                          <td className="sticky left-0 z-10 bg-white group-hover:bg-green-50/40 px-2 py-1 border-r border-gray-100" style={{ minWidth: 220 }}>
                            <div className="flex items-center gap-1" style={{ paddingLeft: indent }}>
                              <div className="flex flex-col flex-shrink-0 -my-0.5">
                                <button
                                  type="button"
                                  onClick={() => moveRow(row.id, 'up')}
                                  disabled={isFirst}
                                  title="Subir fila"
                                  className="opacity-30 group-hover:opacity-100 hover:text-green-600 disabled:opacity-10 disabled:cursor-not-allowed text-gray-400 transition-opacity"
                                >
                                  <ChevronUp size={11} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveRow(row.id, 'down')}
                                  disabled={isLast}
                                  title="Bajar fila"
                                  className="opacity-30 group-hover:opacity-100 hover:text-green-600 disabled:opacity-10 disabled:cursor-not-allowed text-gray-400 transition-opacity"
                                >
                                  <ChevronDown size={11} />
                                </button>
                              </div>
                              <button onClick={() => toggleBold(row.id)}
                                className={`flex-shrink-0 text-xs w-5 h-5 rounded border font-bold transition-colors ${row.bold ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-300 border-gray-200 hover:border-gray-500 hover:text-gray-600'}`}>
                                B
                              </button>
                              {row.sign && (
                                <span className={`flex-shrink-0 text-[10px] font-bold w-3 text-center ${row.sign === '+' ? 'text-emerald-600' : row.sign === '-' ? 'text-red-500' : 'text-gray-500'}`}>
                                  {row.sign}
                                </span>
                              )}
                              <input type="text" value={row.label} onChange={e => updateLabel(row.id, e.target.value)}
                                className={`flex-1 px-1.5 py-0.5 bg-transparent focus:outline-none focus:bg-white focus:ring-1 focus:ring-green-300 focus:rounded text-xs ${row.bold ? 'font-bold text-gray-800' : 'text-gray-700'} ${level > 0 ? 'text-gray-600' : ''}`} />
                            </div>
                          </td>
                          {flatCols.map(({ year, colName, key }, ci) => {
                            const val = row.values[key] ?? ''
                            const isFirst = ci === 0 || flatCols[ci - 1].year !== year
                            const lc = colName.toLowerCase()
                            const isVar  = lc.includes('var')
                            const isPct  = isVar || lc.includes('% vta') || lc.includes('vta neta')
                            const numVal = Number(val)
                            const varColor = isVar && val !== '' ? (numVal >= 0 ? 'text-green-600' : 'text-red-500') : ''
                            return (
                              <td key={key} className={`px-1 py-0.5 text-center ${isFirst ? 'border-l border-gray-100' : ''}`}>
                                <PylCell
                                  value={val}
                                  isPct={isPct}
                                  onChange={v => updateCell(row.id, key, v)}
                                  className={`w-full text-center px-1 py-0.5 bg-transparent focus:outline-none focus:bg-white focus:ring-1 focus:ring-green-300 focus:rounded text-xs ${row.bold ? 'font-bold' : ''} ${varColor}`}
                                />
                              </td>
                            )
                          })}
                          <td className="w-6 px-1 text-center">
                            <button onClick={() => deleteRow(row.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all">
                              <X size={11} />
                            </button>
                          </td>
                        </tr>
                      )})}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-2.5 border-t border-gray-100 flex items-center justify-between">
                  <button onClick={addRow} className="flex items-center gap-1.5 text-xs font-semibold text-green-600 hover:text-green-700 transition-colors">
                    <Plus size={13} /> Agregar fila
                  </button>
                  <span className="text-xs text-gray-400">{pylRows.length} filas · {pylUnit}</span>
                </div>
              </div>
            )}

            {/* ── Visualizaciones del P&L ─────────────────────────────── */}
            {pylRows.length > 0 && pylCols.length > 0 && (() => {

              // ── Helpers comunes ───────────────────────────────────────────
              const findRow = key =>
                pylRows.find(r => r.label && matchPylCanonical(r.label)?.key === key)

              // Pulls a value from a row at a given (year, sub-column type).
              // type: 'real' | 'pct' | 'var' | 'ppto'
              const valueAt = (group, row, type = 'real') => {
                if (!row || !group) return null
                let col
                if (type === 'real') {
                  col = group.colNames.find(n => /^real/i.test(n)) ?? group.colNames[0]
                } else if (type === 'ppto') {
                  col = group.colNames.find(n => /ppto/i.test(n))
                } else if (type === 'pct') {
                  col = group.colNames.find(n => /vta neta|% vta/i.test(n))
                } else if (type === 'var') {
                  col = group.colNames.find(n => /var/i.test(n))
                }
                if (!col) return null
                const v = Number(row.values?.[`${group.year}___${col}`])
                return Number.isFinite(v) ? v : null
              }

              const fmtMoney = v => {
                if (v == null) return '—'
                const abs = Math.abs(v)
                if (abs >= 1e9) return `${(v / 1e9).toFixed(1)}B`
                if (abs >= 1e6) return `${(v / 1e6).toFixed(1)}M`
                if (abs >= 1e3) return `${(v / 1e3).toFixed(0)}k`
                return Number(v).toLocaleString('es-CO', { maximumFractionDigits: 0 })
              }
              const fmtPct = (v, decimals = 1) => v == null ? '—' : `${(v * (Math.abs(v) <= 1 ? 100 : 1)).toFixed(decimals)}%`

              // Identify which year groups are "annual completos" vs "Acum 2026" / "PPTO 2026"
              const annualGroups = pylCols.filter(g => /^\d{4}$/.test(String(g.year)))
              const acumGroup    = pylCols.find(g => /acum/i.test(String(g.year)))
              const pptoGroup    = pylCols.find(g => /ppto/i.test(String(g.year)))
              const latestAnnual = annualGroups[annualGroups.length - 1]

              return (
                <div className="space-y-5">

                  {/* ════════════════════════════════════════════════════════ */}
                  {/* A — WATERFALL del año más reciente                       */}
                  {/* ════════════════════════════════════════════════════════ */}
                  {(() => {
                    const sourceGroup = latestAnnual ?? acumGroup ?? pylCols[0]
                    if (!sourceGroup) return null
                    const ventaBruta = findRow('venta_bruta')
                    const stops = [
                      { key: 'venta_bruta',    label: 'Venta Bruta',     type: 'total', color: '#1A3F6B' },
                      { key: 'descuentos',     label: 'Descuentos',      type: 'down',  color: '#EF4444' },
                      { key: 'venta_neta_1',   label: 'Venta Neta 1',    type: 'total', color: '#1A3F6B' },
                      { key: 'gasto_canal',    label: 'Gasto Canal',     type: 'down',  color: '#EF4444' },
                      { key: 'venta_neta_2',   label: 'Venta Neta 2',    type: 'total', color: '#1A3F6B' },
                      { key: 'costo_total',    label: 'Costo Total',     type: 'down',  color: '#EF4444' },
                      { key: 'utilidad_bruta', label: 'Utilidad Bruta',  type: 'total', color: '#0891B2' },
                      { key: 'gastos_totales', label: 'Gastos Totales',  type: 'down',  color: '#F59E0B' },
                      { key: 'utilidad_neta',  label: 'Utilidad Neta',   type: 'total', color: '#10B981' },
                    ].map(s => ({ ...s, value: valueAt(sourceGroup, findRow(s.key)) }))
                      .filter(s => s.value != null)

                    const vbValue = stops.find(s => s.key === 'venta_bruta')?.value ?? 0
                    if (!vbValue) return null
                    const maxAbs = Math.max(...stops.map(s => Math.abs(s.value)))

                    return (
                      <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                          <div>
                            <h3 className="text-sm font-bold text-gray-900">A · Cascada de rentabilidad — {sourceGroup.year}</h3>
                            <p className="text-xs text-gray-500">Cómo cae la Venta Bruta hasta la Utilidad Neta.</p>
                          </div>
                          <div className="text-xs text-gray-400">Valores en {pylUnit}</div>
                        </div>
                        <div className="space-y-1.5">
                          {stops.map((s, i) => {
                            const widthPct = (Math.abs(s.value) / maxAbs) * 100
                            const pctOfVb = (Math.abs(s.value) / Math.abs(vbValue)) * 100
                            const isTotal = s.type === 'total'
                            return (
                              <div key={s.key} className="flex items-center gap-3">
                                <div className={`w-40 text-xs text-right pr-2 flex-shrink-0 ${isTotal ? 'font-bold text-gray-800' : 'text-gray-600'}`}>
                                  {s.label}
                                </div>
                                <div className="flex-1 relative h-6">
                                  <div
                                    className={`h-full rounded transition-all ${isTotal ? '' : 'opacity-90'}`}
                                    style={{ width: `${widthPct}%`, backgroundColor: s.color }}
                                  />
                                  <div className="absolute inset-0 flex items-center px-2 text-[11px] text-white font-semibold pointer-events-none drop-shadow-sm">
                                    {fmtMoney(s.value)} <span className="ml-2 opacity-80">({pctOfVb.toFixed(1)}% de Vta Bruta)</span>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                        <p className="text-[11px] text-gray-400 mt-3 italic">Lectura: cada barra es proporcional al valor absoluto. Los azules son totales, rojo/ámbar son deducciones, verde es la utilidad final.</p>
                      </div>
                    )
                  })()}

                  {/* ════════════════════════════════════════════════════════ */}
                  {/* B — PLAN vs REAL 2026                                    */}
                  {/* ════════════════════════════════════════════════════════ */}
                  {acumGroup && pptoGroup && (() => {
                    const lines = [
                      { key: 'venta_neta_1',   label: 'Venta Neta 1' },
                      { key: 'costo_total',    label: 'Costo Total' },
                      { key: 'gasto_mercadeo', label: 'Gasto Mercadeo' },
                      { key: 'gastos_op',      label: 'Gastos Operacionales' },
                      { key: 'utilidad_neta',  label: 'Utilidad Neta' },
                    ].map(l => {
                      const row = findRow(l.key)
                      const real = valueAt(acumGroup, row, 'real')
                      const ppto = valueAt(pptoGroup, row, 'ppto') ?? valueAt(pptoGroup, row, 'real')
                      const cumplPct = (real != null && ppto != null && Math.abs(ppto) > 0)
                        ? (real / ppto) * 100 : null
                      return { ...l, real, ppto, cumplPct }
                    }).filter(l => l.real != null || l.ppto != null)
                    if (!lines.length) return null
                    return (
                      <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                          <div>
                            <h3 className="text-sm font-bold text-gray-900">B · Plan vs Real — 2026</h3>
                            <p className="text-xs text-gray-500">Comparación entre lo ejecutado al corte y el presupuesto anual.</p>
                          </div>
                        </div>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart
                            data={lines.map(l => ({
                              line:  l.label,
                              Real:  Math.abs(l.real ?? 0),
                              PPTO:  Math.abs(l.ppto ?? 0),
                            }))}
                            margin={{ top: 8, right: 24, left: 0, bottom: 8 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                            <XAxis dataKey="line" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={fmtMoney} />
                            <Tooltip formatter={v => Number(v).toLocaleString('es-CO')} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="Real" fill="#1A3F6B" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="PPTO" fill="#94A3B8" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                          {lines.map(l => (
                            <div key={l.key} className="flex items-center justify-between gap-2 text-xs px-3 py-1.5 rounded-lg border border-gray-100 bg-gray-50">
                              <span className="text-gray-600 truncate">{l.label}</span>
                              <span className={`font-semibold tabular-nums ${
                                l.cumplPct == null ? 'text-gray-400' :
                                l.cumplPct >= 95 ? 'text-emerald-600' :
                                l.cumplPct >= 70 ? 'text-amber-600' : 'text-red-600'
                              }`}>
                                {l.cumplPct == null ? '—' : `${l.cumplPct.toFixed(0)}%`}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })()}

                  {/* ════════════════════════════════════════════════════════ */}
                  {/* C — RATIOS con alertas (% Vta Neta histórico)           */}
                  {/* ════════════════════════════════════════════════════════ */}
                  {(() => {
                    const ratios = [
                      { key: 'costo_total',    label: 'Costo Total / Vta',         goodIsLow: true },
                      { key: 'costo_directo',  label: 'Costo Directo / Vta',       goodIsLow: true },
                      { key: 'gasto_mercadeo', label: 'Gasto Mercadeo / Vta',      goodIsLow: false }, // mercadeo bajo no siempre es bueno
                      { key: 'gastos_op',      label: 'Gastos Operacionales / Vta', goodIsLow: true },
                      { key: 'utilidad_bruta', label: 'Utilidad Bruta / Vta',      goodIsLow: false },
                      { key: 'utilidad_neta',  label: 'Utilidad Neta / Vta',       goodIsLow: false },
                    ]
                    const yearsForRatios = annualGroups.slice(-5)
                    if (yearsForRatios.length === 0) return null
                    // Venta Neta absoluta — context row at the top so the
                    // user reads the ratios with the underlying scale in
                    // mind. "$10M Costo / 28M Vta" lands different than
                    // "$10M Costo / 1B Vta".
                    const vn1Row = findRow('venta_neta_1')
                    const vn1Series = yearsForRatios.map(g => valueAt(g, vn1Row, 'real'))
                    const vn1Last = vn1Series[vn1Series.length - 1]
                    const vn1Prev = vn1Series[vn1Series.length - 2]
                    const vn1Var = (vn1Last != null && vn1Prev != null && Math.abs(vn1Prev) > 0)
                      ? ((vn1Last - vn1Prev) / Math.abs(vn1Prev)) * 100 : null
                    return (
                      <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <div className="mb-4">
                          <h3 className="text-sm font-bold text-gray-900">C · Ratios sobre Venta Neta — alertas año vs año</h3>
                          <p className="text-xs text-gray-500">Cada ratio con su delta vs el año anterior. Verde = mejora, rojo = deterioro.</p>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-3 py-2 font-semibold text-gray-600 sticky left-0 bg-gray-50">Ratio</th>
                                {yearsForRatios.map(g => (
                                  <th key={g.year} className="text-right px-3 py-2 font-semibold text-gray-600">{g.year}</th>
                                ))}
                                <th className="text-right px-3 py-2 font-semibold text-gray-600">Δ vs prev</th>
                              </tr>
                            </thead>
                            <tbody>
                              {/* Reference row: absolute Venta Neta 1 across years */}
                              {vn1Row && (
                                <tr className="bg-brand-50 border-b border-brand-100">
                                  <td className="px-3 py-2 font-bold text-brand-900 sticky left-0 bg-brand-50">
                                    Venta Neta 1 ({pylUnit})
                                  </td>
                                  {vn1Series.map((v, i) => (
                                    <td key={i} className="text-right px-3 py-2 tabular-nums font-bold text-brand-900">
                                      {fmtMoney(v)}
                                    </td>
                                  ))}
                                  <td className={`text-right px-3 py-2 tabular-nums font-bold ${vn1Var == null ? 'text-gray-400' : vn1Var >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {vn1Var == null ? '—' : `${vn1Var >= 0 ? '↑' : '↓'} ${Math.abs(vn1Var).toFixed(1)}%`}
                                  </td>
                                </tr>
                              )}
                              {ratios.map(r => {
                                const row = findRow(r.key)
                                if (!row) return null
                                const series = yearsForRatios.map(g => {
                                  const pct = valueAt(g, row, 'pct')
                                  return pct != null ? Math.abs(pct) : null
                                })
                                const last  = series[series.length - 1]
                                const prev  = series[series.length - 2]
                                const delta = (last != null && prev != null) ? (last - prev) : null
                                const isImprove = delta != null && (
                                  (r.goodIsLow && delta < 0) ||
                                  (!r.goodIsLow && delta > 0)
                                )
                                const cls = delta == null ? 'text-gray-400' : isImprove ? 'text-emerald-600' : 'text-red-600'
                                const arrow = delta == null ? '' : delta > 0 ? '↑' : delta < 0 ? '↓' : '→'
                                return (
                                  <tr key={r.key} className="border-b border-gray-50">
                                    <td className="px-3 py-2 font-medium text-gray-700 sticky left-0 bg-white">{r.label}</td>
                                    {series.map((v, i) => (
                                      <td key={i} className="text-right px-3 py-2 tabular-nums text-gray-600">
                                        {v == null ? '—' : `${(v <= 1 ? v * 100 : v).toFixed(1)}%`}
                                      </td>
                                    ))}
                                    <td className={`text-right px-3 py-2 tabular-nums font-semibold ${cls}`}>
                                      {delta == null ? '—' : `${arrow} ${Math.abs(delta * (Math.abs(delta) <= 1 ? 100 : 1)).toFixed(1)}pts`}
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )
                  })()}

                  {/* ════════════════════════════════════════════════════════ */}
                  {/* D — HEATMAP de variaciones año a año                     */}
                  {/* ════════════════════════════════════════════════════════ */}
                  {(() => {
                    const heatLines = [
                      'venta_bruta', 'descuentos', 'venta_neta_1', 'costo_total',
                      'costo_directo', 'utilidad_bruta', 'gasto_mercadeo',
                      'gastos_op', 'transporte', 'administrativos', 'utilidad_neta',
                    ].map(k => {
                      const c = PYL_CANONICAL.find(x => x.key === k)
                      return { key: k, label: c?.match[0] ? c.match[0].replace(/\b\w/g, ch => ch.toUpperCase()) : k }
                    })
                    const heatYears = annualGroups.slice(-5)
                    if (heatYears.length < 2) return null
                    const colorFor = (v, goodIsLow) => {
                      if (v == null) return 'bg-gray-50 text-gray-300'
                      const pct = Math.abs(v) <= 1 ? v * 100 : v
                      const isImprove = (goodIsLow && pct < 0) || (!goodIsLow && pct > 0)
                      const intensity = Math.min(Math.abs(pct), 30) / 30   // cap at 30%
                      if (Math.abs(pct) < 1) return 'bg-gray-50 text-gray-500'
                      if (isImprove) return intensity > 0.6 ? 'bg-emerald-500 text-white' : intensity > 0.3 ? 'bg-emerald-300 text-emerald-900' : 'bg-emerald-100 text-emerald-800'
                      return intensity > 0.6 ? 'bg-red-500 text-white' : intensity > 0.3 ? 'bg-red-300 text-red-900' : 'bg-red-100 text-red-800'
                    }
                    return (
                      <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <div className="mb-4">
                          <h3 className="text-sm font-bold text-gray-900">D · Heatmap de variaciones</h3>
                          <p className="text-xs text-gray-500">Cada celda muestra Var % vs el año anterior. Patrones consistentes saltan a la vista.</p>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-separate border-spacing-1">
                            <thead>
                              <tr>
                                <th className="text-left font-semibold text-gray-600 px-2 py-1.5">Línea</th>
                                {heatYears.slice(1).map(g => (
                                  <th key={g.year} className="text-center font-semibold text-gray-600 px-2 py-1.5">vs {g.year}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {heatLines.map(line => {
                                const row = findRow(line.key)
                                if (!row) return null
                                const c = PYL_CANONICAL.find(x => x.key === line.key)
                                const goodIsLow = ['descuentos','costo_total','costo_directo','averias','gastos_totales','gasto_mercadeo','gastos_op','transporte','bodegaje','administrativos','gasto_financiero','otros_gastos','com_directa','com_masiva','com_canal','inv_mercado','inv_consumidor','desarrollo'].includes(line.key)
                                return (
                                  <tr key={line.key}>
                                    <td className="text-left text-gray-700 px-2 py-1 font-medium">{line.label}</td>
                                    {heatYears.slice(1).map(g => {
                                      const v = valueAt(g, row, 'var')
                                      return (
                                        <td key={g.year} className={`text-center px-2 py-1.5 rounded text-[11px] font-semibold tabular-nums ${colorFor(v, goodIsLow)}`}>
                                          {v == null ? '—' : `${(Math.abs(v) <= 1 ? v * 100 : v).toFixed(0)}%`}
                                        </td>
                                      )
                                    })}
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                        <p className="text-[11px] text-gray-400 mt-3 italic">El "bueno" depende de la línea: para Costos/Gastos verde = bajaron; para Ventas/Utilidades verde = subieron.</p>
                      </div>
                    )
                  })()}

                  {/* ════════════════════════════════════════════════════════ */}
                  {/* E — PARETO de gastos                                     */}
                  {/* ════════════════════════════════════════════════════════ */}
                  {(() => {
                    const sourceGroup = latestAnnual ?? acumGroup ?? pylCols[0]
                    if (!sourceGroup) return null
                    const items = [
                      'transporte', 'bodegaje', 'administrativos', 'gasto_financiero', 'otros_gastos',
                      'com_directa', 'com_masiva', 'com_canal', 'inv_mercado', 'inv_consumidor', 'desarrollo',
                    ].map(k => {
                      const c = PYL_CANONICAL.find(x => x.key === k)
                      const row = findRow(k)
                      const v = valueAt(sourceGroup, row, 'real')
                      return v != null && Math.abs(v) > 0
                        ? { name: c?.match[0]?.replace(/\b\w/g, ch => ch.toUpperCase()) ?? k, value: Math.abs(v) }
                        : null
                    }).filter(Boolean).sort((a, b) => b.value - a.value)
                    if (items.length === 0) return null
                    const total = items.reduce((s, i) => s + i.value, 0)
                    let cum = 0
                    const data = items.map(i => {
                      cum += i.value
                      return { name: i.name, value: i.value, cumPct: (cum / total) * 100 }
                    })
                    return (
                      <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <div className="mb-4">
                          <h3 className="text-sm font-bold text-gray-900">E · Pareto de gastos — {sourceGroup.year}</h3>
                          <p className="text-xs text-gray-500">Las pocas categorías que concentran la mayoría del gasto. Foco para recortar.</p>
                        </div>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={data} margin={{ top: 8, right: 24, left: 0, bottom: 50 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#6B7280' }} axisLine={false} tickLine={false}
                              angle={-30} textAnchor="end" height={70} interval={0} />
                            <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={fmtMoney} />
                            <YAxis yAxisId="right" orientation="right" domain={[0, 100]}
                              tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false}
                              tickFormatter={v => `${v}%`} />
                            <Tooltip formatter={(v, n) => n === 'cumPct' ? `${v.toFixed(0)}%` : Number(v).toLocaleString('es-CO')} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                            <Bar yAxisId="left" dataKey="value" name="Valor" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                            <Line yAxisId="right" type="monotone" dataKey="cumPct" name="Acumulado %" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} />
                            <ReferenceLine yAxisId="right" y={80} stroke="#EF4444" strokeDasharray="3 3" label={{ value: '80%', fontSize: 10, fill: '#EF4444', position: 'right' }} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )
                  })()}

                  {/* ════════════════════════════════════════════════════════ */}
                  {/* F — PROYECCIÓN cierre 2026                              */}
                  {/* ════════════════════════════════════════════════════════ */}
                  {acumGroup && pptoGroup && (() => {
                    const lines = [
                      { key: 'venta_neta_1',  label: 'Venta Neta 1', color: '#1A3F6B' },
                      { key: 'utilidad_neta', label: 'Utilidad Neta', color: '#10B981' },
                    ].map(l => {
                      const row = findRow(l.key)
                      const real = valueAt(acumGroup, row, 'real')
                      const ppto = valueAt(pptoGroup, row, 'ppto') ?? valueAt(pptoGroup, row, 'real')
                      const proyeccion = real != null && ppto != null
                        ? real + (ppto * (10/12))   // assume 2 months done, 10 to go at PPTO pace
                        : null
                      const cumplProy = (proyeccion != null && ppto != null && Math.abs(ppto) > 0)
                        ? (proyeccion / ppto) * 100 : null
                      return { ...l, real, ppto, proyeccion, cumplProy }
                    }).filter(l => l.real != null || l.ppto != null)
                    if (!lines.length) return null
                    return (
                      <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <div className="mb-4">
                          <h3 className="text-sm font-bold text-gray-900">F · Proyección de cierre 2026</h3>
                          <p className="text-xs text-gray-500">Real ene-feb + presupuesto restante mar-dic vs PPTO original.</p>
                        </div>
                        <div className="space-y-3">
                          {lines.map(l => {
                            const realPct  = l.ppto && Math.abs(l.ppto) > 0 ? Math.min(100, Math.abs(l.real / l.ppto) * 100) : 0
                            const proyPct  = l.cumplProy != null ? Math.min(150, l.cumplProy) : 0
                            return (
                              <div key={l.key}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-semibold text-gray-700">{l.label}</span>
                                  <span className={`text-xs font-bold tabular-nums ${
                                    l.cumplProy == null ? 'text-gray-400' :
                                    l.cumplProy >= 100 ? 'text-emerald-600' :
                                    l.cumplProy >= 90  ? 'text-amber-600' : 'text-red-600'
                                  }`}>
                                    Proyectado: {l.cumplProy == null ? '—' : `${l.cumplProy.toFixed(0)}% del PPTO`}
                                  </span>
                                </div>
                                <div className="relative h-7 bg-gray-100 rounded-md overflow-hidden">
                                  {/* PPTO marker */}
                                  <div className="absolute top-0 right-0 h-full border-l-2 border-gray-400" style={{ left: '100%' }} />
                                  {/* Proyección bar */}
                                  <div className="absolute top-0 left-0 h-full opacity-40" style={{ width: `${proyPct}%`, backgroundColor: l.color }} />
                                  {/* Real bar */}
                                  <div className="absolute top-0 left-0 h-full" style={{ width: `${realPct}%`, backgroundColor: l.color }} />
                                  <div className="absolute inset-0 flex items-center px-2 text-[11px] font-semibold text-white drop-shadow">
                                    Real: {fmtMoney(l.real)} · Proyec: {fmtMoney(l.proyeccion)} · PPTO: {fmtMoney(l.ppto)}
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                        <p className="text-[11px] text-gray-400 mt-3 italic">La barra sólida es lo ya ejecutado; la barra translúcida es la proyección si seguimos al ritmo del PPTO. La línea vertical marca el 100% del PPTO.</p>
                      </div>
                    )
                  })()}

                  {/* ════════════════════════════════════════════════════════ */}
                  {/* G — CAGR race (5 años)                                   */}
                  {/* ════════════════════════════════════════════════════════ */}
                  {(() => {
                    const fiveYears = annualGroups.slice(-5)
                    if (fiveYears.length < 3) return null
                    const first = fiveYears[0]
                    const last  = fiveYears[fiveYears.length - 1]
                    const years = fiveYears.length - 1
                    const items = [
                      { key: 'venta_neta_1',  label: 'Venta Neta',         goodIsLow: false, color: '#1A3F6B' },
                      { key: 'costo_directo', label: 'Costo Directo',      goodIsLow: true,  color: '#EF4444' },
                      { key: 'costo_total',   label: 'Costo Total',        goodIsLow: true,  color: '#F87171' },
                      { key: 'gasto_mercadeo', label: 'Mercadeo',          goodIsLow: false, color: '#F59E0B' },
                      { key: 'gastos_op',     label: 'Gastos Operacionales', goodIsLow: true, color: '#8B5CF6' },
                      { key: 'utilidad_bruta', label: 'Utilidad Bruta',    goodIsLow: false, color: '#10B981' },
                      { key: 'utilidad_neta',  label: 'Utilidad Neta',     goodIsLow: false, color: '#059669' },
                    ].map(it => {
                      const row = findRow(it.key)
                      const v0 = Math.abs(valueAt(first, row, 'real') ?? 0)
                      const v1 = Math.abs(valueAt(last,  row, 'real') ?? 0)
                      if (v0 === 0 || v1 === 0) return null
                      const cagr = (Math.pow(v1 / v0, 1 / years) - 1) * 100
                      return { ...it, cagr: Number(cagr.toFixed(1)) }
                    }).filter(Boolean).sort((a, b) => b.cagr - a.cagr)
                    if (items.length === 0) return null
                    // Strategic alert: costos crecen más que ventas
                    const ventas  = items.find(i => i.key === 'venta_neta_1')
                    const costoD  = items.find(i => i.key === 'costo_directo')
                    const costoT  = items.find(i => i.key === 'costo_total')
                    const alerta  = ventas && costoT && costoT.cagr > ventas.cagr
                    return (
                      <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <div className="mb-4">
                          <h3 className="text-sm font-bold text-gray-900">G · Carrera de crecimiento — CAGR {first.year}–{last.year}</h3>
                          <p className="text-xs text-gray-500">¿Qué crece más rápido? Si los costos pasan a las ventas, el negocio se está deteriorando.</p>
                        </div>
                        {alerta && (
                          <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
                            🚨 <strong>Alerta de apalancamiento operativo</strong>: Costo Total crece a {costoT.cagr}% CAGR pero Venta Neta solo a {ventas.cagr}% CAGR. Si esto persiste, el margen seguirá comprimiéndose.
                          </div>
                        )}
                        <div className="space-y-1.5">
                          {items.map(i => {
                            const max = Math.max(...items.map(x => Math.abs(x.cagr)))
                            const widthPct = (Math.abs(i.cagr) / max) * 100
                            return (
                              <div key={i.key} className="flex items-center gap-3">
                                <div className="w-44 text-xs text-right pr-2 flex-shrink-0 text-gray-700">{i.label}</div>
                                <div className="flex-1 relative h-6 bg-gray-50 rounded">
                                  <div className="h-full rounded" style={{ width: `${widthPct}%`, backgroundColor: i.color, opacity: 0.85 }} />
                                  <div className="absolute inset-0 flex items-center px-2 text-[11px] font-bold text-white drop-shadow">
                                    {i.cagr > 0 ? '+' : ''}{i.cagr}% / año
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })()}

                </div>
              )
            })()}

            {renderConclusionBlock('pyl', '')}
          </div>
        )
      })()}

      {/* ── Competitor Capacidad Industrial Tab ──────────────── */}
      {activeTab === 'cap_industrial' && (() => {
        async function saveCap() {
          setSavingCap(true)
          const payload = {
            section_id: sectionId,
            competitor_id: section.competitor_id,
            bloque1_aplica: capForm.bloque1_aplica,
            num_plantas: capForm.num_plantas !== '' ? Number(capForm.num_plantas) : null,
            ubicacion_plantas: capForm.ubicacion_plantas || null,
            capacidad_instalada: capForm.capacidad_instalada !== '' ? Number(capForm.capacidad_instalada) : null,
            capacidad_unidad: capForm.capacidad_unidad || null,
            utilizacion_pct: capForm.utilizacion_pct !== '' ? Number(capForm.utilizacion_pct) : null,
            bloque2_aplica: capForm.bloque2_aplica,
            nivel_tecnologico: capForm.nivel_tecnologico || null,
            grado_automatizacion: capForm.grado_automatizacion !== '' ? Number(capForm.grado_automatizacion) : null,
            antiguedad_equipos: capForm.antiguedad_equipos !== '' ? Number(capForm.antiguedad_equipos) : null,
            descripcion_tecnologia: capForm.descripcion_tecnologia || null,
            bloque3_aplica: capForm.bloque3_aplica,
            certificaciones: capForm.certificaciones,
            estandares_calidad: capForm.estandares_calidad || null,
            bloque4_aplica: capForm.bloque4_aplica,
            integracion_vertical: capForm.integracion_vertical || null,
            flexibilidad_linea: capForm.flexibilidad_linea || null,
            capacidad_expansion: capForm.capacidad_expansion || null,
            notas_integracion: capForm.notas_integracion || null,
            bloque5_aplica: capForm.bloque5_aplica,
            ventajas_productivas: capForm.ventajas_productivas || null,
            brechas_limitaciones: capForm.brechas_limitaciones || null,
            updated_at: new Date().toISOString(),
          }
          if (capIndustrial) {
            await supabase.from('competitor_industrial_capacity').update(payload).eq('id', capIndustrial.id)
          } else {
            const { data: nb } = await supabase.from('competitor_industrial_capacity').insert(payload).select().single()
            if (nb) setCapIndustrial(nb)
          }
          setSavingCap(false)
          await logChange('cap_industrial', 'Capacidad industrial actualizada', { registros: capIndustrial ? 1 : 0 })
          setCapSaved(true)
          setTimeout(() => setCapSaved(false), 2500)
        }

        const utilizPct = Number(capForm.utilizacion_pct)
        const utilizColor = utilizPct >= 80 ? 'text-emerald-600' : utilizPct >= 60 ? 'text-amber-600' : utilizPct > 0 ? 'text-red-600' : 'text-gray-400'

        const BloqueHeader = ({ label, bloqueKey }) => (
          <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-4">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">{label}</p>
            <div className="flex gap-1">
              {['Aplica', 'No aplica'].map(opt => (
                <button key={opt} type="button"
                  onClick={() => setCapForm(f => ({ ...f, [bloqueKey]: opt === 'Aplica' }))}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${
                    (capForm[bloqueKey] ? 'Aplica' : 'No aplica') === opt
                      ? opt === 'Aplica' ? 'bg-amber-500 text-white border-amber-500' : 'bg-gray-400 text-white border-gray-400'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                  }`}>{opt}</button>
              ))}
            </div>
          </div>
        )

        return (
          <div className="space-y-5">
            {/* Header */}
            <div className="rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Database size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-base">Capacidad Industrial</h2>
                  <p className="text-amber-100 text-xs mt-0.5">
                    {section.competitor_id ? 'Evaluación de capacidad productiva del competidor' : 'Evaluación de capacidad productiva de la unidad de negocio'}
                  </p>
                </div>
              </div>
              {(isAdmin || isNVC) && !phaseLocked && (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const header = ['Planta / Línea', 'Capacidad instalada', 'Unidad', 'Utilización %', 'Turnos por día', 'Días operación/año', 'Tecnología / proceso', 'Productos elaborados', 'Certificaciones', 'Año instalación', 'Observaciones']
                      const wb = XLSX.utils.book_new()
                      const ws = XLSX.utils.aoa_to_sheet([header, ['Planta Principal', '10000', 'ton/año', '75', '2', '250', 'Molienda húmeda', 'Harina integral', 'ISO 9001', '2010', '']])
                      ws['!cols'] = header.map(() => ({ wch: 22 }))
                      XLSX.utils.book_append_sheet(wb, ws, 'Capacidad Industrial')
                      XLSX.writeFile(wb, 'plantilla_capacidad_industrial.xlsx')
                    }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors">
                    <Download size={14} /> Plantilla
                  </button>
                  <button onClick={saveCap} disabled={savingCap}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 ${capSaved ? 'bg-emerald-400 text-white' : 'bg-white text-amber-700 hover:bg-amber-50'}`}>
                    {savingCap ? <><Loader size={14} className="animate-spin" />Guardando...</> : capSaved ? <><CheckCircle2 size={14} />¡Guardado!</> : <><Save size={14} />Guardar</>}
                  </button>
                </div>
              )}
            </div>

            {/* Bloque 1: Infraestructura productiva */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <BloqueHeader label="Infraestructura productiva" bloqueKey="bloque1_aplica" />
              {capForm.bloque1_aplica ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 block mb-1">Número de plantas / instalaciones</label>
                      <input type="number" min="0" value={capForm.num_plantas} onChange={e => setCapForm(f => ({ ...f, num_plantas: e.target.value }))}
                        placeholder="Ej: 3" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 block mb-1">Ubicación de plantas</label>
                      <input type="text" value={capForm.ubicacion_plantas} onChange={e => setCapForm(f => ({ ...f, ubicacion_plantas: e.target.value }))}
                        placeholder="Ej: Bogotá, Medellín" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 block mb-1">Capacidad instalada</label>
                      <input type="number" min="0" value={capForm.capacidad_instalada} onChange={e => setCapForm(f => ({ ...f, capacidad_instalada: e.target.value }))}
                        placeholder="0" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 block mb-1">Unidad</label>
                      <input type="text" value={capForm.capacidad_unidad} onChange={e => setCapForm(f => ({ ...f, capacidad_unidad: e.target.value }))}
                        placeholder="toneladas/año" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 block mb-1">
                        Utilización de capacidad <span className={`font-bold ${utilizColor}`}>{capForm.utilizacion_pct ? `${capForm.utilizacion_pct}%` : ''}</span>
                      </label>
                      <div className="relative">
                        <input type="number" min="0" max="100" value={capForm.utilizacion_pct} onChange={e => setCapForm(f => ({ ...f, utilizacion_pct: e.target.value }))}
                          placeholder="0" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                      </div>
                      {capForm.utilizacion_pct && (
                        <div className="mt-1 w-full bg-gray-100 rounded-full h-1.5">
                          <div className={`h-1.5 rounded-full ${utilizPct >= 80 ? 'bg-emerald-500' : utilizPct >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                            style={{ width: `${Math.min(utilizPct, 100)}%` }} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
            </div>

            {/* Bloque 2: Tecnología y equipamiento */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <BloqueHeader label="Tecnología y equipamiento" bloqueKey="bloque2_aplica" />
              {capForm.bloque2_aplica ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 block mb-2">Nivel tecnológico</label>
                      <div className="flex flex-col gap-1.5">
                        {NIVEL_TEC_OPTS.map(o => (
                          <button key={o} type="button" onClick={() => setCapForm(f => ({ ...f, nivel_tecnologico: f.nivel_tecnologico === o ? '' : o }))}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors text-left ${capForm.nivel_tecnologico === o ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'}`}>
                            {o}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-semibold text-gray-600 block mb-1">Grado de automatización (%)</label>
                        <div className="relative">
                          <input type="number" min="0" max="100" value={capForm.grado_automatizacion} onChange={e => setCapForm(f => ({ ...f, grado_automatizacion: e.target.value }))}
                            placeholder="0" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white" />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-600 block mb-1">Antigüedad promedio de equipos</label>
                        <div className="relative">
                          <input type="number" min="0" value={capForm.antiguedad_equipos} onChange={e => setCapForm(f => ({ ...f, antiguedad_equipos: e.target.value }))}
                            placeholder="0" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white" />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">años</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 block mb-1">Descripción de tecnología clave</label>
                      <textarea rows={5} value={capForm.descripcion_tecnologia} onChange={e => setCapForm(f => ({ ...f, descripcion_tecnologia: e.target.value }))}
                        placeholder="Equipos, procesos o tecnologías diferenciadoras..." className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none bg-white" />
                    </div>
                  </div>
                </div>
              ) : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
            </div>

            {/* Bloque 3: Calidad y certificaciones */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <BloqueHeader label="Calidad y certificaciones" bloqueKey="bloque3_aplica" />
              {capForm.bloque3_aplica ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-2">Certificaciones</label>
                    <div className="flex gap-2 flex-wrap">
                      {CERT_OPTS.map(c => (
                        <button key={c} type="button"
                          onClick={() => setCapForm(f => ({ ...f, certificaciones: f.certificaciones.includes(c) ? f.certificaciones.filter(x => x !== c) : [...f.certificaciones, c] }))}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${capForm.certificaciones.includes(c) ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'}`}>
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-1">Estándares de calidad adicionales</label>
                    <textarea rows={2} value={capForm.estandares_calidad} onChange={e => setCapForm(f => ({ ...f, estandares_calidad: e.target.value }))}
                      placeholder="Otras certificaciones o estándares relevantes..." className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none bg-white" />
                  </div>
                </div>
              ) : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
            </div>

            {/* Bloque 4: Integración y flexibilidad */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <BloqueHeader label="Integración y flexibilidad" bloqueKey="bloque4_aplica" />
              {capForm.bloque4_aplica ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-6">
                    {[
                      { label: 'Integración vertical', key: 'integracion_vertical' },
                      { label: 'Flexibilidad de línea', key: 'flexibilidad_linea' },
                      { label: 'Capacidad de expansión', key: 'capacidad_expansion' },
                    ].map(({ label, key }) => (
                      <div key={key}>
                        <label className="text-xs font-semibold text-gray-600 block mb-2">{label}</label>
                        <div className="flex flex-col gap-1.5">
                          {INTEG_OPTS.map(o => (
                            <button key={o} type="button" onClick={() => setCapForm(f => ({ ...f, [key]: f[key] === o ? '' : o }))}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${capForm[key] === o
                                ? o === 'Alta' ? 'bg-emerald-500 text-white border-emerald-500'
                                  : o === 'Media' ? 'bg-amber-500 text-white border-amber-500'
                                  : 'bg-red-400 text-white border-red-400'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'}`}>
                              {o}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-1">Notas de integración</label>
                    <textarea rows={2} value={capForm.notas_integracion} onChange={e => setCapForm(f => ({ ...f, notas_integracion: e.target.value }))}
                      placeholder="Integración con proveedores, distribución, etc." className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none bg-white" />
                  </div>
                </div>
              ) : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
            </div>

            {/* Bloque 5: Fortalezas y brechas */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <BloqueHeader label="Fortalezas y brechas" bloqueKey="bloque5_aplica" />
              {capForm.bloque5_aplica ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-1">
                      {section.competitor_id ? 'Ventajas productivas frente al mercado' : 'Ventajas y fortalezas productivas'}
                    </label>
                    <textarea rows={4} value={capForm.ventajas_productivas} onChange={e => setCapForm(f => ({ ...f, ventajas_productivas: e.target.value }))}
                      placeholder="¿Qué ventajas tiene su capacidad productiva?" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none bg-white" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-1">Brechas o limitaciones identificadas</label>
                    <textarea rows={4} value={capForm.brechas_limitaciones} onChange={e => setCapForm(f => ({ ...f, brechas_limitaciones: e.target.value }))}
                      placeholder="¿Qué limitaciones o brechas tiene?" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none bg-white" />
                  </div>
                </div>
              ) : <p className="text-xs text-gray-400 italic">Bloque marcado como No aplica</p>}
            </div>

            {renderConclusionBlock('cap_industrial', '')}
          </div>
        )
      })()}

      {/* ── Competitor Canales Matrix Tab ────────────────────── */}
      {activeTab === 'canales_comp' && (() => {
        async function saveAllCompChans() {
          setSavingCompChans(true)
          // Save presence records (num_clientes, clientes_ponderado)
          await Promise.all(compChanPresence.map(p => {
            const f = compChanForms[p.id] ?? {}
            return supabase.from('competitor_channel_presence').update({
              num_clientes:       f.num_clientes !== '' ? Number(f.num_clientes) : null,
              clientes_ponderado: f.clientes_ponderado !== '' ? Number(f.clientes_ponderado) : null,
              updated_at:         new Date().toISOString(),
            }).eq('id', p.id)
          }))
          // Save weight rows (upsert by channel_presence_id + market_product_id)
          const allWeightUpdates = []
          for (const p of compChanPresence) {
            const wf = compChanWeightForms[p.id] ?? {}
            for (const [productKey, weight] of Object.entries(wf)) {
              allWeightUpdates.push(
                supabase.from('competitor_channel_product_weights')
                  .update({ weight: weight !== '' ? Number(weight) : null })
                  .eq('channel_presence_id', p.id)
                  .eq('market_product_id', productKey)
              )
            }
          }
          await Promise.all(allWeightUpdates)
          setSavingCompChans(false)
          setCompChansSaved(true)
          setTimeout(() => setCompChansSaved(false), 2500)
        }

        function updateChanForm(presenceId, field, value) {
          setCompChanForms(prev => ({ ...prev, [presenceId]: { ...(prev[presenceId] ?? {}), [field]: value } }))
        }

        function updateWeightForm(presenceId, productKey, value) {
          setCompChanWeightForms(prev => ({ ...prev, [presenceId]: { ...(prev[presenceId] ?? {}), [productKey]: value } }))
        }

        // Totals per product (competitor) for validation
        const colTotals = mktProdRef.reduce((acc, prod) => {
          let total = 0
          compChanPresence.forEach(p => {
            const val = compChanWeightForms[p.id]?.[prod.id] ?? ''
            total += val !== '' ? Number(val) : 0
          })
          acc[prod.id] = Math.round(total * 10) / 10
          return acc
        }, {})

        return (
          <div className="space-y-5">
            {/* Header */}
            <div className="rounded-xl bg-gradient-to-r from-teal-600 to-teal-700 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <TrendingUp size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-base">Mix de Canales del Competidor</h2>
                  <p className="text-teal-100 text-xs mt-0.5">
                    {compChanPresence.length} canal{compChanPresence.length !== 1 ? 'es' : ''} · {mktProdRef.length} producto{mktProdRef.length !== 1 ? 's' : ''} · Referencia en azul = mercado
                  </p>
                </div>
              </div>
              {(isAdmin || isNVC) && !phaseLocked && (
                <button onClick={saveAllCompChans} disabled={savingCompChans}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 ${
                    compChansSaved ? 'bg-emerald-400 text-white' : 'bg-white text-teal-700 hover:bg-teal-50'
                  }`}>
                  {savingCompChans ? <><Loader size={14} className="animate-spin" /> Guardando...</>
                    : compChansSaved ? <><CheckCircle2 size={14} /> ¡Guardado!</>
                    : <><Save size={14} /> Guardar todo</>}
                </button>
              )}
            </div>

            {compChansImporting && (
              <div className="flex items-center gap-3 p-4 bg-teal-50 border border-teal-200 rounded-xl text-teal-700 text-sm">
                <Loader size={16} className="animate-spin flex-shrink-0" />
                Importando canales desde Análisis de Mercado...
              </div>
            )}

            {!compChansImporting && compChanPresence.length === 0 && (
              <div className="text-center py-14 text-gray-400">
                <TrendingUp size={36} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium text-gray-500">Sin canales de referencia</p>
                <p className="text-sm mt-1 max-w-sm mx-auto">Primero crea los canales en la sección de Canales del Análisis de Mercado.</p>
              </div>
            )}

            {compChanPresence.length > 0 && (
              <div className="space-y-4">
                {compChanPresence.map((pres, pIdx) => {
                  const mktChan = mktChanRef.find(c => c.id === pres.market_channel_id)
                  const form    = compChanForms[pres.id] ?? {}
                  const wForm   = compChanWeightForms[pres.id] ?? {}

                  return (
                    <div key={pres.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                      {/* Channel header */}
                      <div className="bg-teal-50 border-b border-teal-100 px-5 py-3 flex items-center gap-4 flex-wrap">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className="w-7 h-7 rounded-lg bg-teal-600 flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-xs">{pIdx + 1}</span>
                          </div>
                          <span className="font-semibold text-teal-900 text-sm">{pres.channel_name}</span>
                        </div>
                        {/* Clientes — referencia vs competidor */}
                        <div className="flex items-center gap-4 flex-wrap">
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <p className="text-xs text-gray-400">Clientes mercado</p>
                              <p className="text-xs font-bold text-blue-700">{mktChan?.num_clientes != null ? mktChan.num_clientes.toLocaleString('es-CO') : '—'}</p>
                            </div>
                            <div className="w-px h-8 bg-gray-200" />
                            <div>
                              <p className="text-xs text-gray-500 mb-0.5">Clientes competidor</p>
                              <input type="number" value={form.num_clientes ?? ''} onChange={e => updateChanForm(pres.id, 'num_clientes', e.target.value)}
                                placeholder="—"
                                className="w-24 text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-teal-300 bg-white text-center font-medium" />
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <p className="text-xs text-gray-400">Ponderado mercado</p>
                              <p className="text-xs font-bold text-blue-700">{mktChan?.clientes_ponderado != null ? mktChan.clientes_ponderado.toLocaleString('es-CO') : '—'}</p>
                            </div>
                            <div className="w-px h-8 bg-gray-200" />
                            <div>
                              <p className="text-xs text-gray-500 mb-0.5">Ponderado competidor</p>
                              <input type="number" value={form.clientes_ponderado ?? ''} onChange={e => updateChanForm(pres.id, 'clientes_ponderado', e.target.value)}
                                placeholder="—"
                                className="w-24 text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-teal-300 bg-white text-center font-medium" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Product weights grid */}
                      {mktProdRef.length > 0 && (
                        <div className="px-5 py-4">
                          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Peso del canal por producto (%)</p>
                          <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(mktProdRef.length, 4)}, 1fr)` }}>
                            {mktProdRef.map(prod => {
                              const mktWeight   = mktChanWeightsRef[pres.market_channel_id]?.[prod.id]
                              const compWeight  = wForm[prod.id] ?? ''
                              return (
                                <div key={prod.id} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                                  <p className="text-xs font-semibold text-gray-700 mb-2 truncate" title={prod.name}>{prod.name}</p>
                                  {/* Reference */}
                                  <div className="flex items-center gap-1 mb-1.5">
                                    <span className="text-xs text-blue-500 font-medium">Mercado:</span>
                                    <span className="text-xs font-bold text-blue-700">
                                      {mktWeight != null ? `${mktWeight}%` : '—'}
                                    </span>
                                  </div>
                                  {/* Competitor input */}
                                  <div className="relative">
                                    <input type="number" min="0" max="100" value={compWeight}
                                      onChange={e => updateWeightForm(pres.id, prod.id, e.target.value)}
                                      placeholder="0"
                                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 pr-7 focus:outline-none focus:ring-2 focus:ring-teal-300 bg-white font-semibold text-center" />
                                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-medium">%</span>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                          {/* Column totals */}
                          <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-3">
                            {mktProdRef.map(prod => {
                              const total = colTotals[prod.id] ?? 0
                              const ok = total === 100
                              const has = total > 0
                              return has ? (
                                <span key={prod.id} className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${ok ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                                  {prod.name}: {total}% {ok ? '✓' : ''}
                                </span>
                              ) : null
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {renderConclusionBlock('canales_comp', '')}
          </div>
        )
      })()}

      {/* ── Segmentación: Perfiles de Consumidor Tab ─────────── */}
      {activeTab === 'perfiles' && (() => {
        const PROFILE_SUBSECTIONS = [
          'Necesidades y Tensiones',
          'Sobre la Solución',
          'Adopción de la Categoría',
          'Adopción de Marca y Nivel de Lealtad',
        ]
        const SUBSECTION_COLORS = [
          { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', dot: 'bg-rose-400' },
          { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-400' },
          { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', dot: 'bg-sky-400' },
          { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-400' },
        ]

        async function createSegProfile(e) {
          e.preventDefault()
          setSavingSegProfile(true)
          const nextOrder = segProfiles.length + 1
          const { data: newProfile } = await supabase
            .from('consumer_profiles')
            .insert({
              phase_id:      phaseId,
              name:          segProfileForm.name,
              description:   segProfileForm.description || null,
              profile_order: nextOrder,
            })
            .select()
            .single()

          if (newProfile) {
            // Auto-create the 4 standard sub-sections
            await supabase.from('sections').insert(
              PROFILE_SUBSECTIONS.map((name, i) => ({
                phase_id:            phaseId,
                consumer_profile_id: newProfile.id,
                name,
                section_order:       i + 1,
                status:              'pending',
              }))
            )
            // Reload profiles
            const { data: updated } = await supabase
              .from('consumer_profiles')
              .select('*, sections(id, name, status, section_order, tasks(id, status))')
              .eq('phase_id', phaseId)
              .order('profile_order')
            setSegProfiles((updated ?? []).map(p => ({
              ...p,
              sections: (p.sections ?? []).sort((a, b) => a.section_order - b.section_order),
            })))
            setShowSegProfileModal(false)
            setSegProfileForm({ name: '', description: '' })
          }
          setSavingSegProfile(false)
        }

        async function deleteSegProfile(profileId) {
          if (!confirm('¿Eliminar este perfil y todas sus secciones?')) return
          await supabase.from('consumer_profiles').delete().eq('id', profileId)
          setSegProfiles(prev => prev.filter(p => p.id !== profileId))
        }

        return (
          <div className="space-y-5">
            {/* Header */}
            <div className="rounded-xl bg-gradient-to-r from-violet-600 to-violet-700 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <User size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-base">Perfiles de Consumidor</h2>
                  <p className="text-violet-100 text-xs mt-0.5">
                    {segProfiles.length} perfil{segProfiles.length !== 1 ? 'es' : ''} definido{segProfiles.length !== 1 ? 's' : ''}
                    {' · '}Cada perfil genera automáticamente 4 secciones de análisis
                  </p>
                </div>
              </div>
              {(isAdmin || isNVC) && !phaseLocked && (
                <button onClick={() => setShowSegProfileModal(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-violet-700 hover:bg-violet-50 text-sm font-semibold transition-colors">
                  <Plus size={14} /> Nuevo Perfil
                </button>
              )}
            </div>

            {/* Profile cards */}
            {segProfiles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {segProfiles.map(profile => {
                  const secs = profile.sections ?? []
                  const completed = secs.filter(s => s.status === 'completed').length
                  const pct = secs.length > 0 ? Math.round((completed / secs.length) * 100) : 0
                  return (
                    <div key={profile.id} className="rounded-2xl border border-violet-200 bg-white overflow-hidden shadow-sm hover:border-violet-300 transition-colors">
                      {/* Card header */}
                      <div className="flex items-start gap-3 p-4">
                        <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center text-lg flex-shrink-0">👤</div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-gray-900 text-sm">{profile.name}</p>
                          {profile.description && (
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{profile.description}</p>
                          )}
                          {/* Progress bar */}
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-gray-400">{completed}/{secs.length} secciones</span>
                              <span className={`font-semibold ${pct === 100 ? 'text-emerald-600' : 'text-violet-600'}`}>{pct}%</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                              <div className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-emerald-500' : 'bg-violet-500'}`}
                                style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        </div>
                        {(isAdmin || isNVC) && !phaseLocked && (
                          <button onClick={() => deleteSegProfile(profile.id)}
                            className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0">
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                      {/* Sub-sections */}
                      <div className="border-t border-violet-100 divide-y divide-violet-50">
                        {secs.map((sec, i) => {
                          const colors = SUBSECTION_COLORS[i % SUBSECTION_COLORS.length]
                          const doneTasks  = sec.tasks?.filter(t => t.status === 'completed').length ?? 0
                          const totalTasks = sec.tasks?.length ?? 0
                          const link = buId
                            ? `/consultancy/${id}/bu/${buId}/phase/${phaseId}/section/${sec.id}`
                            : `/consultancy/${id}/phase/${phaseId}/section/${sec.id}`
                          return (
                            <button key={sec.id} onClick={() => navigate(link)}
                              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-violet-50/60 transition-colors`}>
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${sec.status === 'completed' ? 'bg-emerald-400' : colors.dot}`} />
                              <span className="flex-1 text-xs text-gray-700 font-medium">{sec.name}</span>
                              {totalTasks > 0 && (
                                <span className="text-xs text-gray-400 flex-shrink-0">{doneTasks}/{totalTasks}</span>
                              )}
                              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium flex-shrink-0 ${
                                sec.status === 'completed' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : `${colors.bg} ${colors.border} ${colors.text}`
                              }`}>
                                {sec.status === 'completed' ? 'Completo' : sec.status === 'in_progress' ? 'En progreso' : 'Pendiente'}
                              </span>
                              <ChevronRight size={12} className="text-gray-300 flex-shrink-0" />
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-16 card text-gray-400">
                <div className="text-5xl mb-4">👤</div>
                <p className="font-semibold text-gray-600">Sin perfiles definidos</p>
                <p className="text-sm mt-1 max-w-sm mx-auto">Crea los segmentos de consumidor. Cada perfil generará automáticamente las 4 secciones de análisis.</p>
                {(isAdmin || isNVC) && !phaseLocked && (
                  <button onClick={() => setShowSegProfileModal(true)}
                    className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold transition-colors">
                    <Plus size={15} /> Crear primer perfil
                  </button>
                )}
              </div>
            )}

            {/* Create Profile Modal */}
            <Modal open={showSegProfileModal} onClose={() => { setShowSegProfileModal(false); setSegProfileForm({ name: '', description: '' }) }}
              title="Nuevo Perfil de Consumidor">
              <form onSubmit={createSegProfile} className="space-y-4">
                <div>
                  <label className="label">Nombre del segmento *</label>
                  <input className="input" required
                    value={segProfileForm.name}
                    onChange={e => setSegProfileForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Ej: Panaderías independientes, Restaurantes industriales..." />
                </div>
                <div>
                  <label className="label">Descripción del segmento</label>
                  <textarea className="input resize-none" rows={3}
                    value={segProfileForm.description}
                    onChange={e => setSegProfileForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Características principales: tamaño, comportamiento de compra, necesidades clave..." />
                </div>
                <div className="rounded-lg bg-violet-50 border border-violet-100 p-3 space-y-1.5">
                  <p className="text-xs font-semibold text-violet-700">Se crearán automáticamente 4 secciones:</p>
                  {PROFILE_SUBSECTIONS.map((name, i) => (
                    <div key={name} className={`flex items-center gap-2 text-xs px-2 py-1 rounded-md ${SUBSECTION_COLORS[i].bg} ${SUBSECTION_COLORS[i].text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${SUBSECTION_COLORS[i].dot}`} />
                      {name}
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={() => { setShowSegProfileModal(false); setSegProfileForm({ name: '', description: '' }) }}
                    className="btn-secondary flex-1">Cancelar</button>
                  <button type="submit" disabled={savingSegProfile}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold transition-colors disabled:opacity-50">
                    {savingSegProfile ? 'Creando...' : <><User size={14} /> Crear Perfil</>}
                  </button>
                </div>
              </form>
            </Modal>
          </div>
        )
      })()}

      {/* ── Market Precio Tab ────────────────────────────────── */}
      {activeTab === 'precio' && (() => {
        const COLOR_MKT  = '#D97706' // amber-600 — represents market
        const COMP_PALETTE = ['#7C3AED','#0369A1','#BE123C','#059669','#D97706','#9333EA','#0891B2','#DC2626']

        // All unique product names across product history and comp history
        const allProductNames = Array.from(new Set([
          ...precioProductHistory.map(r => r.name),
          ...precioCompHistory.map(r => r.product_name),
        ]))

        // All unique competitor names
        const allCompNames = Array.from(new Set(precioCompHistory.map(r => r.competitor_name)))

        // Build chart data for a specific product (or global market)
        // Returns array of { year, Mercado: x, [compName]: y, ... }
        function buildChartData(productName) {
          // collect all years
          const years = Array.from(new Set([
            ...(productName === '__global__' ? precioMktHistory.map(r => r.year) : []),
            ...precioProductHistory.filter(r => r.name === productName).map(r => r.year),
            ...precioCompHistory.filter(r => r.product_name === productName).map(r => r.year),
          ])).sort((a, b) => a - b)

          return years.map(year => {
            const row = { year }
            if (productName === '__global__') {
              const mkt = precioMktHistory.find(r => r.year === year)
              if (mkt) row['Mercado Global'] = mkt.avg_price
            } else {
              const mkt = precioProductHistory.find(r => r.name === productName && r.year === year)
              if (mkt) row['Precio de mercado'] = mkt.avg_price
            }
            allCompNames.forEach(cName => {
              const compRow = precioCompHistory.find(r => r.product_name === productName && r.competitor_name === cName && r.year === year)
              if (compRow) row[cName] = compRow.avg_price
            })
            return row
          })
        }

        // All unique price units for tooltip display
        function getPriceUnit(productName) {
          const pr = precioProductHistory.find(r => r.name === productName)
          if (pr) return pr.price_unit
          const cr = precioCompHistory.find(r => r.product_name === productName)
          if (cr) return cr.price_unit
          const mkt = precioMktHistory[0]
          if (mkt) return mkt.unit
          return ''
        }

        const hasAnyData = precioMktHistory.length > 0 || precioProductHistory.length > 0 || precioCompHistory.length > 0

        // Determine active product choices
        const productChoices = [
          ...(precioMktHistory.length > 0 ? [{ key: '__global__', label: 'Mercado Global' }] : []),
          ...allProductNames.map(n => ({ key: n, label: n })),
        ]

        // Auto-set first choice if state is still '__all__'
        const activeProd = precioActiveProduct === '__all__' && productChoices.length > 0
          ? productChoices[0].key
          : precioActiveProduct

        const chartData = activeProd !== '__all__' ? buildChartData(activeProd) : []
        const priceUnit = activeProd !== '__all__' && activeProd !== '__global__' ? getPriceUnit(activeProd) : (precioMktHistory[0]?.unit ?? '')

        // Series for the active chart
        const seriesKeys = chartData.length > 0 ? Object.keys(chartData[0]).filter(k => k !== 'year') : []

        return (
          <div className="space-y-5">
            {/* Header */}
            <div className="rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-700 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <TrendingUp size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-base">Posicionamiento de Precio</h2>
                  <p className="text-emerald-100 text-xs mt-0.5">
                    Evolución de precios del mercado y competidores por producto
                  </p>
                </div>
              </div>
              {hasAnyData && (
                <button onClick={exportPrecioToPptx}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors"
                  title="Descargar gráficas en PowerPoint">
                  <Download size={14} /> PPT
                </button>
              )}
            </div>

            {!hasAnyData ? (
              <div className="text-center py-14 card text-gray-400">
                <TrendingUp size={40} className="mx-auto mb-3 opacity-20" />
                <p className="font-medium">Sin datos de precio</p>
                <p className="text-sm mt-1">Agrega precios promedio en las secciones <span className="font-semibold">Desempeño</span>, <span className="font-semibold">Soluciones</span> y <span className="font-semibold">Competidores</span> para ver la comparación aquí.</p>
              </div>
            ) : (
              <>
                {/* Product selector */}
                {productChoices.length > 1 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-500 font-medium">Producto:</span>
                    {productChoices.map(pc => (
                      <button key={pc.key} type="button"
                        onClick={() => setPrecioActiveProduct(pc.key)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                          activeProd === pc.key
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'border-gray-200 text-gray-600 hover:border-emerald-300 hover:text-emerald-700'
                        }`}>
                        {pc.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* Main line chart */}
                {chartData.length > 0 && seriesKeys.length > 0 ? (
                  <div className="card">
                    <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                      <div>
                        <h3 className="text-sm font-bold text-gray-900">
                          {activeProd === '__global__' ? 'Precio Promedio del Mercado Global' : `Precio Promedio — ${activeProd}`}
                        </h3>
                        {priceUnit && <p className="text-xs text-gray-400 mt-0.5">Unidad: {priceUnit}</p>}
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        {seriesKeys.map((key, i) => {
                          const color = key === 'Mercado Global' || key === 'Precio de mercado'
                            ? COLOR_MKT
                            : COMP_PALETTE[(allCompNames.indexOf(key)) % COMP_PALETTE.length]
                          const isMkt = key === 'Mercado Global' || key === 'Precio de mercado'
                          return (
                            <div key={key} className="flex items-center gap-1.5">
                              <div className="w-5 h-0.5 rounded-full" style={{ backgroundColor: color, borderTop: isMkt ? '' : '2px dashed' }} />
                              <span className="text-xs text-gray-600">{key}</span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={320}>
                      <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                        <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#6B7280' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickFormatter={v => v.toLocaleString('es-CO')} />
                        <Tooltip
                          contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }}
                          formatter={(value, name) => [value != null ? value.toLocaleString('es-CO', { maximumFractionDigits: 4 }) : '—', name]}
                          labelFormatter={label => `Año ${label}`}
                        />
                        {seriesKeys.map((key, i) => {
                          const isMkt = key === 'Mercado Global' || key === 'Precio de mercado'
                          const color = isMkt
                            ? COLOR_MKT
                            : COMP_PALETTE[(allCompNames.indexOf(key)) % COMP_PALETTE.length]
                          return (
                            <Line key={key} type="monotone" dataKey={key}
                              stroke={color} strokeWidth={isMkt ? 3 : 2}
                              strokeDasharray={isMkt ? undefined : '5 3'}
                              dot={{ r: 5, fill: color, strokeWidth: 2, stroke: '#fff' }}
                              activeDot={{ r: 7 }}
                              connectNulls
                            />
                          )
                        })}
                      </LineChart>
                    </ResponsiveContainer>

                    {/* Price delta table for latest year */}
                    {(() => {
                      const lastYear = Math.max(...chartData.map(r => r.year))
                      const lastRow = chartData.find(r => r.year === lastYear)
                      if (!lastRow) return null
                      const mktPrice = lastRow['Precio de mercado'] ?? lastRow['Mercado Global']
                      const compRows = allCompNames.filter(c => lastRow[c] != null)
                      if (!mktPrice || compRows.length === 0) return null
                      return (
                        <div className="mt-4 border-t border-gray-100 pt-4">
                          <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Postura vs precio de mercado — {lastYear}</p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                            {compRows.map((cName, i) => {
                              const cp = lastRow[cName]
                              const delta = ((cp - mktPrice) / mktPrice * 100).toFixed(1)
                              const isOver = parseFloat(delta) > 0
                              return (
                                <div key={cName} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                                  <p className="text-xs text-gray-500 truncate mb-1">{cName}</p>
                                  <p className="text-sm font-bold text-gray-800">{cp.toLocaleString('es-CO', { maximumFractionDigits: 4 })}</p>
                                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${isOver ? 'bg-red-50 text-red-600' : parseFloat(delta) < 0 ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                                    {isOver ? '+' : ''}{delta}% vs mercado
                                  </span>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })()}
                  </div>
                ) : (
                  <div className="card text-center py-10 text-gray-400">
                    <TrendingUp size={32} className="mx-auto mb-2 opacity-20" />
                    <p className="text-sm">Sin datos de precios para este producto</p>
                  </div>
                )}

                {/* Mini panel: all products side by side (only when viewing global) */}
                {activeProd === '__global__' && allProductNames.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-bold text-gray-700 px-1">Evolución por producto</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {allProductNames.map(pName => {
                        const pData = buildChartData(pName)
                        const pKeys = pData.length > 0 ? Object.keys(pData[0]).filter(k => k !== 'year') : []
                        if (pData.length === 0 || pKeys.length === 0) return null
                        return (
                          <div key={pName} className="card cursor-pointer hover:border-emerald-200 transition-colors"
                            onClick={() => setPrecioActiveProduct(pName)}>
                            <p className="text-xs font-bold text-gray-700 mb-2">{pName}</p>
                            <ResponsiveContainer width="100%" height={160}>
                              <LineChart data={pData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                                <XAxis dataKey="year" tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                                <YAxis tick={{ fontSize: 9, fill: '#9CA3AF' }} tickFormatter={v => v.toLocaleString('es-CO')} width={55} />
                                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }}
                                  formatter={v => [v?.toLocaleString('es-CO', { maximumFractionDigits: 4 }), '']}
                                  labelFormatter={l => `Año ${l}`} />
                                {pKeys.map((key, i) => {
                                  const isMkt = key === 'Precio de mercado'
                                  const color = isMkt ? COLOR_MKT : COMP_PALETTE[allCompNames.indexOf(key) % COMP_PALETTE.length]
                                  return (
                                    <Line key={key} type="monotone" dataKey={key}
                                      stroke={color} strokeWidth={isMkt ? 2.5 : 1.5}
                                      strokeDasharray={isMkt ? undefined : '4 2'}
                                      dot={{ r: 3, fill: color }}
                                      connectNulls
                                    />
                                  )
                                })}
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )
      })()}

      {/* ── Market Products Tab ──────────────────────────────── */}
      {activeTab === 'productos' && (
        <div className="space-y-5">
          {/* Header */}
          <div className="rounded-xl bg-gradient-to-r from-violet-600 to-violet-700 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <BarChart2 size={18} className="text-white" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-base">Mix de Productos del Mercado</h2>
                <p className="text-violet-100 text-xs mt-0.5">
                  {products.length} producto{products.length !== 1 ? 's' : ''} · Total:{' '}
                  {totalProductsSize > 0 ? `${Number(totalProductsSize).toLocaleString('es-CO')} ${products[0]?.size_unit ?? ''}` : '—'}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {products.length > 0 && (
                <button onClick={exportProductsToPptx}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors"
                  title="Descargar gráfica en PowerPoint">
                  <Download size={14} /> PPT
                </button>
              )}
              {(isAdmin || isNVC || isCaja) && !phaseLocked && (
                <>
                  <button
                    onClick={() => openBulkDelete('product')}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/90 hover:bg-red-500 text-white text-sm font-semibold transition-colors"
                    title="Eliminar un producto y toda su data histórica (todos los años y trimestres)"
                  >
                    <Trash2 size={14} /> Eliminar producto
                  </button>
                  <button
                    onClick={openCreateProduct}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-violet-700 hover:bg-violet-50 text-sm font-semibold transition-colors"
                  >
                    <Plus size={14} /> Agregar producto
                  </button>
                </>
              )}
            </div>
          </div>

          {/* ── Tendencia por Variable ── */}
          {productsTrendData.length > 0 && (() => {
            const allProdNames = [...new Set(productsTrendData.flatMap(yd => Object.keys(yd.products)))]
            const PROD_COLORS = ['#7C3AED','#A78BFA','#5B21B6','#DDD6FE','#4C1D95','#C4B5FD','#6D28D9','#8B5CF6']
            const sizeUnit = productsTrendData[productsTrendData.length - 1]?.sizeUnit ?? 'USD millones'
            const volUnit  = productsTrendData[productsTrendData.length - 1]?.volUnit  ?? 'toneladas'
            const chartData = productsTrendData.map(yd => {
              const row = { year: String(yd.year) }
              allProdNames.forEach(name => {
                row[name] = prodTrendVar === 'ventas'
                  ? (yd.products[name]?.sales  ?? 0)
                  : (yd.products[name]?.volume ?? 0)
              })
              return row
            })
            const hasMultiYear = productsTrendData.length >= 2
            return (
              <div className="card space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="text-base font-bold text-gray-900">Evolución del Mix de Productos</h3>
                  <div className="flex items-center gap-1 p-0.5 bg-violet-50 rounded-lg border border-violet-100">
                    {[{ key: 'ventas', label: `Ventas (${sizeUnit})` }, { key: 'volumen', label: `Volumen (${volUnit})` }].map(v => (
                      <button key={v.key} onClick={() => setProdTrendVar(v.key)}
                        className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                          prodTrendVar === v.key ? 'bg-violet-600 text-white shadow-sm' : 'text-violet-600 hover:text-violet-800'
                        }`}>{v.label}</button>
                    ))}
                  </div>
                </div>
                {!hasMultiYear ? (
                  <p className="text-sm text-gray-400 text-center py-4">Agrega datos de más años para ver la tendencia</p>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                      <XAxis dataKey="year" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={50}
                        tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                      <Tooltip formatter={(v, n) => [v.toLocaleString('es-CO'), n]} labelStyle={{ fontWeight: 600, color: '#374151' }} />
                      <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                      {allProdNames.map((name, i) => (
                        <Bar key={name} dataKey={name} stackId="products"
                          fill={PROD_COLORS[i % PROD_COLORS.length]}
                          radius={i === allProdNames.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            )
          })()}

          {/* Year + Quarter selector */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500 font-medium w-14">Año:</span>
              {Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i).map(y => (
                <button key={y} type="button" onClick={() => setSelectedYear(y)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                    selectedYear === y ? 'bg-violet-600 text-white border-violet-600' : 'border-gray-200 text-gray-600 hover:border-violet-300 hover:text-violet-700'
                  }`}>{y}</button>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500 font-medium w-14">Período:</span>
              {[{ label: 'Anual', value: null }, { label: 'Q1', value: 1 }, { label: 'Q2', value: 2 }, { label: 'Q3', value: 3 }, { label: 'Q4', value: 4 }].map(({ label, value }) => (
                <button key={label} type="button" onClick={() => setSelectedQuarter(value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                    selectedQuarter === value ? 'bg-violet-600 text-white border-violet-600' : 'border-gray-200 text-gray-600 hover:border-violet-300 hover:text-violet-700'
                  }`}>{label}</button>
              ))}
            </div>
          </div>

          {/* Products table (editable) */}
          {products.length > 0 ? (
            <div className="space-y-4">
              <div className="card overflow-hidden p-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-violet-50 border-b border-violet-100">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-violet-700 uppercase tracking-wide">Producto</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-violet-700 uppercase tracking-wide">Ventas</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-violet-700 uppercase tracking-wide hidden md:table-cell">Volumen</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-violet-700 uppercase tracking-wide hidden md:table-cell">P̄ Precio</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-violet-700 uppercase tracking-wide">% Total</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-violet-700 uppercase tracking-wide">CAGR</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-violet-700 uppercase tracking-wide">Crec. último año</th>
                      {(isAdmin || isNVC || isCaja) && <th className="px-4 py-3 text-right text-xs font-semibold text-violet-700 uppercase tracking-wide">Acciones</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sortedProducts.map(p => {
                      const pct = totalProductsSize > 0 ? ((p.sales_size / totalProductsSize) * 100).toFixed(1) : '0.0'
                      const growthPos = parseFloat(p.last_year_growth) >= 0
                      const vol = p.volume != null ? Number(p.volume) : null
                      const avgP = vol != null && vol > 0 ? Number(p.sales_size) / vol : null
                      const avgPStr = avgP != null ? (avgP < 1 ? avgP.toFixed(4) : avgP.toLocaleString('es-CO', { maximumFractionDigits: 2 })) : '—'
                      return (
                        <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-800">{p.name}</td>
                          <td className="px-4 py-3 text-right text-gray-700">
                            {Number(p.sales_size).toLocaleString('es-CO')}
                            <span className="ml-1 text-xs text-gray-400">{p.size_unit}</span>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600 text-xs hidden md:table-cell">
                            {vol != null ? <>{vol.toLocaleString('es-CO')} <span className="text-gray-400">{p.volume_unit}</span></> : '—'}
                          </td>
                          <td className="px-4 py-3 text-right text-xs hidden md:table-cell">
                            {avgP != null
                              ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-semibold">{avgPStr}</span>
                              : <span className="text-gray-400">—</span>
                            }
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-100 text-violet-700">{pct}%</span>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700 text-xs">
                            {p.cagr !== null && p.cagr !== undefined ? `${p.cagr}%` : '—'}
                          </td>
                          <td className="px-4 py-3 text-right text-xs">
                            {p.last_year_growth !== null && p.last_year_growth !== undefined ? (
                              <span className={growthPos ? 'text-green-600 font-semibold' : 'text-red-500 font-semibold'}>
                                {p.last_year_growth > 0 ? '+' : ''}{p.last_year_growth}%
                              </span>
                            ) : '—'}
                          </td>
                          {(isAdmin || isNVC || isCaja) && !phaseLocked && (
                            <td className="px-4 py-3">
                              <div className="flex gap-1.5 justify-end">
                                <button onClick={() => openEditProduct(p)}
                                  title="Editar producto"
                                  className="p-1.5 text-violet-600 hover:bg-violet-100 rounded-lg transition-colors border border-violet-200">
                                  <Pencil size={14} />
                                </button>
                                <button onClick={() => deleteProduct(p.id)}
                                  title="Eliminar producto"
                                  className="p-1.5 text-red-500 hover:bg-red-100 rounded-lg transition-colors border border-red-200">
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-violet-50 border-t-2 border-violet-200">
                      <td className="px-4 py-3 text-sm font-bold text-violet-800">Total Mercado</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-violet-800">
                        {Number(totalProductsSize).toLocaleString('es-CO')}
                        {products[0]?.size_unit && <span className="ml-1 text-xs font-normal text-violet-500">{products[0].size_unit}</span>}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-violet-800">100%</td>
                      <td colSpan={(isAdmin || isNVC) ? 6 : 5} />
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Horizontal bar ranking chart */}
              <div className="card space-y-5">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-bold text-gray-900">Ranking de Productos por Tamaño de Mercado</h3>
                  <span className="text-xs text-gray-400">% sobre mercado total</span>
                </div>

                <div className="space-y-3">
                  {sortedProducts.map((p, idx) => {
                    const pct = totalProductsSize > 0 ? (p.sales_size / totalProductsSize) * 100 : 0
                    const COLORS = ['bg-violet-700','bg-violet-600','bg-violet-500','bg-violet-400','bg-violet-300','bg-violet-200','bg-violet-100']
                    const barColor = COLORS[Math.min(idx, COLORS.length - 1)]
                    const textColor = idx < 3 ? 'text-white' : 'text-violet-800'
                    return (
                      <div key={p.id} className="space-y-1">
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-0.5">
                          <span className="font-semibold text-gray-800">
                            <span className="text-violet-400 mr-1.5">#{idx + 1}</span>{p.name}
                          </span>
                          <span className="text-gray-500 flex items-center gap-2 flex-wrap justify-end">
                            <span>{Number(p.sales_size).toLocaleString('es-CO')} {p.size_unit}</span>
                            {p.volume != null && Number(p.volume) > 0 && (() => {
                              const avgPr = Number(p.sales_size) / Number(p.volume)
                              return <span className="text-violet-600 font-semibold">P̄ {avgPr < 1 ? avgPr.toFixed(3) : avgPr.toLocaleString('es-CO', { maximumFractionDigits: 2 })}</span>
                            })()}
                            <span className="font-semibold text-violet-700">{pct.toFixed(1)}%</span>
                            {p.cagr !== null && p.cagr !== undefined && <span className="text-gray-400">CAGR {p.cagr}%</span>}
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-8 overflow-hidden relative">
                          <div
                            className={`h-full rounded-full ${barColor} transition-all flex items-center px-3`}
                            style={{ width: `${Math.max(pct, 2)}%` }}
                          >
                            {pct > 8 && <span className={`text-xs font-semibold ${textColor}`}>{pct.toFixed(1)}%</span>}
                          </div>
                        </div>
                        {p.description && <p className="text-xs text-gray-400 pl-1">{p.description}</p>}
                      </div>
                    )
                  })}
                </div>

                {/* Summary footer */}
                <div className="border-t border-gray-100 pt-4 grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-violet-700">{products.length}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Productos identificados</p>
                  </div>
                  <div className="text-center border-x border-gray-100">
                    <p className="text-2xl font-bold text-violet-700">{Number(totalProductsSize).toLocaleString('es-CO')}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{products[0]?.size_unit ?? 'Tamaño total'}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-violet-700">{sortedProducts[0]?.name?.split(' ')[0] ?? '—'}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Producto líder</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-14 text-gray-400 card">
              <BarChart2 size={40} className="mx-auto mb-3 opacity-20" />
              {selectedQuarter === null ? (
                <>
                  <p className="font-medium">Selecciona un trimestre para ver los productos</p>
                  <p className="text-sm mt-1">Los datos de productos se registran por trimestre (Q1, Q2, Q3, Q4)</p>
                </>
              ) : (
                <>
                  <p className="font-medium">Sin productos agregados</p>
                  <p className="text-sm mt-1">Agrega los productos del mercado para visualizar el mix</p>
                  {(isAdmin || isNVC) && !phaseLocked && (
                    <button onClick={openCreateProduct} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold transition-colors">
                      <Plus size={15} /> Agregar primer producto
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Evolución Histórica de Productos ── */}
          {productsHistory.length > 1 && (
            <div className="card space-y-4 border border-violet-100">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <TrendingUp size={15} className="text-violet-500" /> Evolución Histórica del Mix de Productos
              </h3>
              <div className="space-y-3">
                {(() => {
                  const maxSize = Math.max(...productsHistory.map(p => p.total_size || 0), 1)
                  return productsHistory.map(h => {
                    const pct = (h.total_size / maxSize) * 100
                    const isActive = h.year === selectedYear
                    return (
                      <div key={h.year} className="space-y-1 cursor-pointer" onClick={() => setSelectedYear(h.year)}>
                        <div className="flex items-center justify-between text-xs">
                          <span className={`font-semibold ${isActive ? 'text-violet-700' : 'text-gray-600'}`}>
                            {h.year} — {h.count} producto{h.count !== 1 ? 's' : ''} {isActive && '← actual'}
                          </span>
                          <span className="text-gray-500">
                            {h.total_size > 0 ? `${h.total_size.toLocaleString('es-CO')} ${h.size_unit ?? ''}` : '—'}
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-6 overflow-hidden">
                          <div className={`h-full rounded-full transition-all flex items-center px-2 ${isActive ? 'bg-violet-600' : 'bg-violet-200'}`}
                            style={{ width: `${Math.max(pct, 2)}%` }}>
                            {pct > 15 && <span className={`text-xs font-semibold ${isActive ? 'text-white' : 'text-violet-700'}`}>{h.total_size.toLocaleString('es-CO')}</span>}
                          </div>
                        </div>
                      </div>
                    )
                  })
                })()}
              </div>
              <p className="text-xs text-gray-400">Haz clic en un año para ver sus productos</p>
            </div>
          )}
        </div>
      )}

      {/* Product modal */}
      <Modal open={showProductModal} onClose={() => setShowProductModal(false)} title={editProduct ? 'Editar producto' : 'Nuevo producto'}>
        <form onSubmit={saveProduct} className="space-y-4">
          <div>
            <label className="label">Nombre del producto *</label>
            <input className="input" required value={productForm.name}
              onChange={e => setProductForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Ej: Harina todo uso" />
          </div>

          {/* Ventas + unidad */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Ventas *</label>
              <input className="input" required type="number" min="0" step="any" value={productForm.sales_size}
                onChange={e => setProductForm(f => ({ ...f, sales_size: e.target.value }))}
                placeholder="Ej: 120" />
            </div>
            <div>
              <label className="label">Unidad de ventas</label>
              <select className="input" value={productForm.size_unit}
                onChange={e => setProductForm(f => ({ ...f, size_unit: e.target.value }))}>
                {['USD millones','USD miles de millones','COP millones','COP miles de millones','EUR millones'].map(u =>
                  <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>

          {/* Volumen + unidad + precio promedio calculado */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Volumen (unidades)</label>
              <input className="input" type="number" min="0" step="any" value={productForm.volume}
                onChange={e => setProductForm(f => ({ ...f, volume: e.target.value }))}
                placeholder="Ej: 85000" />
            </div>
            <div>
              <label className="label">Unidad de volumen</label>
              <select className="input" value={productForm.volume_unit}
                onChange={e => setProductForm(f => ({ ...f, volume_unit: e.target.value }))}>
                {['toneladas','miles de toneladas','millones de toneladas','miles de unidades','millones de unidades','litros','miles de litros','millones de litros','kg','barriles'].map(u =>
                  <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Precio promedio (calculado)</label>
              <div className="input bg-violet-50 border-violet-200 flex items-center gap-2 text-violet-800 font-semibold cursor-default select-none min-h-[2.5rem]">
                {(() => {
                  const s = parseFloat(productForm.sales_size), v = parseFloat(productForm.volume)
                  if (s > 0 && v > 0) {
                    const price = s / v
                    return <><span className="flex-1">{price < 1 ? price.toFixed(4) : price.toLocaleString('es-CO', { maximumFractionDigits: 2 })}</span><span className="text-xs text-violet-500 font-normal">{productForm.size_unit} / {productForm.volume_unit}</span></>
                  }
                  return <span className="text-gray-400 font-normal text-sm">Ventas ÷ Volumen</span>
                })()}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Año de referencia</label>
              <input type="number" className="input" placeholder={new Date().getFullYear()} value={productForm.reference_year}
                onChange={e => setProductForm(f => ({ ...f, reference_year: e.target.value }))} />
            </div>
            <div>
              <label className="label">CAGR (%)</label>
              <input type="number" step="0.1" className="input" placeholder="Ej: 4.5" value={productForm.cagr}
                onChange={e => setProductForm(f => ({ ...f, cagr: e.target.value }))} />
            </div>
            <div>
              <label className="label">Crec. último año (%)</label>
              <input type="number" step="0.1" className="input" placeholder="Ej: 3.2" value={productForm.last_year_growth}
                onChange={e => setProductForm(f => ({ ...f, last_year_growth: e.target.value }))} />
            </div>
          </div>

          <div>
            <label className="label">Fuente de información</label>
            <input type="text" className="input" placeholder="Ej: Nielsen, Euromonitor, datos propios..." value={productForm.data_source}
              onChange={e => setProductForm(f => ({ ...f, data_source: e.target.value }))} />
          </div>

          <div>
            <label className="label">Descripción</label>
            <textarea className="input resize-none" rows={2} value={productForm.description}
              onChange={e => setProductForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Breve descripción del producto..." />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setShowProductModal(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={savingProduct}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold transition-colors disabled:opacity-50">
              {savingProduct ? 'Guardando...' : editProduct ? 'Guardar cambios' : 'Agregar producto'}
            </button>
          </div>
        </form>
      </Modal>

      {activeTab === 'tasks' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            {isNVC && (
              <button onClick={openCreate} className="btn-primary flex items-center gap-2 text-sm">
                <Plus size={16} /> Agregar tarea
              </button>
            )}
            <div className="ml-auto flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-brand-700' : 'text-gray-400 hover:text-gray-600'}`}
                title="Vista lista">
                <LayoutList size={16} />
              </button>
              <button onClick={() => setViewMode('kanban')}
                className={`p-1.5 rounded-md transition-colors ${viewMode === 'kanban' ? 'bg-white shadow-sm text-brand-700' : 'text-gray-400 hover:text-gray-600'}`}
                title="Vista Kanban">
                <Kanban size={16} />
              </button>
            </div>
          </div>

          {/* List view */}
          {viewMode === 'list' && (
            <div className="space-y-3">
              {tasks.map(task => (
                <TaskCard key={task.id} task={task} isNVC={isNVC} isAdmin={isAdmin}
                  onEdit={() => openEdit(task)} onDelete={() => deleteTask(task.id)}
                  onStatusChange={status => updateTaskStatus(task.id, status)} />
              ))}
              {tasks.length === 0 && (
                <div className="text-center py-10 text-gray-400">
                  <CheckSquare size={40} className="mx-auto mb-2 opacity-30" />
                  <p>No hay tareas en esta sección</p>
                </div>
              )}
            </div>
          )}

          {/* Kanban view */}
          {viewMode === 'kanban' && (
            <div className="flex gap-4 overflow-x-auto pb-4">
              {KANBAN_COLS.map(col => {
                const colTasks = tasks.filter(t => t.status === col.key)
                return (
                  <div key={col.key} onDragOver={onDragOver} onDrop={() => onDrop(col.key)}
                    className={`flex-shrink-0 w-60 bg-gray-50 rounded-xl border-t-4 ${col.color} p-3 min-h-[200px]`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`w-2.5 h-2.5 rounded-full ${col.dot}`} />
                      <span className="text-xs font-semibold text-gray-600">{col.label}</span>
                      <span className="ml-auto text-xs bg-white text-gray-500 border border-gray-200 rounded-full px-1.5 py-0.5">
                        {colTasks.length}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {colTasks.map(task => (
                        <KanbanCard key={task.id} task={task} isAdmin={isAdmin}
                          onDragStart={() => onDragStart(task.id)}
                          onEdit={() => openEdit(task)} onDelete={() => deleteTask(task.id)} />
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'historial' && (
        <div className="space-y-4">
          <div className="rounded-xl bg-gradient-to-r from-gray-700 to-gray-900 px-5 py-4 flex items-center gap-3 shadow-sm">
            <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
              <Activity size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-white font-semibold text-base">Historial de Cambios</h2>
              <p className="text-gray-300 text-xs mt-0.5">Registro de todas las modificaciones en esta sección</p>
            </div>
          </div>
          {changeLog.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Activity size={36} className="mx-auto mb-3 opacity-20" />
              <p className="font-medium text-gray-500">Sin historial todavía</p>
              <p className="text-xs mt-1">Los cambios guardados en las pestañas especiales aparecerán aquí.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {changeLog.map(entry => (
                <div key={entry.id} className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">{entry.tab_id}</span>
                        <span className="text-xs text-gray-300">·</span>
                        <span className="text-xs text-gray-500 font-medium">{entry.changed_by_name ?? 'Usuario'}</span>
                      </div>
                      <p className="text-sm text-gray-700">{entry.summary}</p>
                    </div>
                    <time className="text-xs text-gray-400 flex-shrink-0 mt-0.5">
                      {new Date(entry.created_at).toLocaleDateString('es-CO', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                    </time>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'resultado' && (
        <div className="space-y-5">
          <div className="rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 px-5 py-4 flex items-center gap-3 shadow-sm">
            <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
              <Trophy size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-white font-semibold text-base">Resultado de la Sección</h2>
              <p className="text-emerald-100 text-xs mt-0.5">Documento final y análisis consolidado de esta sección</p>
            </div>
          </div>

          <div className="card border border-emerald-100 bg-emerald-50/30">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Edit3 size={15} className="text-emerald-600" />
                <h3 className="text-sm font-semibold text-gray-800">Análisis y conclusiones</h3>
              </div>
              {!editingResult && (
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={generateAIDraft}
                    disabled={generatingDraft}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-brand-700 hover:bg-brand-800 disabled:opacity-60 text-white transition-colors"
                    title="La IA genera un borrador inicial usando lo que ya hay en otras secciones del proyecto"
                  >
                    {generatingDraft
                      ? <><Loader size={12} className="animate-spin" /> Generando…</>
                      : <><Sparkles size={12} /> Borrador IA</>}
                  </button>
                  <button onClick={() => setEditingResult(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-100 transition-colors">
                    <Pencil size={12} /> {savedResult ? 'Editar' : 'Agregar análisis'}
                  </button>
                </div>
              )}
            </div>
            {draftError && (
              <p className="text-xs text-red-600 mb-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{draftError}</p>
            )}
            {editingResult ? (
              <div className="space-y-3">
                <textarea className="input resize-none text-sm" rows={10} value={resultText}
                  onChange={e => setResultText(e.target.value)}
                  placeholder="Escribe aquí el análisis, hallazgos y conclusiones de esta sección. Soporta Markdown..." />
                <div className="flex gap-2">
                  <button onClick={() => { setResultText(savedResult); setEditingResult(false) }} className="btn-secondary text-sm flex-1">Cancelar</button>
                  <button onClick={saveResult} disabled={savingResult || !resultText.trim()}
                    className="text-sm flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-colors disabled:opacity-50">
                    <Save size={14} /> {savingResult ? 'Guardando...' : 'Guardar resultado'}
                  </button>
                </div>
              </div>
            ) : savedResult ? (
              <div className="prose prose-sm max-w-none text-gray-700 prose-headings:text-gray-900 prose-headings:font-semibold prose-h2:text-base prose-h3:text-sm prose-strong:text-gray-800 prose-li:my-0.5 prose-p:my-1.5">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{savedResult}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">Sin análisis agregado aún.{(isAdmin || isNVC) ? ' Haz clic en "Agregar análisis" para documentar el resultado.' : ''}</p>
            )}
          </div>

          <div className="card border border-emerald-100">
            <div className="flex items-center gap-2 mb-4">
              <FileText size={15} className="text-emerald-600" />
              <h3 className="text-sm font-semibold text-gray-800">Documentos del resultado</h3>
            </div>
            <FileUpload entityType="resultado_section" entityId={sectionId} onUploaded={() => setResultFileKey(k => k + 1)} />
            <div className="mt-4"><FileList entityType="resultado_section" entityId={sectionId} refreshKey={resultFileKey} /></div>
          </div>
        </div>
      )}

      {/* ── Definición y Tamaño de la industria Tab ────────────────────── */}
      {activeTab === 'def_industria' && (
        <div className="space-y-5">
          {/* Header */}
          <div className="rounded-xl bg-gradient-to-r from-cyan-600 to-cyan-700 px-5 py-4 flex items-center justify-between gap-3 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <BarChart2 size={18} className="text-white" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-base">Definición y Tamaño de la Industria</h2>
                <p className="text-cyan-100 text-xs mt-0.5">
                  {industryDef?.tamano_ventas
                    ? `Tamaño: ${Number(industryDef.tamano_ventas).toLocaleString('es-CO')} ${industryDef.unidad ?? 'USD'}`
                    : 'Sin datos registrados aún'}
                </p>
              </div>
            </div>
            {(isAdmin || isNVC) && !phaseLocked && !editingIndustryDef && (
              <button
                onClick={() => { setEditingIndustryDef(true); setIndustryDefForm({ descripcion: industryDef?.descripcion ?? '', tamano_ventas: industryDef?.tamano_ventas ?? '', unidad: industryDef?.unidad ?? 'USD' }) }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-cyan-700 hover:bg-cyan-50 text-sm font-semibold transition-colors">
                <Edit3 size={14} /> {industryDef ? 'Editar' : 'Agregar datos'}
              </button>
            )}
          </div>

          {editingIndustryDef ? (
            <form onSubmit={saveIndustryDef} className="card space-y-4">
              <div>
                <label className="label">Descripción corta de la industria</label>
                <textarea
                  className="input resize-none"
                  rows={4}
                  value={industryDefForm.descripcion}
                  onChange={e => setIndustryDefForm(f => ({ ...f, descripcion: e.target.value }))}
                  placeholder="Describe brevemente la industria, sus características principales, dinámica y contexto relevante…"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Tamaño en ventas</label>
                  <input
                    type="number"
                    className="input"
                    value={industryDefForm.tamano_ventas}
                    onChange={e => setIndustryDefForm(f => ({ ...f, tamano_ventas: e.target.value }))}
                    placeholder="Ej: 500000000"
                    min="0"
                  />
                </div>
                <div>
                  <label className="label">Unidad</label>
                  <select
                    className="input"
                    value={industryDefForm.unidad}
                    onChange={e => setIndustryDefForm(f => ({ ...f, unidad: e.target.value }))}>
                    <option value="USD">USD</option>
                    <option value="USD millones">USD millones</option>
                    <option value="COP">COP</option>
                    <option value="COP millones">COP millones</option>
                    <option value="EUR">EUR</option>
                    <option value="EUR millones">EUR millones</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setEditingIndustryDef(false)} className="btn-secondary flex-1">Cancelar</button>
                <button type="submit" disabled={savingIndustryDef}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold transition-colors disabled:opacity-50">
                  <Save size={15} /> {savingIndustryDef ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </form>
          ) : industryDef && (industryDef.descripcion || industryDef.tamano_ventas) ? (
            <div className="card space-y-4">
              {industryDef.descripcion && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Descripción</p>
                  <p className="text-gray-700 text-sm whitespace-pre-wrap leading-relaxed">{industryDef.descripcion}</p>
                </div>
              )}
              {industryDef.tamano_ventas && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Tamaño en ventas</p>
                  <p className="text-2xl font-bold text-cyan-700">
                    {Number(industryDef.tamano_ventas).toLocaleString('es-CO')}
                    <span className="text-base font-medium text-gray-400 ml-2">{industryDef.unidad ?? 'USD'}</span>
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-14 text-gray-400 card">
              <BarChart2 size={40} className="mx-auto mb-3 opacity-20" />
              <p className="text-sm font-medium">Sin datos registrados</p>
              {(isAdmin || isNVC) && !phaseLocked && (
                <button onClick={() => setEditingIndustryDef(true)} className="mt-3 text-sm text-cyan-600 hover:underline">
                  + Agregar definición
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'files' && (
        <div className="space-y-4">
          <FileUpload entityType="section" entityId={sectionId} onUploaded={() => {}} />
          <FileList entityType="section" entityId={sectionId} />
        </div>
      )}

      {activeTab === 'comments' && (
        <Comments entityType="section" entityId={sectionId} />
      )}

      {/* Bulk delete modal — products / channels / competitors */}
      <Modal
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        title={`Eliminar ${bulkDeleteKind === 'channel' ? 'canal' : bulkDeleteKind === 'competitor' ? 'competidor' : 'producto'}`}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 leading-relaxed">
            Esto borra el {bulkDeleteKind === 'channel' ? 'canal' : bulkDeleteKind === 'competitor' ? 'competidor' : 'producto'} elegido <strong>completo</strong> — incluyendo su data de todos los años y trimestres en esta sección. Es irreversible.
          </p>
          {bulkDeleteNames.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No hay {bulkDeleteKind === 'channel' ? 'canales' : bulkDeleteKind === 'competitor' ? 'competidores' : 'productos'} para eliminar.</p>
          ) : (
            <div>
              <label className="label">Selecciona</label>
              <select
                className="input"
                value={bulkDeleteSel}
                onChange={e => setBulkDeleteSel(e.target.value)}
              >
                <option value="">— elegir —</option>
                {bulkDeleteNames.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
          )}
          {bulkDeleteErr && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{bulkDeleteErr}</p>
          )}
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => setBulkDeleteOpen(false)}
              className="btn-secondary flex-1"
              disabled={bulkDeleteBusy}
            >
              Cancelar
            </button>
            <button
              onClick={confirmBulkDelete}
              disabled={!bulkDeleteSel || bulkDeleteBusy || bulkDeleteNames.length === 0}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-medium transition-colors"
            >
              {bulkDeleteBusy ? <><Loader size={14} className="animate-spin" /> Eliminando…</> : <><Trash2 size={14} /> Eliminar definitivamente</>}
            </button>
          </div>
        </div>
      </Modal>

      {/* Task modal */}
      <Modal open={showTaskModal} onClose={() => setShowTaskModal(false)} title={editTask ? 'Editar tarea' : 'Nueva tarea'}>
        <form onSubmit={saveTask} className="space-y-4">
          <div>
            <label className="label">Nombre de la tarea *</label>
            <input className="input" required value={taskForm.name}
              onChange={e => setTaskForm(f => ({ ...f, name: e.target.value }))}
              placeholder="¿Qué hay que hacer?" />
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea className="input resize-none" rows={3} value={taskForm.description}
              onChange={e => setTaskForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Detalle de la tarea..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Responsable</label>
              <select className="input" value={taskForm.assignee_id}
                onChange={e => setTaskForm(f => ({ ...f, assignee_id: e.target.value }))}>
                <option value="">Sin asignar</option>
                {consultants.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Fecha límite</label>
              <input type="date" className="input" value={taskForm.due_date}
                onChange={e => setTaskForm(f => ({ ...f, due_date: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Prioridad</label>
              <select className="input" value={taskForm.priority}
                onChange={e => setTaskForm(f => ({ ...f, priority: e.target.value }))}>
                <option value="low">Baja</option>
                <option value="medium">Media</option>
                <option value="high">Alta</option>
              </select>
            </div>
            <div>
              <label className="label">Estado</label>
              <select className="input" value={taskForm.status}
                onChange={e => setTaskForm(f => ({ ...f, status: e.target.value }))}>
                {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </div>
          </div>
          {/* Move to another section — only when editing */}
          {editTask && (
            <div className="border-t border-gray-100 pt-4 space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Mover a otra sección</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="label">Unidad de Negocio</label>
                  <select
                    className="input"
                    value={moveBuId}
                    onChange={e => { setMoveBuId(e.target.value); setMovePhaseId(''); setMoveSectionId('') }}
                  >
                    <option value="">— Seleccionar —</option>
                    {allBUs.map(b => (
                      <option key={b.id} value={b.id}>
                        {b.id === buId ? `${b.name} (actual)` : b.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Fase / Etapa</label>
                  <select
                    className="input"
                    value={movePhaseId}
                    onChange={e => { setMovePhaseId(e.target.value); setMoveSectionId('') }}
                    disabled={!moveBuId}
                  >
                    <option value="">— Seleccionar —</option>
                    {allPhases
                      .filter(p => p.business_unit_id === moveBuId)
                      .map(p => (
                        <option key={p.id} value={p.id}>
                          {p.id === phaseId ? `${p.name} (actual)` : p.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="label">Sección</label>
                  <select
                    className="input"
                    value={moveSectionId}
                    onChange={e => setMoveSectionId(e.target.value)}
                    disabled={!movePhaseId}
                  >
                    <option value="">— Seleccionar —</option>
                    {(allPhases.find(p => p.id === movePhaseId)?.sections ?? [])
                      .sort((a, b) => (a.section_order ?? 0) - (b.section_order ?? 0))
                      .map(s => (
                        <option key={s.id} value={s.id}>
                          {s.id === sectionId ? `${s.name} (actual)` : s.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              {moveSectionId && moveSectionId !== sectionId && (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  ⚠️ La tarea se moverá a otra sección al guardar.
                </p>
              )}
            </div>
          )}

          {/* Resultado de la tarea — solo al editar */}
          {editTask && (
            <div className="border-t-2 border-emerald-200 pt-4 space-y-3 bg-emerald-50/40 -mx-1 px-1 rounded-b-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Trophy size={14} className="text-emerald-600" />
                  <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Resultado de la tarea</p>
                </div>
                {!taskEditingResult && (isAdmin || isNVC) && (
                  <button type="button" onClick={() => setTaskEditingResult(true)}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-100 transition-colors">
                    <Pencil size={11} /> {taskSavedResult ? 'Editar' : 'Agregar'}
                  </button>
                )}
              </div>
              {taskEditingResult ? (
                <div className="space-y-2">
                  <textarea className="input resize-none text-sm" rows={5} value={taskResultText}
                    onChange={e => setTaskResultText(e.target.value)}
                    placeholder="Documenta el resultado o entregable de esta tarea..." />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => { setTaskResultText(taskSavedResult); setTaskEditingResult(false) }} className="btn-secondary text-xs flex-1">Cancelar</button>
                    <button type="button" onClick={saveTaskResult} disabled={taskSavingResult || !taskResultText.trim()}
                      className="text-xs flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-colors disabled:opacity-50">
                      <Save size={12} /> {taskSavingResult ? 'Guardando...' : 'Guardar'}
                    </button>
                  </div>
                </div>
              ) : taskSavedResult ? (
                <div className="prose prose-xs max-w-none text-gray-700 prose-p:my-1 text-sm">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{taskSavedResult}</ReactMarkdown>
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">Sin resultado documentado aún.</p>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setShowTaskModal(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">
              {saving ? 'Guardando...' : editTask ? 'Guardar cambios' : 'Crear tarea'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

function TaskCard({ task, isNVC, isAdmin, onEdit, onDelete, onStatusChange }) {
  const [showComments, setShowComments] = useState(false)
  const isOverdue = task.due_date && task.status !== 'completed' && new Date(task.due_date + 'T00:00:00') < new Date()

  return (
    <div className={`card p-4 ${isOverdue ? 'border-red-200 bg-red-50/30' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <button
            onClick={() => onStatusChange(task.status === 'completed' ? 'in_progress' : 'completed')}
            className={`mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors
              ${task.status === 'completed' ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-brand-400'}`}
          >
            {task.status === 'completed' && (
              <svg viewBox="0 0 12 12" className="w-3 h-3 fill-current">
                <path d="M10 3L5 8.5 2 5.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
              </svg>
            )}
          </button>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium ${task.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-900'}`}>
              {task.name}
            </p>
            {task.description && <p className="text-xs text-gray-400 mt-0.5">{task.description}</p>}
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <StatusBadge status={task.status} />
              <PriorityBadge priority={task.priority} />
              {task.assignee && (
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <User size={11} /> {task.assignee.full_name}
                </span>
              )}
              {task.due_date && (
                <span className={`flex items-center gap-1 text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                  <Calendar size={11} />
                  {format(new Date(task.due_date + 'T00:00:00'), 'd MMM yyyy', { locale: es })}
                  {isOverdue && ' · Vencida'}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-1 flex-shrink-0 items-center">
          <button onClick={() => setShowComments(v => !v)}
            className={`p-1.5 rounded-lg transition-colors ${showComments ? 'text-brand-600 bg-brand-50' : 'text-gray-400 hover:text-brand-600 hover:bg-brand-50'}`}
            title="Ver comentarios">
            <MessageSquare size={14} />
          </button>
          {isAdmin && (
            <>
              <button onClick={onEdit} className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors">
                <Pencil size={14} />
              </button>
              <button onClick={onDelete} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                <Trash2 size={14} />
              </button>
            </>
          )}
        </div>
      </div>
      {showComments && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <Comments entityType="task" entityId={task.id} />
        </div>
      )}
    </div>
  )
}

function KanbanCard({ task, isAdmin, onDragStart, onEdit, onDelete }) {
  const isOverdue = task.due_date && task.status !== 'completed' && new Date(task.due_date + 'T00:00:00') < new Date()
  return (
    <div draggable onDragStart={onDragStart}
      className={`bg-white rounded-xl border p-3 cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md transition-shadow
        ${isOverdue ? 'border-red-200' : 'border-gray-200'}`}>
      <p className={`text-sm font-medium mb-2 ${task.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-800'}`}>
        {task.name}
      </p>
      <div className="flex items-center gap-1.5 flex-wrap">
        <PriorityBadge priority={task.priority} />
        {task.assignee && (
          <span className="flex items-center gap-1 text-xs text-gray-400">
            <User size={10} /> {task.assignee.full_name.split(' ')[0]}
          </span>
        )}
        {task.due_date && (
          <span className={`flex items-center gap-1 text-xs ${isOverdue ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
            <Calendar size={10} />
            {format(new Date(task.due_date + 'T00:00:00'), 'd MMM', { locale: es })}
          </span>
        )}
      </div>
      {isAdmin && (
        <div className="flex gap-1 mt-2 pt-2 border-t border-gray-50">
          <button onClick={onEdit} className="p-1 text-gray-300 hover:text-brand-500 rounded transition-colors"><Pencil size={12} /></button>
          <button onClick={onDelete} className="p-1 text-gray-300 hover:text-red-500 rounded transition-colors"><Trash2 size={12} /></button>
        </div>
      )}
    </div>
  )
}
